import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Rating,
  TextField,
  Button,
  Box,
  Avatar,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Modal,
  CircularProgress,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Backdrop,
  useMediaQuery,
  Fade,
  Tabs,
  Tab,
  Snackbar,
  TablePagination,
  Skeleton,
} from "@mui/material";
import {
  Person,
  Email,
  Event,
  Business,
  RateReview,
  ArrowBack,
  Close,
  Add,
  Remove,
  LocationOn,
  ThumbUp,
  Send,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import Layout from "../common/Layout";
import apiClient from "../../context/apiClient";

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
}

function TableSkeleton({ columns, rows = 5 }) {
  return (
    <TableContainer component={Paper} elevation={0} sx={{ bgcolor: "mint.main" }}>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((col, index) => (
              <TableCell key={index} sx={{ width: col.width || "auto" }}>
                <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <TableRow key={rowIndex}>
              {columns.map((col, colIndex) => (
                <TableCell key={colIndex}>
                  {col.type === "avatar" ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Skeleton variant="circular" width={48} height={48} />
                      <Skeleton variant="text" width="70%" />
                    </Box>
                  ) : col.type === "chip" ? (
                    <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: "16px" }} />
                  ) : col.type === "button" ? (
                    <Skeleton variant="rectangular" width={110} height={36} sx={{ borderRadius: 2 }} />
                  ) : (
                    <Skeleton variant="text" />
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function EvaluationForm({ interviewDetails, open, onClose, onSuccess }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [formData, setFormData] = useState({
    recommendation: "",
    technical_skills_rating: 0,
    communication_skills_rating: 0,
    cultural_fit_rating: 0,
    strengths: [""],
    weaknesses: [""],
    overall_comments: [""],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const sectionContainerStyle = {
    p: { xs: 2, sm: 3 },
    border: "1px solid",
    borderColor: "divider",
    borderRadius: "16px",
    mb: 2.5,
  };

  useEffect(() => {
    if (open) {
      setFormData({
        recommendation: "",
        technical_skills_rating: 0,
        communication_skills_rating: 0,
        cultural_fit_rating: 0,
        strengths: [""],
        weaknesses: [""],
        overall_comments: [""],
      });
      setIsSubmitting(false);
      setSubmitError(null);
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRatingChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value !== null ? Math.round(value) : 0 }));
  };

  const handleArrayFieldChange = (field, index, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].map((item, i) => (i === index ? value : item)),
    }));
  };

  const addArrayField = (field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], ""],
    }));
  };

  const removeArrayField = (field, index) => {
    if (formData[field].length > 1) {
      setFormData((prev) => ({
        ...prev,
        [field]: prev[field].filter((_, i) => i !== index),
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const submissionData = {
        ...formData,
        strengths: formData.strengths.filter((s) => s.trim()).join("; "),
        weaknesses: formData.weaknesses.filter((w) => w.trim()).join("; "),
        overall_comments: formData.overall_comments.filter((c) => c.trim()).join("; "),
        interview_id: interviewDetails.interviewId,
      };

      await apiClient.post(`/interviews/${interviewDetails.interviewId}/submit-feedback/`, submissionData);
      onSuccess("Evaluation submitted successfully!", "success");
      onClose();
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "An unexpected error occurred.";
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition BackdropComponent={Backdrop} BackdropProps={{ timeout: 500 }}>
      <Fade in={open}>
        <Box
          component={Paper}
          elevation={0}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: "90%", md: "800px" },
            maxWidth: "95vw",
            maxHeight: "90vh",
            bgcolor: "background.paper",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
              color: "primary.contrastText",
              p: { xs: 2, sm: 3 },
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
              <Avatar
                src={interviewDetails?.avatar}
                sx={{
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                  border: "3px solid rgba(255,255,255,0.3)",
                }}
              >
                {interviewDetails?.name?.charAt(0)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" component="h2" fontWeight="bold" noWrap>
                  Evaluate: {interviewDetails?.name}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }} noWrap>
                  {interviewDetails?.jobTitle}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={onClose} sx={{ color: "primary.contrastText" }}>
              <Close />
            </IconButton>
          </Box>
          <Container maxWidth="lg" sx={{ flex: 1, overflow: "auto", py: { xs: 2, sm: 3 } }}>
            <form onSubmit={handleSubmit}>
              {/* 1. Recommendation */}
              <Box sx={sectionContainerStyle}>
                <Typography variant="h6" gutterBottom>1. Recommendation</Typography>
                <FormControl fullWidth>
                  <Select name="recommendation" value={formData.recommendation} onChange={handleChange} required displayEmpty>
                    <MenuItem value="">
                      <em>Select recommendation</em>
                    </MenuItem>
                    <MenuItem value="STRONG_HIRE">Strong Hire</MenuItem>
                    <MenuItem value="HIRE">Hire</MenuItem>
                    <MenuItem value="NO_HIRE">No Hire</MenuItem>
                    <MenuItem value="NOT_SURE">Not Sure / Needs More Review</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* 2. Skills Assessment */}
              <Box sx={sectionContainerStyle}>
                <Typography variant="h6" gutterBottom>2. Skills Assessment</Typography>
                <Grid container spacing={2}>
                  {[
                    { field: "technical_skills_rating", label: "Technical", icon: "💻" },
                    { field: "communication_skills_rating", label: "Communication", icon: "💬" },
                    { field: "cultural_fit_rating", label: "Cultural Fit", icon: "🤝" },
                  ].map((rating) => (
                    <Grid item xs={12} sm={4} key={rating.field}>
                      <Box sx={{ textAlign: "center", p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: "background.default" }}>
                        <Typography variant="h4" sx={{ mb: 1 }}>{rating.icon}</Typography>
                        <Typography variant="body1" fontWeight="medium" gutterBottom>{rating.label}</Typography>
                        <Rating name={rating.field} value={formData[rating.field]} onChange={(e, val) => handleRatingChange(rating.field, val)} size="medium" />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* 3. Key Strengths */}
              <Box sx={sectionContainerStyle}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2 }}>
                  <Typography variant="h6">3. Key Strengths</Typography>
                  <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => addArrayField("strengths")} disabled={formData.strengths[formData.strengths.length - 1].trim() === ""}>Add</Button>
                </Box>
                {formData.strengths.map((item, index) => (
                  <Box key={index} sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "flex-start" }}>
                    <TextField fullWidth multiline rows={2} placeholder="What impressed you?" value={item} onChange={(e) => handleArrayFieldChange("strengths", index, e.target.value)} variant="outlined" />
                    {formData.strengths.length > 1 && (
                      <IconButton onClick={() => removeArrayField("strengths", index)} color="error" sx={{ mt: 1 }}><Remove /></IconButton>
                    )}
                  </Box>
                ))}
              </Box>

              {/* 4. Areas for Improvement */}
              <Box sx={sectionContainerStyle}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2 }}>
                  <Typography variant="h6">4. Areas for Improvement</Typography>
                  <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => addArrayField("weaknesses")} disabled={formData.weaknesses[formData.weaknesses.length - 1].trim() === ""}>Add</Button>
                </Box>
                {formData.weaknesses.map((item, index) => (
                  <Box key={index} sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "flex-start" }}>
                    <TextField fullWidth multiline rows={2} placeholder="What could be developed?" value={item} onChange={(e) => handleArrayFieldChange("weaknesses", index, e.target.value)} variant="outlined" />
                    {formData.weaknesses.length > 1 && (
                      <IconButton onClick={() => removeArrayField("weaknesses", index)} color="error" sx={{ mt: 1 }}><Remove /></IconButton>
                    )}
                  </Box>
                ))}
              </Box>

              {/* 5. Overall Assessment */}
              <Box sx={sectionContainerStyle}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2 }}>
                  <Typography variant="h6">5. Overall Assessment</Typography>
                  <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => addArrayField("overall_comments")} disabled={formData.overall_comments[formData.overall_comments.length - 1].trim() === ""}>Add</Button>
                </Box>
                {formData.overall_comments.map((item, index) => (
                  <Box key={index} sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "flex-start" }}>
                    <TextField fullWidth multiline rows={2} placeholder="Provide comprehensive evaluation..." value={item} onChange={(e) => handleArrayFieldChange("overall_comments", index, e.target.value)} variant="outlined" />
                    {formData.overall_comments.length > 1 && (
                      <IconButton onClick={() => removeArrayField("overall_comments", index)} color="error" sx={{ mt: 1 }}><Remove /></IconButton>
                    )}
                  </Box>
                ))}
              </Box>

              {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3, pt: 3, borderTop: "1px solid", borderColor: "divider" }}>
                <Button onClick={onClose} variant="outlined" size={isMobile ? "medium" : "large"}>Cancel</Button>
                <Button type="submit" variant="contained" color="primary" size={isMobile ? "medium" : "large"} disabled={isSubmitting}>
                  {isSubmitting ? <CircularProgress size={24} /> : "Submit Evaluation"}
                </Button>
              </Box>
            </form>
          </Container>
        </Box>
      </Fade>
    </Modal>
  );
}


function JobDetailsView({ candidate, onBack, onEvaluate }) {
  return (
    <Box>
      <Button variant="outlined" startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: { xs: 2, sm: 3 } }}>
        Back to Candidates
      </Button>
      <Card sx={{ mb: { xs: 2, sm: 3 }, bgcolor: "mint.main" }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
            <Grid item>
              <Avatar src={candidate.avatar} sx={{ width: { xs: 60, sm: 80 }, height: { xs: 60, sm: 80 }, border: "3px solid", borderColor: "primary.main" }} />
            </Grid>
            <Grid item xs>
              <Typography variant="h5" fontWeight="bold">{candidate.name}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                <Email fontSize="small" /> {candidate.email}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <LocationOn fontSize="small" /> {candidate.location}
              </Typography>
            </Grid>
            <Grid item xs={12} sm="auto" sx={{ mt: { xs: 2, sm: 0 } }}>
              <Chip label={`${candidate.interviews.length} Scheduled Interview${candidate.interviews.length !== 1 ? "s" : ""}`} color="primary" variant="outlined" />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {candidate.interviews.map((interview) => (
          <Grid item xs={12} sm={6} md={4} key={interview.id}>
            <Card sx={{ bgcolor: "mint.main", height: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" fontWeight="600" gutterBottom>{interview.job_title}</Typography>
                <Box sx={{ my: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                    <Business fontSize="small" /> {interview.interview_mode}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Event fontSize="small" /> {new Date(`${interview.interview_date}T${interview.interview_time}`).toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ mt: "auto" }}>
                  <Button variant="contained" color="primary" startIcon={<RateReview />} onClick={() => onEvaluate(candidate, interview)} fullWidth>
                    Evaluate
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function ScheduledInterviewsTab({ onEvaluateClick }) {
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [selectedCandidateDetails, setSelectedCandidateDetails] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    useEffect(() => {
        const fetchAndProcessInterviews = async () => {
          try {
            const response = await apiClient.get("/interviews/");
            const data = response.data;
            const filtered = data.filter((interview) => interview.status.toLowerCase() === "scheduled" || interview.status.toLowerCase() === "rescheduled");
            const groupedByCandidate = filtered.reduce((acc, interview) => {
              const { candidate_name, profile_photo, talent_current_location } = interview;
              if (!acc[candidate_name]) {
                acc[candidate_name] = {
                  id: candidate_name.replace(/\s+/g, "-").toLowerCase(),
                  name: candidate_name,
                  email: `${candidate_name.replace(/\s+/g, ".").toLowerCase()}@example.com`,
                  avatar: profile_photo || `https://i.pravatar.cc/150?u=${encodeURIComponent(candidate_name)}`,
                  location: talent_current_location || "Location not specified",
                  interviews: [],
                };
              }
              acc[candidate_name].interviews.push(interview);
              return acc;
            }, {});
            setCandidates(Object.values(groupedByCandidate));
          } catch (e) {
            setError(e.response?.data?.detail || e.message);
          } finally {
            setLoading(false);
          }
        };
        fetchAndProcessInterviews();
      }, []);

    if (loading) return <TableSkeleton columns={[{ type: "avatar", width: "40%" }, { type: "text", width: "30%" }, { type: "button", width: "30%" }]} rows={rowsPerPage} />;
    if (error) return <Alert severity="error">Error fetching interviews: {error}</Alert>;
    if (selectedCandidateDetails) return <JobDetailsView candidate={selectedCandidateDetails} onBack={() => setSelectedCandidateDetails(null)} onEvaluate={onEvaluateClick} />;

    return (
        <Paper elevation={0} sx={{ bgcolor: "mint.main" }}>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Candidate</TableCell>
                            {!isMobile && <TableCell>Interviews</TableCell>}
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {candidates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((candidate) => (
                            <TableRow key={candidate.id} sx={{ "&:hover": { bgcolor: "action.hover" } }}>
                                <TableCell>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                        <Avatar src={candidate.avatar} />
                                        <Typography variant="body1" fontWeight="600">{candidate.name}</Typography>
                                    </Box>
                                </TableCell>
                                {!isMobile && (
                                    <TableCell>
                                        <Chip label={`${candidate.interviews.length} interview${candidate.interviews.length !== 1 ? "s" : ""}`} variant="outlined" size="small" />
                                    </TableCell>
                                )}
                                <TableCell align="right">
                                    <Button variant="contained" color="primary" onClick={() => setSelectedCandidateDetails(candidate)}>
                                        {isMobile ? "View" : "View Details"}
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={candidates.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            />
        </Paper>
    );
}

function ExtendOfferTab({ onAlert }) {
    const [completedCandidates, setCompletedCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [extendingId, setExtendingId] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
    const fetchCompletedCandidates = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get("/interviews/completed-candidates/");
        const eligibleCandidates = response.data.filter((candidate) => candidate.application_status !== "HIRED");
        setCompletedCandidates(eligibleCandidates);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    };
  
    useEffect(() => {
      fetchCompletedCandidates();
    }, []);
  
    const handleExtendOffer = async (applicationId) => {
      setExtendingId(applicationId);
      try {
        await apiClient.post(`/applications/${applicationId}/offer-extend/`);
        onAlert("Offer extended successfully!", "success");
        await fetchCompletedCandidates();
      } catch (err) {
        const errorMessage = err.response?.data?.detail || err.message || "Failed to extend offer.";
        onAlert(errorMessage, "error");
      } finally {
        setExtendingId(null);
      }
    };

    if (loading) return <TableSkeleton columns={[{ type: "text" }, { type: "text" }, { type: "button" }]} rows={rowsPerPage}/>;
    if (error) return <Alert severity="error">Error fetching candidates: {error}</Alert>;

    return (
        <Paper elevation={0} sx={{ bgcolor: "mint.main" }}>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Candidate</TableCell>
                            {!isMobile && <TableCell>Job Title</TableCell>}
                            {!isMobile && <TableCell>Status</TableCell>}
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {completedCandidates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((candidate) => (
                            <TableRow key={candidate.application_id}>
                                <TableCell>{candidate.talent_name}</TableCell>
                                {!isMobile && <TableCell>{candidate.job_posting_title}</TableCell>}
                                {!isMobile && <TableCell><Chip label={candidate.application_status.replace(/_/g, " ")} size="small" /></TableCell>}
                                <TableCell align="right">
                                    <Button 
                                        variant="contained" 
                                        color="primary" 
                                        startIcon={extendingId === candidate.application_id ? null : <Send />}
                                        disabled={extendingId === candidate.application_id || candidate.application_status === "OFFER_EXTENDED"}
                                        onClick={() => handleExtendOffer(candidate.application_id)}
                                    >
                                        {extendingId === candidate.application_id ? <CircularProgress size={24} color="inherit" /> : isMobile ? "Offer" : "Extend Offer"}
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={completedCandidates.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            />
        </Paper>
    );
}

function HiredCandidatesTab() {
    const [hiredCandidates, setHiredCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    
    useEffect(() => {
        const fetchHiredCandidates = async () => {
          try {
            const response = await apiClient.get("/interviews/completed-candidates/");
            const filteredData = response.data.filter((candidate) => candidate.application_status === "HIRED");
            setHiredCandidates(filteredData);
          } catch (e) {
            setError(e.response?.data?.detail || e.message);
          } finally {
            setLoading(false);
          }
        };
    
        fetchHiredCandidates();
      }, []);

    if (loading) return <TableSkeleton columns={[{ type: "text" }, { type: "text" }, { type: "text" }]} rows={rowsPerPage}/>;
    if (error) return <Alert severity="error">Error fetching hired candidates: {error}</Alert>;

    return (
        <Paper elevation={0} sx={{ bgcolor: "mint.main" }}>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Candidate</TableCell>
                            <TableCell>Job Title</TableCell>
                            {!isMobile && <TableCell>Tech Rating</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {hiredCandidates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((candidate) => (
                            <TableRow key={candidate.application_id}>
                                <TableCell>{candidate.talent_name}</TableCell>
                                <TableCell>{candidate.job_posting_title}</TableCell>
                                {!isMobile && <TableCell>{candidate.technical_skills_rating || "N/A"}</TableCell>}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={hiredCandidates.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            />
        </Paper>
    );
}


export default function EvaluationsPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleEvaluateClick = (candidate, interview) => {
    setSelectedInterview({
      name: candidate.name,
      email: candidate.email,
      avatar: candidate.avatar,
      interviewId: interview.id,
      jobTitle: interview.job_title,
    });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedInterview(null);
  };

  const handleAlert = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Layout title="Evaluations">
      <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
        <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 }, bgcolor: "mint.main" }}>
            <Typography variant={isMobile ? "h5" : "h4"} component="h1" color="primary.main" fontWeight="bold">
              Candidate Evaluations
            </Typography>
          </Paper>
          <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "background.paper" }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              aria-label="evaluation tabs"
              variant={isMobile ? "fullWidth" : "standard"}
              centered={!isMobile}
            >
              <Tab label="Scheduled" />
              <Tab label="Extend Offer" />
              <Tab label="Hired" />
            </Tabs>
          </Box>
          <TabPanel value={tabValue} index={0}>
            <ScheduledInterviewsTab onEvaluateClick={handleEvaluateClick} />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <ExtendOfferTab onAlert={handleAlert} />
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <HiredCandidatesTab />
          </TabPanel>
        </Container>
      </Box>
      {selectedInterview && <EvaluationForm open={modalOpen} onClose={handleCloseModal} interviewDetails={selectedInterview} onSuccess={handleAlert} />}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
  
}


// import React, { useState, useEffect } from "react";
// import {
//   Container,
//   Paper,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   Rating,
//   TextField,
//   Button,
//   Box,
//   Avatar,
//   FormControl,
//   Select,
//   MenuItem,
//   Chip,
//   Modal,
//   CircularProgress,
//   Alert,
//   IconButton,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Backdrop,
//   useMediaQuery,
//   Fade,
//   Tabs,
//   Tab,
//   Snackbar,
//   TablePagination,
//   Skeleton,
// } from "@mui/material";
// import {
//   Person,
//   Email,
//   Event,
//   Business,
//   RateReview,
//   ArrowBack,
//   Close,
//   Add,
//   Remove,
//   LocationOn,
//   ThumbUp,
//   Send,
// } from "@mui/icons-material";
// import { useTheme } from "@mui/material/styles";
// import Layout from "../common/Layout";
// import apiClient from "../../context/apiClient";

// function TabPanel(props) {
//   const { children, value, index, ...other } = props;
//   return (
//     <div
//       role="tabpanel"
//       hidden={value !== index}
//       id={`simple-tabpanel-${index}`}
//       aria-labelledby={`simple-tab-${index}`}
//       {...other}
//     >
//       {value === index && <Box sx={{ pt: { xs: 2, sm: 3 } }}>{children}</Box>}
//     </div>
//   );
// }

// function TableSkeleton({ columns, rows = 5 }) {
//   return (
//     <TableContainer component={Paper} elevation={0} sx={{ bgcolor: "mint.main" }}>
//       <Table>
//         <TableHead>
//           <TableRow>
//             {columns.map((col, index) => (
//               <TableCell key={index} sx={{ width: col.width || "auto" }}>
//                 <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
//               </TableCell>
//             ))}
//           </TableRow>
//         </TableHead>
//         <TableBody>
//           {Array.from({ length: rows }).map((_, rowIndex) => (
//             <TableRow key={rowIndex}>
//               {columns.map((col, colIndex) => (
//                 <TableCell key={colIndex}>
//                   {col.type === "avatar" ? (
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                       <Skeleton variant="circular" width={48} height={48} />
//                       <Skeleton variant="text" width="70%" />
//                     </Box>
//                   ) : col.type === "chip" ? (
//                     <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: "16px" }} />
//                   ) : col.type === "button" ? (
//                     <Skeleton variant="rectangular" width={110} height={36} sx={{ borderRadius: 2 }} />
//                   ) : (
//                     <Skeleton variant="text" />
//                   )}
//                 </TableCell>
//               ))}
//             </TableRow>
//           ))}
//         </TableBody>
//       </Table>
//     </TableContainer>
//   );
// }

// function EvaluationForm({ interviewDetails, open, onClose, onSuccess }) {
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
//   const [formData, setFormData] = useState({
//     recommendation: "",
//     technical_skills_rating: 0,
//     communication_skills_rating: 0,
//     cultural_fit_rating: 0,
//     strengths: [""],
//     weaknesses: [""],
//     overall_comments: [""],
//   });
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [submitError, setSubmitError] = useState(null);

//   useEffect(() => {
//     if (open) {
//       setFormData({
//         recommendation: "",
//         technical_skills_rating: 0,
//         communication_skills_rating: 0,
//         cultural_fit_rating: 0,
//         strengths: [""],
//         weaknesses: [""],
//         overall_comments: [""],
//       });
//       setIsSubmitting(false);
//       setSubmitError(null);
//     }
//   }, [open]);

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({ ...prev, [name]: value }));
//   };

//   const handleRatingChange = (field, value) => {
//     setFormData((prev) => ({ ...prev, [field]: value !== null ? Math.round(value) : 0 }));
//   };

//   const handleArrayFieldChange = (field, index, value) => {
//     setFormData((prev) => ({
//       ...prev,
//       [field]: prev[field].map((item, i) => (i === index ? value : item)),
//     }));
//   };

//   const addArrayField = (field) => {
//     setFormData((prev) => ({
//       ...prev,
//       [field]: [...prev[field], ""],
//     }));
//   };

//   const removeArrayField = (field, index) => {
//     if (formData[field].length > 1) {
//       setFormData((prev) => ({
//         ...prev,
//         [field]: prev[field].filter((_, i) => i !== index),
//       }));
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setIsSubmitting(true);
//     setSubmitError(null);
//     try {
//       const submissionData = {
//         ...formData,
//         strengths: formData.strengths.filter((s) => s.trim()).join("; "),
//         weaknesses: formData.weaknesses.filter((w) => w.trim()).join("; "),
//         overall_comments: formData.overall_comments.filter((c) => c.trim()).join("; "),
//         interview_id: interviewDetails.interviewId,
//       };

//       await apiClient.post(`/interviews/${interviewDetails.interviewId}/submit-feedback/`, submissionData);
//       onSuccess("Evaluation submitted successfully!", "success");
//       onClose();
//     } catch (error) {
//       const errorMessage = error.response?.data?.message || error.message || "An unexpected error occurred.";
//       setSubmitError(errorMessage);
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   return (
//     <Modal open={open} onClose={onClose} closeAfterTransition BackdropComponent={Backdrop} BackdropProps={{ timeout: 500 }}>
//       <Fade in={open}>
//         <Box
//           component={Paper}
//           elevation={0}
//           sx={{
//             position: "absolute",
//             top: "50%",
//             left: "50%",
//             transform: "translate(-50%, -50%)",
//             width: { xs: "95%", sm: "90%", md: "800px" },
//             maxWidth: "95vw",
//             maxHeight: "90vh",
//             bgcolor: "background.paper",
//             overflow: "hidden",
//             display: "flex",
//             flexDirection: "column",
//           }}
//         >
//           <Box
//             sx={{
//               background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
//               color: "primary.contrastText",
//               p: { xs: 2, sm: 3 },
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "space-between",
//               flexShrink: 0,
//             }}
//           >
//             <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
//               <Avatar
//                 src={interviewDetails?.avatar}
//                 sx={{
//                   width: { xs: 48, sm: 56 },
//                   height: { xs: 48, sm: 56 },
//                   border: "3px solid rgba(255,255,255,0.3)",
//                 }}
//               >
//                 {interviewDetails?.name?.charAt(0)}
//               </Avatar>
//               <Box sx={{ minWidth: 0 }}>
//                 <Typography variant="h6" component="h2" fontWeight="bold" noWrap>
//                   Evaluate: {interviewDetails?.name}
//                 </Typography>
//                 <Typography variant="body2" sx={{ opacity: 0.9 }} noWrap>
//                   {interviewDetails?.jobTitle}
//                 </Typography>
//               </Box>
//             </Box>
//             <IconButton onClick={onClose} sx={{ color: "primary.contrastText" }}>
//               <Close />
//             </IconButton>
//           </Box>
//           <Container maxWidth="lg" sx={{ flex: 1, overflow: "auto", py: { xs: 2, sm: 3 } }}>
//             <form onSubmit={handleSubmit}>
//               <Grid container spacing={{ xs: 2, sm: 3 }}>
//                 <Grid item xs={12}>
//                   <Card sx={{ p: { xs: 2, sm: 3 }, bgcolor: "mint.main" }}>
//                     <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
//                       <ThumbUp color="primary" />
//                       Recommendation
//                     </Typography>
//                     <FormControl fullWidth>
//                       <Select name="recommendation" value={formData.recommendation} onChange={handleChange} required>
//                         <MenuItem value="">
//                           <em>Select recommendation</em>
//                         </MenuItem>
//                         <MenuItem value="STRONG_HIRE">Strong Hire</MenuItem>
//                         <MenuItem value="HIRE">Hire</MenuItem>
//                         <MenuItem value="NO_HIRE">No Hire</MenuItem>
//                         <MenuItem value="NOT_SURE">Not Sure / Needs More Review</MenuItem>
//                       </Select>
//                     </FormControl>
//                   </Card>
//                 </Grid>
//                 <Grid item xs={12}>
//                   <Card sx={{ p: { xs: 2, sm: 3 }, bgcolor: "mint.main" }}>
//                     <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
//                       Skills Assessment
//                     </Typography>
//                     <Grid container spacing={2}>
//                       {[
//                         { field: "technical_skills_rating", label: "Technical", icon: "💻" },
//                         { field: "communication_skills_rating", label: "Communication", icon: "💬" },
//                         { field: "cultural_fit_rating", label: "Cultural Fit", icon: "🤝" },
//                       ].map((rating) => (
//                         <Grid item xs={12} sm={4} key={rating.field}>
//                           <Box sx={{ textAlign: "center", p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: "background.default" }}>
//                             <Typography variant="h4" sx={{ mb: 1 }}>{rating.icon}</Typography>
//                             <Typography variant="body1" fontWeight="medium" gutterBottom>{rating.label}</Typography>
//                             <Rating name={rating.field} value={formData[rating.field]} onChange={(e, val) => handleRatingChange(rating.field, val)} size="medium" />
//                           </Box>
//                         </Grid>
//                       ))}
//                     </Grid>
//                   </Card>
//                 </Grid>
//                 {[
//                   { field: "strengths", label: "Key Strengths", placeholder: "What impressed you?", icon: "⭐" },
//                   { field: "weaknesses", label: "Areas for Improvement", placeholder: "What could be developed?", icon: "📈" },
//                   { field: "overall_comments", label: "Overall Assessment", placeholder: "Provide comprehensive evaluation...", icon: "📝" },
//                 ].map((section) => (
//                   <Grid item xs={12} key={section.field}>
//                     <Card sx={{ p: { xs: 2, sm: 3 }, bgcolor: "mint.main" }}>
//                       <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2 }}>
//                         <Typography variant="h6">{section.label}</Typography>
//                         <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => addArrayField(section.field)}>Add</Button>
//                       </Box>
//                       {formData[section.field].map((item, index) => (
//                         <Box key={index} sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "flex-start" }}>
//                           <TextField fullWidth multiline rows={2} placeholder={section.placeholder} value={item} onChange={(e) => handleArrayFieldChange(section.field, index, e.target.value)} variant="outlined" />
//                           {formData[section.field].length > 1 && (
//                             <IconButton onClick={() => removeArrayField(section.field, index)} color="error" sx={{ mt: 1 }}>
//                               <Remove />
//                             </IconButton>
//                           )}
//                         </Box>
//                       ))}
//                     </Card>
//                   </Grid>
//                 ))}
//                 {submitError && <Grid item xs={12}><Alert severity="error">{submitError}</Alert></Grid>}
//               </Grid>
//               <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3, pt: 3, borderTop: "1px solid", borderColor: "divider" }}>
//                 <Button onClick={onClose} variant="outlined" size={isMobile ? "medium" : "large"}>Cancel</Button>
//                 <Button type="submit" variant="contained" color="primary" size={isMobile ? "medium" : "large"} disabled={isSubmitting}>
//                   {isSubmitting ? <CircularProgress size={24} /> : "Submit Evaluation"}
//                 </Button>
//               </Box>
//             </form>
//           </Container>
//         </Box>
//       </Fade>
//     </Modal>
//   );
// }

// function JobDetailsView({ candidate, onBack, onEvaluate }) {
//   return (
//     <Box>
//       <Button variant="outlined" startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: { xs: 2, sm: 3 } }}>
//         Back to Candidates
//       </Button>
//       <Card sx={{ mb: { xs: 2, sm: 3 }, bgcolor: "mint.main" }}>
//         <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
//           <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
//             <Grid item>
//               <Avatar src={candidate.avatar} sx={{ width: { xs: 60, sm: 80 }, height: { xs: 60, sm: 80 }, border: "3px solid", borderColor: "primary.main" }} />
//             </Grid>
//             <Grid item xs>
//               <Typography variant="h5" fontWeight="bold">{candidate.name}</Typography>
//               <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
//                 <Email fontSize="small" /> {candidate.email}
//               </Typography>
//               <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
//                 <LocationOn fontSize="small" /> {candidate.location}
//               </Typography>
//             </Grid>
//             <Grid item xs={12} sm="auto" sx={{ mt: { xs: 2, sm: 0 } }}>
//               <Chip label={`${candidate.interviews.length} Scheduled Interview${candidate.interviews.length !== 1 ? "s" : ""}`} color="primary" variant="outlined" />
//             </Grid>
//           </Grid>
//         </CardContent>
//       </Card>

//       <Grid container spacing={{ xs: 2, sm: 3 }}>
//         {candidate.interviews.map((interview) => (
//           <Grid item xs={12} sm={6} md={4} key={interview.id}>
//             <Card sx={{ bgcolor: "mint.main", height: "100%", display: "flex", flexDirection: "column" }}>
//               <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", p: { xs: 2, sm: 3 } }}>
//                 <Typography variant="h6" fontWeight="600" gutterBottom>{interview.job_title}</Typography>
//                 <Box sx={{ my: 2 }}>
//                   <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
//                     <Business fontSize="small" /> {interview.interview_mode}
//                   </Typography>
//                   <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
//                     <Event fontSize="small" /> {new Date(`${interview.interview_date}T${interview.interview_time}`).toLocaleString()}
//                   </Typography>
//                 </Box>
//                 <Box sx={{ mt: "auto" }}>
//                   <Button variant="contained" color="primary" startIcon={<RateReview />} onClick={() => onEvaluate(candidate, interview)} fullWidth>
//                     Evaluate
//                   </Button>
//                 </Box>
//               </CardContent>
//             </Card>
//           </Grid>
//         ))}
//       </Grid>
//     </Box>
//   );
// }

// function ScheduledInterviewsTab({ onEvaluateClick }) {
//     const [candidates, setCandidates] = useState([]);
//     const [loading, setLoading] = useState(true);
//     const [error, setError] = useState(null);
//     const [page, setPage] = useState(0);
//     const [rowsPerPage, setRowsPerPage] = useState(5);
//     const [selectedCandidateDetails, setSelectedCandidateDetails] = useState(null);
//     const theme = useTheme();
//     const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

//     useEffect(() => {
//         const fetchAndProcessInterviews = async () => {
//           try {
//             const response = await apiClient.get("/interviews/");
//             const data = response.data;
//             const filtered = data.filter((interview) => interview.status.toLowerCase() === "scheduled" || interview.status.toLowerCase() === "rescheduled");
//             const groupedByCandidate = filtered.reduce((acc, interview) => {
//               const { candidate_name, profile_photo, talent_current_location } = interview;
//               if (!acc[candidate_name]) {
//                 acc[candidate_name] = {
//                   id: candidate_name.replace(/\s+/g, "-").toLowerCase(),
//                   name: candidate_name,
//                   email: `${candidate_name.replace(/\s+/g, ".").toLowerCase()}@example.com`,
//                   avatar: profile_photo || `https://i.pravatar.cc/150?u=${encodeURIComponent(candidate_name)}`,
//                   location: talent_current_location || "Location not specified",
//                   interviews: [],
//                 };
//               }
//               acc[candidate_name].interviews.push(interview);
//               return acc;
//             }, {});
//             setCandidates(Object.values(groupedByCandidate));
//           } catch (e) {
//             setError(e.response?.data?.detail || e.message);
//           } finally {
//             setLoading(false);
//           }
//         };
//         fetchAndProcessInterviews();
//       }, []);

//     if (loading) return <TableSkeleton columns={[{ type: "avatar", width: "40%" }, { type: "text", width: "30%" }, { type: "button", width: "30%" }]} rows={rowsPerPage} />;
//     if (error) return <Alert severity="error">Error fetching interviews: {error}</Alert>;
//     if (selectedCandidateDetails) return <JobDetailsView candidate={selectedCandidateDetails} onBack={() => setSelectedCandidateDetails(null)} onEvaluate={onEvaluateClick} />;

//     return (
//         <Paper elevation={0} sx={{ bgcolor: "mint.main" }}>
//             <TableContainer>
//                 <Table>
//                     <TableHead>
//                         <TableRow>
//                             <TableCell>Candidate</TableCell>
//                             {!isMobile && <TableCell>Interviews</TableCell>}
//                             <TableCell align="right">Actions</TableCell>
//                         </TableRow>
//                     </TableHead>
//                     <TableBody>
//                         {candidates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((candidate) => (
//                             <TableRow key={candidate.id} sx={{ "&:hover": { bgcolor: "action.hover" } }}>
//                                 <TableCell>
//                                     <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                                         <Avatar src={candidate.avatar} />
//                                         <Typography variant="body1" fontWeight="600">{candidate.name}</Typography>
//                                     </Box>
//                                 </TableCell>
//                                 {!isMobile && (
//                                     <TableCell>
//                                         <Chip label={`${candidate.interviews.length} interview${candidate.interviews.length !== 1 ? "s" : ""}`} variant="outlined" size="small" />
//                                     </TableCell>
//                                 )}
//                                 <TableCell align="right">
//                                     <Button variant="contained" color="primary" onClick={() => setSelectedCandidateDetails(candidate)}>
//                                         {isMobile ? "View" : "View Details"}
//                                     </Button>
//                                 </TableCell>
//                             </TableRow>
//                         ))}
//                     </TableBody>
//                 </Table>
//             </TableContainer>
//             <TablePagination
//                 rowsPerPageOptions={[5, 10, 25]}
//                 component="div"
//                 count={candidates.length}
//                 rowsPerPage={rowsPerPage}
//                 page={page}
//                 onPageChange={(e, newPage) => setPage(newPage)}
//                 onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
//             />
//         </Paper>
//     );
// }

// function ExtendOfferTab({ onAlert }) {
//     const [completedCandidates, setCompletedCandidates] = useState([]);
//     const [loading, setLoading] = useState(true);
//     const [error, setError] = useState(null);
//     const [extendingId, setExtendingId] = useState(null);
//     const [page, setPage] = useState(0);
//     const [rowsPerPage, setRowsPerPage] = useState(5);
//     const theme = useTheme();
//     const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
//     const fetchCompletedCandidates = async () => {
//       setLoading(true);
//       try {
//         const response = await apiClient.get("/interviews/completed-candidates/");
//         const eligibleCandidates = response.data.filter((candidate) => candidate.application_status !== "HIRED");
//         setCompletedCandidates(eligibleCandidates);
//       } catch (e) {
//         setError(e.response?.data?.detail || e.message);
//       } finally {
//         setLoading(false);
//       }
//     };
  
//     useEffect(() => {
//       fetchCompletedCandidates();
//     }, []);
  
//     const handleExtendOffer = async (applicationId) => {
//       setExtendingId(applicationId);
//       try {
//         await apiClient.post(`/applications/${applicationId}/offer-extend/`);
//         onAlert("Offer extended successfully!", "success");
//         await fetchCompletedCandidates();
//       } catch (err) {
//         const errorMessage = err.response?.data?.detail || err.message || "Failed to extend offer.";
//         onAlert(errorMessage, "error");
//       } finally {
//         setExtendingId(null);
//       }
//     };

//     if (loading) return <TableSkeleton columns={[{ type: "text" }, { type: "text" }, { type: "button" }]} rows={rowsPerPage}/>;
//     if (error) return <Alert severity="error">Error fetching candidates: {error}</Alert>;

//     return (
//         <Paper elevation={0} sx={{ bgcolor: "mint.main" }}>
//             <TableContainer>
//                 <Table>
//                     <TableHead>
//                         <TableRow>
//                             <TableCell>Candidate</TableCell>
//                             {!isMobile && <TableCell>Job Title</TableCell>}
//                             {!isMobile && <TableCell>Status</TableCell>}
//                             <TableCell align="right">Actions</TableCell>
//                         </TableRow>
//                     </TableHead>
//                     <TableBody>
//                         {completedCandidates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((candidate) => (
//                             <TableRow key={candidate.application_id}>
//                                 <TableCell>{candidate.talent_name}</TableCell>
//                                 {!isMobile && <TableCell>{candidate.job_posting_title}</TableCell>}
//                                 {!isMobile && <TableCell><Chip label={candidate.application_status.replace(/_/g, " ")} size="small" /></TableCell>}
//                                 <TableCell align="right">
//                                     <Button 
//                                         variant="contained" 
//                                         color="primary" 
//                                         startIcon={extendingId === candidate.application_id ? null : <Send />}
//                                         disabled={extendingId === candidate.application_id || candidate.application_status === "OFFER_EXTENDED"}
//                                         onClick={() => handleExtendOffer(candidate.application_id)}
//                                     >
//                                         {extendingId === candidate.application_id ? <CircularProgress size={24} color="inherit" /> : isMobile ? "Offer" : "Extend Offer"}
//                                     </Button>
//                                 </TableCell>
//                             </TableRow>
//                         ))}
//                     </TableBody>
//                 </Table>
//             </TableContainer>
//             <TablePagination
//                 rowsPerPageOptions={[5, 10, 25]}
//                 component="div"
//                 count={completedCandidates.length}
//                 rowsPerPage={rowsPerPage}
//                 page={page}
//                 onPageChange={(e, newPage) => setPage(newPage)}
//                 onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
//             />
//         </Paper>
//     );
// }

// function HiredCandidatesTab() {
//     const [hiredCandidates, setHiredCandidates] = useState([]);
//     const [loading, setLoading] = useState(true);
//     const [error, setError] = useState(null);
//     const [page, setPage] = useState(0);
//     const [rowsPerPage, setRowsPerPage] = useState(5);
//     const theme = useTheme();
//     const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    
//     useEffect(() => {
//         const fetchHiredCandidates = async () => {
//           try {
//             const response = await apiClient.get("/interviews/completed-candidates/");
//             const filteredData = response.data.filter((candidate) => candidate.application_status === "HIRED");
//             setHiredCandidates(filteredData);
//           } catch (e) {
//             setError(e.response?.data?.detail || e.message);
//           } finally {
//             setLoading(false);
//           }
//         };
    
//         fetchHiredCandidates();
//       }, []);

//     if (loading) return <TableSkeleton columns={[{ type: "text" }, { type: "text" }, { type: "text" }]} rows={rowsPerPage}/>;
//     if (error) return <Alert severity="error">Error fetching hired candidates: {error}</Alert>;

//     return (
//         <Paper elevation={0} sx={{ bgcolor: "mint.main" }}>
//             <TableContainer>
//                 <Table>
//                     <TableHead>
//                         <TableRow>
//                             <TableCell>Candidate</TableCell>
//                             <TableCell>Job Title</TableCell>
//                             {!isMobile && <TableCell>Tech Rating</TableCell>}
//                         </TableRow>
//                     </TableHead>
//                     <TableBody>
//                         {hiredCandidates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((candidate) => (
//                             <TableRow key={candidate.application_id}>
//                                 <TableCell>{candidate.talent_name}</TableCell>
//                                 <TableCell>{candidate.job_posting_title}</TableCell>
//                                 {!isMobile && <TableCell>{candidate.technical_skills_rating || "N/A"}</TableCell>}
//                             </TableRow>
//                         ))}
//                     </TableBody>
//                 </Table>
//             </TableContainer>
//             <TablePagination
//                 rowsPerPageOptions={[5, 10, 25]}
//                 component="div"
//                 count={hiredCandidates.length}
//                 rowsPerPage={rowsPerPage}
//                 page={page}
//                 onPageChange={(e, newPage) => setPage(newPage)}
//                 onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
//             />
//         </Paper>
//     );
// }


// export default function EvaluationsPage() {
//   const [modalOpen, setModalOpen] = useState(false);
//   const [selectedInterview, setSelectedInterview] = useState(null);
//   const [tabValue, setTabValue] = useState(0);
//   const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

//   const handleTabChange = (event, newValue) => {
//     setTabValue(newValue);
//   };

//   const handleEvaluateClick = (candidate, interview) => {
//     setSelectedInterview({
//       name: candidate.name,
//       email: candidate.email,
//       avatar: candidate.avatar,
//       interviewId: interview.id,
//       jobTitle: interview.job_title,
//     });
//     setModalOpen(true);
//   };

//   const handleCloseModal = () => {
//     setModalOpen(false);
//     setSelectedInterview(null);
//   };

//   const handleAlert = (message, severity) => {
//     setSnackbar({ open: true, message, severity });
//   };

//   const handleSnackbarClose = (event, reason) => {
//     if (reason === "clickaway") return;
//     setSnackbar({ ...snackbar, open: false });
//   };

//   return (
//     <Layout title="Evaluations">
//       <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
//         <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
//           <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 }, bgcolor: "mint.main" }}>
//             <Typography variant={isMobile ? "h5" : "h4"} component="h1" color="primary.main" fontWeight="bold">
//               Candidate Evaluations
//             </Typography>
//           </Paper>
//           <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "background.paper" }}>
//             <Tabs 
//               value={tabValue} 
//               onChange={handleTabChange} 
//               aria-label="evaluation tabs"
//               variant={isMobile ? "fullWidth" : "standard"}
//               centered={!isMobile}
//             >
//               <Tab label="Scheduled" />
//               <Tab label="Extend Offer" />
//               <Tab label="Hired" />
//             </Tabs>
//           </Box>
//           <TabPanel value={tabValue} index={0}>
//             <ScheduledInterviewsTab onEvaluateClick={handleEvaluateClick} />
//           </TabPanel>
//           <TabPanel value={tabValue} index={1}>
//             <ExtendOfferTab onAlert={handleAlert} />
//           </TabPanel>
//           <TabPanel value={tabValue} index={2}>
//             <HiredCandidatesTab />
//           </TabPanel>
//         </Container>
//       </Box>
//       {selectedInterview && <EvaluationForm open={modalOpen} onClose={handleCloseModal} interviewDetails={selectedInterview} onSuccess={handleAlert} />}
//       <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose}>
//         <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
//           {snackbar.message}
//         </Alert>
//       </Snackbar>
//     </Layout>
//   );
  
// }


// import React, { useState, useEffect } from "react"
// import {
//   Container,
//   Paper,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   Rating,
//   TextField,
//   Button,
//   Box,
//   Avatar,
//   Divider,
//   FormControl,
//   Select,
//   MenuItem,
//   List,
//   Accordion,
//   Chip,
//   Modal,
//   CircularProgress,
//   Alert,
//   IconButton,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Backdrop,
//   useMediaQuery,
//   Fade,
//   Tabs,
//   Tab,
//   Snackbar,
//   TablePagination,
//   Skeleton,
// } from "@mui/material"
// import {
//   Person,
//   Email,
//   Event,
//   Business,
//   RateReview,
//   ArrowBack,
//   Close,
//   Add,
//   Remove,
//   LocationOn,
//   ThumbUp,
//   Send,
// } from "@mui/icons-material"
// import { createTheme, ThemeProvider, styled, responsiveFontSizes, useTheme } from "@mui/material/styles"
// import Layout from "../common/Layout"
// import apiClient from "../../context/apiClient"

// const baseTheme = createTheme({
//   palette: {
//     primary: {
//       main: "#1976d2",
//       light: "#42a5f5",
//       dark: "#1565c0",
//     },
//     background: {
//       default: "#f8fafc",
//       paper: "#ffffff",
//     },
//     text: {
//       primary: "#1e293b",
//       secondary: "#64748b",
//     },
//   },
//   typography: {
//     fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
//     h4: {
//       fontWeight: 700,
//       letterSpacing: "-0.025em",
//     },
//     h6: {
//       fontWeight: 600,
//     },
//     body1: {
//       lineHeight: 1.6,
//     },
//   },
//   components: {
//     MuiCard: {
//       styleOverrides: {
//         root: {
//           borderRadius: 16,
//           boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
//           transition: "all 0.2s ease-in-out",
//           "&:hover": {
//             boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//             transform: "translateY(-2px)",
//           },
//         },
//       },
//     },
//     MuiButton: {
//       styleOverrides: {
//         root: {
//           borderRadius: 12,
//           textTransform: "none",
//           fontWeight: 600,
//           transition: "all 0.2s ease-in-out",
//         },
//         contained: {
//           boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
//           "&:hover": {
//             boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
//             transform: "translateY(-1px)",
//           },
//         },
//       },
//     },
//     MuiAccordion: {
//       styleOverrides: {
//         root: {
//           borderRadius: "16px !important",
//           boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
//           "&:before": {
//             display: "none",
//           },
//           "&$expanded": {
//             margin: "auto",
//           },
//         },
//       },
//     },
//   },
// })

// const theme = responsiveFontSizes(baseTheme)

// const StyledAccordion = styled(Accordion)(({ theme }) => ({
//   transition: theme.transitions.create(["box-shadow", "margin", "transform"], {
//     duration: theme.transitions.duration.short,
//   }),
//   "&:hover": {
//     transform: "translateY(-2px)",
//     boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//   },
//   "&.Mui-expanded": {
//     boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//     margin: `${theme.spacing(1, 0)} !important`,
//     transform: "translateY(-2px)",
//   },
// }))

// function TabPanel(props) {
//   const { children, value, index, ...other } = props
//   return (
//     <div
//       role="tabpanel"
//       hidden={value !== index}
//       id={`simple-tabpanel-${index}`}
//       aria-labelledby={`simple-tab-${index}`}
//       {...other}
//     >
//       {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
//     </div>
//   )
// }

// function TableSkeleton({ columns, rows = 5 }) {
//   return (
//     <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
//       <Table>
//         <TableHead>
//           <TableRow sx={{ backgroundColor: "#F1FFFA" }}>
//             {columns.map((col, index) => (
//               <TableCell key={index} sx={{ fontWeight: "bold", width: col.width || "auto" }}>
//                 <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
//               </TableCell>
//             ))}
//           </TableRow>
//         </TableHead>
//         <TableBody>
//           {Array.from({ length: rows }).map((_, rowIndex) => (
//             <TableRow key={rowIndex}>
//               {columns.map((col, colIndex) => (
//                 <TableCell key={colIndex}>
//                   {col.type === "avatar" ? (
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                       <Skeleton variant="circular" width={48} height={48} />
//                       <Skeleton variant="text" width="70%" />
//                     </Box>
//                   ) : col.type === "chip" ? (
//                     <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: "16px" }} />
//                   ) : col.type === "button" ? (
//                     <Skeleton variant="rectangular" width={110} height={36} sx={{ borderRadius: 2 }} />
//                   ) : (
//                     <Skeleton variant="text" />
//                   )}
//                 </TableCell>
//               ))}
//             </TableRow>
//           ))}
//         </TableBody>
//       </Table>
//     </TableContainer>
//   )
// }

// function EvaluationForm({ interviewDetails, open, onClose, onSuccess }) {
//   const theme = useTheme()
//   const isXs = useMediaQuery(theme.breakpoints.down("sm"))
//   const [formData, setFormData] = useState({
//     recommendation: "",
//     technical_skills_rating: 0,
//     communication_skills_rating: 0,
//     cultural_fit_rating: 0,
//     strengths: [""],
//     weaknesses: [""],
//     overall_comments: [""],
//   })
//   const [isSubmitting, setIsSubmitting] = useState(false)
//   const [submitError, setSubmitError] = useState(null)

//   useEffect(() => {
//     if (open) {
//       setFormData({
//         recommendation: "",
//         technical_skills_rating: 0,
//         communication_skills_rating: 0,
//         cultural_fit_rating: 0,
//         strengths: [""],
//         weaknesses: [""],
//         overall_comments: [""],
//       })
//       setIsSubmitting(false)
//       setSubmitError(null)
//     }
//   }, [open])

//   const handleChange = (e) => {
//     const { name, value } = e.target
//     setFormData((prev) => ({ ...prev, [name]: value }))
//   }

//   const handleRatingChange = (field, value) => {
//     setFormData((prev) => ({ ...prev, [field]: value !== null ? Math.round(value) : 0 }))
//   }

//   const handleArrayFieldChange = (field, index, value) => {
//     setFormData((prev) => ({
//       ...prev,
//       [field]: prev[field].map((item, i) => (i === index ? value : item)),
//     }))
//   }

//   const addArrayField = (field) => {
//     setFormData((prev) => ({
//       ...prev,
//       [field]: [...prev[field], ""],
//     }))
//   }

//   const removeArrayField = (field, index) => {
//     if (formData[field].length > 1) {
//       setFormData((prev) => ({
//         ...prev,
//         [field]: prev[field].filter((_, i) => i !== index),
//       }))
//     }
//   }

//   const handleSubmit = async (e) => {
//     e.preventDefault()
//     setIsSubmitting(true)
//     setSubmitError(null)
//     try {
//       const submissionData = {
//         ...formData,
//         strengths: formData.strengths.filter((s) => s.trim()).join("; "),
//         weaknesses: formData.weaknesses.filter((w) => w.trim()).join("; "),
//         overall_comments: formData.overall_comments.filter((c) => c.trim()).join("; "),
//         interview_id: interviewDetails.interviewId,
//       }

//       await apiClient.post(
//         `/interviews/${interviewDetails.interviewId}/submit-feedback/`,
//         submissionData,
//       )
//       onSuccess("Evaluation submitted successfully!", "success")
//       onClose()
//     } catch (error) {
//       console.error("[v0] Error submitting evaluation:", error)
//       const errorMessage =
//         error.response?.data?.message || error.message || "An unexpected error occurred."
//       setSubmitError(errorMessage)
//     } finally {
//       setIsSubmitting(false)
//     }
//   }

//   return (
//     <Modal
//       open={open}
//       onClose={onClose}
//       closeAfterTransition
//       BackdropComponent={Backdrop}
//       BackdropProps={{
//         timeout: 500,
//       }}
//     >
//       <Fade in={open}>
//         <Box
//           component={Paper}
//           elevation={0}
//           sx={{
//             position: "absolute",
//             top: "50%",
//             left: "50%",
//             transform: "translate(-50%, -50%)",
//             width: { xs: "95%", sm: "90%", md: "80%", lg: "70%" },
//             maxWidth: "900px",
//             maxHeight: "90vh",
//             bgcolor: "background.paper",
//             borderRadius: 3,
//             boxShadow: 24,
//             overflow: "hidden",
//             display: "flex",
//             flexDirection: "column",
//           }}
//         >
//           <Box
//             sx={{
//               background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
//               color: "white",
//               p: { xs: 2, md: 3 },
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "space-between",
//               flexShrink: 0,
//             }}
//           >
//             <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
//               <Avatar
//                 src={interviewDetails?.avatar}
//                 sx={{
//                   width: { xs: 48, sm: 56 },
//                   height: { xs: 48, sm: 56 },
//                   border: "3px solid rgba(255,255,255,0.3)",
//                   flexShrink: 0,
//                 }}
//               >
//                 {interviewDetails?.name?.charAt(0)}
//               </Avatar>
//               <Box sx={{ minWidth: 0 }}>
//                 <Typography
//                   variant="h5"
//                   fontWeight="bold"
//                   backgroundColor= "#4D3CC3"
//                   sx={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}
//                 >
//                   Evaluate Candidate
//                 </Typography>
//                 <Typography
//                   variant="body1"
//                   sx={{ opacity: 0.9, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}
//                 >
//                   {interviewDetails?.name} • {interviewDetails?.jobTitle}
//                 </Typography>
//               </Box>
//             </Box>
//             <IconButton onClick={onClose} sx={{ color: "white" }}>
//               <Close />
//             </IconButton>
//           </Box>
//           <Container
//             maxWidth="lg"
//             sx={{
//               flex: 1,
//               overflow: "auto",
//               py: { xs: 2, md: 3 },
//               minHeight: 0,
//               maxHeight: "calc(90vh - 140px)",
//               "&::-webkit-scrollbar": { width: "8px" },
//               "&::-webkit-scrollbar-track": { background: "#f1f1f1", borderRadius: "4px" },
//               "&::-webkit-scrollbar-thumb": {
//                 background: "#c1c1c1",
//                 borderRadius: "4px",
//                 "&:hover": { background: "#a8a8a8" },
//               },
//               scrollbarWidth: "thin",
//               scrollbarColor: "#c1c1c1 #f1f1f1",
//             }}
//           >
//             <form onSubmit={handleSubmit}>
//               <Grid container spacing={3}>
//                 <Grid item xs={12}>
//                   <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, bgcolor: "#F1FFFA" }}>
//                     <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                       <ThumbUp color="primary" />
//                       Recommendation
//                     </Typography>
//                     <FormControl fullWidth>
//                       <Select
//                         name="recommendation"
//                         value={formData.recommendation}
//                         onChange={handleChange}
//                         required
//                         sx={{ borderRadius: 2 }}
//                       >
//                         <MenuItem value="">
//                           <em>Select recommendation</em>
//                         </MenuItem>
//                         <MenuItem value="STRONG_HIRE">
//                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <Person color="success" /> Strong Hire
//                           </Box>
//                         </MenuItem>
//                         <MenuItem value="HIRE">
//                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <Person color="success" /> Hire
//                           </Box>
//                         </MenuItem>
//                         <MenuItem value="NO_HIRE">
//                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <Person color="error" /> No Hire
//                           </Box>
//                         </MenuItem>
//                         <MenuItem value="NOT_SURE">
//                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <Person color="warning" /> Not Sure / Needs More Review
//                           </Box>
//                         </MenuItem>
//                       </Select>
//                     </FormControl>
//                   </Card>
//                 </Grid>
//                 <Grid item xs={12}>
//                   <Card
//                     variant="outlined"
//                     sx={{ p: { xs: 2, sm: 3 }, gap: 3, borderRadius: 2, bgcolor: "#F1FFFA", width: "100%" }}
//                   >
//                     <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
//                       Skills Assessment
//                     </Typography>
//                     <Grid container spacing={3}>
//                       {[
//                         { field: "technical_skills_rating", label: "Technical Skills", icon: "💻" },
//                         { field: "communication_skills_rating", label: "Communication", icon: "💬" },
//                         { field: "cultural_fit_rating", label: "Cultural Fit", icon: "🤝" },
//                       ].map((rating) => (
//                         <Grid item xs={12} sm={4} key={rating.field}>
//                           <Box
//                             sx={{
//                               textAlign: "center",
//                               p: 2,
//                               borderRadius: 2,
//                               border: "1px solid",
//                               borderColor: "divider",
//                               bgcolor: "grey.50",
//                             }}
//                           >
//                             <Typography variant="h4" sx={{ mb: 1 }}>
//                               {rating.icon}
//                             </Typography>
//                             <Typography variant="body1" fontWeight="medium" gutterBottom>
//                               {rating.label}
//                             </Typography>
//                             <Rating
//                               name={rating.field}
//                               value={formData[rating.field]}
//                               onChange={(e, val) => handleRatingChange(rating.field, val)}
//                               size={isXs ? "medium" : "large"}
//                               sx={{ mb: 1 }}
//                               max={5}
//                             />
//                             <Typography variant="body2" color="text.secondary">
//                               {formData[rating.field]}/5
//                             </Typography>
//                           </Box>
//                         </Grid>
//                       ))}
//                     </Grid>
//                   </Card>
//                 </Grid>
//                 {[
//                   { field: "strengths", label: "Key Strengths", placeholder: "What impressed you?", icon: "⭐" },
//                   { field: "weaknesses", label: "Areas for Improvement", placeholder: "What could be developed?", icon: "📈" },
//                   { field: "overall_comments", label: "Overall Assessment", placeholder: "Provide comprehensive evaluation...", icon: "📝" },
//                 ].map((section) => (
//                   <Grid item xs={12} key={section.field}>
//                     <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, bgcolor: "#F1FFFA" }}>
//                       <Box
//                         sx={{
//                           display: "flex",
//                           alignItems: { xs: "stretch", sm: "center" },
//                           justifyContent: "space-between",
//                           gap: 2,
//                           mb: 2,
//                           flexDirection: { xs: "column", sm: "row" },
//                         }}
//                       >
//                         <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                           {section.icon} {section.label}
//                         </Typography>
//                         <Button
//                           variant="outlined"
//                           size="medium"
//                           startIcon={<Add />}
//                           onClick={() => addArrayField(section.field)}
//                           sx={{ borderRadius: 2, alignSelf: { xs: "stretch", sm: "auto" } }}
//                           fullWidth={isXs}
//                         >
//                           Add
//                         </Button>
//                       </Box>
//                       {formData[section.field].map((item, index) => (
//                         <Box
//                           key={index}
//                           sx={{
//                             display: "flex",
//                             gap: 1,
//                             mb: 2,
//                             flexDirection: { xs: "column", sm: "row" },
//                             alignItems: { xs: "stretch", sm: "flex-start" },
//                           }}
//                         >
//                           <TextField
//                             fullWidth
//                             multiline
//                             rows={3}
//                             placeholder={section.placeholder}
//                             value={item}
//                             onChange={(e) => handleArrayFieldChange(section.field, index, e.target.value)}
//                             variant="outlined"
//                             sx={{ borderRadius: 2 }}
//                           />
//                           {formData[section.field].length > 1 && (
//                             <IconButton
//                               onClick={() => removeArrayField(section.field, index)}
//                               color="error"
//                               sx={{ alignSelf: { xs: "flex-end", sm: "flex-start" }, mt: { xs: 0, sm: 1 } }}
//                             >
//                               <Remove />
//                             </IconButton>
//                           )}
//                         </Box>
//                       ))}
//                     </Card>
//                   </Grid>
//                 ))}
//                 {submitError && (
//                   <Grid item xs={12}>
//                     <Alert severity="error" sx={{ borderRadius: 2 }}>
//                       {submitError}
//                     </Alert>
//                   </Grid>
//                 )}
//               </Grid>
//               <Box
//                 sx={{
//                   display: "flex",
//                   flexDirection: { xs: "column-reverse", sm: "row" },
//                   justifyContent: "flex-end",
//                   gap: 2,
//                   mt: 4,
//                   pt: 3,
//                   borderTop: "1px solid",
//                   borderColor: "divider",
//                 }}
//               >
//                 <Button onClick={onClose} variant="outlined" size="large" fullWidth={isXs} sx={{ borderRadius: 2, minWidth: 120,  }}>
//                   Cancel
//                 </Button>
//                 <Button type="submit" variant="contained" size="large" disabled={isSubmitting} fullWidth={isXs} sx={{ borderRadius: 2, minWidth: 120,backgroundColor: "#4D3CC3"  }}>
//                   {isSubmitting ? <CircularProgress size={24} /> : "Submit Evaluation"}
//                 </Button>
//               </Box>
//             </form>
//           </Container>
//         </Box>
//       </Fade>
//     </Modal>
//   )
// }

// function JobDetailsView({ candidate, onBack, onEvaluate }) {
//   const theme = useTheme();
//   const isXs = useMediaQuery(theme.breakpoints.down("sm"));
//   return (
//     <Box>
//       <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", color: "#4D3CC3" }}>
//         <Button variant="outlined" startIcon={<ArrowBack />} onClick={onBack} sx={{ borderRadius: 2, bgcolor: "#F1FFFA", color: "#4D3CC3" }}>
//           Back to Candidates
//         </Button>
//       </Box>
//       <Card elevation={1} sx={{ mb: 4, borderRadius: 2, bgcolor: "#F1FFFA" }}>
//         <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
//           <Grid container spacing={3} alignItems="center">
//             <Grid item xs="auto">
//               <Avatar
//                 src={candidate.avatar}
//                 sx={{
//                   width: { xs: 64, sm: 72, md: 80 },
//                   height: { xs: 64, sm: 72, md: 80 },
//                   border: "3px solid",
//                   borderColor: "#4D3CC3",
//                 }}
//               />
//             </Grid>
//             <Grid item xs>
//               <Typography variant="h5" fontWeight="bold" gutterBottom>
//                 {candidate.name}
//               </Typography>
//               <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
//                 <Email fontSize="small" color="action" />
//                 <Typography variant="body2" color="text.secondary">
//                   {candidate.email}
//                 </Typography>
//               </Box>
//               <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
//                 <LocationOn fontSize="small" color="action" />
//                 <Typography variant="body2" color="text.secondary">
//                   {candidate.location}
//                 </Typography>
//               </Box>
//               <Chip
//                 label={`${candidate.interviews.length} Scheduled Interview${candidate.interviews.length !== 1 ? "s" : ""}`}
//                 color="#4D3CC3"
//                 variant="outlined"
//                 sx={{ fontWeight: 600 }}
//               />
//             </Grid>
//           </Grid>
//         </CardContent>
//       </Card>

//       {/* <Grid container spacing={3}>
//         {candidate.interviews.map((interview) => (
//           <Grid item xs={12} sm={6} md={4} key={interview.id} sx={{ display: 'flex' }}>
//             <Card
//               elevation={1}
//               sx={{
//                 borderRadius: 2,
//                 bgcolor: "#F1FFFA",
//                 width: '100%',
//                 display: 'flex',
//                 flexDirection: 'column',
//               }}
//             >
//               <CardContent
//                 sx={{
//                   p: { xs: 2, sm: 3 },
//                   display: "flex",
//                   flexDirection: "column",
//                   flexGrow: 1,
//                 }}
//               >
//                 <Box sx={{ flexGrow: 1 }}>
//                   <Typography variant="h6" fontWeight="600" gutterBottom sx={{ color: "text.primary" }}>
//                     {interview.job_title}
//                   </Typography>
//                   <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 2 }}>
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                       <Business fontSize="small" color="action" />
//                       <Typography variant="body2" color="text.secondary" fontWeight="500">
//                         {interview.interview_mode}
//                       </Typography>
//                     </Box>
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                       <Event fontSize="small" color="action" />
//                       <Typography variant="body2" color="text.secondary" fontWeight="500">
//                         {new Date(`${interview.interview_date}T${interview.interview_time}`).toLocaleString()}
//                       </Typography>
//                     </Box>
//                   </Box>
//                 </Box>
//                 <Box sx={{ mt: "auto", pt: 2 }}>
//                   <Button
//                     variant="contained"
//                     startIcon={<RateReview />}
//                     onClick={() => onEvaluate(candidate, interview)}
//                     fullWidth
//                     sx={{
//                       borderRadius: 2,
//                       backgroundColor: "#4D3CC3",
//                       fontWeight: 600,
//                       boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
//                       "&:hover": {
//                         transform: "translateY(-1px)",
//                         boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
//                       },
//                     }}
//                   >
//                     Evaluate
//                   </Button>
//                 </Box>
//               </CardContent>
//             </Card>
//           </Grid>
//         ))}
//       </Grid> */}

//       <Grid container spacing={3}>
//       {candidate.interviews.map((interview) => (
//         <Grid item xs={12} sm={6} md={4} key={interview.id} sx={{ display: 'flex' }}>
//           <Card
//             elevation={1}
//             sx={{
//               borderRadius: 2,
//               bgcolor: "#F1FFFA",
//               width: '100%',
//               display: 'flex',
//               flexDirection: 'column',
//               height: '100%',
//               width:'100%' // Make card take full height of the grid item
//             }}
//           >
//             <CardContent
//               sx={{
//                 p: { xs: 2, sm: 3 },
//                 display: "flex",
//                 flexDirection: "column",
//                 flexGrow: 1,
//                 width: '320px'
//               }}
//             >
//               <Box sx={{ flexGrow: 1 }}>
//                 <Typography variant="h6" fontWeight="600" gutterBottom sx={{ color: "text.primary" }}>
//                   {interview.job_title}
//                 </Typography>
//                 <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 2 }}>
//                   <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                     <Business fontSize="small" color="action" />
//                     <Typography variant="body2" color="text.secondary" fontWeight="500">
//                       {interview.interview_mode}
//                     </Typography>
//                   </Box>
//                   <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                     <Event fontSize="small" color="action" />
//                     <Typography variant="body2" color="text.secondary" fontWeight="500">
//                       {new Date(`${interview.interview_date}T${interview.interview_time}`).toLocaleString()}
//                     </Typography>
//                   </Box>
//                 </Box>
//               </Box>
//               <Box sx={{ mt: "auto", pt: 2 }}>
//                 <Button
//                   variant="contained"
//                   startIcon={<RateReview />}
//                   onClick={() => onEvaluate(candidate, interview)}
//                   fullWidth
//                   sx={{
//                     borderRadius: 2,
//                     backgroundColor: "#4D3CC3",
//                     fontWeight: 600,
//                     boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
//                     "&:hover": {
//                       transform: "translateY(-1px)",
//                       boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
//                     },
//                   }}
//                 >
//                   Evaluate
//                 </Button>
//               </Box>
//             </CardContent>
//           </Card>
//         </Grid>
//       ))}
//     </Grid>
//     </Box>
//   );
// }

// function ScheduledInterviewsTab({ onEvaluateClick }) {
//   const [candidates, setCandidates] = useState([])
//   const [loading, setLoading] = useState(true)
//   const [error, setError] = useState(null)
//   const [searchTerm, setSearchTerm] = useState("")
//   const [selectedCandidateDetails, setSelectedCandidateDetails] = useState(null)
//   const [showJobDetails, setShowJobDetails] = useState(false)
//   const [page, setPage] = useState(0)
//   const [rowsPerPage, setRowsPerPage] = useState(5)
//   const theme = useTheme()
//   const isXs = useMediaQuery(theme.breakpoints.down("sm"))

//   useEffect(() => {
//     const fetchAndProcessInterviews = async () => {
//       try {
//         const response = await apiClient.get("/interviews/")
//         const data = response.data
//         const filtered = data.filter(
//           (interview) =>
//             interview.status.toLowerCase() === "scheduled" || interview.status.toLowerCase() === "rescheduled",
//         )
//         const groupedByCandidate = filtered.reduce((acc, interview) => {
//           const { candidate_name, profile_photo, talent_current_location } = interview
//           if (!acc[candidate_name]) {
//             acc[candidate_name] = {
//               id: candidate_name.replace(/\s+/g, "-").toLowerCase(),
//               name: candidate_name,
//               email: `${candidate_name.replace(/\s+/g, ".").toLowerCase()}@example.com`,
//               avatar: profile_photo || `https://i.pravatar.cc/150?u=${encodeURIComponent(candidate_name)}`,
//               location: talent_current_location || "Location not specified",
//               interviews: [],
//             }
//           }
//           acc[candidate_name].interviews.push(interview)
//           return acc
//         }, {})
//         setCandidates(Object.values(groupedByCandidate))
//       } catch (e) {
//         setError(e.response?.data?.detail || e.message)
//       } finally {
//         setLoading(false)
//       }
//     }
//     fetchAndProcessInterviews()
//   }, [])

//   const handleViewDetails = (candidate) => {
//     setSelectedCandidateDetails(candidate)
//     setShowJobDetails(true)
//   }

//   const handleBackToList = () => {
//     setShowJobDetails(false)
//     setSelectedCandidateDetails(null)
//   }

//   const handleChangePage = (event, newPage) => {
//     setPage(newPage)
//   }

//   const handleChangeRowsPerPage = (event) => {
//     setRowsPerPage(parseInt(event.target.value, 10))
//     setPage(0)
//   }

//   const filteredCandidates = candidates.filter(
//     (c) =>
//       c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       c.email.toLowerCase().includes(searchTerm.toLowerCase()),
//   )

//   const skeletonColumns = [
//     { type: "avatar", width: "40%" },
//     { type: "chip", width: "30%" },
//     { type: "button", width: "30%" },
//   ]

//   if (loading) {
//     return <TableSkeleton columns={skeletonColumns} rows={rowsPerPage} />
//   }

//   if (error) {
//     return (
//       <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//         <Typography variant="h6" color="error" gutterBottom>
//           Error Fetching Interviews
//         </Typography>
//         <Typography>{error}</Typography>
//       </Paper>
//     )
//   }

//   if (showJobDetails && selectedCandidateDetails) {
//     return (
//       <JobDetailsView candidate={selectedCandidateDetails} onBack={handleBackToList} onEvaluate={onEvaluateClick} />
//     )
//   }

//   return (
//     <Box>
//       {filteredCandidates.length === 0 && !loading ? (
//         <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//           <Typography variant="h6" color="text.secondary">
//             No Scheduled Interviews Found
//           </Typography>
//         </Paper>
//       ) : (
//         <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
//           <Table size={isXs ? "small" : "medium"}>
//             <TableHead>
//               <TableRow sx={{ backgroundColor: "#F1FFFA" }}>
//                 <TableCell sx={{ fontWeight: "bold" }}>Candidate</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Interviews</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {filteredCandidates
//                 .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
//                 .map((candidate) => (
//                   <TableRow key={candidate.id} sx={{ "&:hover": { bgcolor: "grey.50" } }}>
//                     <TableCell>
//                       <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                         <Avatar src={candidate.avatar} sx={{ width: 48, height: 48 }} />
//                         <Typography variant="body1" fontWeight="600">
//                           {candidate.name}
//                         </Typography>
//                       </Box>
//                     </TableCell>
//                     <TableCell>
//                       <Chip
//                         label={`${candidate.interviews.length} interview${candidate.interviews.length !== 1 ? "s" : ""}`}
//                             color= "#4D3CC3"
//                              variant="outlined"
//                         size="small"
//                         sx={{ fontWeight: 600 }}
//                       />
//                     </TableCell>
//                     <TableCell>
//                       <Button
//                         variant="contained"
//                         onClick={() => handleViewDetails(candidate)}
//                         sx={{
//                           borderRadius: 2,
//                           backgroundColor: "#4D3CC3",
//                           fontWeight: 600,
//                           boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
//                           "&:hover": {
//                             transform: "translateY(-1px)",
//                             boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
//                           },
//                         }}
//                       >
//                         View Details
//                       </Button>
//                     </TableCell>
//                   </TableRow>
//                 ))}
//             </TableBody>
//           </Table>
//           <TablePagination
//             rowsPerPageOptions={[5, 10, 25]}
//             component="div"
//             count={filteredCandidates.length}
//             rowsPerPage={rowsPerPage}
//             page={page}
//             onPageChange={handleChangePage}
//             onRowsPerPageChange={handleChangeRowsPerPage}
//           />
//         </TableContainer>
//       )}
//     </Box>
//   )
// }

// function ExtendOfferTab({ onAlert }) {
//   const [completedCandidates, setCompletedCandidates] = useState([])
//   const [loading, setLoading] = useState(true)
//   const [error, setError] = useState(null)
//   const [extendingId, setExtendingId] = useState(null)
//   const [page, setPage] = useState(0)
//   const [rowsPerPage, setRowsPerPage] = useState(5)

//   const fetchCompletedCandidates = async () => {
//     setLoading(true)
//     try {
//       const response = await apiClient.get("/interviews/completed-candidates/")
//       const eligibleCandidates = response.data.filter(
//         (candidate) => candidate.application_status !== "HIRED",
//       )
//       setCompletedCandidates(eligibleCandidates)
//     } catch (e) {
//       setError(e.response?.data?.detail || e.message)
//     } finally {
//       setLoading(false)
//     }
//   }

//   useEffect(() => {
//     fetchCompletedCandidates()
//   }, [])

//   const handleExtendOffer = async (applicationId) => {
//     setExtendingId(applicationId)
//     try {
//       await apiClient.post(`/applications/${applicationId}/offer-extend/`)
//       onAlert("Offer extended successfully!", "success")
//       await fetchCompletedCandidates()
//     } catch (err) {
//       const errorMessage = err.response?.data?.detail || err.message || "Failed to extend offer."
//       onAlert(errorMessage, "error")
//     } finally {
//       setExtendingId(null)
//     }
//   }

//   const handleChangePage = (event, newPage) => {
//     setPage(newPage)
//   }

//   const handleChangeRowsPerPage = (event) => {
//     setRowsPerPage(parseInt(event.target.value, 10))
//     setPage(0)
//   }

//   const skeletonColumns = [
//     { type: "text", width: "25%" },
//     { type: "text", width: "25%" },
//     { type: "text", width: "15%" },
//     { type: "chip", width: "15%" },
//     { type: "button", width: "20%" },
//   ]

//   if (loading) {
//     return <TableSkeleton columns={skeletonColumns} rows={rowsPerPage} />
//   }

//   if (error) {
//     return (
//       <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//         <Typography variant="h6" color="error" gutterBottom>
//           Error Fetching Data
//         </Typography>
//         <Typography>{error}</Typography>
//       </Paper>
//     )
//   }

//   return (
//     <Box>
//       {completedCandidates.length === 0 ? (
//         <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//           <Typography variant="h6" color="text.secondary">
//             No evaluated candidates available to extend an offer.
//           </Typography>
//         </Paper>
//       ) : (
//         <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
//           <Table>
//             <TableHead>
//               <TableRow sx={{ backgroundColor: "#F1FFFA" }}>
//                 <TableCell sx={{ fontWeight: "bold" }}>Candidate</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Job Title</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Tech Rating</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
//                 <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Actions</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {completedCandidates
//                 .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
//                 .map((candidate) => (
//                   <TableRow key={candidate.application_id} sx={{ "&:hover": { bgcolor: "grey.50" } }}>
//                     <TableCell>{candidate.talent_name}</TableCell>
//                     <TableCell>{candidate.job_posting_title}</TableCell>
//                     <TableCell>{candidate.technical_skills_rating || "N/A"}</TableCell>
//                     <TableCell>
//                       <Chip
//                         label={candidate.application_status.replace(/_/g, " ")}
//                         color={candidate.application_status === "OFFER_EXTENDED" ? "success" : "default"}
//                         size="small"
//                         sx={{ textTransform: "capitalize" }}
//                       />
//                     </TableCell>
//                     <TableCell sx={{ textAlign: "center" }}>
//                       <Button
//                         variant="contained"
//                         color="primary"
//                         backgroundColor=   "#4D3CC3"
//                         startIcon={extendingId === candidate.application_id ? null : <Send />}
//                         disabled={
//                           extendingId === candidate.application_id ||
//                           candidate.application_status === "OFFER_EXTENDED"
//                         }
//                         onClick={() => handleExtendOffer(candidate.application_id)}
//                         sx={{
//                           minWidth: 140,
//                           borderRadius: 2,
//                           backgroundColor: "#4D3CC3"
//                         }}
//                       >
//                         {extendingId === candidate.application_id ? (
//                           <CircularProgress size={24} color="inherit" />
//                         ) : candidate.application_status === "OFFER_EXTENDED" ? (
//                           "Offer Extended"
//                         ) : (
//                           "Extend Offer"
//                         )}
//                       </Button>
//                     </TableCell>
//                   </TableRow>
//                 ))}
//             </TableBody>
//           </Table>
//           <TablePagination
//             rowsPerPageOptions={[5, 10, 25]}
//             component="div"
//             count={completedCandidates.length}
//             rowsPerPage={rowsPerPage}
//             page={page}
//             onPageChange={handleChangePage}
//             onRowsPerPageChange={handleChangeRowsPerPage}
//           />
//         </TableContainer>
//       )}
//     </Box>
//   )
// }

// function HiredCandidatesTab() {
//   const [hiredCandidates, setHiredCandidates] = useState([])
//   const [loading, setLoading] = useState(true)
//   const [error, setError] = useState(null)
//   const [page, setPage] = useState(0)
//   const [rowsPerPage, setRowsPerPage] = useState(5)

//   useEffect(() => {
//     const fetchHiredCandidates = async () => {
//       try {
//         const response = await apiClient.get("/interviews/completed-candidates/")
//         const filteredData = response.data.filter(
//           (candidate) => candidate.application_status === "HIRED",
//         )
//         setHiredCandidates(filteredData)
//       } catch (e) {
//         setError(e.response?.data?.detail || e.message)
//       } finally {
//         setLoading(false)
//       }
//     }

//     fetchHiredCandidates()
//   }, [])

//   const handleChangePage = (event, newPage) => {
//     setPage(newPage)
//   }

//   const handleChangeRowsPerPage = (event) => {
//     setRowsPerPage(parseInt(event.target.value, 10))
//     setPage(0)
//   }

//   const skeletonColumns = [
//     { type: "text", width: "20%" },
//     { type: "text", width: "20%" },
//     { type: "text", width: "15%" },
//     { type: "chip", width: "15%" },
//     { type: "text", width: "30%" },
//   ]

//   if (loading) {
//     return <TableSkeleton columns={skeletonColumns} rows={rowsPerPage} />
//   }

//   if (error) {
//     return (
//       <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//         <Typography variant="h6" color="error" gutterBottom>
//           Error Fetching Data
//         </Typography>
//         <Typography>{error}</Typography>
//       </Paper>
//     )
//   }

//   return (
//     <Box>
//       {hiredCandidates.length === 0 ? (
//         <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//           <Typography variant="h6" color="text.secondary">
//             No hired candidates found.
//           </Typography>
//         </Paper>
//       ) : (
//         <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
//           <Table>
//             <TableHead>
//               <TableRow sx={{ backgroundColor: "#F1FFFA" }}>
//                 <TableCell sx={{ fontWeight: "bold" }}>Candidate</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Job Title</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Tech Rating</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Overall Comments</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {hiredCandidates
//                 .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
//                 .map((candidate) => (
//                   <TableRow key={candidate.application_id} sx={{ "&:hover": { bgcolor: "grey.50" } }}>
//                     <TableCell>{candidate.talent_name}</TableCell>
//                     <TableCell>{candidate.job_posting_title}</TableCell>
//                     <TableCell>{candidate.technical_skills_rating || "N/A"}</TableCell>
//                     <TableCell>
//                       <Chip
//                         label={candidate.application_status.replace(/_/g, " ")}
//                         size="small"
//                         sx={{ textTransform: "capitalize", backgroundColor: "#4D3CC3" , color: "white" }}
//                       />
//                     </TableCell>
//                     <TableCell>{candidate.overall_comments || "N/A"}</TableCell>
//                   </TableRow>
//                 ))}
//             </TableBody>
//           </Table>
//           <TablePagination
//             rowsPerPageOptions={[5, 10, 25]}
//             component="div"
//             count={hiredCandidates.length}
//             rowsPerPage={rowsPerPage}
//             page={page}
//             onPageChange={handleChangePage}
//             onRowsPerPageChange={handleChangeRowsPerPage}
//           />
//         </TableContainer>
//       )}
//     </Box>
//   )
// }

// export default function EvaluationsPage() {
//   const [modalOpen, setModalOpen] = useState(false)
//   const [selectedInterview, setSelectedInterview] = useState(null)
//   const [tabValue, setTabValue] = useState(0)
//   const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" })

//   const handleTabChange = (event, newValue) => {
//     setTabValue(newValue)
//   }

//   const handleEvaluateClick = (candidate, interview) => {
//     setSelectedInterview({
//       name: candidate.name,
//       email: candidate.email,
//       avatar: candidate.avatar,
//       interviewId: interview.id,
//       jobTitle: interview.job_title,
//     })
//     setModalOpen(true)
//   }

//   const handleCloseModal = () => {
//     setModalOpen(false)
//     setSelectedInterview(null)
//   }

//   const handleAlert = (message, severity) => {
//     setSnackbar({ open: true, message, severity })
//   }

//   const handleSnackbarClose = (event, reason) => {
//     if (reason === "clickaway") return
//     setSnackbar({ ...snackbar, open: false })
//   }

//   return (
//     <Layout title="Evaluations">
//       <ThemeProvider theme={theme}>
//         <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
//           <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
//             <Paper sx={{ p: { xs: 1, sm: 2 }, borderRadius: 3, backgroundColor: "#F1FFFA", color: "#4D3CC3" }}>
//               <Typography variant="h4" gutterBottom>
//                 Candidate Evaluations & Extend Offers
//               </Typography>
//             </Paper>
//             <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 2  }}>
//               <Tabs value={tabValue} onChange={handleTabChange} aria-label="evaluation tabs" color= "#4D3CC3">
//                 <Tab label="Scheduled Interviews" id="tab-0" />
//                 <Tab label="Extend Offer" id="tab-1" />
//                 <Tab label="Hired Candidates" id="tab-2" />
//               </Tabs>
//             </Box>
//             <TabPanel value={tabValue} index={0}>
//               <ScheduledInterviewsTab onEvaluateClick={handleEvaluateClick} />
//             </TabPanel>
//             <TabPanel value={tabValue} index={1}>
//               <ExtendOfferTab onAlert={handleAlert} />
//             </TabPanel>
//             <TabPanel value={tabValue} index={2}>
//               <HiredCandidatesTab />
//             </TabPanel>
//           </Container>
//         </Box>
//         {selectedInterview && (
//           <EvaluationForm
//             open={modalOpen}
//             onClose={handleCloseModal}
//             interviewDetails={selectedInterview}
//             onSuccess={handleAlert}
//           />
//         )}
//         <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose}>
//           <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
//             {snackbar.message}
//           </Alert>
//         </Snackbar>
//       </ThemeProvider>
//     </Layout>
//   )
// }





// import React, { useState, useEffect } from "react"
// import {
//   Container,
//   Paper,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   Rating,
//   TextField,
//   Button,
//   Box,
//   Avatar,
//   Divider,
//   FormControl,
//   Select,
//   MenuItem,
//   List,
//   Accordion,
//   Chip,
//   Modal,
//   CircularProgress,
//   Alert,
//   IconButton,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Backdrop,
//   useMediaQuery,
//   Fade,
//   Tabs,
//   Tab,
//   Snackbar,
//   TablePagination,
//   Skeleton, // Import Skeleton
// } from "@mui/material"
// import {
//   Person,
//   Email,
//   Event,
//   Business,
//   RateReview,
//   ArrowBack,
//   Close,
//   Add,
//   Remove,
//   LocationOn,
//   ThumbUp,
//   Send,
// } from "@mui/icons-material"
// import { createTheme, ThemeProvider, styled, responsiveFontSizes, useTheme } from "@mui/material/styles"
// import Layout from "../common/Layout"
// import apiClient from "../../context/apiClient"

// const baseTheme = createTheme({
//   palette: {
//     primary: {
//       main: "#1976d2",
//       light: "#42a5f5",
//       dark: "#1565c0",
//     },
//     background: {
//       default: "#f8fafc",
//       paper: "#ffffff",
//     },
//     text: {
//       primary: "#1e293b",
//       secondary: "#64748b",
//     },
//   },
//   typography: {
//     fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
//     h4: {
//       fontWeight: 700,
//       letterSpacing: "-0.025em",
//     },
//     h6: {
//       fontWeight: 600,
//     },
//     body1: {
//       lineHeight: 1.6,
//     },
//   },
//   components: {
//     MuiCard: {
//       styleOverrides: {
//         root: {
//           borderRadius: 16,
//           boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
//           transition: "all 0.2s ease-in-out",
//           "&:hover": {
//             boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//             transform: "translateY(-2px)",
//           },
//         },
//       },
//     },
//     MuiButton: {
//       styleOverrides: {
//         root: {
//           borderRadius: 12,
//           textTransform: "none",
//           fontWeight: 600,
//           transition: "all 0.2s ease-in-out",
//         },
//         contained: {
//           boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
//           "&:hover": {
//             boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
//             transform: "translateY(-1px)",
//           },
//         },
//       },
//     },
//     MuiAccordion: {
//       styleOverrides: {
//         root: {
//           borderRadius: "16px !important",
//           boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
//           "&:before": {
//             display: "none",
//           },
//           "&$expanded": {
//             margin: "auto",
//           },
//         },
//       },
//     },
//   },
// })

// const theme = responsiveFontSizes(baseTheme)

// const StyledAccordion = styled(Accordion)(({ theme }) => ({
//   transition: theme.transitions.create(["box-shadow", "margin", "transform"], {
//     duration: theme.transitions.duration.short,
//   }),
//   "&:hover": {
//     transform: "translateY(-2px)",
//     boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//   },
//   "&.Mui-expanded": {
//     boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//     margin: `${theme.spacing(1, 0)} !important`,
//     transform: "translateY(-2px)",
//   },
// }))

// function TabPanel(props) {
//   const { children, value, index, ...other } = props
//   return (
//     <div
//       role="tabpanel"
//       hidden={value !== index}
//       id={`simple-tabpanel-${index}`}
//       aria-labelledby={`simple-tab-${index}`}
//       {...other}
//     >
//       {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
//     </div>
//   )
// }

// // Reusable Skeleton Component for Tables
// function TableSkeleton({ columns, rows = 5 }) {
//   return (
//     <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
//       <Table>
//         <TableHead>
//           <TableRow sx={{ backgroundColor: "#F1FFFA" }}>
//             {columns.map((col, index) => (
//               <TableCell key={index} sx={{ fontWeight: "bold", width: col.width || "auto" }}>
//                 <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
//               </TableCell>
//             ))}
//           </TableRow>
//         </TableHead>
//         <TableBody>
//           {Array.from({ length: rows }).map((_, rowIndex) => (
//             <TableRow key={rowIndex}>
//               {columns.map((col, colIndex) => (
//                 <TableCell key={colIndex}>
//                   {col.type === "avatar" ? (
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                       <Skeleton variant="circular" width={48} height={48} />
//                       <Skeleton variant="text" width="70%" />
//                     </Box>
//                   ) : col.type === "chip" ? (
//                     <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: "16px" }} />
//                   ) : col.type === "button" ? (
//                     <Skeleton variant="rectangular" width={110} height={36} sx={{ borderRadius: 2 }} />
//                   ) : (
//                     <Skeleton variant="text" />
//                   )}
//                 </TableCell>
//               ))}
//             </TableRow>
//           ))}
//         </TableBody>
//       </Table>
//     </TableContainer>
//   )
// }

// function EvaluationForm({ interviewDetails, open, onClose, onSuccess }) {
//   const theme = useTheme()
//   const isXs = useMediaQuery(theme.breakpoints.down("sm"))
//   const [formData, setFormData] = useState({
//     recommendation: "",
//     technical_skills_rating: 0,
//     communication_skills_rating: 0,
//     cultural_fit_rating: 0,
//     strengths: [""],
//     weaknesses: [""],
//     overall_comments: [""],
//   })
//   const [isSubmitting, setIsSubmitting] = useState(false)
//   const [submitError, setSubmitError] = useState(null)

//   useEffect(() => {
//     if (open) {
//       setFormData({
//         recommendation: "",
//         technical_skills_rating: 0,
//         communication_skills_rating: 0,
//         cultural_fit_rating: 0,
//         strengths: [""],
//         weaknesses: [""],
//         overall_comments: [""],
//       })
//       setIsSubmitting(false)
//       setSubmitError(null)
//     }
//   }, [open])

//   const handleChange = (e) => {
//     const { name, value } = e.target
//     setFormData((prev) => ({ ...prev, [name]: value }))
//   }

//   const handleRatingChange = (field, value) => {
//     setFormData((prev) => ({ ...prev, [field]: value !== null ? Math.round(value) : 0 }))
//   }

//   const handleArrayFieldChange = (field, index, value) => {
//     setFormData((prev) => ({
//       ...prev,
//       [field]: prev[field].map((item, i) => (i === index ? value : item)),
//     }))
//   }

//   const addArrayField = (field) => {
//     setFormData((prev) => ({
//       ...prev,
//       [field]: [...prev[field], ""],
//     }))
//   }

//   const removeArrayField = (field, index) => {
//     if (formData[field].length > 1) {
//       setFormData((prev) => ({
//         ...prev,
//         [field]: prev[field].filter((_, i) => i !== index),
//       }))
//     }
//   }

//   const handleSubmit = async (e) => {
//     e.preventDefault()
//     setIsSubmitting(true)
//     setSubmitError(null)
//     try {
//       const submissionData = {
//         ...formData,
//         strengths: formData.strengths.filter((s) => s.trim()).join("; "),
//         weaknesses: formData.weaknesses.filter((w) => w.trim()).join("; "),
//         overall_comments: formData.overall_comments.filter((c) => c.trim()).join("; "),
//         interview_id: interviewDetails.interviewId,
//       }

//       await apiClient.post(
//         `/interviews/${interviewDetails.interviewId}/submit-feedback/`,
//         submissionData,
//       )
//       onSuccess("Evaluation submitted successfully!", "success")
//       onClose()
//     } catch (error) {
//       console.error("[v0] Error submitting evaluation:", error)
//       const errorMessage =
//         error.response?.data?.message || error.message || "An unexpected error occurred."
//       setSubmitError(errorMessage)
//     } finally {
//       setIsSubmitting(false)
//     }
//   }

//   return (
//     <Modal
//       open={open}
//       onClose={onClose}
//       closeAfterTransition
//       BackdropComponent={Backdrop}
//       BackdropProps={{
//         timeout: 500,
//       }}
//     >
//       <Fade in={open}>
//         <Box
//           component={Paper}
//           elevation={0}
//           sx={{
//             position: "absolute",
//             top: "50%",
//             left: "50%",
//             transform: "translate(-50%, -50%)",
//             width: { xs: "95%", sm: "90%", md: "80%", lg: "70%" },
//             maxWidth: "900px",
//             maxHeight: "90vh",
//             bgcolor: "background.paper",
//             borderRadius: 3,
//             boxShadow: 24,
//             overflow: "hidden",
//             display: "flex",
//             flexDirection: "column",
//           }}
//         >
//           <Box
//             sx={{
//               background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
//               color: "white",
//               p: { xs: 2, md: 3 },
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "space-between",
//               flexShrink: 0,
//             }}
//           >
//             <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
//               <Avatar
//                 src={interviewDetails?.avatar}
//                 sx={{
//                   width: { xs: 48, sm: 56 },
//                   height: { xs: 48, sm: 56 },
//                   border: "3px solid rgba(255,255,255,0.3)",
//                   flexShrink: 0,
//                 }}
//               >
//                 {interviewDetails?.name?.charAt(0)}
//               </Avatar>
//               <Box sx={{ minWidth: 0 }}>
//                 <Typography
//                   variant="h5"
//                   fontWeight="bold"
//                   backgroundColor= "#4D3CC3"
//                   sx={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}
//                 >
//                   Evaluate Candidate
//                 </Typography>
//                 <Typography
//                   variant="body1"
//                   sx={{ opacity: 0.9, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}
//                 >
//                   {interviewDetails?.name} • {interviewDetails?.jobTitle}
//                 </Typography>
//               </Box>
//             </Box>
//             <IconButton onClick={onClose} sx={{ color: "white" }}>
//               <Close />
//             </IconButton>
//           </Box>
//           <Container
//             maxWidth="lg"
//             sx={{
//               flex: 1,
//               overflow: "auto",
//               py: { xs: 2, md: 3 },
//               minHeight: 0,
//               maxHeight: "calc(90vh - 140px)",
//               "&::-webkit-scrollbar": { width: "8px" },
//               "&::-webkit-scrollbar-track": { background: "#f1f1f1", borderRadius: "4px" },
//               "&::-webkit-scrollbar-thumb": {
//                 background: "#c1c1c1",
//                 borderRadius: "4px",
//                 "&:hover": { background: "#a8a8a8" },
//               },
//               scrollbarWidth: "thin",
//               scrollbarColor: "#c1c1c1 #f1f1f1",
//             }}
//           >
//             <form onSubmit={handleSubmit}>
//               <Grid container spacing={3}>
//                 <Grid item xs={12}>
//                   <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, bgcolor: "#F1FFFA" }}>
//                     <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                       <ThumbUp color="primary" />
//                       Recommendation
//                     </Typography>
//                     <FormControl fullWidth>
//                       <Select
//                         name="recommendation"
//                         value={formData.recommendation}
//                         onChange={handleChange}
//                         required
//                         sx={{ borderRadius: 2 }}
//                       >
//                         <MenuItem value="">
//                           <em>Select recommendation</em>
//                         </MenuItem>
//                         <MenuItem value="STRONG_HIRE">
//                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <Person color="success" /> Strong Hire
//                           </Box>
//                         </MenuItem>
//                         <MenuItem value="HIRE">
//                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <Person color="success" /> Hire
//                           </Box>
//                         </MenuItem>
//                         <MenuItem value="NO_HIRE">
//                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <Person color="error" /> No Hire
//                           </Box>
//                         </MenuItem>
//                         <MenuItem value="NOT_SURE">
//                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <Person color="warning" /> Not Sure / Needs More Review
//                           </Box>
//                         </MenuItem>
//                       </Select>
//                     </FormControl>
//                   </Card>
//                 </Grid>
//                 <Grid item xs={12}>
//                   <Card
//                     variant="outlined"
//                     sx={{ p: { xs: 2, sm: 3 }, gap: 3, borderRadius: 2, bgcolor: "#F1FFFA", width: "100%" }}
//                   >
//                     <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
//                       Skills Assessment
//                     </Typography>
//                     <Grid container spacing={3}>
//                       {[
//                         { field: "technical_skills_rating", label: "Technical Skills", icon: "💻" },
//                         { field: "communication_skills_rating", label: "Communication", icon: "💬" },
//                         { field: "cultural_fit_rating", label: "Cultural Fit", icon: "🤝" },
//                       ].map((rating) => (
//                         <Grid item xs={12} sm={4} key={rating.field}>
//                           <Box
//                             sx={{
//                               textAlign: "center",
//                               p: 2,
//                               borderRadius: 2,
//                               border: "1px solid",
//                               borderColor: "divider",
//                               bgcolor: "grey.50",
//                             }}
//                           >
//                             <Typography variant="h4" sx={{ mb: 1 }}>
//                               {rating.icon}
//                             </Typography>
//                             <Typography variant="body1" fontWeight="medium" gutterBottom>
//                               {rating.label}
//                             </Typography>
//                             <Rating
//                               name={rating.field}
//                               value={formData[rating.field]}
//                               onChange={(e, val) => handleRatingChange(rating.field, val)}
//                               size={isXs ? "medium" : "large"}
//                               sx={{ mb: 1 }}
//                               max={5}
//                             />
//                             <Typography variant="body2" color="text.secondary">
//                               {formData[rating.field]}/5
//                             </Typography>
//                           </Box>
//                         </Grid>
//                       ))}
//                     </Grid>
//                   </Card>
//                 </Grid>
//                 {[
//                   { field: "strengths", label: "Key Strengths", placeholder: "What impressed you?", icon: "⭐" },
//                   { field: "weaknesses", label: "Areas for Improvement", placeholder: "What could be developed?", icon: "📈" },
//                   { field: "overall_comments", label: "Overall Assessment", placeholder: "Provide comprehensive evaluation...", icon: "📝" },
//                 ].map((section) => (
//                   <Grid item xs={12} key={section.field}>
//                     <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, bgcolor: "#F1FFFA" }}>
//                       <Box
//                         sx={{
//                           display: "flex",
//                           alignItems: { xs: "stretch", sm: "center" },
//                           justifyContent: "space-between",
//                           gap: 2,
//                           mb: 2,
//                           flexDirection: { xs: "column", sm: "row" },
//                         }}
//                       >
//                         <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                           {section.icon} {section.label}
//                         </Typography>
//                         <Button
//                           variant="outlined"
//                           size="medium"
//                           startIcon={<Add />}
//                           onClick={() => addArrayField(section.field)}
//                           sx={{ borderRadius: 2, alignSelf: { xs: "stretch", sm: "auto" } }}
//                           fullWidth={isXs}
//                         >
//                           Add
//                         </Button>
//                       </Box>
//                       {formData[section.field].map((item, index) => (
//                         <Box
//                           key={index}
//                           sx={{
//                             display: "flex",
//                             gap: 1,
//                             mb: 2,
//                             flexDirection: { xs: "column", sm: "row" },
//                             alignItems: { xs: "stretch", sm: "flex-start" },
//                           }}
//                         >
//                           <TextField
//                             fullWidth
//                             multiline
//                             rows={3}
//                             placeholder={section.placeholder}
//                             value={item}
//                             onChange={(e) => handleArrayFieldChange(section.field, index, e.target.value)}
//                             variant="outlined"
//                             sx={{ borderRadius: 2 }}
//                           />
//                           {formData[section.field].length > 1 && (
//                             <IconButton
//                               onClick={() => removeArrayField(section.field, index)}
//                               color="error"
//                               sx={{ alignSelf: { xs: "flex-end", sm: "flex-start" }, mt: { xs: 0, sm: 1 } }}
//                             >
//                               <Remove />
//                             </IconButton>
//                           )}
//                         </Box>
//                       ))}
//                     </Card>
//                   </Grid>
//                 ))}
//                 {submitError && (
//                   <Grid item xs={12}>
//                     <Alert severity="error" sx={{ borderRadius: 2 }}>
//                       {submitError}
//                     </Alert>
//                   </Grid>
//                 )}
//               </Grid>
//               <Box
//                 sx={{
//                   display: "flex",
//                   flexDirection: { xs: "column-reverse", sm: "row" },
//                   justifyContent: "flex-end",
//                   gap: 2,
//                   mt: 4,
//                   pt: 3,
//                   borderTop: "1px solid",
//                   borderColor: "divider",
//                 }}
//               >
//                 <Button onClick={onClose} variant="outlined" size="large" fullWidth={isXs} sx={{ borderRadius: 2, minWidth: 120,  }}>
//                   Cancel
//                 </Button>
//                 <Button type="submit" variant="contained" size="large" disabled={isSubmitting} fullWidth={isXs} sx={{ borderRadius: 2, minWidth: 120,backgroundColor: "#4D3CC3"  }}>
//                   {isSubmitting ? <CircularProgress size={24} /> : "Submit Evaluation"}
//                 </Button>
//               </Box>
//             </form>
//           </Container>
//         </Box>
//       </Fade>
//     </Modal>
//   )
// }

// function JobDetailsView({ candidate, onBack, onEvaluate }) {
//   const theme = useTheme()
//   const isXs = useMediaQuery(theme.breakpoints.down("sm"))
//   return (
//     <Box>
//       <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",color: "#4D3CC3"  }}>
//         <Button variant="outlined" startIcon={<ArrowBack />} onClick={onBack} sx={{ borderRadius: 2, bgcolor: "#F1FFFA", color: "#4D3CC3"         
//  }}>
//           Back to Candidates
//         </Button>
//       </Box>
//       <Card elevation={1} sx={{ mb: 4, borderRadius: 2, bgcolor: "#F1FFFA" }}>
//         <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
//           <Grid container spacing={3} alignItems="center">
//             <Grid item xs="auto">
//               <Avatar
//                 src={candidate.avatar}
//                 sx={{
//                   width: { xs: 64, sm: 72, md: 80 },
//                   height: { xs: 64, sm: 72, md: 80 },
//                   border: "3px solid",
//                   borderColor: "#4D3CC3",
//                 }}
//               />
//             </Grid>
//             <Grid item xs>
//               <Typography variant="h5" fontWeight="bold" gutterBottom>
//                 {candidate.name}
//               </Typography>
//               <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
//                 <Email fontSize="small" color="action" />
//                 <Typography variant="body2" color="text.secondary">
//                   {candidate.email}
//                 </Typography>
//               </Box>
//               <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
//                 <LocationOn fontSize="small" color="action" />
//                 <Typography variant="body2" color="text.secondary">
//                   {candidate.location}
//                 </Typography>
//               </Box>
//               <Chip
//                 label={`${candidate.interviews.length} Scheduled Interview${candidate.interviews.length !== 1 ? "s" : ""}`}
//                 color="#4D3CC3"
//                 variant="outlined"
//                 sx={{ fontWeight: 600 }}
//               />
//             </Grid>
//           </Grid>
//         </CardContent>
//       </Card>
//       {/* <Grid container spacing={3}>
//         {candidate.interviews.map((interview) => (
//           <Grid item xs={12} md={6} lg={4} key={interview.id}>
//             <Card elevation={1} sx={{ borderRadius: 2, bgcolor: "#F1FFFA", height: "100%" }}>
//               <CardContent sx={{ p: { xs: 2, sm: 3 }, display: "flex", flexDirection: "column", height: "100%" }}>
//                 <Box sx={{ flexGrow: 1 }}>
//                   <Typography variant="h6" fontWeight="600" gutterBottom sx={{ color: "text.primary" }}>
//                     {interview.job_title}
//                   </Typography>
//                   <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 2 }}>
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                       <Business fontSize="small" color="action" />
//                       <Typography variant="body2" color="text.secondary" fontWeight="500">
//                         {interview.interview_mode}
//                       </Typography>
//                     </Box>
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                       <Event fontSize="small" color="action" />
//                       <Typography variant="body2" color="text.secondary" fontWeight="500">
//                         {new Date(`${interview.interview_date}T${interview.interview_time}`).toLocaleString()}
//                       </Typography>
//                     </Box>
//                   </Box>
//                   <Chip
//                     label={interview.status}
//                     color={interview.status.toLowerCase() === "scheduled" ? "success" : "warning"}
//                     size="small"
//                     sx={{ fontWeight: 600, mb: 2 }}
//                   />
//                 </Box>
//                 <Box sx={{ mt: "auto", pt: 2 }}>
//                   <Button
//                     variant="contained"
//                     startIcon={<RateReview />}
//                      backgroundColor= "#4D3CC3"     
//                     onClick={() => onEvaluate(candidate, interview)}
//                     fullWidth
//                     sx={{
//                       borderRadius: 2,
//                       backgroundColor: "#4D3CC3",
//                       fontWeight: 600,
//                       boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
//                       "&:hover": {
//                         transform: "translateY(-1px)",
//                         boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
//                       },
//                     }}
//                   >
//                     Evaluate
//                   </Button>
//                 </Box>
//               </CardContent>
//             </Card>
//           </Grid>
//         ))}
//       </Grid> */}
// <Grid container spacing={3}>
//   {candidate.interviews.map((interview) => (
//     <Grid item xs={12} md={6} lg={4} key={interview.id} sx={{ display: 'flex' }}>
//       <Card
//         elevation={1}
//         sx={{
//           borderRadius: 2,
//           bgcolor: "#F1FFFA",
//           width: '100%', // Ensure card takes full width of the grid item
//           display: 'flex',
//           flexDirection: 'column', // Arrange content vertically
//         }}
//       >
//         <CardContent
//           sx={{
//             p: { xs: 2, sm: 3 },
//             display: "flex",
//             flexDirection: "column",
//             flexGrow: 1, // Allow card content to grow and fill space
//           }}
//         >
//           <Box sx={{ flexGrow: 1 }}>
//             <Typography variant="h6" fontWeight="600" gutterBottom sx={{ color: "text.primary" }}>
//               {interview.job_title}
//             </Typography>
//             <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 2 }}>
//               <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                 <Business fontSize="small" color="action" />
//                 <Typography variant="body2" color="text.secondary" fontWeight="500">
//                   {interview.interview_mode}
//                 </Typography>
//               </Box>
//               <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                 <Event fontSize="small" color="action" />
//                 <Typography variant="body2" color="text.secondary" fontWeight="500">
//                   {new Date(`${interview.interview_date}T${interview.interview_time}`).toLocaleString()}
//                 </Typography>
//               </Box>
//             </Box>
//             {/* <Chip
//               label={interview.status}
//               color={interview.status.toLowerCase() === "scheduled" ? "success" : "warning"}
//               size="small"
//               sx={{ fontWeight: 600, mb: 2 }}
//             /> */}
//           </Box>
//           <Box sx={{ mt: "auto", pt: 2 }}>
//             <Button
//               variant="contained"
//               startIcon={<RateReview />}
//               onClick={() => onEvaluate(candidate, interview)}
//               fullWidth
//               sx={{
//                 borderRadius: 2,
//                 backgroundColor: "#4D3CC3",
//                 fontWeight: 600,
//                 boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
//                 "&:hover": {
//                   transform: "translateY(-1px)",
//                   boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
//                 },
//               }}
//             >
//               Evaluate
//             </Button>
//           </Box>
//         </CardContent>
//       </Card>
//     </Grid>
//   ))}
// </Grid>
//     </Box>

//   )
// }

// function ScheduledInterviewsTab({ onEvaluateClick }) {
//   const [candidates, setCandidates] = useState([])
//   const [loading, setLoading] = useState(true)
//   const [error, setError] = useState(null)
//   const [searchTerm, setSearchTerm] = useState("")
//   const [selectedCandidateDetails, setSelectedCandidateDetails] = useState(null)
//   const [showJobDetails, setShowJobDetails] = useState(false)
//   const [page, setPage] = useState(0)
//   const [rowsPerPage, setRowsPerPage] = useState(5)
//   const theme = useTheme()
//   const isXs = useMediaQuery(theme.breakpoints.down("sm"))

//   useEffect(() => {
//     const fetchAndProcessInterviews = async () => {
//       try {
//         const response = await apiClient.get("/interviews/")
//         const data = response.data
//         const filtered = data.filter(
//           (interview) =>
//             interview.status.toLowerCase() === "scheduled" || interview.status.toLowerCase() === "rescheduled",
//         )
//         const groupedByCandidate = filtered.reduce((acc, interview) => {
//           const { candidate_name, profile_photo, talent_current_location } = interview
//           if (!acc[candidate_name]) {
//             acc[candidate_name] = {
//               id: candidate_name.replace(/\s+/g, "-").toLowerCase(),
//               name: candidate_name,
//               email: `${candidate_name.replace(/\s+/g, ".").toLowerCase()}@example.com`,
//               avatar: profile_photo || `https://i.pravatar.cc/150?u=${encodeURIComponent(candidate_name)}`,
//               location: talent_current_location || "Location not specified",
//               interviews: [],
//             }
//           }
//           acc[candidate_name].interviews.push(interview)
//           return acc
//         }, {})
//         setCandidates(Object.values(groupedByCandidate))
//       } catch (e) {
//         setError(e.response?.data?.detail || e.message)
//       } finally {
//         setLoading(false)
//       }
//     }
//     fetchAndProcessInterviews()
//   }, [])

//   const handleViewDetails = (candidate) => {
//     setSelectedCandidateDetails(candidate)
//     setShowJobDetails(true)
//   }

//   const handleBackToList = () => {
//     setShowJobDetails(false)
//     setSelectedCandidateDetails(null)
//   }

//   const handleChangePage = (event, newPage) => {
//     setPage(newPage)
//   }

//   const handleChangeRowsPerPage = (event) => {
//     setRowsPerPage(parseInt(event.target.value, 10))
//     setPage(0)
//   }

//   const filteredCandidates = candidates.filter(
//     (c) =>
//       c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       c.email.toLowerCase().includes(searchTerm.toLowerCase()),
//   )

//   const skeletonColumns = [
//     { type: "avatar", width: "40%" },
//     { type: "chip", width: "30%" },
//     { type: "button", width: "30%" },
//   ]

//   if (loading) {
//     return <TableSkeleton columns={skeletonColumns} rows={rowsPerPage} />
//   }

//   if (error) {
//     return (
//       <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//         <Typography variant="h6" color="error" gutterBottom>
//           Error Fetching Interviews
//         </Typography>
//         <Typography>{error}</Typography>
//       </Paper>
//     )
//   }

//   if (showJobDetails && selectedCandidateDetails) {
//     return (
//       <JobDetailsView candidate={selectedCandidateDetails} onBack={handleBackToList} onEvaluate={onEvaluateClick} />
//     )
//   }

//   return (
//     <Box>
//       {filteredCandidates.length === 0 && !loading ? (
//         <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//           <Typography variant="h6" color="text.secondary">
//             No Scheduled Interviews Found
//           </Typography>
//         </Paper>
//       ) : (
//         <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
//           <Table size={isXs ? "small" : "medium"}>
//             <TableHead>
//               <TableRow sx={{ backgroundColor: "#F1FFFA" }}>
//                 <TableCell sx={{ fontWeight: "bold" }}>Candidate</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Interviews</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {filteredCandidates
//                 .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
//                 .map((candidate) => (
//                   <TableRow key={candidate.id} sx={{ "&:hover": { bgcolor: "grey.50" } }}>
//                     <TableCell>
//                       <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                         <Avatar src={candidate.avatar} sx={{ width: 48, height: 48 }} />
//                         <Typography variant="body1" fontWeight="600">
//                           {candidate.name}
//                         </Typography>
//                       </Box>
//                     </TableCell>
//                     <TableCell>
//                       <Chip
//                         label={`${candidate.interviews.length} interview${candidate.interviews.length !== 1 ? "s" : ""}`}
//                             color= "#4D3CC3"         
//                              variant="outlined"
//                         size="small"
//                         sx={{ fontWeight: 600 }}
//                       />
//                     </TableCell>
//                     <TableCell>
//                       <Button
//                         variant="contained"
                        
//                         onClick={() => handleViewDetails(candidate)}
//                         sx={{
//                           borderRadius: 2,
//                           backgroundColor: "#4D3CC3",         
//                           fontWeight: 600,
//                           boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
//                           "&:hover": {
//                             transform: "translateY(-1px)",
//                             boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
//                           },
//                         }}
//                       >
//                         View Details
//                       </Button>
//                     </TableCell>
//                   </TableRow>
//                 ))}
//             </TableBody>
//           </Table>
//           <TablePagination
//             rowsPerPageOptions={[5, 10, 25]}
//             component="div"
//             count={filteredCandidates.length}
//             rowsPerPage={rowsPerPage}
//             page={page}
//             onPageChange={handleChangePage}
//             onRowsPerPageChange={handleChangeRowsPerPage}
//           />
//         </TableContainer>
//       )}
//     </Box>
//   )
// }

// function ExtendOfferTab({ onAlert }) {
//   const [completedCandidates, setCompletedCandidates] = useState([])
//   const [loading, setLoading] = useState(true)
//   const [error, setError] = useState(null)
//   const [extendingId, setExtendingId] = useState(null)
//   const [page, setPage] = useState(0)
//   const [rowsPerPage, setRowsPerPage] = useState(5)

//   const fetchCompletedCandidates = async () => {
//     setLoading(true)
//     try {
//       const response = await apiClient.get("/interviews/completed-candidates/")
//       const eligibleCandidates = response.data.filter(
//         (candidate) => candidate.application_status !== "HIRED",
//       )
//       setCompletedCandidates(eligibleCandidates)
//     } catch (e) {
//       setError(e.response?.data?.detail || e.message)
//     } finally {
//       setLoading(false)
//     }
//   }

//   useEffect(() => {
//     fetchCompletedCandidates()
//   }, [])

//   const handleExtendOffer = async (applicationId) => {
//     setExtendingId(applicationId)
//     try {
//       await apiClient.post(`/applications/${applicationId}/offer-extend/`)
//       onAlert("Offer extended successfully!", "success")
//       await fetchCompletedCandidates()
//     } catch (err) {
//       const errorMessage = err.response?.data?.detail || err.message || "Failed to extend offer."
//       onAlert(errorMessage, "error")
//     } finally {
//       setExtendingId(null)
//     }
//   }

//   const handleChangePage = (event, newPage) => {
//     setPage(newPage)
//   }

//   const handleChangeRowsPerPage = (event) => {
//     setRowsPerPage(parseInt(event.target.value, 10))
//     setPage(0)
//   }

//   const skeletonColumns = [
//     { type: "text", width: "25%" },
//     { type: "text", width: "25%" },
//     { type: "text", width: "15%" },
//     { type: "chip", width: "15%" },
//     { type: "button", width: "20%" },
//   ]

//   if (loading) {
//     return <TableSkeleton columns={skeletonColumns} rows={rowsPerPage} />
//   }

//   if (error) {
//     return (
//       <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//         <Typography variant="h6" color="error" gutterBottom>
//           Error Fetching Data
//         </Typography>
//         <Typography>{error}</Typography>
//       </Paper>
//     )
//   }

//   return (
//     <Box>
//       {completedCandidates.length === 0 ? (
//         <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//           <Typography variant="h6" color="text.secondary">
//             No evaluated candidates available to extend an offer.
//           </Typography>
//         </Paper>
//       ) : (
//         <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
//           <Table>
//             <TableHead>
//               <TableRow sx={{ backgroundColor: "#F1FFFA" }}>
//                 <TableCell sx={{ fontWeight: "bold" }}>Candidate</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Job Title</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Tech Rating</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
//                 <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Actions</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {completedCandidates
//                 .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
//                 .map((candidate) => (
//                   <TableRow key={candidate.application_id} sx={{ "&:hover": { bgcolor: "grey.50" } }}>
//                     <TableCell>{candidate.talent_name}</TableCell>
//                     <TableCell>{candidate.job_posting_title}</TableCell>
//                     <TableCell>{candidate.technical_skills_rating || "N/A"}</TableCell>
//                     <TableCell>
//                       <Chip
//                         label={candidate.application_status.replace(/_/g, " ")}
//                         color={candidate.application_status === "OFFER_EXTENDED" ? "success" : "default"}
//                         size="small"
//                         sx={{ textTransform: "capitalize" }}
//                       />
//                     </TableCell>
//                     <TableCell sx={{ textAlign: "center" }}>
//                       <Button
//                         variant="contained"
//                         color="primary"
//                         backgroundColor=   "#4D3CC3"
//                         startIcon={extendingId === candidate.application_id ? null : <Send />}
//                         disabled={
//                           extendingId === candidate.application_id ||
//                           candidate.application_status === "OFFER_EXTENDED"
//                         }
//                         onClick={() => handleExtendOffer(candidate.application_id)}
//                         sx={{
//                           minWidth: 140,
//                           borderRadius: 2,
//                           backgroundColor: "#4D3CC3"
//                         }}
//                       >
//                         {extendingId === candidate.application_id ? (
//                           <CircularProgress size={24} color="inherit" />
//                         ) : candidate.application_status === "OFFER_EXTENDED" ? (
//                           "Offer Extended"
//                         ) : (
//                           "Extend Offer"
//                         )}
//                       </Button>
//                     </TableCell>
//                   </TableRow>
//                 ))}
//             </TableBody>
//           </Table>
//           <TablePagination
//             rowsPerPageOptions={[5, 10, 25]}
//             component="div"
//             count={completedCandidates.length}
//             rowsPerPage={rowsPerPage}
//             page={page}
//             onPageChange={handleChangePage}
//             onRowsPerPageChange={handleChangeRowsPerPage}
//           />
//         </TableContainer>
//       )}
//     </Box>
//   )
// }

// function HiredCandidatesTab() {
//   const [hiredCandidates, setHiredCandidates] = useState([])
//   const [loading, setLoading] = useState(true)
//   const [error, setError] = useState(null)
//   const [page, setPage] = useState(0)
//   const [rowsPerPage, setRowsPerPage] = useState(5)

//   useEffect(() => {
//     const fetchHiredCandidates = async () => {
//       try {
//         const response = await apiClient.get("/interviews/completed-candidates/")
//         const filteredData = response.data.filter(
//           (candidate) => candidate.application_status === "HIRED",
//         )
//         setHiredCandidates(filteredData)
//       } catch (e) {
//         setError(e.response?.data?.detail || e.message)
//       } finally {
//         setLoading(false)
//       }
//     }

//     fetchHiredCandidates()
//   }, [])

//   const handleChangePage = (event, newPage) => {
//     setPage(newPage)
//   }

//   const handleChangeRowsPerPage = (event) => {
//     setRowsPerPage(parseInt(event.target.value, 10))
//     setPage(0)
//   }

//   const skeletonColumns = [
//     { type: "text", width: "20%" },
//     { type: "text", width: "20%" },
//     { type: "text", width: "15%" },
//     { type: "chip", width: "15%" },
//     { type: "text", width: "30%" },
//   ]

//   if (loading) {
//     return <TableSkeleton columns={skeletonColumns} rows={rowsPerPage} />
//   }

//   if (error) {
//     return (
//       <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//         <Typography variant="h6" color="error" gutterBottom>
//           Error Fetching Data
//         </Typography>
//         <Typography>{error}</Typography>
//       </Paper>
//     )
//   }

//   return (
//     <Box>
//       {hiredCandidates.length === 0 ? (
//         <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//           <Typography variant="h6" color="text.secondary">
//             No hired candidates found.
//           </Typography>
//         </Paper>
//       ) : (
//         <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
//           <Table>
//             <TableHead>
//               <TableRow sx={{ backgroundColor: "#F1FFFA" }}>
//                 <TableCell sx={{ fontWeight: "bold" }}>Candidate</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Job Title</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Tech Rating</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Overall Comments</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {hiredCandidates
//                 .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
//                 .map((candidate) => (
//                   <TableRow key={candidate.application_id} sx={{ "&:hover": { bgcolor: "grey.50" } }}>
//                     <TableCell>{candidate.talent_name}</TableCell>
//                     <TableCell>{candidate.job_posting_title}</TableCell>
//                     <TableCell>{candidate.technical_skills_rating || "N/A"}</TableCell>
//                     <TableCell>
//                       <Chip
//                         label={candidate.application_status.replace(/_/g, " ")}
//                         size="small"
//                         sx={{ textTransform: "capitalize", backgroundColor: "#4D3CC3" , color: "white" }}
//                       />
//                     </TableCell>
//                     <TableCell>{candidate.overall_comments || "N/A"}</TableCell>
//                   </TableRow>
//                 ))}
//             </TableBody>
//           </Table>
//           <TablePagination
//             rowsPerPageOptions={[5, 10, 25]}
//             component="div"
//             count={hiredCandidates.length}
//             rowsPerPage={rowsPerPage}
//             page={page}
//             onPageChange={handleChangePage}
//             onRowsPerPageChange={handleChangeRowsPerPage}
//           />
//         </TableContainer>
//       )}
//     </Box>
//   )
// }

// export default function EvaluationsPage() {
//   const [modalOpen, setModalOpen] = useState(false)
//   const [selectedInterview, setSelectedInterview] = useState(null)
//   const [tabValue, setTabValue] = useState(0)
//   const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" })

//   const handleTabChange = (event, newValue) => {
//     setTabValue(newValue)
//   }

//   const handleEvaluateClick = (candidate, interview) => {
//     setSelectedInterview({
//       name: candidate.name,
//       email: candidate.email,
//       avatar: candidate.avatar,
//       interviewId: interview.id,
//       jobTitle: interview.job_title,
//     })
//     setModalOpen(true)
//   }

//   const handleCloseModal = () => {
//     setModalOpen(false)
//     setSelectedInterview(null)
//   }

//   const handleAlert = (message, severity) => {
//     setSnackbar({ open: true, message, severity })
//   }

//   const handleSnackbarClose = (event, reason) => {
//     if (reason === "clickaway") return
//     setSnackbar({ ...snackbar, open: false })
//   }

//   return (
//     <Layout title="Evaluations">
//       <ThemeProvider theme={theme}>
//         <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
//           <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
//             <Paper sx={{ p: { xs: 1, sm: 2 }, borderRadius: 3, backgroundColor: "#F1FFFA", color: "#4D3CC3" }}>
//               <Typography variant="h4" gutterBottom>
//                 Candidate Evaluations & Extend Offers
//               </Typography>
//             </Paper>
//             <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 2  }}>
//               <Tabs value={tabValue} onChange={handleTabChange} aria-label="evaluation tabs" color= "#4D3CC3">
//                 <Tab label="Scheduled Interviews" id="tab-0" />
//                 <Tab label="Extend Offer" id="tab-1" />
//                 <Tab label="Hired Candidates" id="tab-2" />
//               </Tabs>
//             </Box>
//             <TabPanel value={tabValue} index={0}>
//               <ScheduledInterviewsTab onEvaluateClick={handleEvaluateClick} />
//             </TabPanel>
//             <TabPanel value={tabValue} index={1}>
//               <ExtendOfferTab onAlert={handleAlert} />
//             </TabPanel>
//             <TabPanel value={tabValue} index={2}>
//               <HiredCandidatesTab />
//             </TabPanel>
//           </Container>
//         </Box>
//         {selectedInterview && (
//           <EvaluationForm
//             open={modalOpen}
//             onClose={handleCloseModal}
//             interviewDetails={selectedInterview}
//             onSuccess={handleAlert}
//           />
//         )}
//         <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose}>
//           <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
//             {snackbar.message}
//           </Alert>
//         </Snackbar>
//       </ThemeProvider>
//     </Layout>
//   )
// }

// import React, { useState, useEffect } from "react"
// import {
//   Container,
//   Paper,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   Rating,
//   TextField,
//   Button,
//   Box,
//   Avatar,
//   Divider,
//   FormControl,
//   Select,
//   MenuItem,
//   List,
//   Accordion,
//   Chip,
//   Modal,
//   CircularProgress,
//   Alert,
//   IconButton,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Backdrop,
//   useMediaQuery,
//   Fade,
//   Tabs,
//   Tab,
//   Snackbar,
//   TablePagination,
// } from "@mui/material"
// import {
//   Person,
//   Email,
//   Event,
//   Business,
//   RateReview,
//   ArrowBack,
//   Close,
//   Add,
//   Remove,
//   LocationOn,
//   ThumbUp,
//   Send,
// } from "@mui/icons-material"
// import { createTheme, ThemeProvider, styled, responsiveFontSizes, useTheme } from "@mui/material/styles"
// import Layout from "../common/Layout"
// import apiClient from "../../context/apiClient"

// const baseTheme = createTheme({
//   palette: {
//     primary: {
//       main: "#1976d2",
//       light: "#42a5f5",
//       dark: "#1565c0",
//     },
//     background: {
//       default: "#f8fafc",
//       paper: "#ffffff",
//     },
//     text: {
//       primary: "#1e293b",
//       secondary: "#64748b",
//     },
//   },
//   typography: {
//     fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
//     h4: {
//       fontWeight: 700,
//       letterSpacing: "-0.025em",
//     },
//     h6: {
//       fontWeight: 600,
//     },
//     body1: {
//       lineHeight: 1.6,
//     },
//   },
//   components: {
//     MuiCard: {
//       styleOverrides: {
//         root: {
//           borderRadius: 16,
//           boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
//           transition: "all 0.2s ease-in-out",
//           "&:hover": {
//             boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//             transform: "translateY(-2px)",
//           },
//         },
//       },
//     },
//     MuiButton: {
//       styleOverrides: {
//         root: {
//           borderRadius: 12,
//           textTransform: "none",
//           fontWeight: 600,
//           transition: "all 0.2s ease-in-out",
//         },
//         contained: {
//           boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
//           "&:hover": {
//             boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
//             transform: "translateY(-1px)",
//           },
//         },
//       },
//     },
//     MuiAccordion: {
//       styleOverrides: {
//         root: {
//           borderRadius: "16px !important",
//           boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
//           "&:before": {
//             display: "none",
//           },
//           "&$expanded": {
//             margin: "auto",
//           },
//         },
//       },
//     },
//   },
// })

// const theme = responsiveFontSizes(baseTheme)

// const StyledAccordion = styled(Accordion)(({ theme }) => ({
//   transition: theme.transitions.create(["box-shadow", "margin", "transform"], {
//     duration: theme.transitions.duration.short,
//   }),
//   "&:hover": {
//     transform: "translateY(-2px)",
//     boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//   },
//   "&.Mui-expanded": {
//     boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//     margin: `${theme.spacing(1, 0)} !important`,
//     transform: "translateY(-2px)",
//   },
// }))

// function TabPanel(props) {
//   const { children, value, index, ...other } = props
//   return (
//     <div
//       role="tabpanel"
//       hidden={value !== index}
//       id={`simple-tabpanel-${index}`}
//       aria-labelledby={`simple-tab-${index}`}
//       {...other}
//     >
//       {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
//     </div>
//   )
// }

// function EvaluationForm({ interviewDetails, open, onClose, onSuccess }) {
//   const theme = useTheme()
//   const isXs = useMediaQuery(theme.breakpoints.down("sm"))
//   const [formData, setFormData] = useState({
//     recommendation: "",
//     technical_skills_rating: 0,
//     communication_skills_rating: 0,
//     cultural_fit_rating: 0,
//     strengths: [""],
//     weaknesses: [""],
//     overall_comments: [""],
//   })
//   const [isSubmitting, setIsSubmitting] = useState(false)
//   const [submitError, setSubmitError] = useState(null)

//   useEffect(() => {
//     if (open) {
//       setFormData({
//         recommendation: "",
//         technical_skills_rating: 0,
//         communication_skills_rating: 0,
//         cultural_fit_rating: 0,
//         strengths: [""],
//         weaknesses: [""],
//         overall_comments: [""],
//       })
//       setIsSubmitting(false)
//       setSubmitError(null)
//     }
//   }, [open])

//   const handleChange = (e) => {
//     const { name, value } = e.target
//     setFormData((prev) => ({ ...prev, [name]: value }))
//   }

//   const handleRatingChange = (field, value) => {
//     setFormData((prev) => ({ ...prev, [field]: value !== null ? Math.round(value) : 0 }))
//   }

//   const handleArrayFieldChange = (field, index, value) => {
//     setFormData((prev) => ({
//       ...prev,
//       [field]: prev[field].map((item, i) => (i === index ? value : item)),
//     }))
//   }

//   const addArrayField = (field) => {
//     setFormData((prev) => ({
//       ...prev,
//       [field]: [...prev[field], ""],
//     }))
//   }

//   const removeArrayField = (field, index) => {
//     if (formData[field].length > 1) {
//       setFormData((prev) => ({
//         ...prev,
//         [field]: prev[field].filter((_, i) => i !== index),
//       }))
//     }
//   }

//   const handleSubmit = async (e) => {
//     e.preventDefault()
//     setIsSubmitting(true)
//     setSubmitError(null)
//     try {
//       const submissionData = {
//         ...formData,
//         strengths: formData.strengths.filter((s) => s.trim()).join("; "),
//         weaknesses: formData.weaknesses.filter((w) => w.trim()).join("; "),
//         overall_comments: formData.overall_comments.filter((c) => c.trim()).join("; "),
//         interview_id: interviewDetails.interviewId,
//       }

//       await apiClient.post(
//         `/interviews/${interviewDetails.interviewId}/submit-feedback/`,
//         submissionData,
//       )
//       onSuccess("Evaluation submitted successfully!", "success")
//       onClose()
//     } catch (error) {
//       console.error("[v0] Error submitting evaluation:", error)
//       const errorMessage =
//         error.response?.data?.message || error.message || "An unexpected error occurred."
//       setSubmitError(errorMessage)
//     } finally {
//       setIsSubmitting(false)
//     }
//   }

//   return (
//     <Modal
//       open={open}
//       onClose={onClose}
//       closeAfterTransition
//       BackdropComponent={Backdrop}
//       BackdropProps={{
//         timeout: 500,
//       }}
//     >
//       <Fade in={open}>
//         <Box
//           component={Paper}
//           elevation={0}
//           sx={{
//             position: "absolute",
//             top: "50%",
//             left: "50%",
//             transform: "translate(-50%, -50%)",
//             width: { xs: "95%", sm: "90%", md: "80%", lg: "70%" },
//             maxWidth: "900px",
//             maxHeight: "90vh",
//             bgcolor: "background.paper",
//             borderRadius: 3,
//             boxShadow: 24,
//             overflow: "hidden",
//             display: "flex",
//             flexDirection: "column",
//           }}
//         >
//           <Box
//             sx={{
//               background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
//               color: "white",
//               p: { xs: 2, md: 3 },
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "space-between",
//               flexShrink: 0,
//             }}
//           >
//             <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
//               <Avatar
//                 src={interviewDetails?.avatar}
//                 sx={{
//                   width: { xs: 48, sm: 56 },
//                   height: { xs: 48, sm: 56 },
//                   border: "3px solid rgba(255,255,255,0.3)",
//                   flexShrink: 0,
//                 }}
//               >
//                 {interviewDetails?.name?.charAt(0)}
//               </Avatar>
//               <Box sx={{ minWidth: 0 }}>
//                 <Typography
//                   variant="h5"
//                   fontWeight="bold"
//                   sx={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}
//                 >
//                   Evaluate Candidate
//                 </Typography>
//                 <Typography
//                   variant="body1"
//                   sx={{ opacity: 0.9, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}
//                 >
//                   {interviewDetails?.name} • {interviewDetails?.jobTitle}
//                 </Typography>
//               </Box>
//             </Box>
//             <IconButton onClick={onClose} sx={{ color: "white" }}>
//               <Close />
//             </IconButton>
//           </Box>
//           <Container
//             maxWidth="lg"
//             sx={{
//               flex: 1,
//               overflow: "auto",
//               py: { xs: 2, md: 3 },
//               minHeight: 0,
//               maxHeight: "calc(90vh - 140px)",
//               "&::-webkit-scrollbar": { width: "8px" },
//               "&::-webkit-scrollbar-track": { background: "#f1f1f1", borderRadius: "4px" },
//               "&::-webkit-scrollbar-thumb": {
//                 background: "#c1c1c1",
//                 borderRadius: "4px",
//                 "&:hover": { background: "#a8a8a8" },
//               },
//               scrollbarWidth: "thin",
//               scrollbarColor: "#c1c1c1 #f1f1f1",
//             }}
//           >
//             <form onSubmit={handleSubmit}>
//               <Grid container spacing={3}>
//                 <Grid item xs={12}>
//                   <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, bgcolor: "#F1FFFA" }}>
//                     <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                       <ThumbUp color="primary" />
//                       Recommendation
//                     </Typography>
//                     <FormControl fullWidth>
//                       <Select
//                         name="recommendation"
//                         value={formData.recommendation}
//                         onChange={handleChange}
//                         required
//                         sx={{ borderRadius: 2 }}
//                       >
//                         <MenuItem value="">
//                           <em>Select recommendation</em>
//                         </MenuItem>
//                         <MenuItem value="STRONG_HIRE">
//                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <Person color="success" /> Strong Hire
//                           </Box>
//                         </MenuItem>
//                         <MenuItem value="HIRE">
//                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <Person color="success" /> Hire
//                           </Box>
//                         </MenuItem>
//                         <MenuItem value="NO_HIRE">
//                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <Person color="error" /> No Hire
//                           </Box>
//                         </MenuItem>
//                         <MenuItem value="NOT_SURE">
//                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <Person color="warning" /> Not Sure / Needs More Review
//                           </Box>
//                         </MenuItem>
//                       </Select>
//                     </FormControl>
//                   </Card>
//                 </Grid>
//                 <Grid item xs={12}>
//                   <Card
//                     variant="outlined"
//                     sx={{ p: { xs: 2, sm: 3 }, gap: 3, borderRadius: 2, bgcolor: "#F1FFFA", width: "100%" }}
//                   >
//                     <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
//                       Skills Assessment
//                     </Typography>
//                     <Grid container spacing={3}>
//                       {[
//                         { field: "technical_skills_rating", label: "Technical Skills", icon: "💻" },
//                         { field: "communication_skills_rating", label: "Communication", icon: "💬" },
//                         { field: "cultural_fit_rating", label: "Cultural Fit", icon: "🤝" },
//                       ].map((rating) => (
//                         <Grid item xs={12} sm={4} key={rating.field}>
//                           <Box
//                             sx={{
//                               textAlign: "center",
//                               p: 2,
//                               borderRadius: 2,
//                               border: "1px solid",
//                               borderColor: "divider",
//                               bgcolor: "grey.50",
//                             }}
//                           >
//                             <Typography variant="h4" sx={{ mb: 1 }}>
//                               {rating.icon}
//                             </Typography>
//                             <Typography variant="body1" fontWeight="medium" gutterBottom>
//                               {rating.label}
//                             </Typography>
//                             <Rating
//                               name={rating.field}
//                               value={formData[rating.field]}
//                               onChange={(e, val) => handleRatingChange(rating.field, val)}
//                               size={isXs ? "medium" : "large"}
//                               sx={{ mb: 1 }}
//                               max={5}
//                             />
//                             <Typography variant="body2" color="text.secondary">
//                               {formData[rating.field]}/5
//                             </Typography>
//                           </Box>
//                         </Grid>
//                       ))}
//                     </Grid>
//                   </Card>
//                 </Grid>
//                 {[
//                   { field: "strengths", label: "Key Strengths", placeholder: "What impressed you?", icon: "⭐" },
//                   { field: "weaknesses", label: "Areas for Improvement", placeholder: "What could be developed?", icon: "📈" },
//                   { field: "overall_comments", label: "Overall Assessment", placeholder: "Provide comprehensive evaluation...", icon: "📝" },
//                 ].map((section) => (
//                   <Grid item xs={12} key={section.field}>
//                     <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, bgcolor: "#F1FFFA" }}>
//                       <Box
//                         sx={{
//                           display: "flex",
//                           alignItems: { xs: "stretch", sm: "center" },
//                           justifyContent: "space-between",
//                           gap: 2,
//                           mb: 2,
//                           flexDirection: { xs: "column", sm: "row" },
//                         }}
//                       >
//                         <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                           {section.icon} {section.label}
//                         </Typography>
//                         <Button
//                           variant="outlined"
//                           size="medium"
//                           startIcon={<Add />}
//                           onClick={() => addArrayField(section.field)}
//                           sx={{ borderRadius: 2, alignSelf: { xs: "stretch", sm: "auto" } }}
//                           fullWidth={isXs}
//                         >
//                           Add
//                         </Button>
//                       </Box>
//                       {formData[section.field].map((item, index) => (
//                         <Box
//                           key={index}
//                           sx={{
//                             display: "flex",
//                             gap: 1,
//                             mb: 2,
//                             flexDirection: { xs: "column", sm: "row" },
//                             alignItems: { xs: "stretch", sm: "flex-start" },
//                           }}
//                         >
//                           <TextField
//                             fullWidth
//                             multiline
//                             rows={3}
//                             placeholder={section.placeholder}
//                             value={item}
//                             onChange={(e) => handleArrayFieldChange(section.field, index, e.target.value)}
//                             variant="outlined"
//                             sx={{ borderRadius: 2 }}
//                           />
//                           {formData[section.field].length > 1 && (
//                             <IconButton
//                               onClick={() => removeArrayField(section.field, index)}
//                               color="error"
//                               sx={{ alignSelf: { xs: "flex-end", sm: "flex-start" }, mt: { xs: 0, sm: 1 } }}
//                             >
//                               <Remove />
//                             </IconButton>
//                           )}
//                         </Box>
//                       ))}
//                     </Card>
//                   </Grid>
//                 ))}
//                 {submitError && (
//                   <Grid item xs={12}>
//                     <Alert severity="error" sx={{ borderRadius: 2 }}>
//                       {submitError}
//                     </Alert>
//                   </Grid>
//                 )}
//               </Grid>
//               <Box
//                 sx={{
//                   display: "flex",
//                   flexDirection: { xs: "column-reverse", sm: "row" },
//                   justifyContent: "flex-end",
//                   gap: 2,
//                   mt: 4,
//                   pt: 3,
//                   borderTop: "1px solid",
//                   borderColor: "divider",
//                 }}
//               >
//                 <Button onClick={onClose} variant="outlined" size="large" fullWidth={isXs} sx={{ borderRadius: 2, minWidth: 120 }}>
//                   Cancel
//                 </Button>
//                 <Button type="submit" variant="contained" size="large" disabled={isSubmitting} fullWidth={isXs} sx={{ borderRadius: 2, minWidth: 120 }}>
//                   {isSubmitting ? <CircularProgress size={24} /> : "Submit Evaluation"}
//                 </Button>
//               </Box>
//             </form>
//           </Container>
//         </Box>
//       </Fade>
//     </Modal>
//   )
// }

// function JobDetailsView({ candidate, onBack, onEvaluate }) {
//   const theme = useTheme()
//   const isXs = useMediaQuery(theme.breakpoints.down("sm"))
//   return (
//     <Box>
//       <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
//         <Button variant="outlined" startIcon={<ArrowBack />} onClick={onBack} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
//           Back to Candidates
//         </Button>
//       </Box>
//       <Card elevation={1} sx={{ mb: 4, borderRadius: 2, bgcolor: "#F1FFFA" }}>
//         <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
//           <Grid container spacing={3} alignItems="center">
//             <Grid item xs="auto">
//               <Avatar
//                 src={candidate.avatar}
//                 sx={{
//                   width: { xs: 64, sm: 72, md: 80 },
//                   height: { xs: 64, sm: 72, md: 80 },
//                   border: "3px solid",
//                   borderColor: "primary.light",
//                 }}
//               />
//             </Grid>
//             <Grid item xs>
//               <Typography variant="h5" fontWeight="bold" gutterBottom>
//                 {candidate.name}
//               </Typography>
//               <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
//                 <Email fontSize="small" color="action" />
//                 <Typography variant="body2" color="text.secondary">
//                   {candidate.email}
//                 </Typography>
//               </Box>
//               <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
//                 <LocationOn fontSize="small" color="action" />
//                 <Typography variant="body2" color="text.secondary">
//                   {candidate.location}
//                 </Typography>
//               </Box>
//               <Chip
//                 label={`${candidate.interviews.length} Scheduled Interview${candidate.interviews.length !== 1 ? "s" : ""}`}
//                 color="primary"
//                 variant="outlined"
//                 sx={{ fontWeight: 600 }}
//               />
//             </Grid>
//           </Grid>
//         </CardContent>
//       </Card>
//       <Grid container spacing={3}>
//         {candidate.interviews.map((interview) => (
//           <Grid item xs={12} md={6} lg={4} key={interview.id}>
//             <Card elevation={1} sx={{ borderRadius: 2, bgcolor: "#F1FFFA", height: "100%" }}>
//               <CardContent sx={{ p: { xs: 2, sm: 3 }, display: "flex", flexDirection: "column", height: "100%" }}>
//                 <Box sx={{ flexGrow: 1 }}>
//                   <Typography variant="h6" fontWeight="600" gutterBottom sx={{ color: "text.primary" }}>
//                     {interview.job_title}
//                   </Typography>
//                   <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 2 }}>
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                       <Business fontSize="small" color="action" />
//                       <Typography variant="body2" color="text.secondary" fontWeight="500">
//                         {interview.interview_mode}
//                       </Typography>
//                     </Box>
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                       <Event fontSize="small" color="action" />
//                       <Typography variant="body2" color="text.secondary" fontWeight="500">
//                         {new Date(`${interview.interview_date}T${interview.interview_time}`).toLocaleString()}
//                       </Typography>
//                     </Box>
//                   </Box>
//                   <Chip
//                     label={interview.status}
//                     color={interview.status.toLowerCase() === "scheduled" ? "success" : "warning"}
//                     size="small"
//                     sx={{ fontWeight: 600, mb: 2 }}
//                   />
//                 </Box>
//                 <Box sx={{ mt: "auto", pt: 2 }}>
//                   <Button
//                     variant="contained"
//                     startIcon={<RateReview />}
//                     onClick={() => onEvaluate(candidate, interview)}
//                     fullWidth
//                     sx={{
//                       borderRadius: 2,
//                       fontWeight: 600,
//                       boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
//                       "&:hover": {
//                         transform: "translateY(-1px)",
//                         boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
//                       },
//                     }}
//                   >
//                     Evaluate
//                   </Button>
//                 </Box>
//               </CardContent>
//             </Card>
//           </Grid>
//         ))}
//       </Grid>
//     </Box>
//   )
// }

// function ScheduledInterviewsTab({ onEvaluateClick }) {
//   const [candidates, setCandidates] = useState([])
//   const [loading, setLoading] = useState(true)
//   const [error, setError] = useState(null)
//   const [searchTerm, setSearchTerm] = useState("")
//   const [selectedCandidateDetails, setSelectedCandidateDetails] = useState(null)
//   const [showJobDetails, setShowJobDetails] = useState(false)
//   const theme = useTheme()
//   const isXs = useMediaQuery(theme.breakpoints.down("sm"))

//   useEffect(() => {
//     const fetchAndProcessInterviews = async () => {
//       try {
//         const response = await apiClient.get("/interviews/")
//         const data = response.data
//         const filtered = data.filter(
//           (interview) =>
//             interview.status.toLowerCase() === "scheduled" || interview.status.toLowerCase() === "rescheduled",
//         )
//         const groupedByCandidate = filtered.reduce((acc, interview) => {
//           const { candidate_name, profile_photo, talent_current_location } = interview
//           if (!acc[candidate_name]) {
//             acc[candidate_name] = {
//               id: candidate_name.replace(/\s+/g, "-").toLowerCase(),
//               name: candidate_name,
//               email: `${candidate_name.replace(/\s+/g, ".").toLowerCase()}@example.com`,
//               avatar: profile_photo || `https://i.pravatar.cc/150?u=${encodeURIComponent(candidate_name)}`,
//               location: talent_current_location || "Location not specified",
//               interviews: [],
//             }
//           }
//           acc[candidate_name].interviews.push(interview)
//           return acc
//         }, {})
//         setCandidates(Object.values(groupedByCandidate))
//       } catch (e) {
//         setError(e.response?.data?.detail || e.message)
//       } finally {
//         setLoading(false)
//       }
//     }
//     fetchAndProcessInterviews()
//   }, [])

//   const handleViewDetails = (candidate) => {
//     setSelectedCandidateDetails(candidate)
//     setShowJobDetails(true)
//   }

//   const handleBackToList = () => {
//     setShowJobDetails(false)
//     setSelectedCandidateDetails(null)
//   }

//   const filteredCandidates = candidates.filter(
//     (c) =>
//       c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       c.email.toLowerCase().includes(searchTerm.toLowerCase()),
//   )

//   if (loading) {
//     return (
//       <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
//         <CircularProgress />
//       </Box>
//     )
//   }

//   if (error) {
//     return (
//       <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//         <Typography variant="h6" color="error" gutterBottom>
//           Error Fetching Interviews
//         </Typography>
//         <Typography>{error}</Typography>
//       </Paper>
//     )
//   }

//   if (showJobDetails && selectedCandidateDetails) {
//     return (
//       <JobDetailsView candidate={selectedCandidateDetails} onBack={handleBackToList} onEvaluate={onEvaluateClick} />
//     )
//   }

//   return (
//     <Box>
//       {filteredCandidates.length === 0 ? (
//         <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//           <Typography variant="h6" color="text.secondary">
//             No Scheduled Interviews Found
//           </Typography>
//         </Paper>
//       ) : (
//         <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
//           <Table size={isXs ? "small" : "medium"}>
//             <TableHead>
//               <TableRow sx={{ backgroundColor: "#F1FFFA" }}>
//                 <TableCell sx={{ fontWeight: "bold" }}>Candidate</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Interviews</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {filteredCandidates.map((candidate) => (
//                 <TableRow key={candidate.id} sx={{ "&:hover": { bgcolor: "grey.50" } }}>
//                   <TableCell>
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                       <Avatar src={candidate.avatar} sx={{ width: 48, height: 48 }} />
//                       <Typography variant="body1" fontWeight="600">
//                         {candidate.name}
//                       </Typography>
//                     </Box>
//                   </TableCell>
//                   <TableCell>
//                     <Chip
//                       label={`${candidate.interviews.length} interview${candidate.interviews.length !== 1 ? "s" : ""}`}
//                       color="primary"
//                       variant="outlined"
//                       size="small"
//                       sx={{ fontWeight: 600 }}
//                     />
//                   </TableCell>
//                   <TableCell>
//                     <Button
//                       variant="contained"
//                       onClick={() => handleViewDetails(candidate)}
//                       sx={{
//                         borderRadius: 2,
//                         fontWeight: 600,
//                         boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
//                         "&:hover": {
//                           transform: "translateY(-1px)",
//                           boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
//                         },
//                       }}
//                     >
//                       View Details
//                     </Button>
//                   </TableCell>
//                 </TableRow>
//               ))}
//             </TableBody>
//           </Table>
//         </TableContainer>
//       )}
//     </Box>
//   )
// }

// function ExtendOfferTab({ onAlert }) {
//   const [completedCandidates, setCompletedCandidates] = useState([])
//   const [loading, setLoading] = useState(true)
//   const [error, setError] = useState(null)
//   const [extendingId, setExtendingId] = useState(null)
//   const [page, setPage] = useState(0)
//   const [rowsPerPage, setRowsPerPage] = useState(5)

//   const fetchCompletedCandidates = async () => {
//     try {
//       const response = await apiClient.get("/interviews/completed-candidates/")
//       setCompletedCandidates(response.data)
//     } catch (e) {
//       setError(e.response?.data?.detail || e.message)
//     } finally {
//       setLoading(false)
//     }
//   }

//   useEffect(() => {
//     fetchCompletedCandidates()
//   }, [])

//   const handleExtendOffer = async (applicationId) => {
//     setExtendingId(applicationId)
//     try {
//       await apiClient.post(`/applications/${applicationId}/offer-extend/`)
//       onAlert("Offer extended successfully!", "success")
//       await fetchCompletedCandidates()
//     } catch (err) {
//       const errorMessage = err.response?.data?.detail || err.message || "Failed to extend offer."
//       onAlert(errorMessage, "error")
//     } finally {
//       setExtendingId(null)
//     }
//   }

//   const handleChangePage = (event, newPage) => {
//     setPage(newPage)
//   }

//   const handleChangeRowsPerPage = (event) => {
//     setRowsPerPage(parseInt(event.target.value, 10))
//     setPage(0)
//   }

//   if (loading) {
//     return (
//       <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
//         <CircularProgress />
//       </Box>
//     )
//   }

//   if (error) {
//     return (
//       <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//         <Typography variant="h6" color="error" gutterBottom>
//           Error Fetching Data
//         </Typography>
//         <Typography>{error}</Typography>
//       </Paper>
//     )
//   }

//   return (
//     <Box>
//       {completedCandidates.length === 0 ? (
//         <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//           <Typography variant="h6" color="text.secondary">
//             No evaluated candidates available to extend an offer.
//           </Typography>
//         </Paper>
//       ) : (
//         <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
//           <Table>
//             <TableHead>
//               <TableRow sx={{ backgroundColor: "#F1FFFA" }}>
//                 <TableCell sx={{ fontWeight: "bold" }}>Candidate</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Job Title</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Tech Rating</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
//                 <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Actions</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {completedCandidates
//                 .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
//                 .map((candidate) => (
//                   <TableRow key={candidate.application_id} sx={{ "&:hover": { bgcolor: "grey.50" } }}>
//                     <TableCell>{candidate.talent_name}</TableCell>
//                     <TableCell>{candidate.job_posting_title}</TableCell>
//                     <TableCell>{candidate.technical_skills_rating || "N/A"}</TableCell>
//                     <TableCell>
//                       <Chip
//                         label={(
//                           candidate.application_status === "OFFER_EXTENDED" || candidate.application_status === "HIRED"
//                             ? candidate.application_status
//                             : candidate.application_status || candidate.application_status
//                         ).replace(/_/g, " ")}
//                         color={
//                           candidate.application_status === "OFFER_EXTENDED"
//                             ? "success"
//                             : candidate.application_status === "HIRED"
//                               ? "primary"
//                               : "default"
//                         }
//                         size="small"
//                         sx={{ textTransform: "capitalize" }}
//                       />
//                     </TableCell>
//                     <TableCell sx={{ textAlign: "center" }}>
//                       <Button
//                         variant="contained"
//                         color="primary"
//                         startIcon={extendingId === candidate.application_id ? null : <Send />}
//                         disabled={
//                           extendingId === candidate.application_id ||
//                           candidate.application_status === "OFFER_EXTENDED"
//                         }
//                         onClick={() => handleExtendOffer(candidate.application_id)}
//                         sx={{
//                           minWidth: 140,
//                           borderRadius: 2,
//                         }}
//                       >
//                         {extendingId === candidate.application_id ? (
//                           <CircularProgress size={24} color="inherit" />
//                         ) : candidate.application_status === "OFFER_EXTENDED" ? (
//                           "Offer Extended"
//                         ) : (
//                           "Extend Offer"
//                         )}
//                       </Button>
//                     </TableCell>
//                   </TableRow>
//                 ))}
//             </TableBody>
//           </Table>
//           <TablePagination
//             rowsPerPageOptions={[5, 10, 25]}
//             component="div"
//             count={completedCandidates.length}
//             rowsPerPage={rowsPerPage}
//             page={page}
//             onPageChange={handleChangePage}
//             onRowsPerPageChange={handleChangeRowsPerPage}
//           />
//         </TableContainer>
//       )}
//     </Box>
//   )
// }

// export default function EvaluationsPage() {
//   const [modalOpen, setModalOpen] = useState(false)
//   const [selectedInterview, setSelectedInterview] = useState(null)
//   const [tabValue, setTabValue] = useState(0)
//   const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" })

//   const handleTabChange = (event, newValue) => {
//     setTabValue(newValue)
//   }

//   const handleEvaluateClick = (candidate, interview) => {
//     setSelectedInterview({
//       name: candidate.name,
//       email: candidate.email,
//       avatar: candidate.avatar,
//       interviewId: interview.id,
//       jobTitle: interview.job_title,
//     })
//     setModalOpen(true)
//   }

//   const handleCloseModal = () => {
//     setModalOpen(false)
//     setSelectedInterview(null)
//   }

//   const handleAlert = (message, severity) => {
//     setSnackbar({ open: true, message, severity })
//   }

//   const handleSnackbarClose = (event, reason) => {
//     if (reason === "clickaway") return
//     setSnackbar({ ...snackbar, open: false })
//   }

//   return (
//     <Layout title="Evaluations">
//       <ThemeProvider theme={theme}>
//         <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
//           <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
//             <Paper sx={{ p: { xs: 1, sm: 2 }, borderRadius: 3, backgroundColor: "#F1FFFA", color: "#4D3CC3" }}>
//               <Typography variant="h4" gutterBottom>
//                 Candidate Evaluations & Extend Offers
//               </Typography>
//             </Paper>
//             <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 2 }}>
//               <Tabs value={tabValue} onChange={handleTabChange} aria-label="evaluation tabs">
//                 <Tab label="Scheduled Interviews" id="tab-0" />
//                 <Tab label="Extend Offer" id="tab-1" />
//               </Tabs>
//             </Box>
//             <TabPanel value={tabValue} index={0}>
//               <ScheduledInterviewsTab onEvaluateClick={handleEvaluateClick} />
//             </TabPanel>
//             <TabPanel value={tabValue} index={1}>
//               <ExtendOfferTab onAlert={handleAlert} />
//             </TabPanel>
//           </Container>
//         </Box>
//         {selectedInterview && (
//           <EvaluationForm
//             open={modalOpen}
//             onClose={handleCloseModal}
//             interviewDetails={selectedInterview}
//             onSuccess={handleAlert}
//           />
//         )}
//         <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose}>
//           <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
//             {snackbar.message}
//           </Alert>
//         </Snackbar>
//       </ThemeProvider>
//     </Layout>
//   )
// }

// import React, { useState, useEffect } from "react"
// import {
//   Container,
//   Paper,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   Rating,
//   TextField,
//   Button,
//   Box,
//   Avatar,
//   Divider,
//   FormControl,
//   Select,
//   MenuItem,
//   List,
//   Accordion,
//   Chip,
//   Modal,
//   CircularProgress,
//   Alert,
//   IconButton,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Backdrop,
//   useMediaQuery,
//   Fade,
//   Tabs,
//   Tab,
//   Snackbar,
//   TablePagination,
// } from "@mui/material"
// import {
//   Person,
//   Email,
//   Event,
//   Business,
//   RateReview,
//   ArrowBack,
//   Close,
//   Add,
//   Remove,
//   LocationOn,
//   ThumbUp,
//   Send,
// } from "@mui/icons-material"
// import { createTheme, ThemeProvider, styled, responsiveFontSizes, useTheme } from "@mui/material/styles"
// import Layout from "../common/Layout"
// import apiClient from "../../context/apiClient"

// const baseTheme = createTheme({
//   palette: {
//     primary: {
//       main: "#1976d2",
//       light: "#42a5f5",
//       dark: "#1565c0",
//     },
//     background: {
//       default: "#f8fafc",
//       paper: "#ffffff",
//     },
//     text: {
//       primary: "#1e293b",
//       secondary: "#64748b",
//     },
//   },
//   typography: {
//     fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
//     h4: {
//       fontWeight: 700,
//       letterSpacing: "-0.025em",
//     },
//     h6: {
//       fontWeight: 600,
//     },
//     body1: {
//       lineHeight: 1.6,
//     },
//   },
//   components: {
//     MuiCard: {
//       styleOverrides: {
//         root: {
//           borderRadius: 16,
//           boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
//           transition: "all 0.2s ease-in-out",
//           "&:hover": {
//             boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//             transform: "translateY(-2px)",
//           },
//         },
//       },
//     },
//     MuiButton: {
//       styleOverrides: {
//         root: {
//           borderRadius: 12,
//           textTransform: "none",
//           fontWeight: 600,
//           transition: "all 0.2s ease-in-out",
//         },
//         contained: {
//           boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
//           "&:hover": {
//             boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
//             transform: "translateY(-1px)",
//           },
//         },
//       },
//     },
//     MuiAccordion: {
//       styleOverrides: {
//         root: {
//           borderRadius: "16px !important",
//           boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
//           "&:before": {
//             display: "none",
//           },
//           "&$expanded": {
//             margin: "auto",
//           },
//         },
//       },
//     },
//   },
// })

// const theme = responsiveFontSizes(baseTheme)

// const StyledAccordion = styled(Accordion)(({ theme }) => ({
//   transition: theme.transitions.create(["box-shadow", "margin", "transform"], {
//     duration: theme.transitions.duration.short,
//   }),
//   "&:hover": {
//     transform: "translateY(-2px)",
//     boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//   },
//   "&.Mui-expanded": {
//     boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//     margin: `${theme.spacing(1, 0)} !important`,
//     transform: "translateY(-2px)",
//   },
// }))

// function TabPanel(props) {
//   const { children, value, index, ...other } = props
//   return (
//     <div
//       role="tabpanel"
//       hidden={value !== index}
//       id={`simple-tabpanel-${index}`}
//       aria-labelledby={`simple-tab-${index}`}
//       {...other}
//     >
//       {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
//     </div>
//   )
// }

// function EvaluationForm({ interviewDetails, open, onClose, onSuccess }) {
//   const theme = useTheme()
//   const isXs = useMediaQuery(theme.breakpoints.down("sm"))
//   const [formData, setFormData] = useState({
//     recommendation: "",
//     technical_skills_rating: 0,
//     communication_skills_rating: 0,
//     cultural_fit_rating: 0,
//     strengths: [""],
//     weaknesses: [""],
//     overall_comments: [""],
//   })
//   const [isSubmitting, setIsSubmitting] = useState(false)
//   const [submitError, setSubmitError] = useState(null)

//   useEffect(() => {
//     if (open) {
//       setFormData({
//         recommendation: "",
//         technical_skills_rating: 0,
//         communication_skills_rating: 0,
//         cultural_fit_rating: 0,
//         strengths: [""],
//         weaknesses: [""],
//         overall_comments: [""],
//       })
//       setIsSubmitting(false)
//       setSubmitError(null)
//     }
//   }, [open])

//   const handleChange = (e) => {
//     const { name, value } = e.target
//     setFormData((prev) => ({ ...prev, [name]: value }))
//   }

//   const handleRatingChange = (field, value) => {
//     setFormData((prev) => ({ ...prev, [field]: value !== null ? Math.round(value) : 0 }))
//   }

//   const handleArrayFieldChange = (field, index, value) => {
//     setFormData((prev) => ({
//       ...prev,
//       [field]: prev[field].map((item, i) => (i === index ? value : item)),
//     }))
//   }

//   const addArrayField = (field) => {
//     setFormData((prev) => ({
//       ...prev,
//       [field]: [...prev[field], ""],
//     }))
//   }

//   const removeArrayField = (field, index) => {
//     if (formData[field].length > 1) {
//       setFormData((prev) => ({
//         ...prev,
//         [field]: prev[field].filter((_, i) => i !== index),
//       }))
//     }
//   }

//   const handleSubmit = async (e) => {
//     e.preventDefault()
//     setIsSubmitting(true)
//     setSubmitError(null)
//     try {
//       const submissionData = {
//         ...formData,
//         strengths: formData.strengths.filter((s) => s.trim()).join("; "),
//         weaknesses: formData.weaknesses.filter((w) => w.trim()).join("; "),
//         overall_comments: formData.overall_comments.filter((c) => c.trim()).join("; "),
//         interview_id: interviewDetails.interviewId,
//       }

//       await apiClient.post(
//         `/interviews/${interviewDetails.interviewId}/submit-feedback/`,
//         submissionData,
//       )
//       onSuccess("Evaluation submitted successfully!", "success")
//       onClose()
//     } catch (error) {
//       console.error("[v0] Error submitting evaluation:", error)
//       const errorMessage =
//         error.response?.data?.message || error.message || "An unexpected error occurred."
//       setSubmitError(errorMessage)
//     } finally {
//       setIsSubmitting(false)
//     }
//   }

//   return (
//     <Modal
//       open={open}
//       onClose={onClose}
//       closeAfterTransition
//       BackdropComponent={Backdrop}
//       BackdropProps={{
//         timeout: 500,
//       }}
//     >
//       <Fade in={open}>
//         <Box
//           component={Paper}
//           elevation={0}
//           sx={{
//             position: "absolute",
//             top: "50%",
//             left: "50%",
//             transform: "translate(-50%, -50%)",
//             width: { xs: "95%", sm: "90%", md: "80%", lg: "70%" },
//             maxWidth: "900px",
//             maxHeight: "90vh",
//             bgcolor: "background.paper",
//             borderRadius: 3,
//             boxShadow: 24,
//             overflow: "hidden",
//             display: "flex",
//             flexDirection: "column",
//           }}
//         >
//           <Box
//             sx={{
//               background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
//               color: "white",
//               p: { xs: 2, md: 3 },
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "space-between",
//               flexShrink: 0,
//             }}
//           >
//             <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
//               <Avatar
//                 src={interviewDetails?.avatar}
//                 sx={{
//                   width: { xs: 48, sm: 56 },
//                   height: { xs: 48, sm: 56 },
//                   border: "3px solid rgba(255,255,255,0.3)",
//                   flexShrink: 0,
//                 }}
//               >
//                 {interviewDetails?.name?.charAt(0)}
//               </Avatar>
//               <Box sx={{ minWidth: 0 }}>
//                 <Typography
//                   variant="h5"
//                   fontWeight="bold"
//                   sx={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}
//                 >
//                   Evaluate Candidate
//                 </Typography>
//                 <Typography
//                   variant="body1"
//                   sx={{ opacity: 0.9, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}
//                 >
//                   {interviewDetails?.name} • {interviewDetails?.jobTitle}
//                 </Typography>
//               </Box>
//             </Box>
//             <IconButton onClick={onClose} sx={{ color: "white" }}>
//               <Close />
//             </IconButton>
//           </Box>
//           <Container
//             maxWidth="lg"
//             sx={{
//               flex: 1,
//               overflow: "auto",
//               py: { xs: 2, md: 3 },
//               minHeight: 0,
//               maxHeight: "calc(90vh - 140px)",
//               "&::-webkit-scrollbar": { width: "8px" },
//               "&::-webkit-scrollbar-track": { background: "#f1f1f1", borderRadius: "4px" },
//               "&::-webkit-scrollbar-thumb": {
//                 background: "#c1c1c1",
//                 borderRadius: "4px",
//                 "&:hover": { background: "#a8a8a8" },
//               },
//               scrollbarWidth: "thin",
//               scrollbarColor: "#c1c1c1 #f1f1f1",
//             }}
//           >
//             <form onSubmit={handleSubmit}>
//               <Grid container spacing={3}>
//                 <Grid item xs={12}>
//                   <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, bgcolor: "#F1FFFA" }}>
//                     <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                       <ThumbUp color="primary" />
//                       Recommendation
//                     </Typography>
//                     <FormControl fullWidth>
//                       <Select
//                         name="recommendation"
//                         value={formData.recommendation}
//                         onChange={handleChange}
//                         required
//                         sx={{ borderRadius: 2 }}
//                       >
//                         <MenuItem value="">
//                           <em>Select recommendation</em>
//                         </MenuItem>
//                         <MenuItem value="STRONG_HIRE">
//                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <Person color="success" /> Strong Hire
//                           </Box>
//                         </MenuItem>
//                         <MenuItem value="HIRE">
//                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <Person color="success" /> Hire
//                           </Box>
//                         </MenuItem>
//                         <MenuItem value="NO_HIRE">
//                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <Person color="error" /> No Hire
//                           </Box>
//                         </MenuItem>
//                         <MenuItem value="NOT_SURE">
//                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <Person color="warning" /> Not Sure / Needs More Review
//                           </Box>
//                         </MenuItem>
//                       </Select>
//                     </FormControl>
//                   </Card>
//                 </Grid>
//                 <Grid item xs={12}>
//                   <Card
//                     variant="outlined"
//                     sx={{ p: { xs: 2, sm: 3 }, gap: 3, borderRadius: 2, bgcolor: "#F1FFFA", width: "100%" }}
//                   >
//                     <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
//                       Skills Assessment
//                     </Typography>
//                     <Grid container spacing={3}>
//                       {[
//                         { field: "technical_skills_rating", label: "Technical Skills", icon: "💻" },
//                         { field: "communication_skills_rating", label: "Communication", icon: "💬" },
//                         { field: "cultural_fit_rating", label: "Cultural Fit", icon: "🤝" },
//                       ].map((rating) => (
//                         <Grid item xs={12} sm={4} key={rating.field}>
//                           <Box
//                             sx={{
//                               textAlign: "center",
//                               p: 2,
//                               borderRadius: 2,
//                               border: "1px solid",
//                               borderColor: "divider",
//                               bgcolor: "grey.50",
//                             }}
//                           >
//                             <Typography variant="h4" sx={{ mb: 1 }}>
//                               {rating.icon}
//                             </Typography>
//                             <Typography variant="body1" fontWeight="medium" gutterBottom>
//                               {rating.label}
//                             </Typography>
//                             <Rating
//                               name={rating.field}
//                               value={formData[rating.field]}
//                               onChange={(e, val) => handleRatingChange(rating.field, val)}
//                               size={isXs ? "medium" : "large"}
//                               sx={{ mb: 1 }}
//                               max={5}
//                             />
//                             <Typography variant="body2" color="text.secondary">
//                               {formData[rating.field]}/5
//                             </Typography>
//                           </Box>
//                         </Grid>
//                       ))}
//                     </Grid>
//                   </Card>
//                 </Grid>
//                 {[
//                   { field: "strengths", label: "Key Strengths", placeholder: "What impressed you?", icon: "⭐" },
//                   { field: "weaknesses", label: "Areas for Improvement", placeholder: "What could be developed?", icon: "📈" },
//                   { field: "overall_comments", label: "Overall Assessment", placeholder: "Provide comprehensive evaluation...", icon: "📝" },
//                 ].map((section) => (
//                   <Grid item xs={12} key={section.field}>
//                     <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, bgcolor: "#F1FFFA" }}>
//                       <Box
//                         sx={{
//                           display: "flex",
//                           alignItems: { xs: "stretch", sm: "center" },
//                           justifyContent: "space-between",
//                           gap: 2,
//                           mb: 2,
//                           flexDirection: { xs: "column", sm: "row" },
//                         }}
//                       >
//                         <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                           {section.icon} {section.label}
//                         </Typography>
//                         <Button
//                           variant="outlined"
//                           size="medium"
//                           startIcon={<Add />}
//                           onClick={() => addArrayField(section.field)}
//                           sx={{ borderRadius: 2, alignSelf: { xs: "stretch", sm: "auto" } }}
//                           fullWidth={isXs}
//                         >
//                           Add
//                         </Button>
//                       </Box>
//                       {formData[section.field].map((item, index) => (
//                         <Box
//                           key={index}
//                           sx={{
//                             display: "flex",
//                             gap: 1,
//                             mb: 2,
//                             flexDirection: { xs: "column", sm: "row" },
//                             alignItems: { xs: "stretch", sm: "flex-start" },
//                           }}
//                         >
//                           <TextField
//                             fullWidth
//                             multiline
//                             rows={3}
//                             placeholder={section.placeholder}
//                             value={item}
//                             onChange={(e) => handleArrayFieldChange(section.field, index, e.target.value)}
//                             variant="outlined"
//                             sx={{ borderRadius: 2 }}
//                           />
//                           {formData[section.field].length > 1 && (
//                             <IconButton
//                               onClick={() => removeArrayField(section.field, index)}
//                               color="error"
//                               sx={{ alignSelf: { xs: "flex-end", sm: "flex-start" }, mt: { xs: 0, sm: 1 } }}
//                             >
//                               <Remove />
//                             </IconButton>
//                           )}
//                         </Box>
//                       ))}
//                     </Card>
//                   </Grid>
//                 ))}
//                 {submitError && (
//                   <Grid item xs={12}>
//                     <Alert severity="error" sx={{ borderRadius: 2 }}>
//                       {submitError}
//                     </Alert>
//                   </Grid>
//                 )}
//               </Grid>
//               <Box
//                 sx={{
//                   display: "flex",
//                   flexDirection: { xs: "column-reverse", sm: "row" },
//                   justifyContent: "flex-end",
//                   gap: 2,
//                   mt: 4,
//                   pt: 3,
//                   borderTop: "1px solid",
//                   borderColor: "divider",
//                 }}
//               >
//                 <Button onClick={onClose} variant="outlined" size="large" fullWidth={isXs} sx={{ borderRadius: 2, minWidth: 120 }}>
//                   Cancel
//                 </Button>
//                 <Button type="submit" variant="contained" size="large" disabled={isSubmitting} fullWidth={isXs} sx={{ borderRadius: 2, minWidth: 120 }}>
//                   {isSubmitting ? <CircularProgress size={24} /> : "Submit Evaluation"}
//                 </Button>
//               </Box>
//             </form>
//           </Container>
//         </Box>
//       </Fade>
//     </Modal>
//   )
// }

// function JobDetailsView({ candidate, onBack, onEvaluate }) {
//   const theme = useTheme()
//   const isXs = useMediaQuery(theme.breakpoints.down("sm"))
//   return (
//     <Box>
//       <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
//         <Button variant="outlined" startIcon={<ArrowBack />} onClick={onBack} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
//           Back to Candidates
//         </Button>
//       </Box>
//       <Card elevation={1} sx={{ mb: 4, borderRadius: 2, bgcolor: "#F1FFFA" }}>
//         <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
//           <Grid container spacing={3} alignItems="center">
//             <Grid item xs="auto">
//               <Avatar
//                 src={candidate.avatar}
//                 sx={{
//                   width: { xs: 64, sm: 72, md: 80 },
//                   height: { xs: 64, sm: 72, md: 80 },
//                   border: "3px solid",
//                   borderColor: "primary.light",
//                 }}
//               />
//             </Grid>
//             <Grid item xs>
//               <Typography variant="h5" fontWeight="bold" gutterBottom>
//                 {candidate.name}
//               </Typography>
//               <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
//                 <Email fontSize="small" color="action" />
//                 <Typography variant="body2" color="text.secondary">
//                   {candidate.email}
//                 </Typography>
//               </Box>
//               <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
//                 <LocationOn fontSize="small" color="action" />
//                 <Typography variant="body2" color="text.secondary">
//                   {candidate.location}
//                 </Typography>
//               </Box>
//               <Chip
//                 label={`${candidate.interviews.length} Scheduled Interview${candidate.interviews.length !== 1 ? "s" : ""}`}
//                 color="primary"
//                 variant="outlined"
//                 sx={{ fontWeight: 600 }}
//               />
//             </Grid>
//           </Grid>
//         </CardContent>
//       </Card>
//       <Grid container spacing={3}>
//         {candidate.interviews.map((interview) => (
//           <Grid item xs={12} md={6} lg={4} key={interview.id}>
//             <Card elevation={1} sx={{ borderRadius: 2, bgcolor: "#F1FFFA", height: "100%" }}>
//               <CardContent sx={{ p: { xs: 2, sm: 3 }, display: "flex", flexDirection: "column", height: "100%" }}>
//                 <Box sx={{ flexGrow: 1 }}>
//                   <Typography variant="h6" fontWeight="600" gutterBottom sx={{ color: "text.primary" }}>
//                     {interview.job_title}
//                   </Typography>
//                   <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 2 }}>
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                       <Business fontSize="small" color="action" />
//                       <Typography variant="body2" color="text.secondary" fontWeight="500">
//                         {interview.interview_mode}
//                       </Typography>
//                     </Box>
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                       <Event fontSize="small" color="action" />
//                       <Typography variant="body2" color="text.secondary" fontWeight="500">
//                         {new Date(`${interview.interview_date}T${interview.interview_time}`).toLocaleString()}
//                       </Typography>
//                     </Box>
//                   </Box>
//                   <Chip
//                     label={interview.status}
//                     color={interview.status.toLowerCase() === "scheduled" ? "success" : "warning"}
//                     size="small"
//                     sx={{ fontWeight: 600, mb: 2 }}
//                   />
//                 </Box>
//                 <Box sx={{ mt: "auto", pt: 2 }}>
//                   <Button
//                     variant="contained"
//                     startIcon={<RateReview />}
//                     onClick={() => onEvaluate(candidate, interview)}
//                     fullWidth
//                     sx={{
//                       borderRadius: 2,
//                       fontWeight: 600,
//                       boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
//                       "&:hover": {
//                         transform: "translateY(-1px)",
//                         boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
//                       },
//                     }}
//                   >
//                     Evaluate
//                   </Button>
//                 </Box>
//               </CardContent>
//             </Card>
//           </Grid>
//         ))}
//       </Grid>
//     </Box>
//   )
// }

// function ScheduledInterviewsTab({ onEvaluateClick }) {
//   const [candidates, setCandidates] = useState([])
//   const [loading, setLoading] = useState(true)
//   const [error, setError] = useState(null)
//   const [searchTerm, setSearchTerm] = useState("")
//   const [selectedCandidateDetails, setSelectedCandidateDetails] = useState(null)
//   const [showJobDetails, setShowJobDetails] = useState(false)
//   const theme = useTheme()
//   const isXs = useMediaQuery(theme.breakpoints.down("sm"))

//   useEffect(() => {
//     const fetchAndProcessInterviews = async () => {
//       try {
//         const response = await apiClient.get("/interviews/")
//         const data = response.data
//         const filtered = data.filter(
//           (interview) =>
//             interview.status.toLowerCase() === "scheduled" || interview.status.toLowerCase() === "rescheduled",
//         )
//         const groupedByCandidate = filtered.reduce((acc, interview) => {
//           const { candidate_name, profile_photo, talent_current_location } = interview
//           if (!acc[candidate_name]) {
//             acc[candidate_name] = {
//               id: candidate_name.replace(/\s+/g, "-").toLowerCase(),
//               name: candidate_name,
//               email: `${candidate_name.replace(/\s+/g, ".").toLowerCase()}@example.com`,
//               avatar: profile_photo || `https://i.pravatar.cc/150?u=${encodeURIComponent(candidate_name)}`,
//               location: talent_current_location || "Location not specified",
//               interviews: [],
//             }
//           }
//           acc[candidate_name].interviews.push(interview)
//           return acc
//         }, {})
//         setCandidates(Object.values(groupedByCandidate))
//       } catch (e) {
//         setError(e.response?.data?.detail || e.message)
//       } finally {
//         setLoading(false)
//       }
//     }
//     fetchAndProcessInterviews()
//   }, [])

//   const handleViewDetails = (candidate) => {
//     setSelectedCandidateDetails(candidate)
//     setShowJobDetails(true)
//   }

//   const handleBackToList = () => {
//     setShowJobDetails(false)
//     setSelectedCandidateDetails(null)
//   }

//   const filteredCandidates = candidates.filter(
//     (c) =>
//       c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       c.email.toLowerCase().includes(searchTerm.toLowerCase()),
//   )

//   if (loading) {
//     return (
//       <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
//         <CircularProgress />
//       </Box>
//     )
//   }

//   if (error) {
//     return (
//       <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//         <Typography variant="h6" color="error" gutterBottom>
//           Error Fetching Interviews
//         </Typography>
//         <Typography>{error}</Typography>
//       </Paper>
//     )
//   }

//   if (showJobDetails && selectedCandidateDetails) {
//     return (
//       <JobDetailsView candidate={selectedCandidateDetails} onBack={handleBackToList} onEvaluate={onEvaluateClick} />
//     )
//   }

//   return (
//     <Box>
//       {filteredCandidates.length === 0 ? (
//         <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//           <Typography variant="h6" color="text.secondary">
//             No Scheduled Interviews Found
//           </Typography>
//         </Paper>
//       ) : (
//         <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
//           <Table size={isXs ? "small" : "medium"}>
//             <TableHead>
//               <TableRow sx={{ backgroundColor: "#F1FFFA" }}>
//                 <TableCell sx={{ fontWeight: "bold" }}>Candidate</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Interviews</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {filteredCandidates.map((candidate) => (
//                 <TableRow key={candidate.id} sx={{ "&:hover": { bgcolor: "grey.50" } }}>
//                   <TableCell>
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                       <Avatar src={candidate.avatar} sx={{ width: 48, height: 48 }} />
//                       <Typography variant="body1" fontWeight="600">
//                         {candidate.name}
//                       </Typography>
//                     </Box>
//                   </TableCell>
//                   <TableCell>
//                     <Chip
//                       label={`${candidate.interviews.length} interview${candidate.interviews.length !== 1 ? "s" : ""}`}
//                       color="primary"
//                       variant="outlined"
//                       size="small"
//                       sx={{ fontWeight: 600 }}
//                     />
//                   </TableCell>
//                   <TableCell>
//                     <Button
//                       variant="contained"
//                       onClick={() => handleViewDetails(candidate)}
//                       sx={{
//                         borderRadius: 2,
//                         fontWeight: 600,
//                         boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
//                         "&:hover": {
//                           transform: "translateY(-1px)",
//                           boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
//                         },
//                       }}
//                     >
//                       View Details
//                     </Button>
//                   </TableCell>
//                 </TableRow>
//               ))}
//             </TableBody>
//           </Table>
//         </TableContainer>
//       )}
//     </Box>
//   )
// }

// function ExtendOfferTab({ onAlert }) {
//   const [completedCandidates, setCompletedCandidates] = useState([])
//   const [loading, setLoading] = useState(true)
//   const [error, setError] = useState(null)
//   const [extendingId, setExtendingId] = useState(null)
//   const [page, setPage] = useState(0)
//   const [rowsPerPage, setRowsPerPage] = useState(5)

//   useEffect(() => {
//     const fetchCompletedCandidates = async () => {
//       try {
//         const response = await apiClient.get("/interviews/completed-candidates/")
//         setCompletedCandidates(response.data)
//       } catch (e) {
//         setError(e.response?.data?.detail || e.message)
//       } finally {
//         setLoading(false)
//       }
//     }
//     fetchCompletedCandidates()
//   }, [])

//   const handleExtendOffer = async (applicationId) => {
//     setExtendingId(applicationId)
//     try {
//       await apiClient.post(`/applications/${applicationId}/offer-extend/`)
//       onAlert("Offer extended successfully!", "success")
//       setCompletedCandidates((prev) =>
//         prev.map((candidate) =>
//           candidate.application_id === applicationId
//             ? { ...candidate, application_status: "OFFER_EXTENDED" }
//             : candidate,
//         ),
//       )
//     } catch (err) {
//       const errorMessage = err.response?.data?.detail || err.message || "Failed to extend offer."
//       onAlert(errorMessage, "error")
//     } finally {
//       setExtendingId(null)
//     }
//   }

//   const handleChangePage = (event, newPage) => {
//     setPage(newPage)
//   }

//   const handleChangeRowsPerPage = (event) => {
//     setRowsPerPage(parseInt(event.target.value, 10))
//     setPage(0)
//   }

//   if (loading) {
//     return (
//       <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
//         <CircularProgress />
//       </Box>
//     )
//   }

//   if (error) {
//     return (
//       <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//         <Typography variant="h6" color="error" gutterBottom>
//           Error Fetching Data
//         </Typography>
//         <Typography>{error}</Typography>
//       </Paper>
//     )
//   }

//   return (
//     <Box>
//       {completedCandidates.length === 0 ? (
//         <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//           <Typography variant="h6" color="text.secondary">
//             No evaluated candidates available to extend an offer.
//           </Typography>
//         </Paper>
//       ) : (
//         <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
//           <Table>
//             <TableHead>
//               <TableRow sx={{ backgroundColor: "#F1FFFA" }}>
//                 <TableCell sx={{ fontWeight: "bold" }}>Candidate</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Job Title</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Tech Rating</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
//                 <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Actions</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {completedCandidates
//                 .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
//                 .map((candidate) => (
//                   <TableRow key={candidate.application_id} sx={{ "&:hover": { bgcolor: "grey.50" } }}>
//                     <TableCell>{candidate.talent_name}</TableCell>
//                     <TableCell>{candidate.job_posting_title}</TableCell>
//                     <TableCell>{candidate.technical_skills_rating || "N/A"}</TableCell>
//                     <TableCell>
//                       <Chip
//                         label={candidate.application_status.replace("_", " ")}
//                         color={
//                           candidate.application_status === "OFFER_EXTENDED"
//                             ? "success"
//                             : candidate.application_status === "HIRED"
//                               ? "primary"
//                               : "default"
//                         }
//                         size="small"
//                         sx={{ textTransform: "capitalize" }}
//                       />
//                     </TableCell>
//                     <TableCell sx={{ textAlign: "center" }}>
//                       <Button
//                         variant="contained"
//                         color="primary"
//                         startIcon={extendingId === candidate.application_id ? null : <Send />}
//                         disabled={
//                           extendingId === candidate.application_id ||
//                           candidate.application_status === "OFFER_EXTENDED"
//                         }
//                         onClick={() => handleExtendOffer(candidate.application_id)}
//                         sx={{
//                           minWidth: 140,
//                           borderRadius: 2,
//                         }}
//                       >
//                         {extendingId === candidate.application_id ? (
//                           <CircularProgress size={24} color="inherit" />
//                         ) : candidate.application_status === "OFFER_EXTENDED" ? (
//                           "Offer Extended"
//                         ) : (
//                           "Extend Offer"
//                         )}
//                       </Button>
//                     </TableCell>
//                   </TableRow>
//                 ))}
//             </TableBody>
//           </Table>
//           <TablePagination
//             rowsPerPageOptions={[5, 10, 25]}
//             component="div"
//             count={completedCandidates.length}
//             rowsPerPage={rowsPerPage}
//             page={page}
//             onPageChange={handleChangePage}
//             onRowsPerPageChange={handleChangeRowsPerPage}
//           />
//         </TableContainer>
//       )}
//     </Box>
//   )
// }

// export default function EvaluationsPage() {
//   const [modalOpen, setModalOpen] = useState(false)
//   const [selectedInterview, setSelectedInterview] = useState(null)
//   const [tabValue, setTabValue] = useState(0)
//   const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" })

//   const handleTabChange = (event, newValue) => {
//     setTabValue(newValue)
//   }

//   const handleEvaluateClick = (candidate, interview) => {
//     setSelectedInterview({
//       name: candidate.name,
//       email: candidate.email,
//       avatar: candidate.avatar,
//       interviewId: interview.id,
//       jobTitle: interview.job_title,
//     })
//     setModalOpen(true)
//   }

//   const handleCloseModal = () => {
//     setModalOpen(false)
//     setSelectedInterview(null)
//   }

//   const handleAlert = (message, severity) => {
//     setSnackbar({ open: true, message, severity })
//   }

//   const handleSnackbarClose = (event, reason) => {
//     if (reason === "clickaway") return
//     setSnackbar({ ...snackbar, open: false })
//   }

//   return (
//     <Layout title="Evaluations">
//       <ThemeProvider theme={theme}>
//         <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
//           <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
//             <Paper sx={{ p: { xs: 1, sm: 2 }, borderRadius: 3, backgroundColor: "#F1FFFA", color: "#4D3CC3" }}>
//               <Typography variant="h4" gutterBottom>
//                 Candidate Evaluations & Extend Offers
//               </Typography>
//             </Paper>
//             <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 2 }}>
//               <Tabs value={tabValue} onChange={handleTabChange} aria-label="evaluation tabs">
//                 <Tab label="Scheduled Interviews" id="tab-0" />
//                 <Tab label="Extend Offer" id="tab-1" />
//               </Tabs>
//             </Box>
//             <TabPanel value={tabValue} index={0}>
//               <ScheduledInterviewsTab onEvaluateClick={handleEvaluateClick} />
//             </TabPanel>
//             <TabPanel value={tabValue} index={1}>
//               <ExtendOfferTab onAlert={handleAlert} />
//             </TabPanel>
//           </Container>
//         </Box>
//         {selectedInterview && (
//           <EvaluationForm
//             open={modalOpen}
//             onClose={handleCloseModal}
//             interviewDetails={selectedInterview}
//             onSuccess={handleAlert}
//           />
//         )}
//         <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose}>
//           <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
//             {snackbar.message}
//           </Alert>
//         </Snackbar>
//       </ThemeProvider>
//     </Layout>
//   )
// }

// import React, { useState, useEffect } from "react"
// import {
//   Container,
//   Paper,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   Rating,
//   TextField,
//   Button,
//   Box,
//   Avatar,
//   Divider,
//   FormControl,
//   Select,
//   MenuItem,
//   List,
//   Accordion,
//   Chip,
//   Modal,
//   CircularProgress,
//   Alert,
//   IconButton,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Backdrop,
//   useMediaQuery,
//   Fade,
//   Tabs,
//   Tab,
//   Snackbar,
//   TablePagination,
// } from "@mui/material"
// import {
//   Person,
//   Email,
//   Event,
//   Business,
//   RateReview,
//   ArrowBack,
//   Close,
//   Add,
//   Remove,
//   LocationOn,
//   ThumbUp,
//   Send,
// } from "@mui/icons-material"
// import { createTheme, ThemeProvider, styled, responsiveFontSizes, useTheme } from "@mui/material/styles"
// import Layout from "../common/Layout"
// import apiClient from "../../context/apiClient"

// const baseTheme = createTheme({
//   palette: {
//     primary: {
//       main: "#1976d2",
//       light: "#42a5f5",
//       dark: "#1565c0",
//     },
//     background: {
//       default: "#f8fafc",
//       paper: "#ffffff",
//     },
//     text: {
//       primary: "#1e293b",
//       secondary: "#64748b",
//     },
//   },
//   typography: {
//     fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
//     h4: {
//       fontWeight: 700,
//       letterSpacing: "-0.025em",
//     },
//     h6: {
//       fontWeight: 600,
//     },
//     body1: {
//       lineHeight: 1.6,
//     },
//   },
//   components: {
//     MuiCard: {
//       styleOverrides: {
//         root: {
//           borderRadius: 16,
//           boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
//           transition: "all 0.2s ease-in-out",
//           "&:hover": {
//             boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//             transform: "translateY(-2px)",
//           },
//         },
//       },
//     },
//     MuiButton: {
//       styleOverrides: {
//         root: {
//           borderRadius: 12,
//           textTransform: "none",
//           fontWeight: 600,
//           transition: "all 0.2s ease-in-out",
//         },
//         contained: {
//           boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
//           "&:hover": {
//             boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
//             transform: "translateY(-1px)",
//           },
//         },
//       },
//     },
//     MuiAccordion: {
//       styleOverrides: {
//         root: {
//           borderRadius: "16px !important",
//           boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
//           "&:before": {
//             display: "none",
//           },
//           "&$expanded": {
//             margin: "auto",
//           },
//         },
//       },
//     },
//   },
// })

// const theme = responsiveFontSizes(baseTheme)

// const StyledAccordion = styled(Accordion)(({ theme }) => ({
//   transition: theme.transitions.create(["box-shadow", "margin", "transform"], {
//     duration: theme.transitions.duration.short,
//   }),
//   "&:hover": {
//     transform: "translateY(-2px)",
//     boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//   },
//   "&.Mui-expanded": {
//     boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//     margin: `${theme.spacing(1, 0)} !important`,
//     transform: "translateY(-2px)",
//   },
// }))

// function TabPanel(props) {
//   const { children, value, index, ...other } = props
//   return (
//     <div
//       role="tabpanel"
//       hidden={value !== index}
//       id={`simple-tabpanel-${index}`}
//       aria-labelledby={`simple-tab-${index}`}
//       {...other}
//     >
//       {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
//     </div>
//   )
// }

// function EvaluationForm({ interviewDetails, open, onClose }) {
//   const theme = useTheme()
//   const isXs = useMediaQuery(theme.breakpoints.down("sm"))
//   const [formData, setFormData] = useState({
//     recommendation: "",
//     technical_skills_rating: 0,
//     communication_skills_rating: 0,
//     cultural_fit_rating: 0,
//     strengths: [""],
//     weaknesses: [""],
//     overall_comments: [""],
//   })
//   const [isSubmitting, setIsSubmitting] = useState(false)
//   const [submitError, setSubmitError] = useState(null)

//   useEffect(() => {
//     if (open) {
//       setFormData({
//         recommendation: "",
//         technical_skills_rating: 0,
//         communication_skills_rating: 0,
//         cultural_fit_rating: 0,
//         strengths: [""],
//         weaknesses: [""],
//         overall_comments: [""],
//       })
//       setIsSubmitting(false)
//       setSubmitError(null)
//     }
//   }, [open])

//   const handleChange = (e) => {
//     const { name, value } = e.target
//     setFormData((prev) => ({ ...prev, [name]: value }))
//   }

//   const handleRatingChange = (field, value) => {
//     setFormData((prev) => ({ ...prev, [field]: value !== null ? Math.round(value) : 0 }))
//   }

//   const handleArrayFieldChange = (field, index, value) => {
//     setFormData((prev) => ({
//       ...prev,
//       [field]: prev[field].map((item, i) => (i === index ? value : item)),
//     }))
//   }

//   const addArrayField = (field) => {
//     setFormData((prev) => ({
//       ...prev,
//       [field]: [...prev[field], ""],
//     }))
//   }

//   const removeArrayField = (field, index) => {
//     if (formData[field].length > 1) {
//       setFormData((prev) => ({
//         ...prev,
//         [field]: prev[field].filter((_, i) => i !== index),
//       }))
//     }
//   }

//   const handleSubmit = async (e) => {
//     e.preventDefault()
//     setIsSubmitting(true)
//     setSubmitError(null)

//     try {
//       const submissionData = {
//         ...formData,
//         strengths: formData.strengths.filter((s) => s.trim()).join("; "),
//         weaknesses: formData.weaknesses.filter((w) => w.trim()).join("; "),
//         overall_comments: formData.overall_comments.filter((c) => c.trim()).join("; "),
//         interview_id: interviewDetails.interviewId,
//       }

//       await apiClient.post(
//         `/interviews/${interviewDetails.interviewId}/submit-feedback/`,
//         submissionData,
//       )

//       onClose()
//     } catch (error) {
//       console.error("[v0] Error submitting evaluation:", error)
//       const errorMessage =
//         error.response?.data?.message || error.message || "An unexpected error occurred."
//       setSubmitError(errorMessage)
//     } finally {
//       setIsSubmitting(false)
//     }
//   }

//   return (
//     <Modal
//       open={open}
//       onClose={onClose}
//       closeAfterTransition
//       BackdropComponent={Backdrop}
//       BackdropProps={{
//         timeout: 500,
//       }}
//     >
//       <Fade in={open}>
//         <Box
//           component={Paper}
//           elevation={0}
//           sx={{
//             position: "absolute",
//             top: "50%",
//             left: "50%",
//             transform: "translate(-50%, -50%)",
//             width: { xs: "95%", sm: "90%", md: "80%", lg: "70%" },
//             maxWidth: "900px",
//             maxHeight: "90vh",
//             bgcolor: "background.paper",
//             borderRadius: 3,
//             boxShadow: 24,
//             overflow: "hidden",
//             display: "flex",
//             flexDirection: "column",
//           }}
//         >
//           <Box
//             sx={{
//               background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
//               color: "white",
//               p: { xs: 2, md: 3 },
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "space-between",
//               flexShrink: 0,
//             }}
//           >
//             <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
//               <Avatar
//                 src={interviewDetails?.avatar}
//                 sx={{
//                   width: { xs: 48, sm: 56 },
//                   height: { xs: 48, sm: 56 },
//                   border: "3px solid rgba(255,255,255,0.3)",
//                   flexShrink: 0,
//                 }}
//               >
//                 {interviewDetails?.name?.charAt(0)}
//               </Avatar>
//               <Box sx={{ minWidth: 0 }}>
//                 <Typography
//                   variant="h5"
//                   fontWeight="bold"
//                   sx={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}
//                 >
//                   Evaluate Candidate
//                 </Typography>
//                 <Typography
//                   variant="body1"
//                   sx={{ opacity: 0.9, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}
//                 >
//                   {interviewDetails?.name} • {interviewDetails?.jobTitle}
//                 </Typography>
//               </Box>
//             </Box>
//             <IconButton onClick={onClose} sx={{ color: "white" }}>
//               <Close />
//             </IconButton>
//           </Box>
//           <Container
//             maxWidth="lg"
//             sx={{
//               flex: 1,
//               overflow: "auto",
//               py: { xs: 2, md: 3 },
//               minHeight: 0,
//               maxHeight: "calc(90vh - 140px)",
//               "&::-webkit-scrollbar": { width: "8px" },
//               "&::-webkit-scrollbar-track": { background: "#f1f1f1", borderRadius: "4px" },
//               "&::-webkit-scrollbar-thumb": {
//                 background: "#c1c1c1",
//                 borderRadius: "4px",
//                 "&:hover": { background: "#a8a8a8" },
//               },
//               scrollbarWidth: "thin",
//               scrollbarColor: "#c1c1c1 #f1f1f1",
//             }}
//           >
//             <form onSubmit={handleSubmit}>
//               <Grid container spacing={3}>
//                 <Grid item xs={12}>
//                   <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, bgcolor: "#F1FFFA" }}>
//                     <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                       <ThumbUp color="primary" />
//                       Recommendation
//                     </Typography>
//                     <FormControl fullWidth>
//                       <Select
//                         name="recommendation"
//                         value={formData.recommendation}
//                         onChange={handleChange}
//                         required
//                         sx={{ borderRadius: 2 }}
//                       >
//                         <MenuItem value="">
//                           <em>Select recommendation</em>
//                         </MenuItem>
//                         <MenuItem value="HIRE">
//                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <Person color="success" /> Hire
//                           </Box>
//                         </MenuItem>
//                         <MenuItem value="CONSIDER">
//                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <Person color="primary" /> Consider
//                           </Box>
//                         </MenuItem>
//                         <MenuItem value="NO_HIRE">
//                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <Person color="error" /> No Hire
//                           </Box>
//                         </MenuItem>
//                       </Select>
//                     </FormControl>
//                   </Card>
//                 </Grid>
//                 <Grid item xs={12}>
//                   <Card
//                     variant="outlined"
//                     sx={{ p: { xs: 2, sm: 3 }, gap: 3, borderRadius: 2, bgcolor: "#F1FFFA", width: "100%" }}
//                   >
//                     <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
//                       Skills Assessment
//                     </Typography>
//                     <Grid container spacing={3}>
//                       {[
//                         { field: "technical_skills_rating", label: "Technical Skills", icon: "💻" },
//                         { field: "communication_skills_rating", label: "Communication", icon: "💬" },
//                         { field: "cultural_fit_rating", label: "Cultural Fit", icon: "🤝" },
//                       ].map((rating) => (
//                         <Grid item xs={12} sm={4} key={rating.field}>
//                           <Box
//                             sx={{
//                               textAlign: "center",
//                               p: 2,
//                               borderRadius: 2,
//                               border: "1px solid",
//                               borderColor: "divider",
//                               bgcolor: "grey.50",
//                             }}
//                           >
//                             <Typography variant="h4" sx={{ mb: 1 }}>
//                               {rating.icon}
//                             </Typography>
//                             <Typography variant="body1" fontWeight="medium" gutterBottom>
//                               {rating.label}
//                             </Typography>
//                             <Rating
//                               name={rating.field}
//                               value={formData[rating.field]}
//                               onChange={(e, val) => handleRatingChange(rating.field, val)}
//                               size={isXs ? "medium" : "large"}
//                               sx={{ mb: 1 }}
//                               max={5}
//                             />
//                             <Typography variant="body2" color="text.secondary">
//                               {formData[rating.field]}/5
//                             </Typography>
//                           </Box>
//                         </Grid>
//                       ))}
//                     </Grid>
//                   </Card>
//                 </Grid>
//                 {[
//                   { field: "strengths", label: "Key Strengths", placeholder: "What impressed you?", icon: "⭐" },
//                   { field: "weaknesses", label: "Areas for Improvement", placeholder: "What could be developed?", icon: "📈" },
//                   { field: "overall_comments", label: "Overall Assessment", placeholder: "Provide comprehensive evaluation...", icon: "📝" },
//                 ].map((section) => (
//                   <Grid item xs={12} key={section.field}>
//                     <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, bgcolor: "#F1FFFA" }}>
//                       <Box
//                         sx={{
//                           display: "flex",
//                           alignItems: { xs: "stretch", sm: "center" },
//                           justifyContent: "space-between",
//                           gap: 2,
//                           mb: 2,
//                           flexDirection: { xs: "column", sm: "row" },
//                         }}
//                       >
//                         <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                           {section.icon} {section.label}
//                         </Typography>
//                         <Button
//                           variant="outlined"
//                           size="medium"
//                           startIcon={<Add />}
//                           onClick={() => addArrayField(section.field)}
//                           sx={{ borderRadius: 2, alignSelf: { xs: "stretch", sm: "auto" } }}
//                           fullWidth={isXs}
//                         >
//                           Add
//                         </Button>
//                       </Box>
//                       {formData[section.field].map((item, index) => (
//                         <Box
//                           key={index}
//                           sx={{
//                             display: "flex",
//                             gap: 1,
//                             mb: 2,
//                             flexDirection: { xs: "column", sm: "row" },
//                             alignItems: { xs: "stretch", sm: "flex-start" },
//                           }}
//                         >
//                           <TextField
//                             fullWidth
//                             multiline
//                             rows={3}
//                             placeholder={section.placeholder}
//                             value={item}
//                             onChange={(e) => handleArrayFieldChange(section.field, index, e.target.value)}
//                             variant="outlined"
//                             sx={{ borderRadius: 2 }}
//                           />
//                           {formData[section.field].length > 1 && (
//                             <IconButton
//                               onClick={() => removeArrayField(section.field, index)}
//                               color="error"
//                               sx={{ alignSelf: { xs: "flex-end", sm: "flex-start" }, mt: { xs: 0, sm: 1 } }}
//                             >
//                               <Remove />
//                             </IconButton>
//                           )}
//                         </Box>
//                       ))}
//                     </Card>
//                   </Grid>
//                 ))}
//                 {submitError && (
//                   <Grid item xs={12}>
//                     <Alert severity="error" sx={{ borderRadius: 2 }}>
//                       {submitError}
//                     </Alert>
//                   </Grid>
//                 )}
//               </Grid>
//               <Box
//                 sx={{
//                   display: "flex",
//                   flexDirection: { xs: "column-reverse", sm: "row" },
//                   justifyContent: "flex-end",
//                   gap: 2,
//                   mt: 4,
//                   pt: 3,
//                   borderTop: "1px solid",
//                   borderColor: "divider",
//                 }}
//               >
//                 <Button onClick={onClose} variant="outlined" size="large" fullWidth={isXs} sx={{ borderRadius: 2, minWidth: 120 }}>
//                   Cancel
//                 </Button>
//                 <Button type="submit" variant="contained" size="large" disabled={isSubmitting} fullWidth={isXs} sx={{ borderRadius: 2, minWidth: 120 }}>
//                   {isSubmitting ? <CircularProgress size={24} /> : "Submit Evaluation"}
//                 </Button>
//               </Box>
//             </form>
//           </Container>
//         </Box>
//       </Fade>
//     </Modal>
//   )
// }

// function JobDetailsView({ candidate, onBack, onEvaluate }) {
//   const theme = useTheme()
//   const isXs = useMediaQuery(theme.breakpoints.down("sm"))
//   return (
//     <Box>
//       <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
//         <Button variant="outlined" startIcon={<ArrowBack />} onClick={onBack} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
//           Back to Candidates
//         </Button>
//       </Box>
//       <Card elevation={1} sx={{ mb: 4, borderRadius: 2, bgcolor: "#F1FFFA" }}>
//         <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
//           <Grid container spacing={3} alignItems="center">
//             <Grid item xs="auto">
//               <Avatar
//                 src={candidate.avatar}
//                 sx={{
//                   width: { xs: 64, sm: 72, md: 80 },
//                   height: { xs: 64, sm: 72, md: 80 },
//                   border: "3px solid",
//                   borderColor: "primary.light",
//                 }}
//               />
//             </Grid>
//             <Grid item xs>
//               <Typography variant="h5" fontWeight="bold" gutterBottom>
//                 {candidate.name}
//               </Typography>
//               <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
//                 <Email fontSize="small" color="action" />
//                 <Typography variant="body2" color="text.secondary">
//                   {candidate.email}
//                 </Typography>
//               </Box>
//               <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
//                 <LocationOn fontSize="small" color="action" />
//                 <Typography variant="body2" color="text.secondary">
//                   {candidate.location}
//                 </Typography>
//               </Box>
//               <Chip
//                 label={`${candidate.interviews.length} Scheduled Interview${candidate.interviews.length !== 1 ? "s" : ""}`}
//                 color="primary"
//                 variant="outlined"
//                 sx={{ fontWeight: 600 }}
//               />
//             </Grid>
//           </Grid>
//         </CardContent>
//       </Card>
//       <Grid container spacing={3}>
//         {candidate.interviews.map((interview) => (
//           <Grid item xs={12} key={interview.id}>
//             <Card elevation={1} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
//               <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
//                 <Grid container spacing={3} alignItems="center">
//                   <Grid item xs={12} md={8}>
//                     <Typography variant="h6" fontWeight="600" gutterBottom sx={{ color: "text.primary" }}>
//                       {interview.job_title}
//                     </Typography>
//                     <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 2 }}>
//                       <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                         <Business fontSize="small" color="action" />
//                         <Typography variant="body2" color="text.secondary" fontWeight="500">
//                           {interview.interview_mode}
//                         </Typography>
//                       </Box>
//                       <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                         <Event fontSize="small" color="action" />
//                         <Typography variant="body2" color="text.secondary" fontWeight="500">
//                           {new Date(`${interview.interview_date}T${interview.interview_time}`).toLocaleString()}
//                         </Typography>
//                       </Box>
//                     </Box>
//                     <Chip
//                       label={interview.status}
//                       color={interview.status.toLowerCase() === "scheduled" ? "success" : "warning"}
//                       size="small"
//                       sx={{ fontWeight: 600 }}
//                     />
//                   </Grid>
//                   <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
//                     <Button
//                       variant="contained"
//                       startIcon={<RateReview />}
//                       onClick={() => onEvaluate(candidate, interview)}
//                       fullWidth={isXs}
//                       sx={{
//                         minWidth: { xs: "100%", sm: 140 },
//                         borderRadius: 2,
//                         fontWeight: 600,
//                         boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
//                         "&:hover": {
//                           transform: "translateY(-1px)",
//                           boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
//                         },
//                       }}
//                     >
//                       Evaluate
//                     </Button>
//                   </Grid>
//                 </Grid>
//               </CardContent>
//             </Card>
//           </Grid>
//         ))}
//       </Grid>
//     </Box>
//   )
// }

// function ScheduledInterviewsTab({ onEvaluateClick }) {
//   const [candidates, setCandidates] = useState([])
//   const [loading, setLoading] = useState(true)
//   const [error, setError] = useState(null)
//   const [searchTerm, setSearchTerm] = useState("")
//   const [selectedCandidateDetails, setSelectedCandidateDetails] = useState(null)
//   const [showJobDetails, setShowJobDetails] = useState(false)
//   const theme = useTheme()
//   const isXs = useMediaQuery(theme.breakpoints.down("sm"))

//   useEffect(() => {
//     const fetchAndProcessInterviews = async () => {
//       try {
//         const response = await apiClient.get("/interviews/")
//         const data = response.data
//         const filtered = data.filter(
//           (interview) =>
//             interview.status.toLowerCase() === "scheduled" || interview.status.toLowerCase() === "rescheduled",
//         )
//         const groupedByCandidate = filtered.reduce((acc, interview) => {
//           const { candidate_name, profile_photo, talent_current_location } = interview
//           if (!acc[candidate_name]) {
//             acc[candidate_name] = {
//               id: candidate_name.replace(/\s+/g, "-").toLowerCase(),
//               name: candidate_name,
//               email: `${candidate_name.replace(/\s+/g, ".").toLowerCase()}@example.com`,
//               avatar: profile_photo || `https://i.pravatar.cc/150?u=${encodeURIComponent(candidate_name)}`,
//               location: talent_current_location || "Location not specified",
//               interviews: [],
//             }
//           }
//           acc[candidate_name].interviews.push(interview)
//           return acc
//         }, {})
//         setCandidates(Object.values(groupedByCandidate))
//       } catch (e) {
//         setError(e.response?.data?.detail || e.message)
//       } finally {
//         setLoading(false)
//       }
//     }
//     fetchAndProcessInterviews()
//   }, [])

//   const handleViewDetails = (candidate) => {
//     setSelectedCandidateDetails(candidate)
//     setShowJobDetails(true)
//   }

//   const handleBackToList = () => {
//     setShowJobDetails(false)
//     setSelectedCandidateDetails(null)
//   }

//   const filteredCandidates = candidates.filter(
//     (c) =>
//       c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       c.email.toLowerCase().includes(searchTerm.toLowerCase()),
//   )

//   if (loading) {
//     return (
//       <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
//         <CircularProgress />
//       </Box>
//     )
//   }

//   if (error) {
//     return (
//       <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//         <Typography variant="h6" color="error" gutterBottom>
//           Error Fetching Interviews
//         </Typography>
//         <Typography>{error}</Typography>
//       </Paper>
//     )
//   }

//   if (showJobDetails && selectedCandidateDetails) {
//     return (
//       <JobDetailsView candidate={selectedCandidateDetails} onBack={handleBackToList} onEvaluate={onEvaluateClick} />
//     )
//   }

//   return (
//     <Box>
//       {filteredCandidates.length === 0 ? (
//         <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//           <Typography variant="h6" color="text.secondary">
//             No Scheduled Interviews Found
//           </Typography>
//         </Paper>
//       ) : (
//         <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
//           <Table size={isXs ? "small" : "medium"}>
//             <TableHead>
//               <TableRow sx={{ backgroundColor: "#F1FFFA" }}>
//                 <TableCell sx={{ fontWeight: "bold" }}>Candidate</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Interviews</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {filteredCandidates.map((candidate) => (
//                 <TableRow key={candidate.id} sx={{ "&:hover": { bgcolor: "grey.50" } }}>
//                   <TableCell>
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                       <Avatar src={candidate.avatar} sx={{ width: 48, height: 48 }} />
//                       <Typography variant="body1" fontWeight="600">
//                         {candidate.name}
//                       </Typography>
//                     </Box>
//                   </TableCell>
//                   <TableCell>
//                     <Chip
//                       label={`${candidate.interviews.length} interview${candidate.interviews.length !== 1 ? "s" : ""}`}
//                       color="primary"
//                       variant="outlined"
//                       size="small"
//                       sx={{ fontWeight: 600 }}
//                     />
//                   </TableCell>
//                   <TableCell>
//                     <Button
//                       variant="contained"
//                       onClick={() => handleViewDetails(candidate)}
//                       sx={{
//                         borderRadius: 2,
//                         fontWeight: 600,
//                         boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
//                         "&:hover": {
//                           transform: "translateY(-1px)",
//                           boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
//                         },
//                       }}
//                     >
//                       View Details
//                     </Button>
//                   </TableCell>
//                 </TableRow>
//               ))}
//             </TableBody>
//           </Table>
//         </TableContainer>
//       )}
//     </Box>
//   )
// }

// function ExtendOfferTab({ onAlert }) {
//   const [completedCandidates, setCompletedCandidates] = useState([])
//   const [loading, setLoading] = useState(true)
//   const [error, setError] = useState(null)
//   const [extendingId, setExtendingId] = useState(null)
//   const [page, setPage] = useState(0)
//   const [rowsPerPage, setRowsPerPage] = useState(5)

//   useEffect(() => {
//     const fetchCompletedCandidates = async () => {
//       try {
//         const response = await apiClient.get("/interviews/completed-candidates/")
//         setCompletedCandidates(response.data)
//       } catch (e) {
//         setError(e.response?.data?.detail || e.message)
//       } finally {
//         setLoading(false)
//       }
//     }
//     fetchCompletedCandidates()
//   }, [])

//   const handleExtendOffer = async (applicationId) => {
//     setExtendingId(applicationId)
//     try {
//       await apiClient.post(`/applications/${applicationId}/offer-extend/`)
//       onAlert("Offer extended successfully!", "success")
//       setCompletedCandidates((prev) =>
//         prev.map((candidate) =>
//           candidate.application_id === applicationId
//             ? { ...candidate, application_status: "OFFER_EXTENDED" }
//             : candidate,
//         ),
//       )
//     } catch (err) {
//       const errorMessage = err.response?.data?.detail || err.message || "Failed to extend offer."
//       onAlert(errorMessage, "error")
//     } finally {
//       setExtendingId(null)
//     }
//   }

//   const handleChangePage = (event, newPage) => {
//     setPage(newPage)
//   }

//   const handleChangeRowsPerPage = (event) => {
//     setRowsPerPage(parseInt(event.target.value, 10))
//     setPage(0)
//   }

//   if (loading) {
//     return (
//       <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
//         <CircularProgress />
//       </Box>
//     )
//   }

//   if (error) {
//     return (
//       <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//         <Typography variant="h6" color="error" gutterBottom>
//           Error Fetching Data
//         </Typography>
//         <Typography>{error}</Typography>
//       </Paper>
//     )
//   }

//   return (
//     <Box>
//       {completedCandidates.length === 0 ? (
//         <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//           <Typography variant="h6" color="text.secondary">
//             No evaluated candidates available to extend an offer.
//           </Typography>
//         </Paper>
//       ) : (
//         <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
//           <Table>
//             <TableHead>
//               <TableRow sx={{ backgroundColor: "#F1FFFA" }}>
//                 <TableCell sx={{ fontWeight: "bold" }}>Candidate</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Job Title</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Tech Rating</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
//                 <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Actions</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {completedCandidates
//                 .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
//                 .map((candidate) => (
//                   <TableRow key={candidate.application_id} sx={{ "&:hover": { bgcolor: "grey.50" } }}>
//                     <TableCell>{candidate.talent_name}</TableCell>
//                     <TableCell>{candidate.job_posting_title}</TableCell>
//                     <TableCell>{candidate.technical_skills_rating || "N/A"}</TableCell>
//                     <TableCell>
//                       <Chip
//                         label={candidate.application_status.replace("_", " ")}
//                         color={
//                           candidate.application_status === "OFFER_EXTENDED"
//                             ? "success"
//                             : candidate.application_status === "HIRED"
//                             ? "primary"
//                             : "default"
//                         }
//                         size="small"
//                         sx={{ textTransform: "capitalize" }}
//                       />
//                     </TableCell>
//                     <TableCell sx={{ textAlign: "center" }}>
//                       <Button
//                         variant="contained"
//                         color="primary"
//                         startIcon={extendingId === candidate.application_id ? null : <Send />}
//                         disabled={
//                           extendingId === candidate.application_id ||
//                           candidate.application_status === "OFFER_EXTENDED"
//                         }
//                         onClick={() => handleExtendOffer(candidate.application_id)}
//                         sx={{
//                           minWidth: 140,
//                           borderRadius: 2,
//                         }}
//                       >
//                         {extendingId === candidate.application_id ? (
//                           <CircularProgress size={24} color="inherit" />
//                         ) : candidate.application_status === "OFFER_EXTENDED" ? (
//                           "Offer Extended"
//                         ) : (
//                           "Extend Offer"
//                         )}
//                       </Button>
//                     </TableCell>
//                   </TableRow>
//                 ))}
//             </TableBody>
//           </Table>
//           <TablePagination
//             rowsPerPageOptions={[5, 10, 25]}
//             component="div"
//             count={completedCandidates.length}
//             rowsPerPage={rowsPerPage}
//             page={page}
//             onPageChange={handleChangePage}
//             onRowsPerPageChange={handleChangeRowsPerPage}
//           />
//         </TableContainer>
//       )}
//     </Box>
//   )
// }

// export default function EvaluationsPage() {
//   const [modalOpen, setModalOpen] = useState(false)
//   const [selectedInterview, setSelectedInterview] = useState(null)
//   const [tabValue, setTabValue] = useState(0)
//   const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" })

//   const handleTabChange = (event, newValue) => {
//     setTabValue(newValue)
//   }

//   const handleEvaluateClick = (candidate, interview) => {
//     setSelectedInterview({
//       name: candidate.name,
//       email: candidate.email,
//       avatar: candidate.avatar,
//       interviewId: interview.id,
//       jobTitle: interview.job_title,
//     })
//     setModalOpen(true)
//   }

//   const handleCloseModal = () => {
//     setModalOpen(false)
//     setSelectedInterview(null)
//   }

//   const handleAlert = (message, severity) => {
//     setSnackbar({ open: true, message, severity })
//   }

//   const handleSnackbarClose = (event, reason) => {
//     if (reason === "clickaway") return
//     setSnackbar({ ...snackbar, open: false })
//   }

//   return (
//     <Layout title="Evaluations">
//       <ThemeProvider theme={theme}>
//         <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
//           <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
//             <Paper sx={{ p: { xs: 1, sm: 2 }, borderRadius: 3, backgroundColor: "#F1FFFA" , color: "#4D3CC3",}}>
//               <Typography variant="h4" gutterBottom>
//                 Candidate Evaluations & Extend Offers
//               </Typography>
//                           </Paper>

//               <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
//                 <Tabs value={tabValue} onChange={handleTabChange} aria-label="evaluation tabs">
//                   <Tab label="Scheduled Interviews" id="tab-0" />
//                   <Tab label="Extend Offer" id="tab-1" />
//                 </Tabs>
//               </Box>
//               <TabPanel value={tabValue} index={0}>
//                 <ScheduledInterviewsTab onEvaluateClick={handleEvaluateClick} />
//               </TabPanel>
//               <TabPanel value={tabValue} index={1}>
//                 <ExtendOfferTab onAlert={handleAlert} />
//               </TabPanel>
//           </Container>
//         </Box>
//         {selectedInterview && (
//           <EvaluationForm open={modalOpen} onClose={handleCloseModal} interviewDetails={selectedInterview} />
//         )}
//         <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose}>
//           <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
//             {snackbar.message}
//           </Alert>
//         </Snackbar>
//       </ThemeProvider>
//     </Layout>
//   )
// }

// import React, { useState, useEffect } from "react"
  // import {
  //   Container,
  //   Paper,
  //   Typography,
  //   Grid,
  //   Card,
  //   CardContent,
  //   Rating,
  //   TextField,
  //   Button,
  //   Box,
  //   Avatar,
  //   Divider,
  //   FormControl,
  //   Select,
  //   MenuItem,
  //   List,
  //   Accordion,
  //   Chip,
  //   Modal,
  //   CircularProgress,
  //   Alert,
  //   IconButton,
  //   Table,
  //   TableBody,
  //   TableCell,
  //   TableContainer,
  //   TableHead,
  //   TableRow,
  //   Backdrop,
  //   useMediaQuery,
  //   Fade,
  //   Tabs,
  //   Tab,
  //   Snackbar,
  // } from "@mui/material"
  // import {
  //   Person,
  //   Email,
  //   Event,
  //   Business,
  //   RateReview,
  //   ArrowBack,
  //   Close,
  //   Add,
  //   Remove,
  //   LocationOn,
  //   ThumbUp,
  //   Send,
  // } from "@mui/icons-material"
  // import { createTheme, ThemeProvider, styled, responsiveFontSizes, useTheme } from "@mui/material/styles"
  // import Layout from "../common/Layout"
  // import apiClient from "../../context/apiClient"

  // // Make typography responsive and keep palette/overrides as-is
  // const baseTheme = createTheme({
  //   palette: {
  //     primary: {
  //       main: "#1976d2",
  //       light: "#42a5f5",
  //       dark: "#1565c0",
  //     },
  //     background: {
  //       default: "#f8fafc",
  //       paper: "#ffffff",
  //     },
  //     text: {
  //       primary: "#1e293b",
  //       secondary: "#64748b",
  //     },
  //   },
  //   typography: {
  //     fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  //     h4: {
  //       fontWeight: 700,
  //       letterSpacing: "-0.025em",
  //     },
  //     h6: {
  //       fontWeight: 600,
  //     },
  //     body1: {
  //       lineHeight: 1.6,
  //     },
  //   },
  //   components: {
  //     MuiCard: {
  //       styleOverrides: {
  //         root: {
  //           borderRadius: 16,
  //           boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
  //           transition: "all 0.2s ease-in-out",
  //           "&:hover": {
  //             boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  //             transform: "translateY(-2px)",
  //           },
  //         },
  //       },
  //     },
  //     MuiButton: {
  //       styleOverrides: {
  //         root: {
  //           borderRadius: 12,
  //           textTransform: "none",
  //           fontWeight: 600,
  //           transition: "all 0.2s ease-in-out",
  //         },
  //         contained: {
  //           boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
  //           "&:hover": {
  //             boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
  //             transform: "translateY(-1px)",
  //           },
  //         },
  //       },
  //     },
  //     MuiAccordion: {
  //       styleOverrides: {
  //         root: {
  //           borderRadius: "16px !important",
  //           boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
  //           "&:before": {
  //             display: "none",
  //           },
  //           "&$expanded": {
  //             margin: "auto",
  //           },
  //         },
  //       },
  //     },
  //   },
  // })
  // const theme = responsiveFontSizes(baseTheme)

  // const StyledAccordion = styled(Accordion)(({ theme }) => ({
  //   transition: theme.transitions.create(["box-shadow", "margin", "transform"], {
  //     duration: theme.transitions.duration.short,
  //   }),
  //   "&:hover": {
  //     transform: "translateY(-2px)",
  //     boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  //   },
  //   "&.Mui-expanded": {
  //     boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  //     margin: `${theme.spacing(1, 0)} !important`,
  //     transform: "translateY(-2px)",
  //   },
  // }))

  // function TabPanel(props) {
  //   const { children, value, index, ...other } = props

  //   return (
  //     <div
  //       role="tabpanel"
  //       hidden={value !== index}
  //       id={`simple-tabpanel-${index}`}
  //       aria-labelledby={`simple-tab-${index}`}
  //       {...other}
  //     >
  //       {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  //     </div>
  //   )
  // }

  // function EvaluationForm({ interviewDetails, open, onClose }) {
  //   const theme = useTheme()
  //   const isXs = useMediaQuery(theme.breakpoints.down("sm"))

  //   const [formData, setFormData] = useState({
  //     recommendation: "",
  //     technical_skills_rating: 0,
  //     communication_skills_rating: 0,
  //     cultural_fit_rating: 0,
  //     strengths: [""],
  //     weaknesses: [""],
  //     overall_comments: [""],
  //   })
  //   const [isSubmitting, setIsSubmitting] = useState(false)
  //   const [submitError, setSubmitError] = useState(null)

  //   useEffect(() => {
  //     if (open) {
  //       setFormData({
  //         recommendation: "",
  //         technical_skills_rating: 0,
  //         communication_skills_rating: 0,
  //         cultural_fit_rating: 0,
  //         strengths: [""],
  //         weaknesses: [""],
  //         overall_comments: [""],
  //       })
  //       setIsSubmitting(false)
  //       setSubmitError(null)
  //     }
  //   }, [open])

  //   const handleChange = (e) => {
  //     const { name, value } = e.target
  //     setFormData((prev) => ({ ...prev, [name]: value }))
  //   }

  //   const handleRatingChange = (field, value) => {
  //     setFormData((prev) => ({ ...prev, [field]: value !== null ? Math.round(value) : 0 }))
  //   }

  //   const handleArrayFieldChange = (field, index, value) => {
  //     setFormData((prev) => ({
  //       ...prev,
  //       [field]: prev[field].map((item, i) => (i === index ? value : item)),
  //     }))
  //   }

  //   const addArrayField = (field) => {
  //     setFormData((prev) => ({
  //       ...prev,
  //       [field]: [...prev[field], ""],
  //     }))
  //   }

  //   const removeArrayField = (field, index) => {
  //     if (formData[field].length > 1) {
  //       setFormData((prev) => ({
  //         ...prev,
  //         [field]: prev[field].filter((_, i) => i !== index),
  //       }))
  //     }
  //   }

  //   const handleSubmit = async (e) => {
  //     e.preventDefault()
  //     setIsSubmitting(true)
  //     setSubmitError(null)

  //     try {
  //       const submissionData = {
  //         ...formData,
  //         strengths: formData.strengths.filter((s) => s.trim()).join("; "),
  //         weaknesses: formData.weaknesses.filter((w) => w.trim()).join("; "),
  //         overall_comments: formData.overall_comments.filter((c) => c.trim()).join("; "),
  //         interview_id: interviewDetails.interviewId,
  //       }

  //       await apiClient.post(
  //         `/interviews/${interviewDetails.interviewId}/submit-feedback/`,
  //         submissionData,
  //       )

  //       onClose()
  //     } catch (error) {
  //       console.error("[v0] Error submitting evaluation:", error)
  //       const errorMessage =
  //         error.response?.data?.message || error.message || "An unexpected error occurred."
  //       setSubmitError(errorMessage)
  //     } finally {
  //       setIsSubmitting(false)
  //     }
  //   }

  //   return (
  //     <Modal
  //       open={open}
  //       onClose={onClose}
  //       closeAfterTransition
  //       BackdropComponent={Backdrop}
  //       BackdropProps={{
  //         timeout: 500,
  //       }}
  //     >
  //       <Fade in={open}>
  //         <Box
  //           component={Paper}
  //           elevation={0}
  //           sx={{
  //             position: "absolute",
  //             top: "50%",
  //             left: "50%",
  //             transform: "translate(-50%, -50%)",
  //             width: { xs: "95%", sm: "90%", md: "80%", lg: "70%" },
  //             maxWidth: "900px",
  //             maxHeight: "90vh",
  //             bgcolor: "background.paper",
  //             borderRadius: 3,
  //             boxShadow: 24,
  //             overflow: "hidden",
  //             display: "flex",
  //             flexDirection: "column",
  //           }}
  //         >
  //           <Box
  //             sx={{
  //               background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  //               color: "white",
  //               p: { xs: 2, md: 3 },
  //               display: "flex",
  //               alignItems: "center",
  //               justifyContent: "space-between",
  //               flexShrink: 0,
  //             }}
  //           >
  //             <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
  //               <Avatar
  //                 src={interviewDetails?.avatar}
  //                 sx={{
  //                   width: { xs: 48, sm: 56 },
  //                   height: { xs: 48, sm: 56 },
  //                   border: "3px solid rgba(255,255,255,0.3)",
  //                   flexShrink: 0,
  //                 }}
  //               >
  //                 {interviewDetails?.name?.charAt(0)}
  //               </Avatar>
  //               <Box sx={{ minWidth: 0 }}>
  //                 <Typography
  //                   variant="h5"
  //                   fontWeight="bold"
  //                   sx={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}
  //                 >
  //                   Evaluate Candidate
  //                 </Typography>
  //                 <Typography
  //                   variant="body1"
  //                   sx={{ opacity: 0.9, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}
  //                 >
  //                   {interviewDetails?.name} • {interviewDetails?.jobTitle}
  //                 </Typography>
  //               </Box>
  //             </Box>
  //             <IconButton onClick={onClose} sx={{ color: "white" }}>
  //               <Close />
  //             </IconButton>
  //           </Box>
  //           <Container
  //             maxWidth="lg"
  //             sx={{
  //               flex: 1,
  //               overflow: "auto",
  //               py: { xs: 2, md: 3 },
  //               minHeight: 0,
  //               maxHeight: "calc(90vh - 140px)",
  //               "&::-webkit-scrollbar": { width: "8px" },
  //               "&::-webkit-scrollbar-track": { background: "#f1f1f1", borderRadius: "4px" },
  //               "&::-webkit-scrollbar-thumb": {
  //                 background: "#c1c1c1",
  //                 borderRadius: "4px",
  //                 "&:hover": { background: "#a8a8a8" },
  //               },
  //               scrollbarWidth: "thin",
  //               scrollbarColor: "#c1c1c1 #f1f1f1",
  //             }}
  //           >
  //             <form onSubmit={handleSubmit}>
  //               <Grid container spacing={3}>
  //                 <Grid item xs={12}>
  //                   <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, bgcolor: "#F1FFFA" }}>
  //                     <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
  //                       <ThumbUp color="primary" />
  //                       Recommendation
  //                     </Typography>
  //                     <FormControl fullWidth>
  //                       <Select
  //                         name="recommendation"
  //                         value={formData.recommendation}
  //                         onChange={handleChange}
  //                         required
  //                         sx={{ borderRadius: 2 }}
  //                       >
  //                         <MenuItem value="">
  //                           <em>Select recommendation</em>
  //                         </MenuItem>
  //                         <MenuItem value="HIRE">
  //                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
  //                             <Person color="success" /> Hire
  //                           </Box>
  //                         </MenuItem>
  //                         <MenuItem value="CONSIDER">
  //                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
  //                             <Person color="primary" /> Consider
  //                           </Box>
  //                         </MenuItem>
  //                         <MenuItem value="NO_HIRE">
  //                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
  //                             <Person color="error" /> No Hire
  //                           </Box>
  //                         </MenuItem>
  //                       </Select>
  //                     </FormControl>
  //                   </Card>
  //                 </Grid>
  //                 <Grid item xs={12}>
  //                   <Card
  //                     variant="outlined"
  //                     sx={{ p: { xs: 2, sm: 3 }, gap: 3, borderRadius: 2, bgcolor: "#F1FFFA", width: "100%" }}
  //                   >
  //                     <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
  //                       Skills Assessment
  //                     </Typography>
  //                     <Grid container spacing={3}>
  //                       {[
  //                         { field: "technical_skills_rating", label: "Technical Skills", icon: "💻" },
  //                         { field: "communication_skills_rating", label: "Communication", icon: "💬" },
  //                         { field: "cultural_fit_rating", label: "Cultural Fit", icon: "🤝" },
  //                       ].map((rating) => (
  //                         <Grid item xs={12} sm={4} key={rating.field}>
  //                           <Box
  //                             sx={{
  //                               textAlign: "center",
  //                               p: 2,
  //                               borderRadius: 2,
  //                               border: "1px solid",
  //                               borderColor: "divider",
  //                               bgcolor: "grey.50",
  //                             }}
  //                           >
  //                             <Typography variant="h4" sx={{ mb: 1 }}>
  //                               {rating.icon}
  //                             </Typography>
  //                             <Typography variant="body1" fontWeight="medium" gutterBottom>
  //                               {rating.label}
  //                             </Typography>
  //                             <Rating
  //                               name={rating.field}
  //                               value={formData[rating.field]}
  //                               onChange={(e, val) => handleRatingChange(rating.field, val)}
  //                               size={isXs ? "medium" : "large"}
  //                               sx={{ mb: 1 }}
  //                               max={5}
  //                             />
  //                             <Typography variant="body2" color="text.secondary">
  //                               {formData[rating.field]}/5
  //                             </Typography>
  //                           </Box>
  //                         </Grid>
  //                       ))}
  //                     </Grid>
  //                   </Card>
  //                 </Grid>
  //                 {[
  //                   { field: "strengths", label: "Key Strengths", placeholder: "What impressed you?", icon: "⭐" },
  //                   { field: "weaknesses", label: "Areas for Improvement", placeholder: "What could be developed?", icon: "📈" },
  //                   { field: "overall_comments", label: "Overall Assessment", placeholder: "Provide comprehensive evaluation...", icon: "📝" },
  //                 ].map((section) => (
  //                   <Grid item xs={12} key={section.field}>
  //                     <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, bgcolor: "#F1FFFA" }}>
  //                       <Box
  //                         sx={{
  //                           display: "flex",
  //                           alignItems: { xs: "stretch", sm: "center" },
  //                           justifyContent: "space-between",
  //                           gap: 2,
  //                           mb: 2,
  //                           flexDirection: { xs: "column", sm: "row" },
  //                         }}
  //                       >
  //                         <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
  //                           {section.icon} {section.label}
  //                         </Typography>
  //                         <Button
  //                           variant="outlined"
  //                           size="medium"
  //                           startIcon={<Add />}
  //                           onClick={() => addArrayField(section.field)}
  //                           sx={{ borderRadius: 2, alignSelf: { xs: "stretch", sm: "auto" } }}
  //                           fullWidth={isXs}
  //                         >
  //                           Add
  //                         </Button>
  //                       </Box>
  //                       {formData[section.field].map((item, index) => (
  //                         <Box
  //                           key={index}
  //                           sx={{
  //                             display: "flex",
  //                             gap: 1,
  //                             mb: 2,
  //                             flexDirection: { xs: "column", sm: "row" },
  //                             alignItems: { xs: "stretch", sm: "flex-start" },
  //                           }}
  //                         >
  //                           <TextField
  //                             fullWidth
  //                             multiline
  //                             rows={3}
  //                             placeholder={section.placeholder}
  //                             value={item}
  //                             onChange={(e) => handleArrayFieldChange(section.field, index, e.target.value)}
  //                             variant="outlined"
  //                             sx={{ borderRadius: 2 }}
  //                           />
  //                           {formData[section.field].length > 1 && (
  //                             <IconButton
  //                               onClick={() => removeArrayField(section.field, index)}
  //                               color="error"
  //                               sx={{ alignSelf: { xs: "flex-end", sm: "flex-start" }, mt: { xs: 0, sm: 1 } }}
  //                             >
  //                               <Remove />
  //                             </IconButton>
  //                           )}
  //                         </Box>
  //                       ))}
  //                     </Card>
  //                   </Grid>
  //                 ))}
  //                 {submitError && (
  //                   <Grid item xs={12}>
  //                     <Alert severity="error" sx={{ borderRadius: 2 }}>
  //                       {submitError}
  //                     </Alert>
  //                   </Grid>
  //                 )}
  //               </Grid>
  //               <Box
  //                 sx={{
  //                   display: "flex",
  //                   flexDirection: { xs: "column-reverse", sm: "row" },
  //                   justifyContent: "flex-end",
  //                   gap: 2,
  //                   mt: 4,
  //                   pt: 3,
  //                   borderTop: "1px solid",
  //                   borderColor: "divider",
  //                 }}
  //               >
  //                 <Button onClick={onClose} variant="outlined" size="large" fullWidth={isXs} sx={{ borderRadius: 2, minWidth: 120 }}>
  //                   Cancel
  //                 </Button>
  //                 <Button type="submit" variant="contained" size="large" disabled={isSubmitting} fullWidth={isXs} sx={{ borderRadius: 2, minWidth: 120 }}>
  //                   {isSubmitting ? <CircularProgress size={24} /> : "Submit Evaluation"}
  //                 </Button>
  //               </Box>
  //             </form>
  //           </Container>
  //         </Box>
  //       </Fade>
  //     </Modal>
  //   )
  // }

  // function JobDetailsView({ candidate, onBack, onEvaluate }) {
  //   const theme = useTheme()
  //   const isXs = useMediaQuery(theme.breakpoints.down("sm"))

  //   return (
  //     <Box>
  //       <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
  //         <Button variant="outlined" startIcon={<ArrowBack />} onClick={onBack} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
  //           Back to Candidates
  //         </Button>
  //       </Box>
  //       <Card elevation={1} sx={{ mb: 4, borderRadius: 2, bgcolor: "#F1FFFA" }}>
  //         <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
  //           <Grid container spacing={3} alignItems="center">
  //             <Grid item xs="auto">
  //               <Avatar
  //                 src={candidate.avatar}
  //                 sx={{
  //                   width: { xs: 64, sm: 72, md: 80 },
  //                   height: { xs: 64, sm: 72, md: 80 },
  //                   border: "3px solid",
  //                   borderColor: "primary.light",
  //                 }}
  //               />
  //             </Grid>
  //             <Grid item xs>
  //               <Typography variant="h5" fontWeight="bold" gutterBottom>
  //                 {candidate.name}
  //               </Typography>
  //               <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
  //                 <Email fontSize="small" color="action" />
  //                 <Typography variant="body2" color="text.secondary">
  //                   {candidate.email}
  //                 </Typography>
  //               </Box>
  //               <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
  //                 <LocationOn fontSize="small" color="action" />
  //                 <Typography variant="body2" color="text.secondary">
  //                   {candidate.location}
  //                 </Typography>
  //               </Box>
  //               <Chip
  //                 label={`${candidate.interviews.length} Scheduled Interview${candidate.interviews.length !== 1 ? "s" : ""}`}
  //                 color="primary"
  //                 variant="outlined"
  //                 sx={{ fontWeight: 600 }}
  //               />
  //             </Grid>
  //           </Grid>
  //         </CardContent>
  //       </Card>
  //       <Grid container spacing={3}>
  //         {candidate.interviews.map((interview) => (
  //           <Grid item xs={12} key={interview.id}>
  //             <Card elevation={1} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
  //               <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
  //                 <Grid container spacing={3} alignItems="center">
  //                   <Grid item xs={12} md={8}>
  //                     <Typography variant="h6" fontWeight="600" gutterBottom sx={{ color: "text.primary" }}>
  //                       {interview.job_title}
  //                     </Typography>
  //                     <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 2 }}>
  //                       <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
  //                         <Business fontSize="small" color="action" />
  //                         <Typography variant="body2" color="text.secondary" fontWeight="500">
  //                           {interview.interview_mode}
  //                         </Typography>
  //                       </Box>
  //                       <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
  //                         <Event fontSize="small" color="action" />
  //                         <Typography variant="body2" color="text.secondary" fontWeight="500">
  //                           {new Date(`${interview.interview_date}T${interview.interview_time}`).toLocaleString()}
  //                         </Typography>
  //                       </Box>
  //                     </Box>
  //                     <Chip
  //                       label={interview.status}
  //                       color={interview.status.toLowerCase() === "scheduled" ? "success" : "warning"}
  //                       size="small"
  //                       sx={{ fontWeight: 600 }}
  //                     />
  //                   </Grid>
  //                   <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
  //                     <Button
  //                       variant="contained"
  //                       startIcon={<RateReview />}
  //                       onClick={() => onEvaluate(candidate, interview)}
  //                       fullWidth={isXs}
  //                       sx={{
  //                         minWidth: { xs: "100%", sm: 140 },
  //                         borderRadius: 2,
  //                         fontWeight: 600,
  //                         boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
  //                         "&:hover": {
  //                           transform: "translateY(-1px)",
  //                           boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
  //                         },
  //                       }}
  //                     >
  //                       Evaluate
  //                     </Button>
  //                   </Grid>
  //                 </Grid>
  //               </CardContent>
  //             </Card>
  //           </Grid>
  //         ))}
  //       </Grid>
  //     </Box>
  //   )
  // }

  // function ScheduledInterviewsTab({ onEvaluateClick }) {
  //   const [candidates, setCandidates] = useState([])
  //   const [loading, setLoading] = useState(true)
  //   const [error, setError] = useState(null)
  //   const [searchTerm, setSearchTerm] = useState("")
  //   const [selectedCandidateDetails, setSelectedCandidateDetails] = useState(null)
  //   const [showJobDetails, setShowJobDetails] = useState(false)

  //   const theme = useTheme()
  //   const isXs = useMediaQuery(theme.breakpoints.down("sm"))

  //   useEffect(() => {
  //     const fetchAndProcessInterviews = async () => {
  //       try {
  //         const response = await apiClient.get("/interviews/")
  //         const data = response.data
  //         const filtered = data.filter(
  //           (interview) =>
  //             interview.status.toLowerCase() === "scheduled" || interview.status.toLowerCase() === "rescheduled",
  //         )
  //         const groupedByCandidate = filtered.reduce((acc, interview) => {
  //           const { candidate_name, profile_photo, talent_current_location } = interview
  //           if (!acc[candidate_name]) {
  //             acc[candidate_name] = {
  //               id: candidate_name.replace(/\s+/g, "-").toLowerCase(),
  //               name: candidate_name,
  //               email: `${candidate_name.replace(/\s+/g, ".").toLowerCase()}@example.com`,
  //               avatar: profile_photo || `https://i.pravatar.cc/150?u=${encodeURIComponent(candidate_name)}`,
  //               location: talent_current_location || "Location not specified",
  //               interviews: [],
  //             }
  //           }
  //           acc[candidate_name].interviews.push(interview)
  //           return acc
  //         }, {})
  //         setCandidates(Object.values(groupedByCandidate))
  //       } catch (e) {
  //         setError(e.response?.data?.detail || e.message)
  //       } finally {
  //         setLoading(false)
  //       }
  //     }
  //     fetchAndProcessInterviews()
  //   }, [])

  //   const handleViewDetails = (candidate) => {
  //     setSelectedCandidateDetails(candidate)
  //     setShowJobDetails(true)
  //   }

  //   const handleBackToList = () => {
  //     setShowJobDetails(false)
  //     setSelectedCandidateDetails(null)
  //   }

  //   const filteredCandidates = candidates.filter(
  //     (c) =>
  //       c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //       c.email.toLowerCase().includes(searchTerm.toLowerCase()),
  //   )

  //   if (loading) {
  //     return (
  //       <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
  //         <CircularProgress />
  //       </Box>
  //     )
  //   }

  //   if (error) {
  //     return (
  //       <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
  //         <Typography variant="h6" color="error" gutterBottom>
  //           Error Fetching Interviews
  //         </Typography>
  //         <Typography>{error}</Typography>
  //       </Paper>
  //     )
  //   }

  //   if (showJobDetails && selectedCandidateDetails) {
  //     return (
  //       <JobDetailsView candidate={selectedCandidateDetails} onBack={handleBackToList} onEvaluate={onEvaluateClick} />
  //     )
  //   }

  //   return (
  //     <Box>
  //       {filteredCandidates.length === 0 ? (
  //         <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
  //           <Typography variant="h6" color="text.secondary">
  //             No Scheduled Interviews Found
  //           </Typography>
  //         </Paper>
  //       ) : (
  //         <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
  //           <Table size={isXs ? "small" : "medium"}>
  //             <TableHead>
  //               <TableRow sx={{ backgroundColor: "#F1FFFA" }}>
  //                 <TableCell sx={{ fontWeight: "bold" }}>Candidate</TableCell>
  //                 <TableCell sx={{ fontWeight: "bold" }}>Interviews</TableCell>
  //                 <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
  //               </TableRow>
  //             </TableHead>
  //             <TableBody>
  //               {filteredCandidates.map((candidate) => (
  //                 <TableRow key={candidate.id} sx={{ "&:hover": { bgcolor: "grey.50" } }}>
  //                   <TableCell>
  //                     <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
  //                       <Avatar src={candidate.avatar} sx={{ width: 48, height: 48 }} />
  //                       <Typography variant="body1" fontWeight="600">
  //                         {candidate.name}
  //                       </Typography>
  //                     </Box>
  //                   </TableCell>
  //                   <TableCell>
  //                     <Chip
  //                       label={`${candidate.interviews.length} interview${candidate.interviews.length !== 1 ? "s" : ""}`}
  //                       color="primary"
  //                       variant="outlined"
  //                       size="small"
  //                       sx={{ fontWeight: 600 }}
  //                     />
  //                   </TableCell>
  //                   <TableCell>
  //                     <Button
  //                       variant="contained"
  //                       onClick={() => handleViewDetails(candidate)}
  //                       sx={{
  //                         borderRadius: 2,
  //                         fontWeight: 600,
  //                         boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
  //                         "&:hover": {
  //                           transform: "translateY(-1px)",
  //                           boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
  //                         },
  //                       }}
  //                     >
  //                       View Details
  //                     </Button>
  //                   </TableCell>
  //                 </TableRow>
  //               ))}
  //             </TableBody>
  //           </Table>
  //         </TableContainer>
  //       )}
  //     </Box>
  //   )
  // }

  // function ExtendOfferTab({ onAlert }) {
  //   const [completedCandidates, setCompletedCandidates] = useState([])
  //   const [loading, setLoading] = useState(true)
  //   const [error, setError] = useState(null)
  //   const [extendingId, setExtendingId] = useState(null)

  //   useEffect(() => {
  //     const fetchCompletedCandidates = async () => {
  //       try {
  //         const response = await apiClient.get("/interviews/completed-candidates/")
  //         setCompletedCandidates(response.data)
  //       } catch (e) {
  //         setError(e.response?.data?.detail || e.message)
  //       } finally {
  //         setLoading(false)
  //       }
  //     }
  //     fetchCompletedCandidates()
  //   }, [])

  //   const handleExtendOffer = async (applicationId) => {
  //     setExtendingId(applicationId)
  //     try {
  //       await apiClient.post(`/applications/${applicationId}/offer-extend/`)
  //       onAlert("Offer extended successfully!", "success")
  //       setCompletedCandidates((prev) =>
  //         prev.map((candidate) =>
  //           candidate.application_id === applicationId
  //             ? { ...candidate, application_status: "OFFER_EXTENDED" }
  //             : candidate,
  //         ),
  //       )
  //     } catch (err) {
  //       const errorMessage = err.response?.data?.detail || err.message || "Failed to extend offer."
  //       onAlert(errorMessage, "error")
  //     } finally {
  //       setExtendingId(null)
  //     }
  //   }

  //   if (loading) {
  //     return (
  //       <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
  //         <CircularProgress />
  //       </Box>
  //     )
  //   }

  //   if (error) {
  //     return (
  //       <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
  //         <Typography variant="h6" color="error" gutterBottom>
  //           Error Fetching Data
  //         </Typography>
  //         <Typography>{error}</Typography>
  //       </Paper>
  //     )
  //   }

  //   return (
  //     <Box>
  //       {completedCandidates.length === 0 ? (
  //         <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
  //           <Typography variant="h6" color="text.secondary">
  //             No evaluated candidates available to extend an offer.
  //           </Typography>
  //         </Paper>
  //       ) : (
  //         <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
  //           <Table>
  //             <TableHead>
  //               <TableRow sx={{ backgroundColor: "#F1FFFA" }}>
  //                 <TableCell sx={{ fontWeight: "bold" }}>Candidate</TableCell>
  //                 <TableCell sx={{ fontWeight: "bold" }}>Job Title</TableCell>
  //                 <TableCell sx={{ fontWeight: "bold" }}>Tech Rating</TableCell>
  //                 <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
  //                 <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Actions</TableCell>
  //               </TableRow>
  //             </TableHead>
  //             <TableBody>
  //               {completedCandidates.map((candidate) => (
  //                 <TableRow key={candidate.application_id} sx={{ "&:hover": { bgcolor: "grey.50" } }}>
  //                   <TableCell>{candidate.talent_name}</TableCell>
  //                   <TableCell>{candidate.job_posting_title}</TableCell>
  //                   <TableCell>{candidate.technical_skills_rating || "N/A"}</TableCell>
  //                   <TableCell>
  //                     <Chip
  //                       label={candidate.application_status.replace("_", " ")}
  //                       color={
  //                         candidate.application_status === "OFFER_EXTENDED"
  //                           ? "success"
  //                           : candidate.application_status === "HIRED"
  //                           ? "primary"
  //                           : "default"
  //                       }
  //                       size="small"
  //                       sx={{ textTransform: "capitalize" }}
  //                     />
  //                   </TableCell>
  //                   <TableCell sx={{ textAlign: "center" }}>
  //                     <Button
  //                       variant="contained"
  //                       color="primary"
  //                       startIcon={extendingId === candidate.application_id ? null : <Send />}
  //                       disabled={
  //                         extendingId === candidate.application_id ||
  //                         candidate.application_status === "OFFER_EXTENDED"
  //                       }
  //                       onClick={() => handleExtendOffer(candidate.application_id)}
  //                       sx={{
  //                         minWidth: 140,
  //                         borderRadius: 2,
  //                       }}
  //                     >
  //                       {extendingId === candidate.application_id ? (
  //                         <CircularProgress size={24} color="inherit" />
  //                       ) : candidate.application_status === "OFFER_EXTENDED" ? (
  //                         "Offer Extended"
  //                       ) : (
  //                         "Extend Offer"
  //                       )}
  //                     </Button>
  //                   </TableCell>
  //                 </TableRow>
  //               ))}
  //             </TableBody>
  //           </Table>
  //         </TableContainer>
  //       )}
  //     </Box>
  //   )
  // }

  // export default function EvaluationsPage() {
  //   const [modalOpen, setModalOpen] = useState(false)
  //   const [selectedInterview, setSelectedInterview] = useState(null)
  //   const [tabValue, setTabValue] = useState(0)
  //   const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" })

  //   const handleTabChange = (event, newValue) => {
  //     setTabValue(newValue)
  //   }

  //   const handleEvaluateClick = (candidate, interview) => {
  //     setSelectedInterview({
  //       name: candidate.name,
  //       email: candidate.email,
  //       avatar: candidate.avatar,
  //       interviewId: interview.id,
  //       jobTitle: interview.job_title,
  //     })
  //     setModalOpen(true)
  //   }

  //   const handleCloseModal = () => {
  //     setModalOpen(false)
  //     setSelectedInterview(null)
  //   }

  //   const handleAlert = (message, severity) => {
  //     setSnackbar({ open: true, message, severity })
  //   }

  //   const handleSnackbarClose = (event, reason) => {
  //     if (reason === "clickaway") return
  //     setSnackbar({ ...snackbar, open: false })
  //   }

  //   return (
  //     <Layout title="Evaluations">
  //       <ThemeProvider theme={theme}>
  //         <Box sx={{ bgcolor: "background.default", minHeight: "100vh", }}>
  //           <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
  //             <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, backgroundColor:"#F1FFFA" }}>
  //               <Typography variant="h4" gutterBottom>
  //                 Candidate Evaluations & Extend Offers
  //               </Typography>
  //               <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
  //                 <Tabs value={tabValue} onChange={handleTabChange} aria-label="evaluation tabs">
  //                   <Tab label="Scheduled Interviews" id="tab-0" />
  //                   <Tab label="Extend Offer" id="tab-1" />
  //                 </Tabs>
  //               </Box>
  //               <TabPanel value={tabValue} index={0}>
  //                 <ScheduledInterviewsTab onEvaluateClick={handleEvaluateClick} />
  //               </TabPanel>
  //               <TabPanel value={tabValue} index={1}>
  //                 <ExtendOfferTab onAlert={handleAlert} />
  //               </TabPanel>
  //             </Paper>
  //           </Container>
  //         </Box>

  //         {selectedInterview && (
  //           <EvaluationForm open={modalOpen} onClose={handleCloseModal} interviewDetails={selectedInterview} />
  //         )}

  //         <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose}>
  //           <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
  //             {snackbar.message}
  //           </Alert>
  //         </Snackbar>
  //       </ThemeProvider>
  //     </Layout>
  //   )
  // }


// import React, { useState, useEffect } from "react"
// import {
//   Container,
//   Paper,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   Rating,
//   TextField,
//   Button,
//   Box,
//   Avatar,
//   Divider,
//   FormControl,
//   Select,
//   MenuItem,
//   List,
//   Accordion,
//   Chip,
//   Modal,
//   CircularProgress,
//   Alert,
//   IconButton,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Backdrop,
//   useMediaQuery,
//   Fade,
//   Tabs,
//   Tab,
//   Snackbar,
// } from "@mui/material"
// import {
//   Person,
//   Email,
//   Event,
//   Business,
//   RateReview,
//   ArrowBack,
//   Close,
//   Add,
//   Remove,
//   LocationOn,
//   ThumbUp,
//   Send,
// } from "@mui/icons-material"
// import { createTheme, ThemeProvider, styled, responsiveFontSizes, useTheme } from "@mui/material/styles"
// import Layout from "../common/Layout"

// // Make typography responsive and keep palette/overrides as-is
// const baseTheme = createTheme({
//   palette: {
//     primary: {
//       main: "#1976d2",
//       light: "#42a5f5",
//       dark: "#1565c0",
//     },
//     background: {
//       default: "#f8fafc",
//       paper: "#ffffff",
//     },
//     text: {
//       primary: "#1e293b",
//       secondary: "#64748b",
//     },
//   },
//   typography: {
//     fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
//     h4: {
//       fontWeight: 700,
//       letterSpacing: "-0.025em",
//     },
//     h6: {
//       fontWeight: 600,
//     },
//     body1: {
//       lineHeight: 1.6,
//     },
//   },
//   components: {
//     MuiCard: {
//       styleOverrides: {
//         root: {
//           borderRadius: 16,
//           boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
//           transition: "all 0.2s ease-in-out",
//           "&:hover": {
//             boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//             transform: "translateY(-2px)",
//           },
//         },
//       },
//     },
//     MuiButton: {
//       styleOverrides: {
//         root: {
//           borderRadius: 12,
//           textTransform: "none",
//           fontWeight: 600,
//           transition: "all 0.2s ease-in-out",
//         },
//         contained: {
//           boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
//           "&:hover": {
//             boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
//             transform: "translateY(-1px)",
//           },
//         },
//       },
//     },
//     MuiAccordion: {
//       styleOverrides: {
//         root: {
//           borderRadius: "16px !important",
//           boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
//           "&:before": {
//             display: "none",
//           },
//           "&$expanded": {
//             margin: "auto",
//           },
//         },
//       },
//     },
//   },
// })
// const theme = responsiveFontSizes(baseTheme)

// const StyledAccordion = styled(Accordion)(({ theme }) => ({
//   transition: theme.transitions.create(["box-shadow", "margin", "transform"], {
//     duration: theme.transitions.duration.short,
//   }),
//   "&:hover": {
//     transform: "translateY(-2px)",
//     boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//   },
//   "&.Mui-expanded": {
//     boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//     margin: `${theme.spacing(1, 0)} !important`,
//     transform: "translateY(-2px)",
//   },
// }))

// function TabPanel(props) {
//   const { children, value, index, ...other } = props

//   return (
//     <div
//       role="tabpanel"
//       hidden={value !== index}
//       id={`simple-tabpanel-${index}`}
//       aria-labelledby={`simple-tab-${index}`}
//       {...other}
//     >
//       {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
//     </div>
//   )
// }

// function EvaluationForm({ interviewDetails, open, onClose }) {
//   const theme = useTheme()
//   const isXs = useMediaQuery(theme.breakpoints.down("sm"))

//   const [formData, setFormData] = useState({
//     recommendation: "",
//     technical_skills_rating: 0,
//     communication_skills_rating: 0,
//     cultural_fit_rating: 0,
//     strengths: [""],
//     weaknesses: [""],
//     overall_comments: [""],
//   })
//   const [isSubmitting, setIsSubmitting] = useState(false)
//   const [submitError, setSubmitError] = useState(null)

//   useEffect(() => {
//     if (open) {
//       setFormData({
//         recommendation: "",
//         technical_skills_rating: 0,
//         communication_skills_rating: 0,
//         cultural_fit_rating: 0,
//         strengths: [""],
//         weaknesses: [""],
//         overall_comments: [""],
//       })
//       setIsSubmitting(false)
//       setSubmitError(null)
//     }
//   }, [open])

//   const handleChange = (e) => {
//     const { name, value } = e.target
//     setFormData((prev) => ({ ...prev, [name]: value }))
//   }

//   const handleRatingChange = (field, value) => {
//     setFormData((prev) => ({ ...prev, [field]: value !== null ? Math.round(value) : 0 }))
//   }

//   const handleArrayFieldChange = (field, index, value) => {
//     setFormData((prev) => ({
//       ...prev,
//       [field]: prev[field].map((item, i) => (i === index ? value : item)),
//     }))
//   }

//   const addArrayField = (field) => {
//     setFormData((prev) => ({
//       ...prev,
//       [field]: [...prev[field], ""],
//     }))
//   }

//   const removeArrayField = (field, index) => {
//     if (formData[field].length > 1) {
//       setFormData((prev) => ({
//         ...prev,
//         [field]: prev[field].filter((_, i) => i !== index),
//       }))
//     }
//   }

//   const handleSubmit = async (e) => {
//     e.preventDefault()
//     setIsSubmitting(true)
//     setSubmitError(null)

//     try {
//       const submissionData = {
//         ...formData,
//         strengths: formData.strengths.filter((s) => s.trim()).join("; "),
//         weaknesses: formData.weaknesses.filter((w) => w.trim()).join("; "),
//         overall_comments: formData.overall_comments.filter((c) => c.trim()).join("; "),
//         interview_id: interviewDetails.interviewId,
//       }

//       const accessToken = localStorage.getItem("access_token")
//       const headers = {
//         "Content-Type": "application/json",
//       }

//       if (accessToken) {
//         headers.Authorization = `Bearer ${accessToken}`
//       }

//       const response = await fetch(
//         `http://localhost:8000/api/interviews/${interviewDetails.interviewId}/submit-feedback/`,
//         {
//           method: "POST",
//           headers,
//           body: JSON.stringify(submissionData),
//         },
//       )

//       if (!response.ok) {
//         const contentType = response.headers.get("content-type")
//         let errorMessage = `HTTP ${response.status}: ${response.statusText}`

//         if (contentType && contentType.includes("application/json")) {
//           try {
//             const errorData = await response.json()
//             errorMessage = errorData.message || errorMessage
//           } catch (jsonError) {
//             console.error("[v0] Failed to parse error JSON:", jsonError)
//           }
//         } else {
//           errorMessage = `Server returned ${response.status}. Check if the API endpoint exists and the server is running.`
//         }

//         throw new Error(errorMessage)
//       }

//       onClose()
//     } catch (error) {
//       console.error("[v0] Error submitting evaluation:", error)
//       setSubmitError(error.message)
//     } finally {
//       setIsSubmitting(false)
//     }
//   }

//   return (
//     <Modal
//       open={open}
//       onClose={onClose}
//       closeAfterTransition
//       BackdropComponent={Backdrop}
//       BackdropProps={{
//         timeout: 500,
//       }}
//     >
//       <Fade in={open}>
//         <Box
//           component={Paper}
//           elevation={0}
//           sx={{
//             position: "absolute",
//             top: "50%",
//             left: "50%",
//             transform: "translate(-50%, -50%)",
//             width: { xs: "95%", sm: "90%", md: "80%", lg: "70%" },
//             maxWidth: "900px",
//             maxHeight: "90vh",
//             bgcolor: "background.paper",
//             borderRadius: 3,
//             boxShadow: 24,
//             overflow: "hidden",
//             display: "flex",
//             flexDirection: "column",
//           }}
//         >
//           <Box
//             sx={{
//               background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
//               color: "white",
//               p: { xs: 2, md: 3 },
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "space-between",
//               flexShrink: 0,
//             }}
//           >
//             <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
//               <Avatar
//                 src={interviewDetails?.avatar}
//                 sx={{
//                   width: { xs: 48, sm: 56 },
//                   height: { xs: 48, sm: 56 },
//                   border: "3px solid rgba(255,255,255,0.3)",
//                   flexShrink: 0,
//                 }}
//               >
//                 {interviewDetails?.name?.charAt(0)}
//               </Avatar>
//               <Box sx={{ minWidth: 0 }}>
//                 <Typography
//                   variant="h5"
//                   fontWeight="bold"
//                   sx={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}
//                 >
//                   Evaluate Candidate
//                 </Typography>
//                 <Typography
//                   variant="body1"
//                   sx={{ opacity: 0.9, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}
//                 >
//                   {interviewDetails?.name} • {interviewDetails?.jobTitle}
//                 </Typography>
//               </Box>
//             </Box>
//             <IconButton onClick={onClose} sx={{ color: "white" }}>
//               <Close />
//             </IconButton>
//           </Box>
//           <Container
//             maxWidth="lg"
//             sx={{
//               flex: 1,
//               overflow: "auto",
//               py: { xs: 2, md: 3 },
//               minHeight: 0,
//               maxHeight: "calc(90vh - 140px)",
//               "&::-webkit-scrollbar": { width: "8px" },
//               "&::-webkit-scrollbar-track": { background: "#f1f1f1", borderRadius: "4px" },
//               "&::-webkit-scrollbar-thumb": {
//                 background: "#c1c1c1",
//                 borderRadius: "4px",
//                 "&:hover": { background: "#a8a8a8" },
//               },
//               scrollbarWidth: "thin",
//               scrollbarColor: "#c1c1c1 #f1f1f1",
//             }}
//           >
//             <form onSubmit={handleSubmit}>
//               <Grid container spacing={3}>
//                 <Grid item xs={12}>
//                   <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, bgcolor: "#F1FFFA" }}>
//                     <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                       <ThumbUp color="primary" />
//                       Recommendation
//                     </Typography>
//                     <FormControl fullWidth>
//                       <Select
//                         name="recommendation"
//                         value={formData.recommendation}
//                         onChange={handleChange}
//                         required
//                         sx={{ borderRadius: 2 }}
//                       >
//                         <MenuItem value="">
//                           <em>Select recommendation</em>
//                         </MenuItem>
//                         <MenuItem value="HIRE">
//                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <Person color="success" /> Hire
//                           </Box>
//                         </MenuItem>
//                         <MenuItem value="CONSIDER">
//                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <Person color="primary" /> Consider
//                           </Box>
//                         </MenuItem>
//                         <MenuItem value="NO_HIRE">
//                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <Person color="error" /> No Hire
//                           </Box>
//                         </MenuItem>
//                       </Select>
//                     </FormControl>
//                   </Card>
//                 </Grid>
//                 <Grid item xs={12}>
//                   <Card
//                     variant="outlined"
//                     sx={{ p: { xs: 2, sm: 3 }, gap: 3, borderRadius: 2, bgcolor: "#F1FFFA", width: "100%" }}
//                   >
//                     <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
//                       Skills Assessment
//                     </Typography>
//                     <Grid container spacing={3}>
//                       {[
//                         { field: "technical_skills_rating", label: "Technical Skills", icon: "💻" },
//                         { field: "communication_skills_rating", label: "Communication", icon: "💬" },
//                         { field: "cultural_fit_rating", label: "Cultural Fit", icon: "🤝" },
//                       ].map((rating) => (
//                         <Grid item xs={12} sm={4} key={rating.field}>
//                           <Box
//                             sx={{
//                               textAlign: "center",
//                               p: 2,
//                               borderRadius: 2,
//                               border: "1px solid",
//                               borderColor: "divider",
//                               bgcolor: "grey.50",
//                             }}
//                           >
//                             <Typography variant="h4" sx={{ mb: 1 }}>
//                               {rating.icon}
//                             </Typography>
//                             <Typography variant="body1" fontWeight="medium" gutterBottom>
//                               {rating.label}
//                             </Typography>
//                             <Rating
//                               name={rating.field}
//                               value={formData[rating.field]}
//                               onChange={(e, val) => handleRatingChange(rating.field, val)}
//                               size={isXs ? "medium" : "large"}
//                               sx={{ mb: 1 }}
//                               max={5}
//                             />
//                             <Typography variant="body2" color="text.secondary">
//                               {formData[rating.field]}/5
//                             </Typography>
//                           </Box>
//                         </Grid>
//                       ))}
//                     </Grid>
//                   </Card>
//                 </Grid>
//                 {[
//                   { field: "strengths", label: "Key Strengths", placeholder: "What impressed you?", icon: "⭐" },
//                   { field: "weaknesses", label: "Areas for Improvement", placeholder: "What could be developed?", icon: "📈" },
//                   { field: "overall_comments", label: "Overall Assessment", placeholder: "Provide comprehensive evaluation...", icon: "📝" },
//                 ].map((section) => (
//                   <Grid item xs={12} key={section.field}>
//                     <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, bgcolor: "#F1FFFA" }}>
//                       <Box
//                         sx={{
//                           display: "flex",
//                           alignItems: { xs: "stretch", sm: "center" },
//                           justifyContent: "space-between",
//                           gap: 2,
//                           mb: 2,
//                           flexDirection: { xs: "column", sm: "row" },
//                         }}
//                       >
//                         <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                           {section.icon} {section.label}
//                         </Typography>
//                         <Button
//                           variant="outlined"
//                           size="medium"
//                           startIcon={<Add />}
//                           onClick={() => addArrayField(section.field)}
//                           sx={{ borderRadius: 2, alignSelf: { xs: "stretch", sm: "auto" } }}
//                           fullWidth={isXs}
//                         >
//                           Add
//                         </Button>
//                       </Box>
//                       {formData[section.field].map((item, index) => (
//                         <Box
//                           key={index}
//                           sx={{
//                             display: "flex",
//                             gap: 1,
//                             mb: 2,
//                             flexDirection: { xs: "column", sm: "row" },
//                             alignItems: { xs: "stretch", sm: "flex-start" },
//                           }}
//                         >
//                           <TextField
//                             fullWidth
//                             multiline
//                             rows={3}
//                             placeholder={section.placeholder}
//                             value={item}
//                             onChange={(e) => handleArrayFieldChange(section.field, index, e.target.value)}
//                             variant="outlined"
//                             sx={{ borderRadius: 2 }}
//                           />
//                           {formData[section.field].length > 1 && (
//                             <IconButton
//                               onClick={() => removeArrayField(section.field, index)}
//                               color="error"
//                               sx={{ alignSelf: { xs: "flex-end", sm: "flex-start" }, mt: { xs: 0, sm: 1 } }}
//                             >
//                               <Remove />
//                             </IconButton>
//                           )}
//                         </Box>
//                       ))}
//                     </Card>
//                   </Grid>
//                 ))}
//                 {submitError && (
//                   <Grid item xs={12}>
//                     <Alert severity="error" sx={{ borderRadius: 2 }}>
//                       {submitError}
//                     </Alert>
//                   </Grid>
//                 )}
//               </Grid>
//               <Box
//                 sx={{
//                   display: "flex",
//                   flexDirection: { xs: "column-reverse", sm: "row" },
//                   justifyContent: "flex-end",
//                   gap: 2,
//                   mt: 4,
//                   pt: 3,
//                   borderTop: "1px solid",
//                   borderColor: "divider",
//                 }}
//               >
//                 <Button onClick={onClose} variant="outlined" size="large" fullWidth={isXs} sx={{ borderRadius: 2, minWidth: 120 }}>
//                   Cancel
//                 </Button>
//                 <Button type="submit" variant="contained" size="large" disabled={isSubmitting} fullWidth={isXs} sx={{ borderRadius: 2, minWidth: 120 }}>
//                   {isSubmitting ? <CircularProgress size={24} /> : "Submit Evaluation"}
//                 </Button>
//               </Box>
//             </form>
//           </Container>
//         </Box>
//       </Fade>
//     </Modal>
//   )
// }

// function JobDetailsView({ candidate, onBack, onEvaluate }) {
//   const theme = useTheme()
//   const isXs = useMediaQuery(theme.breakpoints.down("sm"))

//   return (
//     <Box>
//       <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
//         <Button variant="outlined" startIcon={<ArrowBack />} onClick={onBack} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
//           Back to Candidates
//         </Button>
//       </Box>
//       <Card elevation={1} sx={{ mb: 4, borderRadius: 2, bgcolor: "#F1FFFA" }}>
//         <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
//           <Grid container spacing={3} alignItems="center">
//             <Grid item xs="auto">
//               <Avatar
//                 src={candidate.avatar}
//                 sx={{
//                   width: { xs: 64, sm: 72, md: 80 },
//                   height: { xs: 64, sm: 72, md: 80 },
//                   border: "3px solid",
//                   borderColor: "primary.light",
//                 }}
//               />
//             </Grid>
//             <Grid item xs>
//               <Typography variant="h5" fontWeight="bold" gutterBottom>
//                 {candidate.name}
//               </Typography>
//               <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
//                 <Email fontSize="small" color="action" />
//                 <Typography variant="body2" color="text.secondary">
//                   {candidate.email}
//                 </Typography>
//               </Box>
//               <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
//                 <LocationOn fontSize="small" color="action" />
//                 <Typography variant="body2" color="text.secondary">
//                   {candidate.location}
//                 </Typography>
//               </Box>
//               <Chip
//                 label={`${candidate.interviews.length} Scheduled Interview${candidate.interviews.length !== 1 ? "s" : ""}`}
//                 color="primary"
//                 variant="outlined"
//                 sx={{ fontWeight: 600 }}
//               />
//             </Grid>
//           </Grid>
//         </CardContent>
//       </Card>
//       <Grid container spacing={3}>
//         {candidate.interviews.map((interview) => (
//           <Grid item xs={12} key={interview.id}>
//             <Card elevation={1} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
//               <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
//                 <Grid container spacing={3} alignItems="center">
//                   <Grid item xs={12} md={8}>
//                     <Typography variant="h6" fontWeight="600" gutterBottom sx={{ color: "text.primary" }}>
//                       {interview.job_title}
//                     </Typography>
//                     <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 2 }}>
//                       <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                         <Business fontSize="small" color="action" />
//                         <Typography variant="body2" color="text.secondary" fontWeight="500">
//                           {interview.interview_mode}
//                         </Typography>
//                       </Box>
//                       <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                         <Event fontSize="small" color="action" />
//                         <Typography variant="body2" color="text.secondary" fontWeight="500">
//                           {new Date(`${interview.interview_date}T${interview.interview_time}`).toLocaleString()}
//                         </Typography>
//                       </Box>
//                     </Box>
//                     <Chip
//                       label={interview.status}
//                       color={interview.status.toLowerCase() === "scheduled" ? "success" : "warning"}
//                       size="small"
//                       sx={{ fontWeight: 600 }}
//                     />
//                   </Grid>
//                   <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
//                     <Button
//                       variant="contained"
//                       startIcon={<RateReview />}
//                       onClick={() => onEvaluate(candidate, interview)}
//                       fullWidth={isXs}
//                       sx={{
//                         minWidth: { xs: "100%", sm: 140 },
//                         borderRadius: 2,
//                         fontWeight: 600,
//                         boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
//                         "&:hover": {
//                           transform: "translateY(-1px)",
//                           boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
//                         },
//                       }}
//                     >
//                       Evaluate
//                     </Button>
//                   </Grid>
//                 </Grid>
//               </CardContent>
//             </Card>
//           </Grid>
//         ))}
//       </Grid>
//     </Box>
//   )
// }

// function ScheduledInterviewsTab({ onEvaluateClick }) {
//   const [candidates, setCandidates] = useState([])
//   const [loading, setLoading] = useState(true)
//   const [error, setError] = useState(null)
//   const [searchTerm, setSearchTerm] = useState("")
//   const [selectedCandidateDetails, setSelectedCandidateDetails] = useState(null)
//   const [showJobDetails, setShowJobDetails] = useState(false)

//   const theme = useTheme()
//   const isXs = useMediaQuery(theme.breakpoints.down("sm"))

//   useEffect(() => {
//     const fetchAndProcessInterviews = async () => {
//       const token = localStorage.getItem("access_token")
//       if (!token) {
//         setError("Authentication token not found. Please log in.")
//         setLoading(false)
//         return
//       }
//       try {
//         const response = await fetch("http://127.0.0.1:8000/api/interviews/", {
//           headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
//         })
//         if (!response.ok) {
//           if (response.status === 401) throw new Error("Unauthorized: Please log in again.")
//           throw new Error(`HTTP error! status: ${response.status}`)
//         }
//         const data = await response.json()
//         const filtered = data.filter(
//           (interview) =>
//             interview.status.toLowerCase() === "scheduled" || interview.status.toLowerCase() === "rescheduled",
//         )
//         const groupedByCandidate = filtered.reduce((acc, interview) => {
//           const { candidate_name, profile_photo, talent_current_location } = interview
//           if (!acc[candidate_name]) {
//             acc[candidate_name] = {
//               id: candidate_name.replace(/\s+/g, "-").toLowerCase(),
//               name: candidate_name,
//               email: `${candidate_name.replace(/\s+/g, ".").toLowerCase()}@example.com`,
//               avatar: profile_photo || `https://i.pravatar.cc/150?u=${encodeURIComponent(candidate_name)}`,
//               location: talent_current_location || "Location not specified",
//               interviews: [],
//             }
//           }
//           acc[candidate_name].interviews.push(interview)
//           return acc
//         }, {})
//         setCandidates(Object.values(groupedByCandidate))
//       } catch (e) {
//         setError(e.message)
//       } finally {
//         setLoading(false)
//       }
//     }
//     fetchAndProcessInterviews()
//   }, [])

//   const handleViewDetails = (candidate) => {
//     setSelectedCandidateDetails(candidate)
//     setShowJobDetails(true)
//   }

//   const handleBackToList = () => {
//     setShowJobDetails(false)
//     setSelectedCandidateDetails(null)
//   }

//   const filteredCandidates = candidates.filter(
//     (c) =>
//       c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       c.email.toLowerCase().includes(searchTerm.toLowerCase()),
//   )

//   if (loading) {
//     return (
//       <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
//         <CircularProgress />
//       </Box>
//     )
//   }

//   if (error) {
//     return (
//       <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//         <Typography variant="h6" color="error" gutterBottom>
//           Error Fetching Interviews
//         </Typography>
//         <Typography>{error}</Typography>
//       </Paper>
//     )
//   }

//   if (showJobDetails && selectedCandidateDetails) {
//     return (
//       <JobDetailsView candidate={selectedCandidateDetails} onBack={handleBackToList} onEvaluate={onEvaluateClick} />
//     )
//   }

//   return (
//     <Box>
//       {filteredCandidates.length === 0 ? (
//         <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//           <Typography variant="h6" color="text.secondary">
//             No Scheduled Interviews Found
//           </Typography>
//         </Paper>
//       ) : (
//         <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
//           <Table size={isXs ? "small" : "medium"}>
//             <TableHead>
//               <TableRow sx={{ backgroundColor: "#F1FFFA" }}>
//                 <TableCell sx={{ fontWeight: "bold" }}>Candidate</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Interviews</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {filteredCandidates.map((candidate) => (
//                 <TableRow key={candidate.id} sx={{ "&:hover": { bgcolor: "grey.50" } }}>
//                   <TableCell>
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                       <Avatar src={candidate.avatar} sx={{ width: 48, height: 48 }} />
//                       <Typography variant="body1" fontWeight="600">
//                         {candidate.name}
//                       </Typography>
//                     </Box>
//                   </TableCell>
//                   <TableCell>
//                     <Chip
//                       label={`${candidate.interviews.length} interview${candidate.interviews.length !== 1 ? "s" : ""}`}
//                       color="primary"
//                       variant="outlined"
//                       size="small"
//                       sx={{ fontWeight: 600 }}
//                     />
//                   </TableCell>
//                   <TableCell>
//                     <Button
//                       variant="contained"
//                       onClick={() => handleViewDetails(candidate)}
//                       sx={{
//                         borderRadius: 2,
//                         fontWeight: 600,
//                         boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
//                         "&:hover": {
//                           transform: "translateY(-1px)",
//                           boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
//                         },
//                       }}
//                     >
//                       View Details
//                     </Button>
//                   </TableCell>
//                 </TableRow>
//               ))}
//             </TableBody>
//           </Table>
//         </TableContainer>
//       )}
//     </Box>
//   )
// }

// function ExtendOfferTab({ onAlert }) {
//   const [completedCandidates, setCompletedCandidates] = useState([])
//   const [loading, setLoading] = useState(true)
//   const [error, setError] = useState(null)
//   const [extendingId, setExtendingId] = useState(null)

//   useEffect(() => {
//     const fetchCompletedCandidates = async () => {
//       const token = localStorage.getItem("access_token")
//       if (!token) {
//         setError("Authentication token not found. Please log in.")
//         setLoading(false)
//         return
//       }
//       try {
//         const response = await fetch("http://localhost:8000/api/interviews/completed-candidates/", {
//           headers: { Authorization: `Bearer ${token}` },
//         })
//         if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
//         const data = await response.json()
//         setCompletedCandidates(data)
//       } catch (e) {
//         setError(e.message)
//       } finally {
//         setLoading(false)
//       }
//     }
//     fetchCompletedCandidates()
//   }, [])

//   const handleExtendOffer = async (applicationId) => {
//     setExtendingId(applicationId)
//     const token = localStorage.getItem("access_token")
//     try {
//       const response = await fetch(`http://localhost:8000/api/applications/${applicationId}/offer-extend/`, {
//         method: "POST",
//         headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
//       })

//       if (!response.ok) {
//         const errorData = await response.json()
//         throw new Error(errorData.detail || "Failed to extend offer.")
//       }

//       await response.json()
//       onAlert("Offer extended successfully!", "success")
//       setCompletedCandidates((prev) =>
//         prev.map((candidate) =>
//           candidate.application_id === applicationId ? { ...candidate, application_status: "OFFER_EXTENDED" } : candidate,
//         ),
//       )
//     } catch (err) {
//       onAlert(err.message, "error")
//     } finally {
//       setExtendingId(null)
//     }
//   }

//   if (loading) {
//     return (
//       <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
//         <CircularProgress />
//       </Box>
//     )
//   }

//   if (error) {
//     return (
//       <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//         <Typography variant="h6" color="error" gutterBottom>
//           Error Fetching Data
//         </Typography>
//         <Typography>{error}</Typography>
//       </Paper>
//     )
//   }

//   return (
//     <Box>
//       {completedCandidates.length === 0 ? (
//         <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
//           <Typography variant="h6" color="text.secondary">
//             No evaluated candidates available to extend an offer.
//           </Typography>
//         </Paper>
//       ) : (
//         <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
//           <Table>
//             <TableHead>
//               <TableRow sx={{ backgroundColor: "#F1FFFA" }}>
//                 <TableCell sx={{ fontWeight: "bold" }}>Candidate</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Job Title</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Tech Rating</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
//                 <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Actions</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {completedCandidates.map((candidate) => (
//                 <TableRow key={candidate.application_id} sx={{ "&:hover": { bgcolor: "grey.50" } }}>
//                   <TableCell>{candidate.talent_name}</TableCell>
//                   <TableCell>{candidate.job_posting_title}</TableCell>
//                   <TableCell>{candidate.technical_skills_rating || "N/A"}</TableCell>
//                   <TableCell>
//                     <Chip
//                       label={candidate.application_status.replace("_", " ")}
//                       color={
//                         candidate.application_status === "OFFER_EXTENDED"
//                           ? "success"
//                           : candidate.application_status === "HIRED"
//                           ? "primary"
//                           : "default"
//                       }
//                       size="small"
//                       sx={{ textTransform: "capitalize" }}
//                     />
//                   </TableCell>
//                   <TableCell sx={{ textAlign: "center" }}>
//                     <Button
//                       variant="contained"
//                       color="primary"
//                       startIcon={extendingId === candidate.application_id ? null : <Send />}
//                       disabled={extendingId === candidate.application_id || candidate.application_status === "OFFER_EXTENDED"}
//                       onClick={() => handleExtendOffer(candidate.application_id)}
//                       sx={{
//                         minWidth: 140,
//                         borderRadius: 2,
//                       }}
//                     >
//                       {extendingId === candidate.application_id ? (
//                         <CircularProgress size={24} color="inherit" />
//                       ) : candidate.application_status === "OFFER_EXTENDED" ? (
//                         "Offer Extended"
//                       ) : (
//                         "Extend Offer"
//                       )}
//                     </Button>
//                   </TableCell>
//                 </TableRow>
//               ))}
//             </TableBody>
//           </Table>
//         </TableContainer>
//       )}
//     </Box>
//   )
// }

// export default function EvaluationsPage() {
//   const [modalOpen, setModalOpen] = useState(false)
//   const [selectedInterview, setSelectedInterview] = useState(null)
//   const [tabValue, setTabValue] = useState(0)
//   const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" })

//   const handleTabChange = (event, newValue) => {
//     setTabValue(newValue)
//   }

//   const handleEvaluateClick = (candidate, interview) => {
//     setSelectedInterview({
//       name: candidate.name,
//       email: candidate.email,
//       avatar: candidate.avatar,
//       interviewId: interview.id,
//       jobTitle: interview.job_title,
//     })
//     setModalOpen(true)
//   }

//   const handleCloseModal = () => {
//     setModalOpen(false)
//     setSelectedInterview(null)
//   }

//   const handleAlert = (message, severity) => {
//     setSnackbar({ open: true, message, severity })
//   }

//   const handleSnackbarClose = (event, reason) => {
//     if (reason === "clickaway") return
//     setSnackbar({ ...snackbar, open: false })
//   }

//   return (
//     <Layout title="Evaluations">
//       <ThemeProvider theme={theme}>
//         <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
//           <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
//             <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
//               <Typography variant="h4" gutterBottom>
//                 Candidate Evaluations
//               </Typography>
//               <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
//                 <Tabs value={tabValue} onChange={handleTabChange} aria-label="evaluation tabs">
//                   <Tab label="Scheduled Interviews" id="tab-0" />
//                   <Tab label="Extend Offer" id="tab-1" />
//                 </Tabs>
//               </Box>
//               <TabPanel value={tabValue} index={0}>
//                 <ScheduledInterviewsTab onEvaluateClick={handleEvaluateClick} />
//               </TabPanel>
//               <TabPanel value={tabValue} index={1}>
//                 <ExtendOfferTab onAlert={handleAlert} />
//               </TabPanel>
//             </Paper>
//           </Container>
//         </Box>

//         {selectedInterview && (
//           <EvaluationForm open={modalOpen} onClose={handleCloseModal} interviewDetails={selectedInterview} />
//         )}

//         <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose}>
//           <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
//             {snackbar.message}
//           </Alert>
//         </Snackbar>
//       </ThemeProvider>
//     </Layout>
//   )
// }
// import React, { useState, useEffect } from "react"
// import {
//   Container,
//   Paper,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   Rating,
//   TextField,
//   Button,
//   Box,
//   Avatar,
//   Divider,
//   FormControl,
//   Select,
//   MenuItem,
//   List,
//   Accordion,
//   Chip,
//   Modal,
//   CircularProgress,
//   Alert,
//   IconButton,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Backdrop,
//   useMediaQuery,
//   Fade, // restore Fade transition import for modals
// } from "@mui/material"
// import {
//   Person,
//   Email,
//   Event,
//   Business,
//   RateReview,
//   ArrowBack,
//   Close,
//   Add,
//   Remove,
//   LocationOn,
//   ThumbUp,
// } from "@mui/icons-material"
// import { createTheme, ThemeProvider, styled, responsiveFontSizes, useTheme } from "@mui/material/styles"
// import Layout from "../common/Layout"

// // Make typography responsive and keep palette/overrides as-is
// const baseTheme = createTheme({
//   palette: {
//     primary: {
//       main: "#1976d2",
//       light: "#42a5f5",
//       dark: "#1565c0",
//     },
//     background: {
//       default: "#f8fafc",
//       paper: "#ffffff",
//     },
//     text: {
//       primary: "#1e293b",
//       secondary: "#64748b",
//     },
//   },
//   typography: {
//     fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
//     h4: {
//       fontWeight: 700,
//       letterSpacing: "-0.025em",
//     },
//     h6: {
//       fontWeight: 600,
//     },
//     body1: {
//       lineHeight: 1.6,
//     },
//   },
//   components: {
//     MuiCard: {
//       styleOverrides: {
//         root: {
//           borderRadius: 16,
//           boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
//           transition: "all 0.2s ease-in-out",
//           "&:hover": {
//             boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//             transform: "translateY(-2px)",
//           },
//         },
//       },
//     },
//     MuiButton: {
//       styleOverrides: {
//         root: {
//           borderRadius: 12,
//           textTransform: "none",
//           fontWeight: 600,
//           transition: "all 0.2s ease-in-out",
//         },
//         contained: {
//           boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
//           "&:hover": {
//             boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
//             transform: "translateY(-1px)",
//           },
//         },
//       },
//     },
//     MuiAccordion: {
//       styleOverrides: {
//         root: {
//           borderRadius: "16px !important",
//           boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
//           "&:before": {
//             display: "none",
//           },
//           "&$expanded": {
//             margin: "auto",
//           },
//         },
//       },
//     },
//   },
// })
// const theme = responsiveFontSizes(baseTheme)

// const StyledAccordion = styled(Accordion)(({ theme }) => ({
//   transition: theme.transitions.create(["box-shadow", "margin", "transform"], {
//     duration: theme.transitions.duration.short,
//   }),
//   "&:hover": {
//     transform: "translateY(-2px)",
//     boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//   },
//   "&.Mui-expanded": {
//     boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//     margin: `${theme.spacing(1, 0)} !important`,
//     transform: "translateY(-2px)",
//   },
// }))

// function Navigation({ mobileOpen, handleDrawerToggle }) {
//   const [anchorEl, setAnchorEl] = useState(null)

//   const handleProfileMenuOpen = (event) => {
//     setAnchorEl(event.currentTarget)
//   }

//   const handleProfileMenuClose = () => {
//     setAnchorEl(null)
//   }

//   // Intentionally left as in source; not used by this page and no functional changes.
// }

// function JobListDialog({ candidate, open, onClose, onEvaluate }) {
//   const theme = useTheme()
//   const isXs = useMediaQuery(theme.breakpoints.down("sm"))

//   if (!candidate) return null

//   return (
//     <Modal open={open} onClose={onClose} closeAfterTransition>
//       <Fade in={open}>
//         <Box
//           component={Paper}
//           elevation={0}
//           sx={{
//             position: "absolute",
//             top: "50%",
//             left: "50%",
//             transform: "translate(-50%, -50%)",
//             width: { xs: "95%", sm: "90%", md: "80%" },
//             maxWidth: "900px",
//             bgcolor: "#F1FFFA",
//             borderRadius: 3,
//             maxHeight: "90vh",
//             overflowY: "auto",
//             outline: "none",
//           }}
//         >
//           <Box
//             sx={{
//               background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
//               color: "white",
//               p: { xs: 2, sm: 3, md: 4 },
//             }}
//           >
//             <Grid container spacing={3} alignItems="center">
//               <Grid item xs={12} sm="auto">
//                 <Avatar
//                   src={candidate.avatar}
//                   sx={{
//                     width: { xs: 64, sm: 80 },
//                     height: { xs: 64, sm: 80 },
//                     border: "3px solid rgba(255,255,255,0.3)",
//                   }}
//                 />
//               </Grid>
//               <Grid item xs={12} sm>
//                 <Typography variant="h4" fontWeight="bold" gutterBottom>
//                   {candidate.name}
//                 </Typography>
//                 <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
//                   {candidate.email}
//                 </Typography>
//                 <Typography variant="body2" sx={{ opacity: 0.8 }}>
//                   {candidate.interviews.length} Scheduled Interview{candidate.interviews.length !== 1 ? "s" : ""}
//                 </Typography>
//                 <Typography variant="body2" sx={{ opacity: 0.8 }}>
//                   Location: {candidate.location}
//                 </Typography>
//               </Grid>
//             </Grid>
//           </Box>

//           <Container maxWidth="lg" sx={{ p: { xs: 2, md: 4 }, bgcolor: "#F1FFFA" }}>
//             <Typography variant="h6" gutterBottom color="primary" fontWeight="bold" sx={{ mb: 3 }}>
//               Interview Schedule
//             </Typography>

//             <List disablePadding sx={{ bgcolor: "#F1FFFA" }}>
//               {candidate.interviews.map((interview, index) => (
//                 <React.Fragment key={interview.id}>
//                   {index > 0 && <Divider sx={{ my: 2 }} />}
//                   <Card elevation={1} sx={{ mb: 2, borderRadius: 2, bgcolor: "#F1FFFA" }}>
//                     <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
//                       <Grid container spacing={3} alignItems="center">
//                         <Grid item xs={12} md={8}>
//                           <Typography variant="h6" fontWeight="600" gutterBottom sx={{ color: "text.primary" }}>
//                             {interview.job_title}
//                           </Typography>
//                           <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 2 }}>
//                             <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                               <Business fontSize="small" color="action" />
//                               <Typography variant="body2" color="text.secondary" fontWeight="500">
//                                 {interview.interview_mode}
//                               </Typography>
//                             </Box>
//                             <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                               <Event fontSize="small" color="action" />
//                               <Typography variant="body2" color="text.secondary" fontWeight="500">
//                                 {new Date(`${interview.interview_date}T${interview.interview_time}`).toLocaleString()}
//                               </Typography>
//                             </Box>
//                           </Box>
//                           <Chip
//                             label={interview.status}
//                             color={interview.status.toLowerCase() === "scheduled" ? "success" : "warning"}
//                             size="small"
//                             sx={{ fontWeight: 600 }}
//                           />
//                         </Grid>
//                         <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
//                           <Button
//                             variant="contained"
//                             startIcon={<RateReview />}
//                             onClick={() => onEvaluate(candidate, interview)}
//                             fullWidth={isXs}
//                             sx={{
//                               minWidth: { xs: "100%", sm: 140 },
//                               borderRadius: 2,
//                               fontWeight: 600,
//                               boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
//                               "&:hover": {
//                                 transform: "translateY(-1px)",
//                                 boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
//                               },
//                             }}
//                           >
//                             Evaluate
//                           </Button>
//                         </Grid>
//                       </Grid>
//                     </CardContent>
//                   </Card>
//                 </React.Fragment>
//               ))}
//             </List>

//             <Box
//               sx={{
//                 display: "flex",
//                 flexDirection: { xs: "column-reverse", sm: "row" },
//                 gap: 2,
//                 justifyContent: "flex-end",
//                 mt: 4,
//                 pt: 3,
//                 borderTop: 1,
//                 borderColor: "divider",
//               }}
//             >
//               <Button variant="outlined" size="large" onClick={onClose} fullWidth={isXs} sx={{ minWidth: 120 }}>
//                 Close
//               </Button>
//             </Box>
//           </Container>
//         </Box>
//       </Fade>
//     </Modal>
//   )
// }

// function EvaluationForm({ interviewDetails, open, onClose }) {
//   const theme = useTheme()
//   const isXs = useMediaQuery(theme.breakpoints.down("sm"))

//   const [formData, setFormData] = useState({
//     recommendation: "",
//     technical_skills_rating: 0,
//     communication_skills_rating: 0,
//     cultural_fit_rating: 0,
//     strengths: [""],
//     weaknesses: [""],
//     overall_comments: [""],
//   })
//   const [isSubmitting, setIsSubmitting] = useState(false)
//   const [submitError, setSubmitError] = useState(null)

//   useEffect(() => {
//     if (open) {
//       setFormData({
//         recommendation: "",
//         technical_skills_rating: 0,
//         communication_skills_rating: 0,
//         cultural_fit_rating: 0,
//         strengths: [""],
//         weaknesses: [""],
//         overall_comments: [""],
//       })
//       setIsSubmitting(false)
//       setSubmitError(null)
//     }
//   }, [open])

//   const handleChange = (e) => {
//     const { name, value } = e.target
//     setFormData((prev) => ({ ...prev, [name]: value }))
//   }

//   const handleRatingChange = (field, value) => {
//     setFormData((prev) => ({ ...prev, [field]: value !== null ? Math.round(value) : 0 }))
//   }

//   const handleArrayFieldChange = (field, index, value) => {
//     setFormData((prev) => ({
//       ...prev,
//       [field]: prev[field].map((item, i) => (i === index ? value : item)),
//     }))
//   }

//   const addArrayField = (field) => {
//     setFormData((prev) => ({
//       ...prev,
//       [field]: [...prev[field], ""],
//     }))
//   }

//   const removeArrayField = (field, index) => {
//     if (formData[field].length > 1) {
//       setFormData((prev) => ({
//         ...prev,
//         [field]: prev[field].filter((_, i) => i !== index),
//       }))
//     }
//   }

//   const handleSubmit = async (e) => {
//     e.preventDefault()
//     setIsSubmitting(true)
//     setSubmitError(null)

//     try {
//       const submissionData = {
//         ...formData,
//         strengths: formData.strengths.filter((s) => s.trim()).join("; "),
//         weaknesses: formData.weaknesses.filter((w) => w.trim()).join("; "),
//         overall_comments: formData.overall_comments.filter((c) => c.trim()).join("; "),
//         interview_id: interviewDetails.interviewId,
//       }

//       console.log("[v0] Submitting evaluation data:", submissionData)

//       const accessToken = localStorage.getItem("access_token")
//       const headers = {
//         "Content-Type": "application/json",
//       }

//       if (accessToken) {
//         headers.Authorization = `Bearer ${accessToken}`
//       }

//       const response = await fetch(
//         `http://localhost:8000/api/interviews/${interviewDetails.interviewId}/submit-feedback/`,
//         {
//           method: "POST",
//           headers,
//           body: JSON.stringify(submissionData),
//         },
//       )

//       console.log("[v0] Response status:", response.status)
//       console.log("[v0] Response headers:", response.headers.get("content-type"))

//       if (!response.ok) {
//         const contentType = response.headers.get("content-type")
//         let errorMessage = `HTTP ${response.status}: ${response.statusText}`

//         if (contentType && contentType.includes("application/json")) {
//           try {
//             const errorData = await response.json()
//             errorMessage = errorData.message || errorMessage
//           } catch (jsonError) {
//             console.error("[v0] Failed to parse error JSON:", jsonError)
//           }
//         } else {
//           const textResponse = await response.text()
//           console.log("[v0] Non-JSON response:", textResponse.substring(0, 200))
//           errorMessage = `Server returned ${response.status}. Check if the API endpoint exists and the server is running.`
//         }

//         throw new Error(errorMessage)
//       }

//       const contentType = response.headers.get("content-type")
//       if (contentType && contentType.includes("application/json")) {
//         const result = await response.json()
//         console.log("[v0] Success response:", result)
//       }

//       onClose()
//     } catch (error) {
//       console.error("[v0] Error submitting evaluation:", error)
//       setSubmitError(error.message)
//     } finally {
//       setIsSubmitting(false)
//     }
//   }

//   return (
//     <Modal
//       open={open}
//       onClose={onClose}
//       closeAfterTransition
//       BackdropComponent={Backdrop}
//       BackdropProps={{
//         timeout: 500,
//       }}
//     >
//       <Fade in={open}>
//         <Box
//           component={Paper}
//           elevation={0}
//           sx={{
//             position: "absolute",
//             top: "50%",
//             left: "50%",
//             transform: "translate(-50%, -50%)",
//             width: { xs: "95%", sm: "90%", md: "80%", lg: "70%" },
//             maxWidth: "900px",
//             maxHeight: "90vh",
//             bgcolor: "background.paper",
//             borderRadius: 3,
//             boxShadow: 24,
//             overflow: "hidden",
//             display: "flex",
//             flexDirection: "column",
//           }}
//         >
//           {/* Header */}
//           <Box
//             sx={{
//               background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
//               color: "white",
//               p: { xs: 2, md: 3 },
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "space-between",
//               flexShrink: 0,
//             }}
//           >
//             <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
//               <Avatar
//                 src={interviewDetails?.avatar}
//                 sx={{
//                   width: { xs: 48, sm: 56 },
//                   height: { xs: 48, sm: 56 },
//                   border: "3px solid rgba(255,255,255,0.3)",
//                   flexShrink: 0,
//                 }}
//               >
//                 {interviewDetails?.name?.charAt(0)}
//               </Avatar>
//               <Box sx={{ minWidth: 0 }}>
//                 <Typography
//                   variant="h5"
//                   fontWeight="bold"
//                   sx={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}
//                 >
//                   Evaluate Candidate
//                 </Typography>
//                 <Typography
//                   variant="body1"
//                   sx={{ opacity: 0.9, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}
//                 >
//                   {interviewDetails?.name} • {interviewDetails?.jobTitle}
//                 </Typography>
//               </Box>
//             </Box>
//             <IconButton onClick={onClose} sx={{ color: "white" }}>
//               <Close />
//             </IconButton>
//           </Box>

//           {/* Content */}
//           <Container
//             maxWidth="lg"
//             sx={{
//               flex: 1,
//               overflow: "auto",
//               py: { xs: 2, md: 3 },
//               minHeight: 0,
//               maxHeight: "calc(90vh - 140px)",
//               "&::-webkit-scrollbar": {
//                 width: "8px",
//               },
//               "&::-webkit-scrollbar-track": {
//                 background: "#f1f1f1",
//                 borderRadius: "4px",
//               },
//               "&::-webkit-scrollbar-thumb": {
//                 background: "#c1c1c1",
//                 borderRadius: "4px",
//                 "&:hover": {
//                   background: "#a8a8a8",
//                 },
//               },
//               scrollbarWidth: "thin",
//               scrollbarColor: "#c1c1c1 #f1f1f1",
//             }}
//           >
//             <form onSubmit={handleSubmit}>
//               <Grid container spacing={3}>
//                 {/* Recommendation */}
//                 <Grid item xs={12}>
//                   <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, bgcolor: "#F1FFFA" }}>
//                     <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                       <ThumbUp color="primary" />
//                       Recommendation
//                     </Typography>
//                     <FormControl fullWidth>
//                       <Select
//                         name="recommendation"
//                         value={formData.recommendation}
//                         onChange={handleChange}
//                         required
//                         sx={{ borderRadius: 2 }}
//                       >
//                         <MenuItem value="">
//                           <em>Select recommendation</em>
//                         </MenuItem>
//                         <MenuItem value="HIRE">
//                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <Person color="success" />
//                             Hire
//                           </Box>
//                         </MenuItem>
//                         <MenuItem value="CONSIDER">
//                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <Person color="primary" />
//                             consider
//                           </Box>
//                         </MenuItem>
//                         <MenuItem value="NO_HIRE">
//                           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                             <Person color="error" />
//                             No Hire
//                           </Box>
//                         </MenuItem>
//                       </Select>
//                     </FormControl>
//                   </Card>
//                 </Grid>

//                 {/* Ratings */}
//                 <Grid item xs={12}>
//                   <Card
//                     variant="outlined"
//                     sx={{ p: { xs: 2, sm: 3 }, gap: 3, borderRadius: 2, bgcolor: "#F1FFFA", width: "100%" }}
//                   >
//                     <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
//                       Skills Assessment
//                     </Typography>
//                     <Grid container spacing={3}>
//                       {[
//                         { field: "technical_skills_rating", label: "Technical Skills", icon: "💻", color: "#2196f3" },
//                         { field: "communication_skills_rating", label: "Communication", icon: "💬", color: "#4caf50" },
//                         { field: "cultural_fit_rating", label: "Cultural Fit", icon: "🤝", color: "#ff9800" },
//                       ].map((rating) => (
//                         <Grid item xs={12} sm={4} key={rating.field}>
//                           <Box
//                             sx={{
//                               textAlign: "center",
//                               p: 2,
//                               borderRadius: 2,
//                               border: "1px solid",
//                               borderColor: "divider",
//                               bgcolor: "grey.50",
//                             }}
//                           >
//                             <Typography variant="h4" sx={{ mb: 1 }}>
//                               {rating.icon}
//                             </Typography>
//                             <Typography variant="body1" fontWeight="medium" gutterBottom>
//                               {rating.label}
//                             </Typography>
//                             <Rating
//                               name={rating.field}
//                               value={formData[rating.field]}
//                               onChange={(e, val) => handleRatingChange(rating.field, val)}
//                               size={isXs ? "medium" : "large"}
//                               sx={{ mb: 1 }}
//                               max={5}
//                             />
//                             <Typography variant="body2" color="text.secondary">
//                               {formData[rating.field]}/5
//                             </Typography>
//                           </Box>
//                         </Grid>
//                       ))}
//                     </Grid>
//                   </Card>
//                 </Grid>

//                 {/* Dynamic Fields */}
//                 {[
//                   {
//                     field: "strengths",
//                     label: "Key Strengths",
//                     placeholder: "What impressed you most about this candidate?",
//                     icon: "⭐",
//                   },
//                   {
//                     field: "weaknesses",
//                     label: "Areas for Improvement",
//                     placeholder: "What areas could the candidate develop?",
//                     icon: "📈",
//                   },
//                   {
//                     field: "overall_comments",
//                     label: "Overall Assessment",
//                     placeholder: "Provide your comprehensive evaluation...",
//                     icon: "📝",
//                   },
//                 ].map((section) => (
//                   <Grid item xs={12} key={section.field}>
//                     <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, bgcolor: "#F1FFFA" }}>
//                       <Box
//                         sx={{
//                           display: "flex",
//                           alignItems: { xs: "stretch", sm: "center" },
//                           justifyContent: "space-between",
//                           gap: 2,
//                           mb: 2,
//                           flexDirection: { xs: "column", sm: "row" },
//                         }}
//                       >
//                         <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                           {section.icon} {section.label}
//                         </Typography>
//                         <Button
//                           variant="outlined"
//                           size="medium"
//                           startIcon={<Add />}
//                           onClick={() => addArrayField(section.field)}
//                           sx={{ borderRadius: 2, alignSelf: { xs: "stretch", sm: "auto" } }}
//                           fullWidth={isXs}
//                         >
//                           Add
//                         </Button>
//                       </Box>
//                       {formData[section.field].map((item, index) => (
//                         <Box
//                           key={index}
//                           sx={{
//                             display: "flex",
//                             gap: 1,
//                             mb: 2,
//                             flexDirection: { xs: "column", sm: "row" },
//                             alignItems: { xs: "stretch", sm: "flex-start" },
//                           }}
//                         >
//                           <TextField
//                             fullWidth
//                             multiline
//                             rows={3}
//                             placeholder={section.placeholder}
//                             value={item}
//                             onChange={(e) => handleArrayFieldChange(section.field, index, e.target.value)}
//                             variant="outlined"
//                             sx={{ borderRadius: 2 }}
//                           />
//                           {formData[section.field].length > 1 && (
//                             <IconButton
//                               onClick={() => removeArrayField(section.field, index)}
//                               color="error"
//                               sx={{ alignSelf: { xs: "flex-end", sm: "flex-start" }, mt: { xs: 0, sm: 1 } }}
//                             >
//                               <Remove />
//                             </IconButton>
//                           )}
//                         </Box>
//                       ))}
//                     </Card>
//                   </Grid>
//                 ))}

//                 {/* Error Display */}
//                 {submitError && (
//                   <Grid item xs={12}>
//                     <Alert severity="error" sx={{ borderRadius: 2 }}>
//                       {submitError}
//                     </Alert>
//                   </Grid>
//                 )}
//               </Grid>

//               {/* Footer */}
//               <Box
//                 sx={{
//                   display: "flex",
//                   flexDirection: { xs: "column-reverse", sm: "row" },
//                   justifyContent: "flex-end",
//                   gap: 2,
//                   mt: 4,
//                   pt: 3,
//                   borderTop: "1px solid",
//                   borderColor: "divider",
//                 }}
//               >
//                 <Button
//                   onClick={onClose}
//                   variant="outlined"
//                   size="large"
//                   fullWidth={isXs}
//                   sx={{ borderRadius: 2, minWidth: 120 }}
//                 >
//                   Cancel
//                 </Button>
//                 <Button
//                   type="submit"
//                   variant="contained"
//                   size="large"
//                   disabled={isSubmitting}
//                   fullWidth={isXs}
//                   sx={{ borderRadius: 2, minWidth: 120 }}
//                 >
//                   {isSubmitting ? <CircularProgress size={24} /> : "Submit Evaluation"}
//                 </Button>
//               </Box>
//             </form>
//           </Container>
//         </Box>
//       </Fade>
//     </Modal>
//   )
// }

// function JobDetailsView({ candidate, onBack, onEvaluate }) {
//   const theme = useTheme()
//   const isXs = useMediaQuery(theme.breakpoints.down("sm"))

//   return (
//     <Box>
//       <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
//         <Button
//           variant="outlined"
//           startIcon={<ArrowBack />}
//           onClick={onBack}
//           sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}
//         >
//           Back to Candidates
//         </Button>
//       </Box>

//       <Card elevation={1} sx={{ mb: 4, borderRadius: 2, bgcolor: "#F1FFFA" }}>
//         <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
//           <Grid container spacing={3} alignItems="center">
//             <Grid item xs="auto">
//               <Avatar
//                 src={candidate.avatar}
//                 sx={{
//                   width: { xs: 64, sm: 72, md: 80 },
//                   height: { xs: 64, sm: 72, md: 80 },
//                   border: "3px solid",
//                   borderColor: "primary.light",
//                 }}
//               />
//             </Grid>
//             <Grid item xs>
//               <Typography variant="h5" fontWeight="bold" gutterBottom>
//                 {candidate.name}
//               </Typography>
//               <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
//                 <Email fontSize="small" color="action" />
//                 <Typography variant="body2" color="text.secondary">
//                   {candidate.email}
//                 </Typography>
//               </Box>
//               <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
//                 <LocationOn fontSize="small" color="action" />
//                 <Typography variant="body2" color="text.secondary">
//                   {candidate.location}
//                 </Typography>
//               </Box>
//               <Chip
//                 label={`${candidate.interviews.length} Scheduled Interview${candidate.interviews.length !== 1 ? "s" : ""}`}
//                 color="primary"
//                 variant="outlined"
//                 sx={{ fontWeight: 600 }}
//               />
//             </Grid>
//           </Grid>
//         </CardContent>
//       </Card>

//       <Grid container spacing={3}>
//         {candidate.interviews.map((interview) => (
//           <Grid item xs={12} key={interview.id}>
//             <Card elevation={1} sx={{ borderRadius: 2, bgcolor: "#F1FFFA" }}>
//               <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
//                 <Grid container spacing={3} alignItems="center">
//                   <Grid item xs={12} md={8}>
//                     <Typography variant="h6" fontWeight="600" gutterBottom sx={{ color: "text.primary" }}>
//                       {interview.job_title}
//                     </Typography>
//                     <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 2 }}>
//                       <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                         <Business fontSize="small" color="action" />
//                         <Typography variant="body2" color="text.secondary" fontWeight="500">
//                           {interview.interview_mode}
//                         </Typography>
//                       </Box>
//                       <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                         <Event fontSize="small" color="action" />
//                         <Typography variant="body2" color="text.secondary" fontWeight="500">
//                           {new Date(`${interview.interview_date}T${interview.interview_time}`).toLocaleString()}
//                         </Typography>
//                       </Box>
//                     </Box>
//                     <Chip
//                       label={interview.status}
//                       color={interview.status.toLowerCase() === "scheduled" ? "success" : "warning"}
//                       size="small"
//                       sx={{ fontWeight: 600 }}
//                     />
//                   </Grid>
//                   <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
//                     <Button
//                       variant="contained"
//                       startIcon={<RateReview />}
//                       onClick={() => onEvaluate(candidate, interview)}
//                       fullWidth={isXs}
//                       sx={{
//                         minWidth: { xs: "100%", sm: 140 },
//                         borderRadius: 2,
//                         fontWeight: 600,
//                         boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
//                         "&:hover": {
//                           transform: "translateY(-1px)",
//                           boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
//                         },
//                       }}
//                     >
//                       Evaluate
//                     </Button>
//                   </Grid>
//                 </Grid>
//               </CardContent>
//             </Card>
//           </Grid>
//         ))}
//       </Grid>
//     </Box>
//   )
// }

// export default function CandidateInterviewList() {
//   const [candidates, setCandidates] = useState([])
//   const [loading, setLoading] = useState(true)
//   const [error, setError] = useState(null)
//   const [modalOpen, setModalOpen] = useState(false)
//   const [selectedInterview, setSelectedInterview] = useState(null)
//   const [mobileOpen, setMobileOpen] = useState(false)
//   const [searchTerm, setSearchTerm] = useState("")
//   const [filterStatus, setFilterStatus] = useState("all")
//   const [jobListOpen, setJobListOpen] = useState(false)
//   const [selectedCandidate, setSelectedCandidate] = useState(null)
//   const [selectedCandidateDetails, setSelectedCandidateDetails] = useState(null)
//   const [showJobDetails, setShowJobDetails] = useState(false)

//   const theme = useTheme()
//   const isXs = useMediaQuery(theme.breakpoints.down("sm"))

//   useEffect(() => {
//     const fetchAndProcessInterviews = async () => {
//       const token = localStorage.getItem("access_token")

//       if (!token) {
//         setError("Authentication token not found. Please log in.")
//         setLoading(false)
//         return
//       }

//       try {
//         const response = await fetch("http://127.0.0.1:8000/api/interviews/", {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json",
//           },
//         })

//         if (!response.ok) {
//           if (response.status === 401) {
//             throw new Error("Unauthorized: Your session may have expired. Please log in again.")
//           }
//           throw new Error(`HTTP error! status: ${response.status}`)
//         }
//         const data = await response.json()

//         const filtered = data.filter(
//           (interview) =>
//             interview.status.toLowerCase() === "scheduled" || interview.status.toLowerCase() === "rescheduled",
//         )

//         const groupedByCandidate = filtered.reduce((acc, interview) => {
//           const { candidate_name, profile_photo, talent_current_location } = interview
//           if (!acc[candidate_name]) {
//             acc[candidate_name] = {
//               id: candidate_name.replace(/\s+/g, "-").toLowerCase(),
//               name: candidate_name,
//               email: `${candidate_name.replace(/\s+/g, ".").toLowerCase()}@example.com`,
//               avatar: profile_photo || `https://i.pravatar.cc/150?u=${encodeURIComponent(candidate_name)}`,
//               location: talent_current_location || "Location not specified",
//               interviews: [],
//             }
//           }
//           acc[candidate_name].interviews.push(interview)
//           return acc
//         }, {})

//         setCandidates(Object.values(groupedByCandidate))
//       } catch (e) {
//         setError(e.message)
//         console.error("Failed to fetch interviews:", e)
//       } finally {
//         setLoading(false)
//       }
//     }

//     fetchAndProcessInterviews()
//   }, [])

//   const handleDrawerToggle = () => {
//     setMobileOpen(!mobileOpen)
//   }

//   const handleViewDetails = (candidate) => {
//     setSelectedCandidateDetails(candidate)
//     setShowJobDetails(true)
//   }

//   const handleBackToList = () => {
//     setShowJobDetails(false)
//     setSelectedCandidateDetails(null)
//   }

//   const handleEvaluateClick = (candidate, interview) => {
//     const detailsForModal = {
//       name: candidate.name,
//       email: candidate.email,
//       avatar: candidate.avatar,
//       interviewId: interview.id,
//       jobTitle: interview.job_title,
//       department: interview.interview_mode || "General",
//       interviewDate: `${interview.interview_date}T${interview.interview_time}:00Z`,
//     }
//     setSelectedInterview(detailsForModal)
//     setModalOpen(true)
//     setJobListOpen(false)
//   }

//   const handleCloseModal = () => {
//     setModalOpen(false)
//     setSelectedInterview(null)
//   }

//   const handleCloseJobList = () => {
//     setJobListOpen(false)
//     setSelectedCandidate(null)
//   }

//   const filteredCandidates = candidates.filter((candidate) => {
//     const matchesSearch =
//       candidate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       candidate.email.toLowerCase().includes(searchTerm.toLowerCase())
//     return matchesSearch
//   })

//   const renderContent = () => {
//     if (loading) {
//       return (
//         <Box display="flex" justifyContent="center" alignItems="center" minHeight={{ xs: "160px", sm: "200px" }}>
//           <CircularProgress size={40} />
//         </Box>
//       )
//     }

//     if (error) {
//       return (
//         <Paper elevation={1} sx={{ p: { xs: 3, sm: 4, md: 6 }, textAlign: "center", borderRadius: 2 }}>
//           <Typography variant="h6" color="text.secondary" gutterBottom>
//             Error Fetching Interviews
//           </Typography>
//           <Typography variant="body2" color="text.secondary">
//             {error}
//           </Typography>
//         </Paper>
//       )
//     }

//     if (showJobDetails && selectedCandidateDetails) {
//       return (
//         <JobDetailsView
//           candidate={selectedCandidateDetails}
//           onBack={handleBackToList}
//           onEvaluate={handleEvaluateClick}
//         />
//       )
//     }

//     if (filteredCandidates.length === 0) {
//       return (
//         <Paper elevation={1} sx={{ p: { xs: 3, sm: 4, md: 6 }, textAlign: "center", borderRadius: 2 }}>
//           <Typography variant="h6" color="text.secondary" gutterBottom>
//             No candidates found
//           </Typography>
//           <Typography variant="body2" color="text.secondary">
//             {searchTerm || filterStatus !== "all" ? "Try adjusting your filters" : "No interviews scheduled"}
//           </Typography>
//         </Paper>
//       )
//     }

//     return (
//       <TableContainer
//         component={Paper}
//         elevation={1}
//         sx={{ borderRadius: 2, bgcolor: "#F1FFFA", maxWidth: "100%", overflowX: "auto" }}
//       >
//         <Table size={isXs ? "small" : "medium"}>
//           <TableHead>
//             <TableRow sx={{ backgroundColor: "#F1FFFA" }}>
//               <TableCell sx={{ fontWeight: "bold", py: { xs: 1.5, sm: 2 } }}>Candidate</TableCell>
//               <TableCell sx={{ fontWeight: "bold", py: { xs: 1.5, sm: 2 } }}>Interviews</TableCell>
//               <TableCell sx={{ fontWeight: "bold", py: { xs: 1.5, sm: 2 } }}>Actions</TableCell>
//             </TableRow>
//           </TableHead>
//           <TableBody>
//             {filteredCandidates.map((candidate) => (
//               <TableRow
//                 key={candidate.id}
//                 sx={{
//                   "&:hover": { bgcolor: "grey.50" },
//                 }}
//               >
//                 <TableCell>
//                   <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                     <Avatar
//                       src={candidate.avatar}
//                       sx={{
//                         width: { xs: 40, sm: 48 },
//                         height: { xs: 40, sm: 48 },
//                         border: "2px solid",
//                         borderColor: "primary.light",
//                       }}
//                     />
//                     <Typography variant="body1" fontWeight="600">
//                       {candidate.name}
//                     </Typography>
//                   </Box>
//                 </TableCell>
//                 <TableCell>
//                   <Chip
//                     label={`${candidate.interviews.length} interview${candidate.interviews.length !== 1 ? "s" : ""}`}
//                     color="primary"
//                     variant="outlined"
//                     size="small"
//                     sx={{ fontWeight: 600 }}
//                   />
//                 </TableCell>
//                 <TableCell>
//                   <Button
//                     variant="contained"
//                     onClick={() => handleViewDetails(candidate)}
//                     fullWidth={isXs}
//                     sx={{
//                       minWidth: { xs: "100%", sm: 120 },
//                       borderRadius: 2,
//                       fontWeight: 600,
//                       boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
//                       "&:hover": {
//                         transform: "translateY(-1px)",
//                         boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
//                       },
//                     }}
//                   >
//                     View Details
//                   </Button>
//                 </TableCell>
//               </TableRow>
//             ))}
//           </TableBody>
//         </Table>
//       </TableContainer>
//     )
//   }

//   return (
//     <Layout title="Evaluations">
//       <ThemeProvider theme={theme}>
//         <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
//           <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 }, px: { xs: 1.5, sm: 2 } }}>
//             {renderContent()}
//           </Container>
//         </Box>

//         {selectedInterview && (
//           <EvaluationForm open={modalOpen} onClose={handleCloseModal} interviewDetails={selectedInterview} />
//         )}
//       </ThemeProvider>
//     </Layout>
//   )
// }

// import React, { useState, useEffect } from "react"
// import {
//   Container,
//   Paper,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   Rating,
//   TextField,
//   Button,
//   Box,
//   Avatar,
//   Divider,
//   FormControl,
//   Select,
//   MenuItem,
//   List,
//   ListItemText,
//   Accordion,
//   Chip,
//   Modal,
//   CircularProgress,
//   Alert,
//   AppBar,
//   Toolbar,
//   IconButton,
//   Menu,
//   Fade,
//   ListItemButton,
//   ListItemIcon,
//   Badge,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Backdrop,
// } from "@mui/material"
// import {
//   Person,
//   Email,
//   Work,
//   TrendingUp,
//   CheckCircle,
//   ExpandMore,
//   Event,
//   Business,
//   RateReview,
//   ArrowBack,
//   Close,
//   ThumbUp,
//   Add,
//   Remove,
//   LocationOn,
// } from "@mui/icons-material"
// import { createTheme, ThemeProvider, styled } from "@mui/material/styles"
// import Layout from "../common/Layout"

// const theme = createTheme({
//   palette: {
//     primary: {
//       main: "#1976d2",
//       light: "#42a5f5",
//       dark: "#1565c0",
//     },
//     background: {
//       default: "#f8fafc",
//       paper: "#ffffff",
//     },
//     text: {
//       primary: "#1e293b",
//       secondary: "#64748b",
//     },
//   },
//   typography: {
//     fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
//     h4: {
//       fontWeight: 700,
//       letterSpacing: "-0.025em",
//     },
//     h6: {
//       fontWeight: 600,
//     },
//     body1: {
//       lineHeight: 1.6,
//     },
//   },
//   components: {
//     MuiCard: {
//       styleOverrides: {
//         root: {
//           borderRadius: 16,
//           boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
//           transition: "all 0.2s ease-in-out",
//           "&:hover": {
//             boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//             transform: "translateY(-2px)",
//           },
//         },
//       },
//     },
//     MuiButton: {
//       styleOverrides: {
//         root: {
//           borderRadius: 12,
//           textTransform: "none",
//           fontWeight: 600,
//           transition: "all 0.2s ease-in-out",
//         },
//         contained: {
//           boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
//           "&:hover": {
//             boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
//             transform: "translateY(-1px)",
//           },
//         },
//       },
//     },
//     MuiAccordion: {
//       styleOverrides: {
//         root: {
//           borderRadius: "16px !important",
//           boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
//           "&:before": {
//             display: "none",
//           },
//           "&$expanded": {
//             margin: "auto",
//           },
//         },
//       },
//     },
//   },
// })

// const StyledAccordion = styled(Accordion)(({ theme }) => ({
//   transition: theme.transitions.create(["box-shadow", "margin", "transform"], {
//     duration: theme.transitions.duration.short,
//   }),
//   "&:hover": {
//     transform: "translateY(-2px)",
//     boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//   },
//   "&.Mui-expanded": {
//     boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//     margin: `${theme.spacing(1, 0)} !important`,
//     transform: "translateY(-2px)",
//   },
// }))

// function Navigation({ mobileOpen, handleDrawerToggle }) {
//   const [anchorEl, setAnchorEl] = useState(null)

//   const handleProfileMenuOpen = (event) => {
//     setAnchorEl(event.currentTarget)
//   }

//   const handleProfileMenuClose = () => {
//     setAnchorEl(null)
//   }

  


        
// }

// function JobListDialog({ candidate, open, onClose, onEvaluate }) {
//   if (!candidate) return null

//   return (
//     <Modal open={open} onClose={onClose} closeAfterTransition>
//       <Fade in={open}>
//         <Box
//           sx={{
//             position: "absolute",
//             top: "50%",
//             left: "50%",
//             transform: "translate(-50%, -50%)",
//             width: "90%",
//             maxWidth: "800px",
//             backgroundColor: "#F1FFFA",
//             borderRadius: 3,
//             maxHeight: "90vh",
//             overflowY: "auto",
//             border: "none",
//             outline: "none",
//           }}
//         >
//           <Paper elevation={0} sx={{ borderRadius: 3, overflow: "hidden",backgroundColor: "#F1FFFA", }}>
//             <Box
//               sx={{
//                 background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
//                 color: "white",
//                 p: 4,
//               }}
//             >
//               <Grid container spacing={3} alignItems="center" backgroundColor= "#F1FFFA">
//                 <Grid item sx={{  backgroundColor: "#F1FFFA",}}>
//                   <Avatar
//                     src={candidate.avatar}
//                     sx={{ width: 80, height: 80, border: "3px solid rgba(255,255,255,0.3)", }}
//                   />
//                 </Grid>
//                 <Grid item xs>
//                   <Typography variant="h4" fontWeight="bold" gutterBottom>
//                     {candidate.name}
//                   </Typography>
//                   <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
//                     {candidate.email}
//                   </Typography>
//                   <Typography variant="body2" sx={{ opacity: 0.8 }}>
//                     {candidate.interviews.length} Scheduled Interview{candidate.interviews.length !== 1 ? "s" : ""}
//                   </Typography>
//                   <Typography variant="body2" sx={{ opacity: 0.8 }}>
//                     Location: {candidate.location}
//                   </Typography>
//                 </Grid>
//               </Grid>
//             </Box>

//             <Container maxWidth="lg" sx={{ p: 4 ,   backgroundColor: "#F1FFFA",}}>
//               <Typography variant="h6" gutterBottom color="primary" fontWeight="bold" sx={{ mb: 3 }}>
//                 Interview Schedule
//               </Typography>

//               <List disablePadding sx={{backgroundColor: "#F1FFFA",}}>
//                 {candidate.interviews.map((interview, index) => (
//                   <React.Fragment key={interview.id}>
//                     {index > 0 && <Divider sx={{ my: 2 }} />}
//                     <Card elevation={1} sx={{ mb: 2, borderRadius: 2,   backgroundColor: "#F1FFFA", }}>
//                       <CardContent sx={{ p: 3 }}>
//                         <Grid container spacing={3} alignItems="center" backgroundColor="#F1FFFA">
//                           <Grid item xs={12} md={8}>
//                             <Typography variant="h6" fontWeight="600" gutterBottom sx={{ color: "text.primary" }}>
//                               {interview.job_title}
//                             </Typography>
//                             <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 2 }}>
//                               <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                                 <Business fontSize="small" color="action" />
//                                 <Typography variant="body2" color="text.secondary" fontWeight="500">
//                                   {interview.interview_mode}
//                                 </Typography>
//                               </Box>
//                               <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                                 <Event fontSize="small" color="action" />
//                                 <Typography variant="body2" color="text.secondary" fontWeight="500">
//                                   {new Date(`${interview.interview_date}T${interview.interview_time}`).toLocaleString()}
//                                 </Typography>
//                               </Box>
//                             </Box>
//                             <Chip
//                               label={interview.status}
//                               color={interview.status.toLowerCase() === "scheduled" ? "success" : "warning"}
//                               size="small"
//                               sx={{ fontWeight: 600 }}
//                             />
//                           </Grid>
//                           <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
//                             <Button
//                               variant="contained"
//                               startIcon={<RateReview />}
//                               onClick={() => onEvaluate(candidate, interview)}
//                               sx={{
//                                 minWidth: 140,
//                                 borderRadius: 2,
//                                 fontWeight: 600,
//                                 boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
//                                 "&:hover": {
//                                   transform: "translateY(-1px)",
//                                   boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
//                                 },
//                               }}
//                             >
//                               Evaluate
//                             </Button>
//                           </Grid>
//                         </Grid>
//                       </CardContent>
//                     </Card>
//                   </React.Fragment>
//                 ))}
//               </List>

//               <Box
//                 sx={{ display: "flex", justifyContent: "flex-end", mt: 4, pt: 3, borderTop: 1, borderColor: "divider" }}
//               >
//                 <Button variant="outlined" size="large" onClick={onClose} sx={{ minWidth: 120 }}>
//                   Close
//                 </Button>
//               </Box>
//             </Container>
//           </Paper>
//         </Box>
//       </Fade>
//     </Modal>
//   )
// }

// function EvaluationForm({ interviewDetails, open, onClose }) {
//   const [formData, setFormData] = useState({
//     recommendation: "",
//     technical_skills_rating: 0,
//     communication_skills_rating: 0,
//     cultural_fit_rating: 0,
//     strengths: [""],
//     weaknesses: [""],
//     overall_comments: [""],
//   })
//   const [isSubmitting, setIsSubmitting] = useState(false)
//   const [submitError, setSubmitError] = useState(null)

//   useEffect(() => {
//     if (open) {
//       setFormData({
//         recommendation: "",
//         technical_skills_rating: 0,
//         communication_skills_rating: 0,
//         cultural_fit_rating: 0,
//         strengths: [""],
//         weaknesses: [""],
//         overall_comments: [""],
//       })
//       setIsSubmitting(false)
//       setSubmitError(null)
//     }
//   }, [open])

//   const handleChange = (e) => {
//     const { name, value } = e.target
//     setFormData((prev) => ({ ...prev, [name]: value }))
//   }

//   const handleRatingChange = (field, value) => {
//     setFormData((prev) => ({ ...prev, [field]: value !== null ? Math.round(value) : 0 }))
//   }

//   const handleArrayFieldChange = (field, index, value) => {
//     setFormData((prev) => ({
//       ...prev,
//       [field]: prev[field].map((item, i) => (i === index ? value : item)),
//     }))
//   }

//   const addArrayField = (field) => {
//     setFormData((prev) => ({
//       ...prev,
//       [field]: [...prev[field], ""],
//     }))
//   }

//   const removeArrayField = (field, index) => {
//     if (formData[field].length > 1) {
//       setFormData((prev) => ({
//         ...prev,
//         [field]: prev[field].filter((_, i) => i !== index),
//       }))
//     }
//   }

//   const handleSubmit = async (e) => {
//     e.preventDefault()
//     setIsSubmitting(true)
//     setSubmitError(null)

//     try {
//       const submissionData = {
//         ...formData,
//         strengths: formData.strengths.filter((s) => s.trim()).join("; "),
//         weaknesses: formData.weaknesses.filter((w) => w.trim()).join("; "),
//         overall_comments: formData.overall_comments.filter((c) => c.trim()).join("; "),
//         interview_id: interviewDetails.interviewId,
//       }

//       console.log("[v0] Submitting evaluation data:", submissionData)

//       const accessToken = localStorage.getItem("access_token")
//       const headers = {
//         "Content-Type": "application/json",
//       }

//       if (accessToken) {
//         headers.Authorization = `Bearer ${accessToken}`
//       }

//       const response = await fetch(`http://localhost:8000/api/interviews/${interviewDetails.interviewId}/submit-feedback/`,
//  {
//         method: "POST",
//         headers,
//         body: JSON.stringify(submissionData),
//       })

//       console.log("[v0] Response status:", response.status)
//       console.log("[v0] Response headers:", response.headers.get("content-type"))

//       if (!response.ok) {
//         const contentType = response.headers.get("content-type")
//         let errorMessage = `HTTP ${response.status}: ${response.statusText}`

//         if (contentType && contentType.includes("application/json")) {
//           try {
//             const errorData = await response.json()
//             errorMessage = errorData.message || errorMessage
//           } catch (jsonError) {
//             console.error("[v0] Failed to parse error JSON:", jsonError)
//           }
//         } else {
//           // If it's not JSON, it might be HTML (like a 404 page)
//           const textResponse = await response.text()
//           console.log("[v0] Non-JSON response:", textResponse.substring(0, 200))
//           errorMessage = `Server returned ${response.status}. Check if the API endpoint exists and the server is running.`
//         }

//         throw new Error(errorMessage)
//       }

//       const contentType = response.headers.get("content-type")
//       if (contentType && contentType.includes("application/json")) {
//         const result = await response.json()
//         console.log("[v0] Success response:", result)
//       }

//       onClose()
//     } catch (error) {
//       console.error("[v0] Error submitting evaluation:", error)
//       setSubmitError(error.message)
//     } finally {
//       setIsSubmitting(false)
//     }
//   }

//   return (
//     <Modal
//       open={open}
//       onClose={onClose}
//       closeAfterTransition
//       BackdropComponent={Backdrop}
//       BackdropProps={{
//         timeout: 500,
//       }}
//     >
//       <Fade in={open}>
//         <Box
//           sx={{
//             position: "absolute",
//             top: "50%",
//             left: "50%",
//             transform: "translate(-50%, -50%)",
//             width: { xs: "95%", sm: "90%", md: "80%", lg: "70%" },
//             maxWidth: "900px",
//             maxHeight: "90vh",
//             bgcolor: "background.paper",
//             borderRadius: 3,
//             boxShadow: 24,
//             overflow: "hidden",
//             display: "flex",
//             flexDirection: "column",
//           }}
//         >
//           <Paper elevation={0} sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
//             {/* Header */}
//             <Box
//               sx={{
//                 background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
//                 color: "white",
//                 p: 3,
//                 display: "flex",
//                 alignItems: "center",
//                 justifyContent: "space-between",
//                 flexShrink: 0,
//               }}
//             >
//               <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                 <Avatar
//                   src={interviewDetails?.avatar}
//                   sx={{ width: 56, height: 56, border: "3px solid rgba(255,255,255,0.3)" }}
//                 >
//                   {interviewDetails?.name?.charAt(0)}
//                 </Avatar>
//                 <Box>
//                   <Typography variant="h5" fontWeight="bold">
//                     Evaluate Candidate
//                   </Typography>
//                   <Typography variant="body1" sx={{ opacity: 0.9 }}>
//                     {interviewDetails?.name} • {interviewDetails?.jobTitle}
//                   </Typography>
//                 </Box>
//               </Box>
//               <IconButton onClick={onClose} sx={{ color: "white" }}>
//                 <Close />
//               </IconButton>
//             </Box>

//             {/* Content */}
//             <Container
//               maxWidth="lg"
//               sx={{
//                 flex: 1,
//                 overflow: "auto",
//                 py: 3,
//                 minHeight: 0,
//                 maxHeight: "calc(90vh - 140px)", // Account for header and footer space
//                 "&::-webkit-scrollbar": {
//                   width: "8px",
//                 },
//                 "&::-webkit-scrollbar-track": {
//                   background: "#f1f1f1",
//                   borderRadius: "4px",
//                 },
//                 "&::-webkit-scrollbar-thumb": {
//                   background: "#c1c1c1",
//                   borderRadius: "4px",
//                   "&:hover": {
//                     background: "#a8a8a8",
//                   },
//                 },
//                 // For Firefox
//                 scrollbarWidth: "thin",
//                 scrollbarColor: "#c1c1c1 #f1f1f1",
//               }}
//             >
//               <form onSubmit={handleSubmit}>
//                 <Grid container spacing={3}>
//                   {/* Recommendation */}
//                   <Grid item xs={12}>
//                     <Card variant="outlined" sx={{ p: 2, borderRadius: 2 , backgroundColor: "#F1FFFA",}}>
//                       <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                         <ThumbUp color="primary" />
//                         Recommendation
//                       </Typography>
//                       <FormControl fullWidth>
//                         <Select
//                           name="recommendation"
//                           value={formData.recommendation}
//                           onChange={handleChange}
//                           required
//                           sx={{ borderRadius: 2 }}
//                         >
//                           <MenuItem value="">
//                             <em>Select recommendation</em>
//                           </MenuItem>
//                           <MenuItem value="HIRE">
//                             <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                               <Person color="success" />
//                                Hire
//                             </Box>
//                           </MenuItem>
//                           <MenuItem value="CONSIDER">
//                             <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                               <Person color="primary" />
//                              consider
//                             </Box>
//                           </MenuItem>
//                           <MenuItem value="NO_HIRE">
//                             <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                               <Person color="error" />
//                               No Hire
//                             </Box>
//                           </MenuItem>
//                         </Select>
//                       </FormControl>
//                     </Card>
//                   </Grid>

//                   {/* Ratings */}
//                   <Grid item xs={12}>
//                     <Card variant="outlined" sx={{ p: 2, gap: 3, borderRadius: 2 , backgroundColor: "#F1FFFA",width:"100%"}}>
//                       <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
//                         Skills Assessment
//                       </Typography>
//                       <Grid container spacing={3}>
//                         {[
//                           { field: "technical_skills_rating", label: "Technical Skills", icon: "💻", color: "#2196f3" },
//                           {
//                             field: "communication_skills_rating",
//                             label: "Communication",
//                             icon: "💬",
//                             color: "#4caf50",
//                           },
//                           { field: "cultural_fit_rating", label: "Cultural Fit", icon: "🤝", color: "#ff9800" },
//                         ].map((rating) => (
//                           <Grid item xs={12} sm={4} key={rating.field}>
//                             <Box
//                               sx={{
//                                 textAlign: "center",
//                                 p: 2,
//                                 borderRadius: 2,
//                                 border: "1px solid",
//                                 borderColor: "divider",
//                                 bgcolor: "grey.50",
//                               }}
//                             >
//                               <Typography variant="h4" sx={{ mb: 1 }}>
//                                 {rating.icon}
//                               </Typography>
//                               <Typography variant="body1" fontWeight="medium" gutterBottom>
//                                 {rating.label}
//                               </Typography>
//                               <Rating
//                                 name={rating.field}
//                                 value={formData[rating.field]}
//                                 onChange={(e, val) => handleRatingChange(rating.field, val)}
//                                 size="large"
//                                 sx={{ mb: 1 }}
//                                 max={5}
//                               />
//                               <Typography variant="body2" color="text.secondary">
//                                 {formData[rating.field]}/5
//                               </Typography>
//                             </Box>
//                           </Grid>
//                         ))}
//                       </Grid>
//                     </Card>
//                   </Grid>

//                   {/* Dynamic Fields */}
//                   {[
//                     {
//                       field: "strengths",
//                       label: "Key Strengths   ",
//                       placeholder: "What impressed you most about this candidate?",
//                       icon: "⭐",
//                     },
//                     {
//                       field: "weaknesses",
//                       label: "Areas for Improvement    ",
//                       placeholder: "What areas could the candidate develop?",
//                       icon: "📈",
//                     },
//                     {
//                       field: "overall_comments",
//                       label: "Overall Assessment   ",
//                       placeholder: "Provide your comprehensive evaluation...",
//                       icon: "📝",
//                     },
//                   ].map((section) => (
//                     <Grid item xs={12} key={section.field}>
//                       <Card variant="outlined" sx={{ p: 2, borderRadius: 2,  backgroundColor: "#F1FFFA",}}>
//                         <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
//                           <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 14}}>
//                             {section.icon} {section.label}
//                           </Typography> 
//                           <Button
//                             variant="outlined"
//                             size="medium"
//                             startIcon={<Add/>}
//                             onClick={() => addArrayField(section.field)}
//                             sx={{ borderRadius: 2, marginleft:4 }}
//                           >
//                             Add
//                           </Button>
//                         </Box>
//                         {formData[section.field].map((item, index) => (
//                           <Box key={index} sx={{ display: "flex", gap: 1, mb: 2 }}>
//                             <TextField
//                               fullWidth
//                               multiline
//                               rows={2}
//                               placeholder={section.placeholder}
//                               value={item}
//                               onChange={(e) => handleArrayFieldChange(section.field, index, e.target.value)}
//                               variant="outlined"
//                               sx={{ borderRadius: 2 }}
//                             />
//                             {formData[section.field].length > 1 && (
//                               <IconButton
//                                 onClick={() => removeArrayField(section.field, index)}
//                                 color="error"
//                                 sx={{ alignSelf: "flex-start", mt: 1 }}
//                               >
//                                 <Remove />
//                               </IconButton>
//                             )}
//                           </Box>
//                         ))}
//                       </Card>
//                     </Grid>
//                   ))}

//                   {/* Error Display */}
//                   {submitError && (
//                     <Grid item xs={12}>
//                       <Alert severity="error" sx={{ borderRadius: 2 }}>
//                         {submitError}
//                       </Alert>
//                     </Grid>
//                   )}
//                 </Grid>

//                 {/* Footer */}
//                 <Box
//                   sx={{
//                     display: "flex",
//                     justifyContent: "flex-end",
//                     gap: 2,
//                     mt: 4,
//                     pt: 3,
//                     borderTop: "1px solid",
//                     borderColor: "divider",
//                   }}
//                 >
//                   <Button onClick={onClose} variant="outlined" size="large" sx={{ borderRadius: 2, minWidth: 120 }}>
//                     Cancel
//                   </Button>
//                   <Button
//                     type="submit"
//                     variant="contained"
//                     size="large"
//                     disabled={isSubmitting}
//                     sx={{ borderRadius: 2, minWidth: 120 }}
//                   >
//                     {isSubmitting ? <CircularProgress size={24} /> : "Submit Evaluation"}
//                   </Button>
//                 </Box>
//               </form>
//             </Container>
//           </Paper>
//         </Box>
//       </Fade>
//     </Modal>
//   )
// }

// function JobDetailsView({ candidate, onBack, onEvaluate }) {
//   return (
//     <Box>
//       <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 2 }}>
//         <Button variant="outlined" startIcon={<ArrowBack />} onClick={onBack} sx={{ borderRadius: 2 ,backgroundColor: "#F1FFFA",}}>
//           Back to Candidates
//         </Button>
//         {/* <Typography variant="h5" fontWeight="bold" color="primary">
//           {candidate.name}
//         </Typography> */}
//       </Box>

//       <Card elevation={1} sx={{ mb: 4, borderRadius: 2 , backgroundColor: "#F1FFFA",}}>
//         <CardContent sx={{ p: 4 }}>
//           <Grid container spacing={3} alignItems="center">
//             <Grid item>
//               <Avatar
//                 src={candidate.avatar}
//                 sx={{ width: 80, height: 80, border: "3px solid", borderColor: "primary.light" }}
//               />
//             </Grid>
//             <Grid item xs>
//               <Typography variant="h5" fontWeight="bold" gutterBottom>
//                 {candidate.name}
//               </Typography>
//               <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
//                 <Email fontSize="small" color="action" />
//                 <Typography variant="body2" color="text.secondary">
//                   {candidate.email}
//                 </Typography>
//               </Box>
//               <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
//                 <LocationOn fontSize="small" color="action" />
//                 <Typography variant="body2" color="text.secondary">
//                   {candidate.location}
//                 </Typography>
//               </Box>
//               <Chip
//                 label={`${candidate.interviews.length} Scheduled Interview${candidate.interviews.length !== 1 ? "s" : ""}`}
//                 color="primary"
//                 variant="outlined"
//                 sx={{ fontWeight: 600 }}
//               />
//             </Grid>
//           </Grid>
//         </CardContent>
//       </Card>
//       {/* 
//       <Typography variant="h6" gutterBottom color="primary" fontWeight="bold" sx={{ mb: 3 }}>
//         Interview Schedule
//       </Typography> */}
//       <Grid container spacing={3}>
//         {candidate.interviews.map((interview) => (
//           <Grid item xs={12} key={interview.id}>
//             <Card elevation={1} sx={{ borderRadius: 2, backgroundColor: "#F1FFFA",}}>
//               <CardContent sx={{ p: 3 }}>
//                 <Grid container spacing={3} alignItems="center">
//                   <Grid item xs={12} md={8}>
//                     <Typography variant="h6" fontWeight="600" gutterBottom sx={{ color: "text.primary" }}>
//                       {interview.job_title}
//                     </Typography>
//                     <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 2 }}>
//                       <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                         <Business fontSize="small" color="action" />
//                         <Typography variant="body2" color="text.secondary" fontWeight="500">
//                           {interview.interview_mode}
//                         </Typography>
//                       </Box>
//                       <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                         <Event fontSize="small" color="action" />
//                         <Typography variant="body2" color="text.secondary" fontWeight="500">
//                           {new Date(`${interview.interview_date}T${interview.interview_time}`).toLocaleString()}
//                         </Typography>
//                       </Box>
//                     </Box>
//                     <Chip
//                       label={interview.status}
//                       color={interview.status.toLowerCase() === "scheduled" ? "success" : "warning"}
//                       size="small"
//                       sx={{ fontWeight: 600 }}
//                     />
//                   </Grid>
//                   <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
//                     <Button
//                       variant="contained"
//                       startIcon={<RateReview />}
//                       onClick={() => onEvaluate(candidate, interview)}
//                       sx={{
//                         minWidth: 140,
//                         borderRadius: 2,
//                         fontWeight: 600,
//                         boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
//                         "&:hover": {
//                           transform: "translateY(-1px)",
//                           boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
//                         },
//                       }}
//                     >
//                       Evaluate
//                     </Button>
//                   </Grid>
//                 </Grid>
//               </CardContent>
//             </Card>
//           </Grid>
//         ))}
//       </Grid>
//     </Box>
//   )
// }

// export default function CandidateInterviewList() {
//   const [candidates, setCandidates] = useState([])
//   const [loading, setLoading] = useState(true)
//   const [error, setError] = useState(null)
//   const [modalOpen, setModalOpen] = useState(false)
//   const [selectedInterview, setSelectedInterview] = useState(null)
//   const [mobileOpen, setMobileOpen] = useState(false)
//   const [searchTerm, setSearchTerm] = useState("")
//   const [filterStatus, setFilterStatus] = useState("all")
//   const [jobListOpen, setJobListOpen] = useState(false)
//   const [selectedCandidate, setSelectedCandidate] = useState(null)
//   const [selectedCandidateDetails, setSelectedCandidateDetails] = useState(null)
//   const [showJobDetails, setShowJobDetails] = useState(false)

//   useEffect(() => {
//     const fetchAndProcessInterviews = async () => {
//       const token = localStorage.getItem("access_token")

//       if (!token) {
//         setError("Authentication token not found. Please log in.")
//         setLoading(false)
//         return
//       }

//       try {
//         const response = await fetch("http://127.0.0.1:8000/api/interviews/", {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json",
//           },
//         })

//         if (!response.ok) {
//           if (response.status === 401) {
//             throw new Error("Unauthorized: Your session may have expired. Please log in again.")
//           }
//           throw new Error(`HTTP error! status: ${response.status}`)
//         }
//         const data = await response.json()

//         const filtered = data.filter(
//           (interview) =>
//             interview.status.toLowerCase() === "scheduled" || interview.status.toLowerCase() === "rescheduled",
//         )

//         const groupedByCandidate = filtered.reduce((acc, interview) => {
//           const { candidate_name, profile_photo, talent_current_location } = interview
//           if (!acc[candidate_name]) {
//             acc[candidate_name] = {
//               id: candidate_name.replace(/\s+/g, "-").toLowerCase(),
//               name: candidate_name,
//               email: `${candidate_name.replace(/\s+/g, ".").toLowerCase()}@example.com`,
//               avatar: profile_photo || `https://i.pravatar.cc/150?u=${encodeURIComponent(candidate_name)}`,
//               location: talent_current_location || "Location not specified",
//               interviews: [],
//             }
//           }
//           acc[candidate_name].interviews.push(interview)
//           return acc
//         }, {})

//         setCandidates(Object.values(groupedByCandidate))
//       } catch (e) {
//         setError(e.message)
//         console.error("Failed to fetch interviews:", e)
//       } finally {
//         setLoading(false)
//       }
//     }

//     fetchAndProcessInterviews()
//   }, [])

//   const handleDrawerToggle = () => {
//     setMobileOpen(!mobileOpen)
//   }

//   const handleViewDetails = (candidate) => {
//     setSelectedCandidateDetails(candidate)
//     setShowJobDetails(true)
//   }

//   const handleBackToList = () => {
//     setShowJobDetails(false)
//     setSelectedCandidateDetails(null)
//   }

//   const handleEvaluateClick = (candidate, interview) => {
//     const detailsForModal = {
//       name: candidate.name,
//       email: candidate.email,
//       avatar: candidate.avatar,
//       interviewId: interview.id,
//       jobTitle: interview.job_title,
//       department: interview.interview_mode || "General",
//       interviewDate: `${interview.interview_date}T${interview.interview_time}:00Z`,
//     }
//     setSelectedInterview(detailsForModal)
//     setModalOpen(true)
//     setJobListOpen(false)
//   }

//   const handleCloseModal = () => {
//     setModalOpen(false)
//     setSelectedInterview(null)
//   }

//   const handleCloseJobList = () => {
//     setJobListOpen(false)
//     setSelectedCandidate(null)
//   }

//   const filteredCandidates = candidates.filter((candidate) => {
//     const matchesSearch =
//       candidate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       candidate.email.toLowerCase().includes(searchTerm.toLowerCase())
//     return matchesSearch
//   })

//   const renderContent = () => {
//     if (loading) {
//       return (
//         <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
//           <CircularProgress size={40} />
//         </Box>
//       )
//     }

//     if (error) {
//       return (
//         <Paper elevation={1} sx={{ p: 6, textAlign: "center", borderRadius: 2 }}>
//           <Typography variant="h6" color="text.secondary" gutterBottom>
//             Error Fetching Interviews
//           </Typography>
//           <Typography variant="body2" color="text.secondary">
//             {error}
//           </Typography>
//         </Paper>
//       )
//     }

//     if (showJobDetails && selectedCandidateDetails) {
//       return (
//         <JobDetailsView
//           candidate={selectedCandidateDetails}
//           onBack={handleBackToList}
//           onEvaluate={handleEvaluateClick}
//         />
//       )
//     }

//     if (filteredCandidates.length === 0) {
//       return (
//         <Paper elevation={1} sx={{ p: 6, textAlign: "center", borderRadius: 2 }}>
//           <Typography variant="h6" color="text.secondary" gutterBottom>
//             No candidates found
//           </Typography>
//           <Typography variant="body2" color="text.secondary">
//             {searchTerm || filterStatus !== "all" ? "Try adjusting your filters" : "No interviews scheduled"}
//           </Typography>
//         </Paper>
//       )
//     }

//     return (
//       <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2 ,  backgroundColor: "#F1FFFA",}}>
//         <Table>
//           <TableHead>
//             <TableRow sx={{ backgroundColor: "#F1FFFA", }}>
//               <TableCell sx={{ fontWeight: "bold", py: 2 }}>Candidate</TableCell>
//               <TableCell sx={{ fontWeight: "bold", py: 2 }}>Interviews</TableCell>
//               <TableCell sx={{ fontWeight: "bold", py: 2 }}>Actions</TableCell>
//             </TableRow>
//           </TableHead>
//           <TableBody>
//             {filteredCandidates.map((candidate) => (
//               <TableRow
//                 key={candidate.id}
//                 sx={{
//                   "&:hover": {
//                     bgcolor: "grey.50",
//                   },
//                 }}
//               >
//                 <TableCell>
//                   <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                     <Avatar
//                       src={candidate.avatar}
//                       sx={{
//                         width: 48,
//                         height: 48,
//                         border: "2px solid",
//                         borderColor: "primary.light",
//                       }}
//                     />
//                     <Typography variant="body1" fontWeight="600">
//                       {candidate.name}
//                     </Typography>
//                   </Box>
//                 </TableCell>
//                 <TableCell>
//                   <Chip
//                     label={`${candidate.interviews.length} interview${candidate.interviews.length !== 1 ? "s" : ""}`}
//                     color="primary"
//                     variant="outlined"
//                     size="small"
//                     sx={{ fontWeight: 600 }}
//                   />
//                 </TableCell>
//                 <TableCell>
//                   <Button
//                     variant="contained"
//                     onClick={() => handleViewDetails(candidate)}
//                     sx={{
//                       minWidth: 120,
//                       borderRadius: 2,
//                       fontWeight: 600,
//                       boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
//                       "&:hover": {
//                         transform: "translateY(-1px)",
//                         boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
//                       },
//                     }}
//                   >
//                     View Details
//                   </Button>
//                 </TableCell>
//               </TableRow>
//             ))}
//           </TableBody>
//         </Table>
//       </TableContainer>
//     )
//   }

//   return (
//     <Layout title="Evaluations">
//       <ThemeProvider theme={theme}>
//         <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
//           <Container maxWidth="xl" sx={{ py: 4, px: 2 }}>
//             {/* <Typography
//               variant="h4"
//               component="h1"
//               gutterBottom
//               sx={{
//                 fontWeight: 700,
//                 color: "text.primary",
//                 letterSpacing: "-0.025em",
//                 mb: 1,
//               }}
//             >
//               Scheduled Interviews
//             </Typography> */}
//             {renderContent()}
//           </Container>
//         </Box>

//         {selectedInterview && (
//           <EvaluationForm open={modalOpen} onClose={handleCloseModal} interviewDetails={selectedInterview} />
//         )}
//       </ThemeProvider>
//     </Layout>
//   )
// }

// "use client"


// import React, { useState, useEffect } from "react"
// import {
//   Container,
//   Paper,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   Rating,
//   TextField,
//   Button,
//   Box,
//   Avatar,
//   Divider,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   List,
//   ListItemText,
//   Accordion,
//   Chip,
//   Modal,
//   CircularProgress,
//   Alert,
//   AppBar,
//   Toolbar,
//   IconButton,
//   Menu,
//   Fade,
//   ListItemButton,
//   ListItemIcon,
//   Badge,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
// } from "@mui/material"
// import {
//   Person,
//   Email,
//   Work,
//   TrendingUp,
//   CheckCircle,
//   ExpandMore,
//   Event,
//   Business,
//   RateReview,
//   ArrowBack,
// } from "@mui/icons-material"
// import { createTheme, ThemeProvider, styled } from "@mui/material/styles"
// import Layout from "../common/Layout"

// const theme = createTheme({
//   palette: {
//     primary: {
//       main: "#1976d2",
//       light: "#42a5f5",
//       dark: "#1565c0",
//     },
//     background: {
//       default: "#f8fafc",
//       paper: "#ffffff",
//     },
//     text: {
//       primary: "#1e293b",
//       secondary: "#64748b",
//     },
//   },
//   typography: {
//     fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
//     h4: {
//       fontWeight: 700,
//       letterSpacing: "-0.025em",
//     },
//     h6: {
//       fontWeight: 600,
//     },
//     body1: {
//       lineHeight: 1.6,
//     },
//   },
//   components: {
//     MuiCard: {
//       styleOverrides: {
//         root: {
//           borderRadius: 16,
//           boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
//           transition: "all 0.2s ease-in-out",
//           "&:hover": {
//             boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//             transform: "translateY(-2px)",
//           },
//         },
//       },
//     },
//     MuiButton: {
//       styleOverrides: {
//         root: {
//           borderRadius: 12,
//           textTransform: "none",
//           fontWeight: 600,
//           transition: "all 0.2s ease-in-out",
//         },
//         contained: {
//           boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
//           "&:hover": {
//             boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
//             transform: "translateY(-1px)",
//           },
//         },
//       },
//     },
//     MuiAccordion: {
//       styleOverrides: {
//         root: {
//           borderRadius: "16px !important",
//           boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
//           "&:before": {
//             display: "none",
//           },
//           "&$expanded": {
//             margin: "auto",
//           },
//         },
//       },
//     },
//   },
// })

// const StyledAccordion = styled(Accordion)(({ theme }) => ({
//   transition: theme.transitions.create(["box-shadow", "margin", "transform"], {
//     duration: theme.transitions.duration.short,
//   }),
//   "&:hover": {
//     transform: "translateY(-2px)",
//     boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//   },
//   "&.Mui-expanded": {
//     boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
//     margin: `${theme.spacing(1, 0)} !important`,
//     transform: "translateY(-2px)",
//   },
// }))

// function Navigation({ mobileOpen, handleDrawerToggle }) {
//   const [anchorEl, setAnchorEl] = useState(null)

//   const handleProfileMenuOpen = (event) => {
//     setAnchorEl(event.currentTarget)
//   }

//   const handleProfileMenuClose = () => {
//     setAnchorEl(null)
//   }

//   const drawerContent = (
//     <Box sx={{ p: 3 }}>
//       <Box sx={{ mb: 4 }}>
//         <Typography variant="h6" sx={{ fontWeight: 700, color: "primary.main" }}>
//           InterviewPro
//         </Typography>
//         <Typography variant="body2" color="text.secondary">
//           Talent Management
//         </Typography>
//       </Box>

//       <List>
//         {[
//           { text: "Dashboard", icon: <Person />, active: false },
//           { text: "Interviews", icon: <Event />, active: true },
//           { text: "Candidates", icon: <Work />, active: false },
//           { text: "Analytics", icon: <TrendingUp />, active: false },
//           { text: "Settings", icon: <CheckCircle />, active: false },
//         ].map((item) => (
//           <ListItemButton
//             key={item.text}
//             selected={item.active}
//             sx={{
//               borderRadius: 2,
//               mb: 1,
//               "&.Mui-selected": {
//                 backgroundColor: "primary.main",
//                 color: "white",
//                 "&:hover": {
//                   backgroundColor: "primary.dark",
//                 },
//               },
//             }}
//           >
//             <ListItemIcon sx={{ color: item.active ? "white" : "text.secondary" }}>{item.icon}</ListItemIcon>
//             <ListItemText primary={item.text} />
//           </ListItemButton>
//         ))}
//       </List>
//     </Box>
//   )

//   return (
//     <>
//       <AppBar
//         position="fixed"
//         sx={{
//           backgroundColor: "background.paper",
//           color: "text.primary",
//           boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.05)",
//           borderBottom: "1px solid #e2e8f0",
//         }}
//       >
//         <Toolbar>
//           <IconButton
//             color="inherit"
//             aria-label="open drawer"
//             edge="start"
//             onClick={handleDrawerToggle}
//             sx={{ mr: 2, display: { sm: "none" } }}
//           >
//             <ExpandMore />
//           </IconButton>

//           <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }}>
//             Interview Management
//           </Typography>

//           <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//             <IconButton color="inherit">
//               <Badge badgeContent={4} color="error">
//                 <RateReview />
//               </Badge>
//             </IconButton>

//             <IconButton onClick={handleProfileMenuOpen}>
//               <Avatar sx={{ width: 32, height: 32 }}>JD</Avatar>
//             </IconButton>
//           </Box>
//         </Toolbar>
//       </AppBar>

//       <Menu
//         anchorEl={anchorEl}
//         open={Boolean(anchorEl)}
//         onClose={handleProfileMenuClose}
//         transformOrigin={{ horizontal: "right", vertical: "top" }}
//         anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
//       >
//         <MenuItem onClick={handleProfileMenuClose}>Profile</MenuItem>
//         <MenuItem onClick={handleProfileMenuClose}>Settings</MenuItem>
//         <MenuItem onClick={handleProfileMenuClose}>Logout</MenuItem>
//       </Menu>

//       {/* Drawer component is not included in the updates, so it remains unchanged */}
//     </>
//   )
// }

// function JobListDialog({ candidate, open, onClose, onEvaluate }) {
//   if (!candidate) return null

//   return (
//     <Modal open={open} onClose={onClose} closeAfterTransition>
//       <Fade in={open}>
//         <Box
//           sx={{
//             position: "absolute",
//             top: "50%",
//             left: "50%",
//             transform: "translate(-50%, -50%)",
//             width: "90%",
//             maxWidth: "800px",
//             bgcolor: "background.paper",
//             borderRadius: 3,
//             maxHeight: "90vh",
//             overflowY: "auto",
//             border: "none",
//             outline: "none",
//           }}
//         >
//           <Paper elevation={0} sx={{ borderRadius: 3, overflow: "hidden" }}>
//             <Box
//               sx={{
//                 background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
//                 color: "white",
//                 p: 4,
//               }}
//             >
//               <Grid container spacing={3} alignItems="center">
//                 <Grid item>
//                   <Avatar
//                     src={candidate.avatar}
//                     sx={{ width: 80, height: 80, border: "3px solid rgba(255,255,255,0.3)" }}
//                   />
//                 </Grid>
//                 <Grid item xs>
//                   <Typography variant="h4" fontWeight="bold" gutterBottom>
//                     {candidate.name}
//                   </Typography>
//                   <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
//                     {candidate.email}
//                   </Typography>
//                   <Typography variant="body2" sx={{ opacity: 0.8 }}>
//                     {candidate.interviews.length} Scheduled Interview{candidate.interviews.length !== 1 ? "s" : ""}
//                   </Typography>
//                 </Grid>
//               </Grid>
//             </Box>

//             <Container maxWidth="lg" sx={{ p: 4 }}>
//               <Typography variant="h6" gutterBottom color="primary" fontWeight="bold" sx={{ mb: 3 }}>
//                 Interview Schedule
//               </Typography>

//               <List disablePadding>
//                 {candidate.interviews.map((interview, index) => (
//                   <React.Fragment key={interview.id}>
//                     {index > 0 && <Divider sx={{ my: 2 }} />}
//                     <Card elevation={1} sx={{ mb: 2, borderRadius: 2 }}>
//                       <CardContent sx={{ p: 3 }}>
//                         <Grid container spacing={3} alignItems="center">
//                           <Grid item xs={12} md={8}>
//                             <Typography variant="h6" fontWeight="600" gutterBottom sx={{ color: "text.primary" }}>
//                               {interview.job_title}
//                             </Typography>
//                             <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 2 }}>
//                               <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                                 <Business fontSize="small" color="action" />
//                                 <Typography variant="body2" color="text.secondary" fontWeight="500">
//                                   {interview.interview_mode}
//                                 </Typography>
//                               </Box>
//                               <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                                 <Event fontSize="small" color="action" />
//                                 <Typography variant="body2" color="text.secondary" fontWeight="500">
//                                   {new Date(`${interview.interview_date}T${interview.interview_time}`).toLocaleString()}
//                                 </Typography>
//                               </Box>
//                             </Box>
//                             <Chip
//                               label={interview.status}
//                               color={interview.status.toLowerCase() === "scheduled" ? "success" : "warning"}
//                               size="small"
//                               sx={{ fontWeight: 600 }}
//                             />
//                           </Grid>
//                           <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
//                             <Button
//                               variant="contained"
//                               startIcon={<RateReview />}
//                               onClick={() => onEvaluate(candidate, interview)}
//                               sx={{
//                                 minWidth: 140,
//                                 borderRadius: 2,
//                                 fontWeight: 600,
//                                 boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
//                                 "&:hover": {
//                                   transform: "translateY(-1px)",
//                                   boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
//                                 },
//                               }}
//                             >
//                               Evaluate
//                             </Button>
//                           </Grid>
//                         </Grid>
//                       </CardContent>
//                     </Card>
//                   </React.Fragment>
//                 ))}
//               </List>

//               <Box
//                 sx={{ display: "flex", justifyContent: "flex-end", mt: 4, pt: 3, borderTop: 1, borderColor: "divider" }}
//               >
//                 <Button variant="outlined" size="large" onClick={onClose} sx={{ minWidth: 120 }}>
//                   Close
//                 </Button>
//               </Box>
//             </Container>
//           </Paper>
//         </Box>
//       </Fade>
//     </Modal>
//   )
// }

// function EvaluationForm({ interviewDetails, open, onClose }) {
//   const [formData, setFormData] = useState({
//     recommendation: "",
//     technical_skills_rating: 0,
//     communication_skills_rating: 0,
//     cultural_fit_rating: 0,
//     strengths: "",
//     weaknesses: "",
//     overall_comments: "",
//   })
//   const [isSubmitting, setIsSubmitting] = useState(false)
//   const [submitError, setSubmitError] = useState(null)

//   useEffect(() => {
//     if (open) {
//       setFormData({
//         recommendation: "",
//         technical_skills_rating: 0,
//         communication_skills_rating: 0,
//         cultural_fit_rating: 0,
//         strengths: "",
//         weaknesses: "",
//         overall_comments: "",
//       })
//       setIsSubmitting(false)
//       setSubmitError(null)
//     }
//   }, [open])

//   const handleChange = (e) => {
//     const { name, value } = e.target
//     setFormData((prev) => ({ ...prev, [name]: value }))
//   }

//   const handleRatingChange = (field, value) => {
//     setFormData((prev) => ({ ...prev, [field]: value !== null ? Math.round(value * 2) : 0 }))
//   }

//   const handleSubmit = async (e) => {
//     e.preventDefault()
//     setIsSubmitting(true)
//     setSubmitError(null)

//     const token = localStorage.getItem("access_token")
//     if (!token) {
//       setSubmitError("Authentication token not found. Please log in again.")
//       setIsSubmitting(false)
//       return
//     }

//     try {
//       const response = await fetch(
//         `http://localhost:8000/api/interviews/${interviewDetails.interviewId}/submit-feedback/`,
//         {
//           method: "POST",
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json",
//           },
//           body: JSON.stringify(formData),
//         },
//       )

//       if (!response.ok) {
//         const errorData = await response.json()
//         throw new Error(errorData.detail || `Failed to submit feedback. Status: ${response.status}`)
//       }

//       const result = await response.json()
//       alert(result.message || `Feedback for ${interviewDetails.name} submitted successfully!`)
//       onClose()
//     } catch (error) {
//       console.error("Failed to submit evaluation:", error)
//       setSubmitError(error.message)
//     } finally {
//       setIsSubmitting(false)
//     }
//   }

//   if (!interviewDetails) return null

//   const evaluatorInfo = {
//     name: "Jane Doe",
//     title: "Hiring Manager",
//     evaluationDate: new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
//   }

//   const modalStyle = {
//     position: "absolute",
//     top: "50%",
//     left: "50%",
//     transform: "translate(-50%, -50%)",
//     width: "90%",
//     maxWidth: "900px",
//     bgcolor: "background.paper",
//     borderRadius: 3,
//     maxHeight: "95vh",
//     overflow: "hidden",
//     border: "none",
//     outline: "none",
//   }

//   return (
//     <Modal open={open} onClose={onClose} closeAfterTransition>
//       <Fade in={open}>
//         <Box sx={modalStyle}>
//           <Paper elevation={0} sx={{ borderRadius: 3, overflow: "hidden", height: "95vh" }}>
//             <Box
//               sx={{
//                 background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
//                 color: "white",
//                 p: 3,
//               }}
//             >
//               <Grid container spacing={2} alignItems="center">
//                 <Grid item>
//                   <Avatar
//                     src={interviewDetails.avatar}
//                     sx={{ width: 60, height: 60, border: "3px solid rgba(255,255,255,0.3)" }}
//                   />
//                 </Grid>
//                 <Grid item xs>
//                   <Typography variant="h5" fontWeight="bold" gutterBottom>
//                     Interview Evaluation
//                   </Typography>
//                   <Typography variant="body1" sx={{ opacity: 0.9, mb: 1 }}>
//                     {interviewDetails.name} • {interviewDetails.jobTitle}
//                   </Typography>
//                   <Box sx={{ display: "flex", gap: 2, opacity: 0.8 }}>
//                     <Typography variant="body2">
//                       📅 {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
//                     </Typography>
//                     <Typography variant="body2">👤 Jane Doe</Typography>
//                   </Box>
//                 </Grid>
//               </Grid>
//             </Box>

//             <Box
//               sx={{
//                 p: 3,
//                 height: "calc(95vh - 140px)",
//                 overflowY: "auto",
//                 "&::-webkit-scrollbar": { display: "none" },
//                 scrollbarWidth: "none",
//               }}
//             >
//               <Box component="form" onSubmit={handleSubmit}>
//                 <Grid container spacing={3}>
//                   <Grid item xs={12}>
//                     <Card elevation={1} sx={{ p: 3, mb: 3 }}>
//                       <CardContent>
//                         <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
//                           <RateReview sx={{ mr: 1, verticalAlign: "middle" }} />
//                           Interview Evaluation
//                         </Typography>
//                         <Divider sx={{ mb: 3 }} />

//                         <Grid container spacing={3} sx={{ mb: 4 }}>
//                           <Grid item xs={12}>
//                             <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="text.secondary">
//                               Candidate Information
//                             </Typography>
//                           </Grid>
//                           <Grid item xs={12} md={4}>
//                             <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                               <Email color="action" />
//                               <Typography variant="body2">{interviewDetails.email}</Typography>
//                             </Box>
//                           </Grid>
//                           <Grid item xs={12} md={4}>
//                             <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                               <Work color="action" />
//                               <Typography variant="body2">{interviewDetails.department}</Typography>
//                             </Box>
//                           </Grid>
//                           <Grid item xs={12} md={4}>
//                             <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                               <Event color="action" />
//                               <Typography variant="body2">
//                                 {new Date(interviewDetails.interviewDate).toLocaleDateString()}
//                               </Typography>
//                             </Box>
//                           </Grid>
//                         </Grid>

//                         <Divider sx={{ mb: 3 }} />

//                         <Grid container spacing={3}>
//                           <Grid item xs={12}>
//                             <FormControl fullWidth required>
//                               <InputLabel>Final Recommendation</InputLabel>
//                               <Select
//                                 name="recommendation"
//                                 value={formData.recommendation}
//                                 label="Final Recommendation"
//                                 onChange={handleChange}
//                               >
//                                 <MenuItem value="HIRE">
//                                   <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                                     <CheckCircle color="success" />
//                                     Strong Hire
//                                   </Box>
//                                 </MenuItem>
//                                 <MenuItem value="CONSIDER">
//                                   <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                                     <RateReview color="warning" />
//                                     Consider
//                                   </Box>
//                                 </MenuItem>
//                                 <MenuItem value="NO_HIRE">
//                                   <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                                     <Person color="error" />
//                                     No Hire
//                                   </Box>
//                                 </MenuItem>
//                               </Select>
//                             </FormControl>
//                           </Grid>

//                           {[
//                             { field: "technical_skills_rating", label: "Technical Skills", icon: "💻" },
//                             { field: "communication_skills_rating", label: "Communication", icon: "💬" },
//                             { field: "cultural_fit_rating", label: "Cultural Fit", icon: "🤝" },
//                           ].map((rating) => (
//                             <Grid item xs={12} sm={4} key={rating.field}>
//                               <Card variant="outlined" sx={{ p: 2, textAlign: "center" }}>
//                                 <Typography variant="h6" gutterBottom>
//                                   {rating.icon}
//                                 </Typography>
//                                 <Typography variant="body1" fontWeight="medium" gutterBottom>
//                                   {rating.label}
//                                 </Typography>
//                                 <Rating
//                                   name={rating.field}
//                                   value={formData[rating.field] / 2}
//                                   precision={0.5}
//                                   onChange={(e, val) => handleRatingChange(rating.field, val)}
//                                   size="large"
//                                   sx={{ mb: 1 }}
//                                   max={10}
//                                 />
//                                 <Typography variant="body2" color="text.secondary">
//                                   {formData[rating.field]}/10
//                                 </Typography>
//                               </Card>
//                             </Grid>
//                           ))}

//                           <Grid item xs={12} md={6}>
//                             <TextField
//                               name="strengths"
//                               fullWidth
//                               multiline
//                               rows={3}
//                               label="Key Strengths"
//                               placeholder="What impressed you most about this candidate?"
//                               value={formData.strengths}
//                               onChange={handleChange}
//                               required
//                               variant="outlined"
//                             />
//                           </Grid>
//                           <Grid item xs={12} md={6}>
//                             <TextField
//                               name="weaknesses"
//                               fullWidth
//                               multiline
//                               rows={3}
//                               label="Areas for Improvement"
//                               placeholder="What areas could the candidate develop?"
//                               value={formData.weaknesses}
//                               onChange={handleChange}
//                               required
//                               variant="outlined"
//                             />
//                           </Grid>
//                           <Grid item xs={12}>
//                             <TextField
//                               name="overall_comments"
//                               fullWidth
//                               multiline
//                               rows={3}
//                               label="Overall Assessment"
//                               placeholder="Provide your comprehensive evaluation and any additional insights..."
//                               value={formData.overall_comments}
//                               onChange={handleChange}
//                               required
//                               variant="outlined"
//                             />
//                           </Grid>
//                         </Grid>
//                       </CardContent>
//                     </Card>
//                   </Grid>
//                 </Grid>

//                 {submitError && (
//                   <Alert severity="error" sx={{ mt: 2 }}>
//                     {submitError}
//                   </Alert>
//                 )}

//                 <Box
//                   sx={{
//                     display: "flex",
//                     gap: 2,
//                     justifyContent: "flex-end",
//                     mt: 3,
//                     pt: 2,
//                     borderTop: 1,
//                     borderColor: "divider",
//                   }}
//                 >
//                   <Button
//                     variant="outlined"
//                     size="large"
//                     onClick={onClose}
//                     disabled={isSubmitting}
//                     sx={{ minWidth: 120 }}
//                   >
//                     Cancel
//                   </Button>
//                   <Button
//                     type="submit"
//                     variant="contained"
//                     size="large"
//                     startIcon={!isSubmitting && <CheckCircle />}
//                     disabled={isSubmitting}
//                     sx={{ minWidth: 160 }}
//                   >
//                     {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Submit Evaluation"}
//                   </Button>
//                 </Box>
//               </Box>
//             </Box>
//           </Paper>
//         </Box>
//       </Fade>
//     </Modal>
//   )
// }

// function JobDetailsView({ candidate, onBack, onEvaluate }) {
//   return (
//     <Box>
//       <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 2 }}>
//         <Button variant="outlined" startIcon={<ArrowBack />} onClick={onBack} sx={{ borderRadius: 2 }}>
//           Back to Candidates
//         </Button>
//         {/* <Typography variant="h5" fontWeight="bold" color="primary">
//           {candidate.name}
//         </Typography> */}
//       </Box>

//       <Card elevation={1} sx={{ mb: 4, borderRadius: 2 }}>
//         <CardContent sx={{ p: 4 }}>
//           <Grid container spacing={3} alignItems="center">
//             <Grid item>
//               <Avatar
//                 src={candidate.avatar}
//                 sx={{ width: 80, height: 80, border: "3px solid", borderColor: "primary.light" }}
//               />
//             </Grid>
//             <Grid item xs>
//               <Typography variant="h5" fontWeight="bold" gutterBottom>
//                 {candidate.name}
//               </Typography>
//               <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
//                 {candidate.email}
//               </Typography>
//               <Chip
//                 label={`${candidate.interviews.length} Scheduled Interview${candidate.interviews.length !== 1 ? "s" : ""}`}
//                 color="primary"
//                 variant="outlined"
//                 sx={{ fontWeight: 600 }}
//               />
//             </Grid>
//           </Grid>
//         </CardContent>
//       </Card>
// {/* 
//       <Typography variant="h6" gutterBottom color="primary" fontWeight="bold" sx={{ mb: 3 }}>
//         Interview Schedule
//       </Typography> */}

//       <Grid container spacing={3}>
//         {candidate.interviews.map((interview) => (
//           <Grid item xs={12} key={interview.id}>
//             <Card elevation={1} sx={{ borderRadius: 2 }}>
//               <CardContent sx={{ p: 3 }}>
//                 <Grid container spacing={3} alignItems="center">
//                   <Grid item xs={12} md={8}>
//                     <Typography variant="h6" fontWeight="600" gutterBottom sx={{ color: "text.primary" }}>
//                       {interview.job_title}
//                     </Typography>
//                     <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 2 }}>
//                       <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                         <Business fontSize="small" color="action" />
//                         <Typography variant="body2" color="text.secondary" fontWeight="500">
//                           {interview.interview_mode}
//                         </Typography>
//                       </Box>
//                       <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                         <Event fontSize="small" color="action" />
//                         <Typography variant="body2" color="text.secondary" fontWeight="500">
//                           {new Date(`${interview.interview_date}T${interview.interview_time}`).toLocaleString()}
//                         </Typography>
//                       </Box>
//                     </Box>
//                     <Chip
//                       label={interview.status}
//                       color={interview.status.toLowerCase() === "scheduled" ? "success" : "warning"}
//                       size="small"
//                       sx={{ fontWeight: 600 }}
//                     />
//                   </Grid>
//                   <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
//                     <Button
//                       variant="contained"
//                       startIcon={<RateReview />}
//                       onClick={() => onEvaluate(candidate, interview)}
//                       sx={{
//                         minWidth: 140,
//                         borderRadius: 2,
//                         fontWeight: 600,
//                         boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
//                         "&:hover": {
//                           transform: "translateY(-1px)",
//                           boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
//                         },
//                       }}
//                     >
//                       Evaluate
//                     </Button>
//                   </Grid>
//                 </Grid>
//               </CardContent>
//             </Card>
//           </Grid>
//         ))}
//       </Grid>
//     </Box>
//   )
// }

// export default function CandidateInterviewList() {
//   const [candidates, setCandidates] = useState([])
//   const [loading, setLoading] = useState(true)
//   const [error, setError] = useState(null)
//   const [modalOpen, setModalOpen] = useState(false)
//   const [selectedInterview, setSelectedInterview] = useState(null)
//   const [mobileOpen, setMobileOpen] = useState(false)
//   const [searchTerm, setSearchTerm] = useState("")
//   const [filterStatus, setFilterStatus] = useState("all")
//   const [jobListOpen, setJobListOpen] = useState(false)
//   const [selectedCandidate, setSelectedCandidate] = useState(null)
//   const [selectedCandidateDetails, setSelectedCandidateDetails] = useState(null)
//   const [showJobDetails, setShowJobDetails] = useState(false)

//   useEffect(() => {
//     const fetchAndProcessInterviews = async () => {
//       const token = localStorage.getItem("access_token")

//       if (!token) {
//         setError("Authentication token not found. Please log in.")
//         setLoading(false)
//         return
//       }

//       try {
//         const response = await fetch("http://127.0.0.1:8000/api/interviews/", {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json",
//           },
//         })

//         if (!response.ok) {
//           if (response.status === 401) {
//             throw new Error("Unauthorized: Your session may have expired. Please log in again.")
//           }
//           throw new Error(`HTTP error! status: ${response.status}`)
//         }
//         const data = await response.json()

//         const filtered = data.filter(
//           (interview) =>
//             interview.status.toLowerCase() === "scheduled" || interview.status.toLowerCase() === "rescheduled",
//         )

//         const groupedByCandidate = filtered.reduce((acc, interview) => {
//           const { candidate_name } = interview
//           if (!acc[candidate_name]) {
//             acc[candidate_name] = {
//               id: candidate_name.replace(/\s+/g, "-").toLowerCase(),
//               name: candidate_name,
//               email: `${candidate_name.replace(/\s+/g, ".").toLowerCase()}@example.com`,
//               avatar: `https://i.pravatar.cc/150?u=${encodeURIComponent(candidate_name)}`,
//               interviews: [],
//             }
//           }
//           acc[candidate_name].interviews.push(interview)
//           return acc
//         }, {})

//         setCandidates(Object.values(groupedByCandidate))
//       } catch (e) {
//         setError(e.message)
//         console.error("Failed to fetch interviews:", e)
//       } finally {
//         setLoading(false)
//       }
//     }

//     fetchAndProcessInterviews()
//   }, [])

//   const handleDrawerToggle = () => {
//     setMobileOpen(!mobileOpen)
//   }

//   const handleViewDetails = (candidate) => {
//     setSelectedCandidateDetails(candidate)
//     setShowJobDetails(true)
//   }

//   const handleBackToList = () => {
//     setShowJobDetails(false)
//     setSelectedCandidateDetails(null)
//   }

//   const handleEvaluateClick = (candidate, interview) => {
//     const detailsForModal = {
//       name: candidate.name,
//       email: candidate.email,
//       avatar: candidate.avatar,
//       interviewId: interview.id,
//       jobTitle: interview.job_title,
//       department: interview.interview_mode || "General",
//       interviewDate: `${interview.interview_date}T${interview.interview_time}:00Z`,
//     }
//     setSelectedInterview(detailsForModal)
//     setModalOpen(true)
//     setJobListOpen(false)
//   }

//   const handleCloseModal = () => {
//     setModalOpen(false)
//     setSelectedInterview(null)
//   }

//   const handleCloseJobList = () => {
//     setJobListOpen(false)
//     setSelectedCandidate(null)
//   }

//   const filteredCandidates = candidates.filter((candidate) => {
//     const matchesSearch =
//       candidate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       candidate.email.toLowerCase().includes(searchTerm.toLowerCase())
//     return matchesSearch
//   })

//   const renderContent = () => {
//     if (loading) {
//       return (
//         <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
//           <CircularProgress size={40} />
//         </Box>
//       )
//     }

//     if (error) {
//       return (
//         <Paper elevation={1} sx={{ p: 6, textAlign: "center", borderRadius: 2 }}>
//           <Typography variant="h6" color="text.secondary" gutterBottom>
//             Error Fetching Interviews
//           </Typography>
//           <Typography variant="body2" color="text.secondary">
//             {error}
//           </Typography>
//         </Paper>
//       )
//     }

//     if (showJobDetails && selectedCandidateDetails) {
//       return (
//         <JobDetailsView
//           candidate={selectedCandidateDetails}
//           onBack={handleBackToList}
//           onEvaluate={handleEvaluateClick}
//         />
//       )
//     }

//     if (filteredCandidates.length === 0) {
//       return (
//         <Paper elevation={1} sx={{ p: 6, textAlign: "center", borderRadius: 2 }}>
//           <Typography variant="h6" color="text.secondary" gutterBottom>
//             No candidates found
//           </Typography>
//           <Typography variant="body2" color="text.secondary">
//             {searchTerm || filterStatus !== "all" ? "Try adjusting your filters" : "No interviews scheduled"}
//           </Typography>
//         </Paper>
//       )
//     }

//     return (
//       <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2 }}>
//         <Table>
//           <TableHead>
//             <TableRow sx={{ bgcolor: "grey.50" }}>
//               <TableCell sx={{ fontWeight: "bold", py: 2 }}>Candidate</TableCell>
//               <TableCell sx={{ fontWeight: "bold", py: 2 }}>Interviews</TableCell>
//               <TableCell sx={{ fontWeight: "bold", py: 2 }}>Actions</TableCell>
//             </TableRow>
//           </TableHead>
//           <TableBody>
//             {filteredCandidates.map((candidate) => (
//               <TableRow
//                 key={candidate.id}
//                 sx={{
//                   "&:hover": {
//                     bgcolor: "grey.50",
//                   },
//                 }}
//               >
//                 <TableCell>
//                   <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                     <Avatar
//                       src={candidate.avatar}
//                       sx={{
//                         width: 48,
//                         height: 48,
//                         border: "2px solid",
//                         borderColor: "primary.light",
//                       }}
//                     />
//                     <Typography variant="body1" fontWeight="600">
//                       {candidate.name}
//                     </Typography>
//                   </Box>
//                 </TableCell>
//                 <TableCell>
//                   <Chip
//                     label={`${candidate.interviews.length} interview${candidate.interviews.length !== 1 ? "s" : ""}`}
//                     color="primary"
//                     variant="outlined"
//                     size="small"
//                     sx={{ fontWeight: 600 }}
//                   />
//                 </TableCell>
//                 <TableCell>
//                   <Button
//                     variant="contained"
//                     onClick={() => handleViewDetails(candidate)}
//                     sx={{
//                       minWidth: 120,
//                       borderRadius: 2,
//                       fontWeight: 600,
//                       boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
//                       "&:hover": {
//                         transform: "translateY(-1px)",
//                         boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
//                       },
//                     }}
//                   >
//                     View Details
//                   </Button>
//                 </TableCell>
//               </TableRow>
//             ))}
//           </TableBody>
//         </Table>
//       </TableContainer>
//     )
//   }

//   return (
//     <Layout title="Evaluation">
//       <ThemeProvider theme={theme}>
//         <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
//           <Container maxWidth="xl" sx={{ py: 4, px: 2 }}>
//             {/* <Typography
//               variant="h4"
//               component="h1"
//               gutterBottom
//               sx={{
//                 fontWeight: 700,
//                 color: "text.primary",
//                 letterSpacing: "-0.025em",
//                 mb: 1,
//               }}
//             >
//               Scheduled Interviews
//             </Typography> */}
//             {renderContent()}
//           </Container>
//         </Box>

//         {selectedInterview && (
//           <EvaluationForm open={modalOpen} onClose={handleCloseModal} interviewDetails={selectedInterview} />
//         )}
//       </ThemeProvider>
//     </Layout>
//   )
// }


// import React, { useState, useEffect } from "react";
// import {
//   Container,
//   Paper,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   Rating,
//   TextField,
//   Button,
//   Box,
//   Avatar,
//   Divider,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   List,
//   ListItem,
//   ListItemAvatar,
//   ListItemText,
//   Accordion,
//   AccordionSummary,
//   AccordionDetails,
//   Chip,
//   Modal,
//   CircularProgress,
//   Alert,
// } from "@mui/material";
// import {
//   Person,
//   Email,
//   Work,
//   TrendingUp,
//   CheckCircle,
//   ExpandMore,
//   Event,
//   Business,
//   RateReview,
// } from "@mui/icons-material";
// import { createTheme, ThemeProvider, styled } from '@mui/material/styles';
// import Layout from "../common/Layout";


// function EvaluationForm({ interviewDetails, open, onClose }) {
//   const [formData, setFormData] = useState({
//     recommendation: "",
//     technical_skills_rating: 0,
//     communication_skills_rating: 0,
//     cultural_fit_rating: 0,
//     strengths: "",
//     weaknesses: "",
//     overall_comments: "",
//   });
//   const [overallRating, setOverallRating] = useState(0);
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [submitError, setSubmitError] = useState(null);

//   useEffect(() => {
//     const { technical_skills_rating, communication_skills_rating, cultural_fit_rating } = formData;
//     const ratings = [technical_skills_rating, communication_skills_rating, cultural_fit_rating].filter(
//       (r) => r > 0
//     );

//     if (ratings.length > 0) {
//       const avg = ratings.reduce((sum, current) => sum + current, 0) / ratings.length;
//       setOverallRating(avg.toFixed(1));
//     } else {
//       setOverallRating(0);
//     }
//   }, [formData.technical_skills_rating, formData.communication_skills_rating, formData.cultural_fit_rating]);

//   useEffect(() => {
//     if (open) {
//       setFormData({
//         recommendation: "",
//         technical_skills_rating: 0,
//         communication_skills_rating: 0,
//         cultural_fit_rating: 0,
//         strengths: "",
//         weaknesses: "",
//         overall_comments: "",
//       });
//       setIsSubmitting(false);
//       setSubmitError(null);
//     }
//   }, [open]);

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({ ...prev, [name]: value }));
//   };

//   const handleRatingChange = (field, value) => {
//     setFormData((prev) => ({ ...prev, [field]: value !== null ? Math.round(value * 2) : 0 }));
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setIsSubmitting(true);
//     setSubmitError(null);

//     const token = localStorage.getItem('access_token');
//     if (!token) {
//       setSubmitError("Authentication token not found. Please log in again.");
//       setIsSubmitting(false);
//       return;
//     }

//     try {
//       const response = await fetch(
//         `http://localhost:8000/api/interviews/${interviewDetails.interviewId}/submit-feedback/`,
//         {
//           method: 'POST',
//           headers: {
//             'Authorization': `Bearer ${token}`,
//             'Content-Type': 'application/json',
//           },
//           body: JSON.stringify(formData),
//         }
//       );

//       if (!response.ok) {
//         const errorData = await response.json();
//         throw new Error(errorData.detail || `Failed to submit feedback. Status: ${response.status}`);
//       }

//       const result = await response.json();
//       alert(result.message || `Feedback for ${interviewDetails.name} submitted successfully!`);
//       onClose();

//     } catch (error) {
//       console.error("Failed to submit evaluation:", error);
//       setSubmitError(error.message);
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   if (!interviewDetails) return null;

//   const evaluatorInfo = {
//     name: 'Jane Doe',
//     title: 'Hiring Manager',
//     evaluationDate: new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
//   };

//   const modalStyle = {
//     position: 'absolute',
//     top: '50%',
//     left: '50%',
//     transform: 'translate(-50%, -50%)',
//     width: '90%',
//     maxWidth: '1000px',
//     bgcolor: 'background.paper',
//     boxShadow: 24,
//     p: 1,
//     borderRadius: 2,
//     maxHeight: '90vh',
//     overflowY: 'auto'
//   };

//   return (
//     <Modal open={open} onClose={onClose}>
//       <Box sx={modalStyle}>
//         <Container maxWidth="lg" sx={{ py: 3 }}>
//           <Paper
//             elevation={3}
//             sx={{ p: 3, mb: 4, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}
//           >
//             <Box display="flex" gap={3}>
//               <Avatar src={interviewDetails.avatar} sx={{ width: 80, height: 80, border: "3px solid white" }} />
//               <Box flex={1}>
//                 <Typography variant="h4" fontWeight="bold" gutterBottom>
//                   Interview Feedback
//                 </Typography>
//                 <Typography variant="h6" sx={{ opacity: 0.9 }}>
//                   {interviewDetails.name} - {interviewDetails.jobTitle}
//                 </Typography>
//                 <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
//                   Evaluation Date: {evaluatorInfo.evaluationDate} | Evaluator: {evaluatorInfo.name}
//                 </Typography>
//               </Box>
//               <Box textAlign="center">
//                 <Typography variant="h3" fontWeight="bold">
//                   {overallRating}
//                 </Typography>
//                 <Typography variant="body2">Overall Score / 10</Typography>
//               </Box>
//             </Box>
//           </Paper>

//           <Grid container spacing={4}>
//             <Grid item xs={12} md={4}>
//               <Card elevation={2}>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
//                     <Person sx={{ mr: 1, verticalAlign: "middle" }} />
//                     Candidate Information
//                   </Typography>
//                   <Divider sx={{ mb: 2 }} />
//                   <Box display="flex" alignItems="center" mb={2}>
//                     <Email sx={{ mr: 2, color: "text.secondary" }} />
//                     <Typography variant="body2">{interviewDetails.email}</Typography>
//                   </Box>
//                   {/* <Box display="flex" alignItems="center" mb={2}>
//                     <Phone sx={{ mr: 2, color: "text.secondary" }} />
//                     <Typography variant="body2">{interviewDetails.phone}</Typography>
//                   </Box> */}
//                   <Box display="flex" alignItems="center" mb={2}>
//                     <Work sx={{ mr: 2, color: "text.secondary" }} />
//                     <Typography variant="body2">{interviewDetails.department}</Typography>
//                   </Box>
//                 </CardContent>
//               </Card>
//             </Grid>

//             <Grid item xs={12} md={8}>
//               <Card elevation={2}>
//                 <CardContent>
//                   <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
//                     <TrendingUp sx={{ mr: 1, verticalAlign: "middle" }} />
//                     Evaluation Details
//                   </Typography>
//                   <Divider sx={{ mb: 3 }} />

//                   <Box component="form" onSubmit={handleSubmit}>
//                     <Grid container spacing={3}>
//                       <Grid item xs={12}>
//                         <FormControl fullWidth required>
//                           <InputLabel>Recommendation</InputLabel>
//                           <Select name="recommendation" value={formData.recommendation} label="Recommendation" onChange={handleChange}>
//                             <MenuItem value="HIRE">Hire</MenuItem>
//                             <MenuItem value="CONSIDER">Consider</MenuItem>
//                             <MenuItem value="NO_HIRE">No Hire</MenuItem>
//                           </Select>
//                         </FormControl>
//                       </Grid>

//                       <Grid item xs={12} sm={4}>
//                         <Typography variant="body1" fontWeight="medium" gutterBottom>Technical Skills</Typography>
//                         <Rating name="technical_skills_rating" value={formData.technical_skills_rating / 2} precision={0.5} onChange={(e, val) => handleRatingChange("technical_skills_rating", val)} size="large" />
//                       </Grid>
//                       <Grid item xs={12} sm={4}>
//                         <Typography variant="body1" fontWeight="medium" gutterBottom>Communication</Typography>
//                         <Rating name="communication_skills_rating" value={formData.communication_skills_rating / 2} precision={0.5} onChange={(e, val) => handleRatingChange("communication_skills_rating", val)} size="large" />
//                       </Grid>
//                       <Grid item xs={12} sm={4}>
//                         <Typography variant="body1" fontWeight="medium" gutterBottom>Cultural Fit</Typography>
//                         <Rating name="cultural_fit_rating" value={formData.cultural_fit_rating / 2} precision={0.5} onChange={(e, val) => handleRatingChange("cultural_fit_rating", val)} size="large" />
//                       </Grid>

//                       <Grid item xs={12}><TextField name="strengths" fullWidth multiline rows={3} label="Strengths" value={formData.strengths} onChange={handleChange} required /></Grid>
//                       <Grid item xs={12}><TextField name="weaknesses" fullWidth multiline rows={3} label="Weaknesses" value={formData.weaknesses} onChange={handleChange} required /></Grid>
//                       <Grid item xs={12}><TextField name="overall_comments" fullWidth multiline rows={4} label="Overall Comments" value={formData.overall_comments} onChange={handleChange} required /></Grid>
                      
//                       {submitError && (
//                           <Grid item xs={12}>
//                               <Alert severity="error">{submitError}</Alert>
//                           </Grid>
//                       )}

//                       <Grid item xs={12}>
//                         <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
//                           <Button variant="outlined" size="large" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
//                           <Button type="submit" variant="contained" size="large" startIcon={!isSubmitting && <CheckCircle />} disabled={isSubmitting}>
//                             {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Submit Evaluation"}
//                           </Button>
//                         </Box>
//                       </Grid>
//                     </Grid>
//                   </Box>
//                 </CardContent>
//               </Card>
//             </Grid>
//           </Grid>
//         </Container>
//       </Box>
//     </Modal>
//   );
// }

// const theme = createTheme({
//   palette: {
//     background: {
//       default: '#f4f6f8',
//     },
//   },
//   components: {
//     MuiAccordion: {
//       styleOverrides: {
//         root: {
//           '&:before': {
//             display: 'none',
//           },
//           '&$expanded': {
//             margin: 'auto',
//           },
//         },
//       },
//     },
//   },
// });

// const StyledAccordion = styled(Accordion)(({ theme }) => ({
//   transition: theme.transitions.create(['box-shadow', 'margin'], {
//     duration: theme.transitions.duration.short,
//   }),
//   '&.Mui-expanded': {
//     boxShadow: theme.shadows[4],
//     margin: `${theme.spacing(1, 0)} !important`,
//   },
// }));

// export default function CandidateInterviewList() {
//   const [candidates, setCandidates] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [expanded, setExpanded] = useState(false);
//   const [modalOpen, setModalOpen] = useState(false);
//   const [selectedInterview, setSelectedInterview] = useState(null);

//   useEffect(() => {
//     const fetchAndProcessInterviews = async () => {
//       const token = localStorage.getItem('access_token');

//       if (!token) {
//         setError("Authentication token not found. Please log in.");
//         setLoading(false);
//         return;
//       }

//       try {
//         const response = await fetch("http://127.0.0.1:8000/api/interviews/", {
//           headers: {
//             'Authorization': `Bearer ${token}`,
//             'Content-Type': 'application/json',
//           },
//         });

//         if (!response.ok) {
//           if (response.status === 401) {
//             throw new Error("Unauthorized: Your session may have expired. Please log in again.");
//           }
//           throw new Error(`HTTP error! status: ${response.status}`);
//         }
//         const data = await response.json();

//         const filtered = data.filter(
//           (interview) =>
//             interview.status.toLowerCase() === 'scheduled' ||
//             interview.status.toLowerCase() === 'rescheduled'
//         );

//         const groupedByCandidate = filtered.reduce((acc, interview) => {
//           const { candidate_name } = interview;
//           if (!acc[candidate_name]) {
//             acc[candidate_name] = {
//               id: candidate_name.replace(/\s+/g, '-').toLowerCase(),
//               name: candidate_name,
//               email: `${candidate_name.replace(/\s+/g, '.').toLowerCase()}@example.com`,
//               avatar: `https://i.pravatar.cc/150?u=${encodeURIComponent(candidate_name)}`,
//               interviews: [],
//             };
//           }
//           acc[candidate_name].interviews.push(interview);
//           return acc;
//         }, {});

//         setCandidates(Object.values(groupedByCandidate));
//       } catch (e) {
//         setError(e.message);
//         console.error("Failed to fetch interviews:", e);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchAndProcessInterviews();
//   }, []);

//   const handleAccordionChange = (panel) => (event, isExpanded) => {
//     setExpanded(isExpanded ? panel : false);
//   };

//   const handleEvaluateClick = (candidate, interview) => {
//     const detailsForModal = {
//       name: candidate.name,
//       email: candidate.email,
//       avatar: candidate.avatar,
//       interviewId: interview.id,
//       jobTitle: interview.job_title,
//       department: interview.interview_mode || "General",
//       interviewDate: `${interview.interview_date}T${interview.interview_time}:00Z`,
//     };
//     setSelectedInterview(detailsForModal);
//     setModalOpen(true);
//   };

//   const handleCloseModal = () => {
//     setModalOpen(false);
//     setSelectedInterview(null);
//   };

//   const renderContent = () => {
//     if (loading) {
//       return (
//         <Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 8 }}>
//           <CircularProgress />
//           <Typography sx={{ ml: 2 }}>Loading interviews...</Typography>
//         </Box>
//       );
//     }

//     if (error) {
//       return (
//         <Alert severity="error" sx={{ mt: 4 }}>
//           {error}
//         </Alert>
//       );
//     }

//     if (candidates.length === 0) {
//       return (
//         <Typography variant="body1" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
//           No scheduled or rescheduled interviews found.
//         </Typography>
//       );
//     }

//     return (
//       <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
//         {candidates.map((candidate) => (
//           <StyledAccordion
//             key={candidate.id}
//             expanded={expanded === `panel-${candidate.id}`}
//             onChange={handleAccordionChange(`panel-${candidate.id}`)}
//           >
//             <AccordionSummary expandIcon={<ExpandMore />}>
//               <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
//                 <ListItemAvatar>
//                   <Avatar src={candidate.avatar} sx={{ width: 56, height: 56 }} />
//                 </ListItemAvatar>
//                 <ListItemText
//                   primary={<Typography variant="h6">{candidate.name}</Typography>}
//                   secondary={candidate.email}
//                 />
//                 <Chip
//                   label={`${candidate.interviews.length} interview(s)`}
//                   color="primary"
//                   variant="outlined"
//                   sx={{ ml: 'auto', mr: 2 }}
//                 />
//               </Box>
//             </AccordionSummary>
//             <AccordionDetails sx={{ backgroundColor: '#fafafa', p: 0 }}>
//               <List disablePadding>
//                 {candidate.interviews.map((interview) => (
//                   <React.Fragment key={interview.id}>
//                     <Divider component="li" />
//                     <ListItem sx={{ py: 2, px: 3 }}>
//                       <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
//                         <Box flexGrow={1}>
//                           <Typography variant="body1" fontWeight="500">{interview.job_title}</Typography>
//                           <Box display="flex" alignItems="center" color="text.secondary" mt={1} gap={2}>
//                             <Box display="flex" alignItems="center" gap={0.5}><Business fontSize="small" />{interview.interview_mode}</Box>
//                             <Box display="flex" alignItems="center" gap={0.5}><Event fontSize="small" />{new Date(`${interview.interview_date}T${interview.interview_time}`).toLocaleString()}</Box>
//                           </Box>
//                         </Box>
//                         <Button
//                           variant="contained"
//                           startIcon={<RateReview />}
//                           onClick={() => handleEvaluateClick(candidate, interview)}
//                           sx={{ mt: { xs: 2, sm: 0 } }}
//                         >
//                           Evaluate
//                         </Button>
//                       </Box>
//                     </ListItem>
//                   </React.Fragment>
//                 ))}
//               </List>
//             </AccordionDetails>
//           </StyledAccordion>
//         ))}
//       </Box>
//     );
//   };

//   return (
//     <Layout title="Evaluation">
//       <ThemeProvider theme={theme}>
//         <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
//           <Container maxWidth="md" sx={{ py: 4 }}>
//             <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="text.primary">
//               Scheduled Interviews
//             </Typography>
//             <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
//               Review and evaluate candidates scheduled for interviews.
//             </Typography>
//             {renderContent()}
//           </Container>
//         </Box>

//         {selectedInterview && (
//           <EvaluationForm
//             open={modalOpen}
//             onClose={handleCloseModal}
//             interviewDetails={selectedInterview}
//           />
//         )}
//       </ThemeProvider>
//     </Layout>
//   );
// }

// import React, { useState, useEffect } from "react";
// import {
//   Container,
//   Paper,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   Rating,
//   TextField,
//   Button,
//   Box,
//   Avatar,
//   Divider,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   List,
//   ListItem,
//   ListItemAvatar,
//   ListItemText,
//   Accordion,
//   AccordionSummary,
//   AccordionDetails,
//   Chip,
//   Modal,
//   CircularProgress,
//   Alert
// } from "@mui/material";
// import {
//   Person,
//   Email,
//   Phone,
//   Work,
//   TrendingUp,
//   CheckCircle,
//   ExpandMore,
//   Event,
//   Business,
//   RateReview,
// } from "@mui/icons-material";
// import { createTheme, ThemeProvider, styled } from '@mui/material/styles';
// import Layout from "../common/Layout";


// function EvaluationForm({ interviewDetails, open, onClose }) {
//   const [formData, setFormData] = useState({
//     recommendation: "",
//     technical_skills_rating: 0,
//     communication_skills_rating: 0,
//     cultural_fit_rating: 0,
//     strengths: "",
//     weaknesses: "",
//     overall_comments: "",
//   });

//   const [overallRating, setOverallRating] = useState(0);

//   useEffect(() => {
//     const { technical_skills_rating, communication_skills_rating, cultural_fit_rating } = formData;
//     const ratings = [technical_skills_rating, communication_skills_rating, cultural_fit_rating].filter(
//       (r) => r > 0
//     );

//     if (ratings.length > 0) {
//       const avg = ratings.reduce((sum, current) => sum + current, 0) / ratings.length;
//       setOverallRating(avg.toFixed(1));
//     } else {
//       setOverallRating(0);
//     }
//   }, [formData.technical_skills_rating, formData.communication_skills_rating, formData.cultural_fit_rating]);

//   useEffect(() => {
//     if (open) {
//       setFormData({
//         recommendation: "",
//         technical_skills_rating: 0,
//         communication_skills_rating: 0,
//         cultural_fit_rating: 0,
//         strengths: "",
//         weaknesses: "",
//         overall_comments: "",
//       });
//     }
//   }, [open]);

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({ ...prev, [name]: value }));
//   };

//   const handleRatingChange = (field, value) => {
//     setFormData((prev) => ({ ...prev, [field]: value !== null ? value * 2 : 0 }));
//   };
  
//   const handleSubmit = (e) => {
//     e.preventDefault();
//     console.log("Submitting Evaluation Data:", {
//       interviewId: interviewDetails.interviewId,
//       ...formData,
//     });
//     alert(`Feedback for ${interviewDetails.name} (${interviewDetails.jobTitle}) submitted successfully!`);
//     onClose();
//   };

//   if (!interviewDetails) return null;

//   const evaluatorInfo = {
//       name: 'Jane Doe',
//       title: 'Hiring Manager',
//       evaluationDate: new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
//   };

//   const modalStyle = {
//     position: 'absolute',
//     top: '50%',
//     left: '50%',
//     transform: 'translate(-50%, -50%)',
//     width: '90%',
//     maxWidth: '1000px',
//     bgcolor: 'background.paper',
//     boxShadow: 24,
//     p: 1,
//     borderRadius: 2,
//     maxHeight: '90vh',
//     overflowY: 'auto'
//   };

//   return (
//     <Modal open={open} onClose={onClose}>
//         <Box sx={modalStyle}>
//           <Container maxWidth="lg" sx={{ py: 3 }}>
//             <Paper
//               elevation={3}
//               sx={{ p: 3, mb: 4, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}
//             >
//               <Box display="flex"  gap={3}>
//                 <Avatar src={interviewDetails.avatar} sx={{ width: 80, height: 80, border: "3px solid white" }} />
//                 <Box flex={1}>
//                   <Typography variant="h4" fontWeight="bold" gutterBottom>
//                     Interview Feedback
//                   </Typography>
//                   <Typography variant="h6" sx={{ opacity: 0.9 }}>
//                     {interviewDetails.name} - {interviewDetails.jobTitle}
//                   </Typography>
//                   <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
//                     Evaluation Date: {evaluatorInfo.evaluationDate} | Evaluator: {evaluatorInfo.name}
//                   </Typography>
//                 </Box>
//                 <Box textAlign="center">
//                   <Typography variant="h3" fontWeight="bold">
//                     {overallRating}
//                   </Typography>
//                   <Typography variant="body2">Overall Score / 10</Typography>
//                 </Box>
//               </Box>
//             </Paper>

//             <Grid container spacing={4}>
//               <Grid item xs={12} md={4}>
//                 <Card elevation={2}>
//                   <CardContent>
//                     <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
//                       <Person sx={{ mr: 1, verticalAlign: "middle" }} />
//                       Candidate Information
//                     </Typography>
//                     <Divider sx={{ mb: 2 }} />
//                     <Box display="flex" alignItems="center" mb={2}>
//                       <Email sx={{ mr: 2, color: "text.secondary" }} />
//                       <Typography variant="body2">{interviewDetails.email}</Typography>
//                     </Box>
//                     <Box display="flex" alignItems="center" mb={2}>
//                       <Phone sx={{ mr: 2, color: "text.secondary" }} />
//                       <Typography variant="body2">{interviewDetails.phone}</Typography>
//                     </Box>
//                     <Box display="flex" alignItems="center" mb={2}>
//                       <Work sx={{ mr: 2, color: "text.secondary" }} />
//                       <Typography variant="body2">{interviewDetails.department}</Typography>
//                     </Box>
//                   </CardContent>
//                 </Card>
//               </Grid>

//               <Grid item xs={12} md={8}>
//                 <Card elevation={2}>
//                   <CardContent>
//                     <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
//                       <TrendingUp sx={{ mr: 1, verticalAlign: "middle" }} />
//                       Evaluation Details
//                     </Typography>
//                     <Divider sx={{ mb: 3 }} />

//                     <Box component="form" onSubmit={handleSubmit}>
//                       <Grid container spacing={3}>
//                         <Grid item xs={12}>
//                           <FormControl fullWidth>
//                             <InputLabel>Recommendation</InputLabel>
//                             <Select name="recommendation" value={formData.recommendation} label="Recommendation" onChange={handleChange}>
//                               <MenuItem value="HIRE">Hire</MenuItem>
//                               <MenuItem value="CONSIDER">Consider</MenuItem>
//                               <MenuItem value="NO_HIRE">No Hire</MenuItem>
//                             </Select>
//                           </FormControl>
//                         </Grid>

//                         <Grid item xs={12} sm={4}>
//                           <Typography variant="body1" fontWeight="medium" gutterBottom>Technical Skills</Typography>
//                           <Rating name="technical_skills_rating" value={formData.technical_skills_rating / 2} precision={0.5} onChange={(e, val) => handleRatingChange("technical_skills_rating", val)} size="large" />
//                         </Grid>
//                         <Grid item xs={12} sm={4}>
//                           <Typography variant="body1" fontWeight="medium" gutterBottom>Communication</Typography>
//                           <Rating name="communication_skills_rating" value={formData.communication_skills_rating / 2} precision={0.5} onChange={(e, val) => handleRatingChange("communication_skills_rating", val)} size="large" />
//                         </Grid>
//                         <Grid item xs={12} sm={4}>
//                           <Typography variant="body1" fontWeight="medium" gutterBottom>Cultural Fit</Typography>
//                           <Rating name="cultural_fit_rating" value={formData.cultural_fit_rating / 2} precision={0.5} onChange={(e, val) => handleRatingChange("cultural_fit_rating", val)} size="large" />
//                         </Grid>
                        
//                         <Grid item xs={12}><TextField name="strengths" fullWidth multiline rows={3} label="Strengths" value={formData.strengths} onChange={handleChange}/></Grid>
//                         <Grid item xs={12}><TextField name="weaknesses" fullWidth multiline rows={3} label="Weaknesses" value={formData.weaknesses} onChange={handleChange}/></Grid>
//                         <Grid item xs={12}><TextField name="overall_comments" fullWidth multiline rows={4} label="Overall Comments" value={formData.overall_comments} onChange={handleChange}/></Grid>

//                         <Grid item xs={12}>
//                           <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
//                             <Button variant="outlined" size="large" onClick={onClose}>Cancel</Button>
//                             <Button type="submit" variant="contained" size="large" startIcon={<CheckCircle />}>Submit Evaluation</Button>
//                           </Box>
//                         </Grid>
//                       </Grid>
//                     </Box>
//                   </CardContent>
//                 </Card>
//               </Grid>
//             </Grid>
//           </Container>
//         </Box>
//     </Modal>
//   );
// }

// const theme = createTheme({
//   palette: {
//     background: {
//       default: '#f4f6f8',
//     },
//   },
//   components: {
//     MuiAccordion: {
//       styleOverrides: {
//         root: {
//           '&:before': {
//             display: 'none',
//           },
//           '&$expanded': {
//             margin: 'auto',
//           },
//         },
//       },
//     },
//   },
// });

// const StyledAccordion = styled(Accordion)(({ theme }) => ({
//   transition: theme.transitions.create(['box-shadow', 'margin'], {
//     duration: theme.transitions.duration.short,
//   }),
//   '&.Mui-expanded': {
//     boxShadow: theme.shadows[4],
//     margin: `${theme.spacing(1, 0)} !important`,
//   },
// }));

// export default function CandidateInterviewList() {
//   const [candidates, setCandidates] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [expanded, setExpanded] = useState(false);
//   const [modalOpen, setModalOpen] = useState(false);
//   const [selectedInterview, setSelectedInterview] = useState(null);
  
//   useEffect(() => {
//     const fetchAndProcessInterviews = async () => {
//       const token = localStorage.getItem('access_token');

//       if (!token) {
//         setError("Authentication token not found. Please log in.");
//         setLoading(false);
//         return;
//       }
      
//       try {
//         const response = await fetch("http://127.0.0.1:8000/api/interviews/", {
//           headers: {
//             'Authorization': `Bearer ${token}`,
//             'Content-Type': 'application/json',
//           },
//         });

//         if (!response.ok) {
//            if (response.status === 401) {
//              throw new Error("Unauthorized: Your session may have expired. Please log in again.");
//           }
//           throw new Error(`HTTP error! status: ${response.status}`);
//         }
//         const data = await response.json();

//         const filtered = data.filter(
//           (interview) =>
//             interview.status.toLowerCase() === 'scheduled' ||
//             interview.status.toLowerCase() === 'rescheduled'
//         );

//         const groupedByCandidate = filtered.reduce((acc, interview) => {
//           const { candidate_name } = interview;
//           if (!acc[candidate_name]) {
//             acc[candidate_name] = {
//               id: candidate_name.replace(/\s+/g, '-').toLowerCase(),
//               name: candidate_name,
//               email: `${candidate_name.replace(/\s+/g, '.').toLowerCase()}@example.com`,
//               phone: `+1 (555) ${Math.floor(100 + Math.random() * 900)}-${Math.floor(1000 + Math.random() * 9000)}`,
//               avatar: `https://i.pravatar.cc/150?u=${encodeURIComponent(candidate_name)}`,
//               interviews: [],
//             };
//           }
//           acc[candidate_name].interviews.push(interview);
//           return acc;
//         }, {});
        
//         setCandidates(Object.values(groupedByCandidate));
//       } catch (e) {
//         setError(e.message);
//         console.error("Failed to fetch interviews:", e);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchAndProcessInterviews();
//   }, []);

//   const handleAccordionChange = (panel) => (event, isExpanded) => {
//     setExpanded(isExpanded ? panel : false);
//   };

//   const handleEvaluateClick = (candidate, interview) => {
//     const detailsForModal = {
//       name: candidate.name,
//       email: candidate.email,
//       phone: candidate.phone,
//       avatar: candidate.avatar,
//       interviewId: interview.id,
//       jobTitle: interview.job_title,
//       department: interview.interview_mode || "General",
//       interviewDate: `${interview.interview_date}T${interview.interview_time}:00Z`,
//     };
//     setSelectedInterview(detailsForModal);
//     setModalOpen(true);
//   };

//   const handleCloseModal = () => {
//     setModalOpen(false);
//     setSelectedInterview(null);
//   };

//   const renderContent = () => {
//     if (loading) {
//       return (
//         <Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 8 }}>
//           <CircularProgress />
//           <Typography sx={{ ml: 2 }}>Loading interviews...</Typography>
//         </Box>
//       );
//     }

//     if (error) {
//       return (
//         <Alert severity="error" sx={{ mt: 4 }}>
//           {error}
//         </Alert>
//       );
//     }

//     if (candidates.length === 0) {
//       return (
//         <Typography variant="body1" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
//           No scheduled or rescheduled interviews found.
//         </Typography>
//       );
//     }

//     return (
//       <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
//         {candidates.map((candidate) => (
//           <StyledAccordion
//             key={candidate.id}
//             expanded={expanded === `panel-${candidate.id}`}
//             onChange={handleAccordionChange(`panel-${candidate.id}`)}
//           >
//             <AccordionSummary expandIcon={<ExpandMore />}>
//               <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
//                 <ListItemAvatar>
//                   <Avatar src={candidate.avatar} sx={{ width: 56, height: 56 }} />
//                 </ListItemAvatar>
//                 <ListItemText
//                   primary={<Typography variant="h6">{candidate.name}</Typography>}
//                   secondary={candidate.email}
//                 />
//                 <Chip
//                   label={`${candidate.interviews.length} interview(s)`}
//                   color="primary"
//                   variant="outlined"
//                   sx={{ ml: 'auto', mr: 2 }}
//                 />
//               </Box>
//             </AccordionSummary>
//             <AccordionDetails sx={{ backgroundColor: '#fafafa', p: 0 }}>
//               <List disablePadding>
//                 {candidate.interviews.map((interview) => (
//                   <React.Fragment key={interview.id}>
//                     <Divider component="li" />
//                     <ListItem sx={{ py: 2, px: 3 }}>
//                       <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
//                         <Box flexGrow={1}>
//                           <Typography variant="body1" fontWeight="500">{interview.job_title}</Typography>
//                           <Box display="flex" alignItems="center" color="text.secondary" mt={1} gap={2}>
//                             <Box display="flex" alignItems="center" gap={0.5}><Business fontSize="small" />{interview.interview_mode}</Box>
//                             <Box display="flex" alignItems="center" gap={0.5}><Event fontSize="small" />{new Date(`${interview.interview_date}T${interview.interview_time}`).toLocaleString()}</Box>
//                           </Box>
//                         </Box>
//                         <Button
//                           variant="contained"
//                           startIcon={<RateReview />}
//                           onClick={() => handleEvaluateClick(candidate, interview)}
//                           sx={{ mt: { xs: 2, sm: 0 } }}
//                         >
//                           Evaluate
//                         </Button>
//                       </Box>
//                     </ListItem>
//                   </React.Fragment>
//                 ))}
//               </List>
//             </AccordionDetails>
//           </StyledAccordion>
//         ))}
//       </Box>
//     );
//   };

//   return (
//     <Layout title="Evaluation">
//       <ThemeProvider theme={theme}>
//         <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
//           <Container maxWidth="md" sx={{ py: 4 }}>
//             <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="text.primary">
//               Scheduled Interviews
//             </Typography>
//             <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
//               Review and evaluate candidates scheduled for interviews.
//             </Typography>
//             {renderContent()}
//           </Container>
//         </Box>

//         {selectedInterview && (
//             <EvaluationForm
//                 open={modalOpen}
//                 onClose={handleCloseModal}
//                 interviewDetails={selectedInterview}
//             />
//         )}
//       </ThemeProvider>
//     </Layout>
//   );
// }
// // import { useState, useEffect } from "react";
// // import {
// //   Container,
// //   Paper,
// //   Typography,
// //   Grid,
// //   Card,
// //   CardContent,
// //   Rating,
// //   TextField,
// //   Button,
// //   Box,
// //   Avatar,
// //   Divider,
// //   FormControl,
// //   InputLabel,
// //   Select,
// //   MenuItem,
// //   List,
// //   ListItem,
// //   ListItemAvatar,
// //   ListItemText,
// //   Accordion,
// //   AccordionSummary,
// //   AccordionDetails,
// //   Chip,
// //   Modal,
// // } from "@mui/material";
// // import {
// //   Person,
// //   Email,
// //   Phone,
// //   Work,
// //   TrendingUp,
// //   CheckCircle,
// //   ExpandMore,
// //   Event,
// //   Business,
// //   RateReview,
// // } from "@mui/icons-material";
// // import { createTheme, ThemeProvider, styled } from '@mui/material/styles';
// // import Layout from "../common/Layout"

// // // --- 1. STATIC DATA ---
// // // A list of candidates with their scheduled interviews.
// // // One candidate (Sarah Johnson) has two interviews scheduled.

// // const staticCandidates = [
// //   {
// //     id: 1,
// //     name: "Sarah Johnson",
// //     email: "sarah.johnson@example.com",
// //     phone: "+1 (555) 123-4567",
// //     avatar: "https://i.pravatar.cc/150?img=1",
// //     interviews: [
// //       {
// //         interviewId: 101,
// //         jobTitle: "Senior Frontend Developer",
// //         department: "Engineering",
// //         interviewDate: "2023-11-15T10:00:00Z",
// //         status: "Interview Scheduled",
// //       },
// //       {
// //         interviewId: 102,
// //         jobTitle: "UX Lead",
// //         department: "Design",
// //         interviewDate: "2023-11-17T14:30:00Z",
// //         status: "Interview Scheduled",
// //       },
// //     ],
// //   },
// //   {
// //     id: 2,
// //     name: "Michael Chen",
// //     email: "michael.chen@example.com",
// //     phone: "+1 (555) 987-6543",
// //     avatar: "https://i.pravatar.cc/150?img=2",
// //     interviews: [
// //       {
// //         interviewId: 201,
// //         jobTitle: "Backend Python Developer",
// //         department: "Engineering",
// //         interviewDate: "2023-11-16T11:00:00Z",
// //         status: "Interview Scheduled",
// //       },
// //     ],
// //   },
// //   {
// //     id: 3,
// //     name: "Emily Rodriguez",
// //     email: "emily.r@example.com",
// //     phone: "+1 (555) 234-5678",
// //     avatar: "https://i.pravatar.cc/150?img=3",
// //     interviews: [
// //       {
// //         interviewId: 301,
// //         jobTitle: "Product Manager",
// //         department: "Product",
// //         interviewDate: "2023-11-20T09:00:00Z",
// //         status: "Interview Scheduled",
// //       },
// //     ],
// //   },
// // ];


// // // --- 2. THE EVALUATION FORM (Adapted for Modal and Static Data) ---
// // // This is your form component, modified to accept props and work without API calls.

// // function EvaluationForm({ interviewDetails, open, onClose }) {
// //   const [formData, setFormData] = useState({
// //     recommendation: "",
// //     technical_skills_rating: 0,
// //     communication_skills_rating: 0,
// //     cultural_fit_rating: 0,
// //     strengths: "",
// //     weaknesses: "",
// //     overall_comments: "",
// //   });

// //   const [overallRating, setOverallRating] = useState(0);

// //   // Calculate overall rating whenever individual ratings change
// //   useEffect(() => {
// //     const { technical_skills_rating, communication_skills_rating, cultural_fit_rating } = formData;
// //     const ratings = [technical_skills_rating, communication_skills_rating, cultural_fit_rating].filter(
// //       (r) => r > 0
// //     );

// //     if (ratings.length > 0) {
// //       const avg = ratings.reduce((sum, current) => sum + current, 0) / ratings.length;
// //       setOverallRating(avg.toFixed(1));
// //     } else {
// //       setOverallRating(0);
// //     }
// //   }, [formData.technical_skills_rating, formData.communication_skills_rating, formData.cultural_fit_rating]);

// //   // Reset form when modal opens with new details
// //   useEffect(() => {
// //     if (open) {
// //       setFormData({
// //         recommendation: "",
// //         technical_skills_rating: 0,
// //         communication_skills_rating: 0,
// //         cultural_fit_rating: 0,
// //         strengths: "",
// //         weaknesses: "",
// //         overall_comments: "",
// //       });
// //     }
// //   }, [open]);

// //   const handleChange = (e) => {
// //     const { name, value } = e.target;
// //     setFormData((prev) => ({ ...prev, [name]: value }));
// //   };

// //   const handleRatingChange = (field, value) => {
// //     setFormData((prev) => ({ ...prev, [field]: value !== null ? value * 2 : 0 }));
// //   };
  
// //   // Static submit handler
// //   const handleSubmit = (e) => {
// //     e.preventDefault();
// //     console.log("Submitting Evaluation Data:", {
// //       interviewId: interviewDetails.interviewId,
// //       ...formData,
// //     });
// //     alert(`Feedback for ${interviewDetails.name} (${interviewDetails.jobTitle}) submitted successfully!`);
// //     onClose(); // Close the modal on successful submission
// //   };

// //   if (!interviewDetails) return null;

// //   const evaluatorInfo = {
// //       name: 'Jane Doe', // Static evaluator name
// //       title: 'Hiring Manager',
// //       evaluationDate: new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
// //   };

// //   // Style for the modal content
// //   const modalStyle = {
// //     position: 'absolute',
// //     top: '50%',
// //     left: '50%',
// //     transform: 'translate(-50%, -50%)',
// //     width: '90%',
// //     maxWidth: '1000px',
// //     bgcolor: 'background.paper',
// //     boxShadow: 24,
// //     p: 1,
// //     borderRadius: 2,
// //     maxHeight: '90vh',
// //     overflowY: 'auto'
// //   };


// //   return (
// //   <Layout title="Evaluation">
// //     <Modal open={open} onClose={onClose}>
// //         <Box sx={modalStyle}>
// //           <Container maxWidth="lg" sx={{ py: 3 }}>
// //             <Paper
// //               elevation={3}
// //               sx={{ p: 3, mb: 4, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}
// //             >
// //               <Box display="flex"  gap={3}>
// //                 <Avatar src={interviewDetails.avatar} sx={{ width: 80, height: 80, border: "3px solid white" }} />
// //                 <Box flex={1}>
// //                   <Typography variant="h4" fontWeight="bold" gutterBottom>
// //                     Interview Feedback
// //                   </Typography>
// //                   <Typography variant="h6" sx={{ opacity: 0.9 }}>
// //                     {interviewDetails.name} - {interviewDetails.jobTitle}
// //                   </Typography>
// //                   <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
// //                     Evaluation Date: {evaluatorInfo.evaluationDate} | Evaluator: {evaluatorInfo.name}
// //                   </Typography>
// //                 </Box>
// //                 <Box textAlign="center">
// //                   <Typography variant="h3" fontWeight="bold">
// //                     {overallRating}
// //                   </Typography>
// //                   <Typography variant="body2">Overall Score / 10</Typography>
// //                 </Box>
// //               </Box>
// //             </Paper>

// //             <Grid container spacing={4}>
// //               <Grid item xs={12} md={4}>
// //                 <Card elevation={2}>
// //                   <CardContent>
// //                     <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
// //                       <Person sx={{ mr: 1, verticalAlign: "middle" }} />
// //                       Candidate Information
// //                     </Typography>
// //                     <Divider sx={{ mb: 2 }} />
// //                     <Box display="flex" alignItems="center" mb={2}>
// //                       <Email sx={{ mr: 2, color: "text.secondary" }} />
// //                       <Typography variant="body2">{interviewDetails.email}</Typography>
// //                     </Box>
// //                     <Box display="flex" alignItems="center" mb={2}>
// //                       <Phone sx={{ mr: 2, color: "text.secondary" }} />
// //                       <Typography variant="body2">{interviewDetails.phone}</Typography>
// //                     </Box>
// //                     <Box display="flex" alignItems="center" mb={2}>
// //                       <Work sx={{ mr: 2, color: "text.secondary" }} />
// //                       <Typography variant="body2">{interviewDetails.department}</Typography>
// //                     </Box>
// //                   </CardContent>
// //                 </Card>
// //               </Grid>

// //               <Grid item xs={12} md={8}>
// //                 <Card elevation={2}>
// //                   <CardContent>
// //                     <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
// //                       <TrendingUp sx={{ mr: 1, verticalAlign: "middle" }} />
// //                       Evaluation Details
// //                     </Typography>
// //                     <Divider sx={{ mb: 3 }} />

// //                     <Box component="form" onSubmit={handleSubmit}>
// //                       <Grid container spacing={3}>
// //                         <Grid item xs={12}>
// //                           <FormControl fullWidth>
// //                             <InputLabel>Recommendation</InputLabel>
// //                             <Select name="recommendation" value={formData.recommendation} label="Recommendation" onChange={handleChange}>
// //                               <MenuItem value="HIRE">Hire</MenuItem>
// //                               <MenuItem value="CONSIDER">Consider</MenuItem>
// //                               <MenuItem value="NO_HIRE">No Hire</MenuItem>
// //                             </Select>
// //                           </FormControl>
// //                         </Grid>

// //                         <Grid item xs={12} sm={4}>
// //                           <Typography variant="body1" fontWeight="medium" gutterBottom>Technical Skills</Typography>
// //                           <Rating name="technical_skills_rating" value={formData.technical_skills_rating / 2} precision={0.5} onChange={(e, val) => handleRatingChange("technical_skills_rating", val)} size="large" />
// //                         </Grid>
// //                         <Grid item xs={12} sm={4}>
// //                           <Typography variant="body1" fontWeight="medium" gutterBottom>Communication</Typography>
// //                           <Rating name="communication_skills_rating" value={formData.communication_skills_rating / 2} precision={0.5} onChange={(e, val) => handleRatingChange("communication_skills_rating", val)} size="large" />
// //                         </Grid>
// //                         <Grid item xs={12} sm={4}>
// //                           <Typography variant="body1" fontWeight="medium" gutterBottom>Cultural Fit</Typography>
// //                           <Rating name="cultural_fit_rating" value={formData.cultural_fit_rating / 2} precision={0.5} onChange={(e, val) => handleRatingChange("cultural_fit_rating", val)} size="large" />
// //                         </Grid>
                        
// //                         <Grid item xs={12}><TextField name="strengths" fullWidth multiline rows={3} label="Strengths" value={formData.strengths} onChange={handleChange}/></Grid>
// //                         <Grid item xs={12}><TextField name="weaknesses" fullWidth multiline rows={3} label="Weaknesses" value={formData.weaknesses} onChange={handleChange}/></Grid>
// //                         <Grid item xs={12}><TextField name="overall_comments" fullWidth multiline rows={4} label="Overall Comments" value={formData.overall_comments} onChange={handleChange}/></Grid>

// //                         <Grid item xs={12}>
// //                           <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
// //                             <Button variant="outlined" size="large" onClick={onClose}>Cancel</Button>
// //                             <Button type="submit" variant="contained" size="large" startIcon={<CheckCircle />}>Submit Evaluation</Button>
// //                           </Box>
// //                         </Grid>
// //                       </Grid>
// //                     </Box>
// //                   </CardContent>
// //                 </Card>
// //               </Grid>
// //             </Grid>
// //           </Container>
// //         </Box>
// //     </Modal>
// //     </Layout>
// //   );
// // }


// // // --- 3. MAIN COMPONENT to display the list of candidates ---

// // const theme = createTheme({
// //   palette: {
// //     background: {
// //       default: '#f4f6f8',
// //     },
// //   },
// //   components: {
// //     MuiAccordion: {
// //       styleOverrides: {
// //         root: {
// //           '&:before': {
// //             display: 'none',
// //           },
// //           '&$expanded': {
// //             margin: 'auto',
// //           },
// //         },
// //       },
// //     },
// //   },
// // });

// // const StyledAccordion = styled(Accordion)(({ theme }) => ({
// //   transition: theme.transitions.create(['box-shadow', 'margin'], {
// //     duration: theme.transitions.duration.short,
// //   }),
// //   '&.Mui-expanded': {
// //     boxShadow: theme.shadows[4],
// //     margin: `${theme.spacing(1, 0)} !important`,
// //   },
// // }));

// // export default function CandidateInterviewList() {
// //   const [expanded, setExpanded] = useState(false);
// //   const [modalOpen, setModalOpen] = useState(false);
// //   const [selectedInterview, setSelectedInterview] = useState(null);

// //   const handleAccordionChange = (panel) => (event, isExpanded) => {
// //     setExpanded(isExpanded ? panel : false);
// //   };

// //   const handleEvaluateClick = (candidate, interview) => {
// //     // Combine candidate and interview info to pass to the modal
// //     setSelectedInterview({ ...candidate, ...interview });
// //     setModalOpen(true);
// //   };

// //   const handleCloseModal = () => {
// //     setModalOpen(false);
// //     setSelectedInterview(null);
// //   };

// //   return (
// //   <Layout title="Evaluation">

// //     <ThemeProvider theme={theme}>
// //       <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
// //         <Container maxWidth="md" sx={{ py: 4 }}>
// //           <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="text.primary">
// //             Scheduled Interviews
// //           </Typography>
// //           <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
// //             Review and evaluate candidates scheduled for interviews.
// //           </Typography>

// //           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
// //             {staticCandidates.map((candidate) => (
// //               <StyledAccordion
// //                 key={candidate.id}
// //                 expanded={expanded === `panel-${candidate.id}`}
// //                 onChange={handleAccordionChange(`panel-${candidate.id}`)}
// //               >
// //                 <AccordionSummary expandIcon={<ExpandMore />}>
// //                   <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
// //                     <ListItemAvatar>
// //                       <Avatar src={candidate.avatar} sx={{ width: 56, height: 56 }} />
// //                     </ListItemAvatar>
// //                     <ListItemText
// //                       primary={<Typography variant="h6">{candidate.name}</Typography>}
// //                       secondary={candidate.email}
// //                     />
// //                     <Chip
// //                       label={`${candidate.interviews.length} interview(s)`}
// //                       color="primary"
// //                       variant="outlined"
// //                       sx={{ ml: 'auto', mr: 2 }}
// //                     />
// //                   </Box>
// //                 </AccordionSummary>
// //                 <AccordionDetails sx={{ backgroundColor: '#fafafa', p: 0 }}>
// //                   <List disablePadding>
// //                     {candidate.interviews.map((interview, index) => (
// //                       <>
// //                       <Divider component="li" />
// //                       <ListItem key={interview.interviewId} sx={{ py: 2, px: 3 }}>
// //                         <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
// //                           <Box flexGrow={1}>
// //                             <Typography variant="body1" fontWeight="500">{interview.jobTitle}</Typography>
// //                             <Box display="flex" alignItems="center" color="text.secondary" mt={1} gap={2}>
// //                               <Box display="flex" alignItems="center" gap={0.5}><Business fontSize="small" />{interview.department}</Box>
// //                               <Box display="flex" alignItems="center" gap={0.5}><Event fontSize="small" />{new Date(interview.interviewDate).toLocaleString()}</Box>
// //                             </Box>
// //                           </Box>
// //                           <Button
// //                             variant="contained"
// //                             startIcon={<RateReview />}
// //                             onClick={() => handleEvaluateClick(candidate, interview)}
// //                             sx={{ mt: { xs: 2, sm: 0 } }}
// //                           >
// //                             Evaluate
// //                           </Button>
// //                         </Box>
// //                       </ListItem>
// //                       </>
// //                     ))}
// //                   </List>
// //                 </AccordionDetails>
// //               </StyledAccordion>
// //             ))}
// //           </Box>
// //         </Container>
// //       </Box>

// //       {/* Evaluation Modal */}
// //       <EvaluationForm
// //         open={modalOpen}
// //         onClose={handleCloseModal}
// //         interviewDetails={selectedInterview}
// //       />
// //     </ThemeProvider>
// //     </Layout>
// //   );
// // }
// // import { useState, useEffect } from "react";
// // import { useParams } from "react-router-dom"; // Standard React hook for URL params
// // import axios from "axios";
// // import {
// //   Container,
// //   Paper,
// //   Typography,
// //   Grid,
// //   Card,
// //   CardContent,
// //   Rating,
// //   TextField,
// //   Button,
// //   Box,
// //   Avatar,
// //   Divider,
// //   FormControl,
// //   InputLabel,
// //   Select,
// //   MenuItem,
// //   CircularProgress,
// //   Alert,
// // } from "@mui/material";
// // import { Person, Email, Phone, Work, TrendingUp, CheckCircle } from "@mui/icons-material";
// // import Layout from "../common/Layout";

// // export default function EvaluationPage() {
// //   const [formData, setFormData] = useState({
// //     recommendation: "",
// //     technical_skills_rating: 0,
// //     communication_skills_rating: 0,
// //     cultural_fit_rating: 0,
// //     strengths: "",
// //     weaknesses: "",
// //     overall_comments: "",
// //   });

// //   const [candidateInfo, setCandidateInfo] = useState({
// //     name: "Loading...",
// //     email: "",
// //     phone: "",
// //     position: "",
// //     department: "",
// //     avatar: "/professional-woman-diverse.png",
// //   });

// //   const [evaluatorInfo, setEvaluatorInfo] = useState({
// //     name: "",
// //     title: "",
// //     evaluationDate: "",
// //   });

// //   const [overallRating, setOverallRating] = useState(0);
// //   const [loading, setLoading] = useState(true);
// //   const [error, setError] = useState(null);

// //   const { id: interviewId } = useParams(); // Gets the 'id' from the URL, e.g., /evaluation/62

// //   useEffect(() => {
// //     const { technical_skills_rating, communication_skills_rating, cultural_fit_rating } = formData;
// //     const ratings = [technical_skills_rating, communication_skills_rating, cultural_fit_rating].filter(
// //       (r) => r > 0
// //     );

// //     if (ratings.length > 0) {
// //       const avg = ratings.reduce((sum, current) => sum + current, 0) / ratings.length;
// //       setOverallRating(avg.toFixed(1));
// //     } else {
// //       setOverallRating(0);
// //     }
// //   }, [formData.technical_skills_rating, formData.communication_skills_rating, formData.cultural_fit_rating]);

// //   useEffect(() => {
// //     if (!interviewId) {
// //       setError("Interview ID is missing from the URL.");
// //       setLoading(false);
// //       return;
// //     }

// //     const fetchData = async () => {
// //       setLoading(true);
// //       setError(null);
// //       const token = localStorage.getItem("accessToken");

// //       if (!token) {
// //         setError("Authentication token not found. Please log in.");
// //         setLoading(false);
// //         return;
// //       }

// //       const headers = {
// //         Authorization: `Bearer ${token}`,
// //       };

// //       try {
// //         // NOTE: Candidate info is often from a different API endpoint.
// //         // We'll use static data for the candidate's profile for this example.
// //         setCandidateInfo({
// //           name: "Sarah Johnson",
// //           email: "sarah.johnson@email.com",
// //           phone: "+1 (555) 123-4567",
// //           position: "Senior Frontend Developer",
// //           department: "Engineering",
// //           avatar: "/professional-woman-diverse.png",
// //         });

// //         // Attempt to fetch existing feedback
// //         const feedbackResponse = await axios.get(`http://localhost:8000/api/interviews/${interviewId}/feedback/`, {
// //           headers,
// //         });

// //         if (feedbackResponse.data) {
// //           const data = feedbackResponse.data;
// //           setFormData({
// //             recommendation: data.recommendation || "",
// //             technical_skills_rating: data.technical_skills_rating || 0,
// //             communication_skills_rating: data.communication_skills_rating || 0,
// //             cultural_fit_rating: data.cultural_fit_rating || 0,
// //             strengths: data.strengths || "",
// //             weaknesses: data.weaknesses || "",
// //             overall_comments: data.overall_comments || "",
// //           });
// //           setEvaluatorInfo({
// //             name: data.interviewer_name || "N/A",
// //             title: "Engineering Manager", // Assuming a static title
// //             evaluationDate: data.submitted_at
// //               ? new Date(data.submitted_at).toLocaleDateString("en-US", {
// //                   year: "numeric",
// //                   month: "long",
// //                   day: "numeric",
// //                 })
// //               : new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
// //           });
// //         }
// //       } catch (err) {
// //         if (err.response && err.response.status === 404) {
// //           console.log("No existing feedback found. Ready for new submission.");
// //           // Set default evaluator info for a new evaluation form
// //           setEvaluatorInfo({
// //             name: "Michael Chen",
// //             title: "Engineering Manager",
// //             evaluationDate: new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
// //           });
// //         } else {
// //           setError("Failed to fetch evaluation data. Please try again.");
// //           console.error(err);
// //         }
// //       } finally {
// //         setLoading(false);
// //       }
// //     };

// //     fetchData();
// //   }, [interviewId]);

// //   const handleChange = (e) => {
// //     const { name, value } = e.target;
// //     setFormData((prev) => ({
// //       ...prev,
// //       [name]: value,
// //     }));
// //   };

// //   const handleRatingChange = (field, value) => {
// //     // Convert 5-star rating (e.g., 4.5) to a 1-10 scale (e.g., 9) for the API
// //     setFormData((prev) => ({
// //       ...prev,
// //       [field]: value !== null ? value * 2 : 0,
// //     }));
// //   };

// //   const handleSubmit = async (e) => {
// //     e.preventDefault();
// //     setError(null);
// //     const token = localStorage.getItem("accessToken");

// //     if (!token) {
// //       setError("Authentication token not found.");
// //       return;
// //     }

// //     // Ensure ratings are integers for the payload
// //     const payload = {
// //       ...formData,
// //       technical_skills_rating: Math.round(formData.technical_skills_rating),
// //       communication_skills_rating: Math.round(formData.communication_skills_rating),
// //       cultural_fit_rating: Math.round(formData.cultural_fit_rating),
// //     };

// //     try {
// //       const response = await axios.post(
// //         `http://localhost:8000/api/interviews/${interviewId}/submit-feedback/`,
// //         payload,
// //         {
// //           headers: {
// //             Authorization: `Bearer ${token}`,
// //             "Content-Type": "application/json",
// //           },
// //         }
// //       );
// //       alert(response.data.message || "Feedback submitted successfully!");
// //     } catch (err) {
// //       setError("Failed to submit evaluation. Please check your input and try again.");
// //       console.error(err);
// //     }
// //   };

// //   if (loading) {
// //     return (
// //       <Layout title="Loading Evaluation">
// //         <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: "80vh" }}>
// //           <CircularProgress />
// //         </Box>
// //       </Layout>
// //     );
// //   }

// //   return (
// //     <Layout title="Evaluation">
// //       <Container maxWidth="lg" sx={{ py: 4 }}>
// //         {error && (
// //           <Alert severity="error" sx={{ mb: 3 }}>
// //             {error}
// //           </Alert>
// //         )}
// //         <Paper
// //           elevation={3}
// //           sx={{ p: 3, mb: 4, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}
// //         >
// //           <Box display="flex" alignItems="center" gap={3}>
// //             <Avatar src={candidateInfo.avatar} sx={{ width: 80, height: 80, border: "3px solid white" }} />
// //             <Box flex={1}>
// //               <Typography variant="h4" fontWeight="bold" gutterBottom>
// //                 Interview Feedback
// //               </Typography>
// //               <Typography variant="h6" sx={{ opacity: 0.9 }}>
// //                 {candidateInfo.name} - {candidateInfo.position}
// //               </Typography>
// //               <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
// //                 Evaluation Date: {evaluatorInfo.evaluationDate} | Evaluator: {evaluatorInfo.name}
// //               </Typography>
// //             </Box>
// //             <Box textAlign="center">
// //               <Typography variant="h3" fontWeight="bold">
// //                 {overallRating}
// //               </Typography>
// //               <Typography variant="body2">Overall Score / 10</Typography>
// //             </Box>
// //           </Box>
// //         </Paper>

// //         <Grid container spacing={4}>
// //           <Grid item xs={12} md={4}>
// //             <Card elevation={2} sx={{ height: "fit-content" }}>
// //               <CardContent>
// //                 <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
// //                   <Person sx={{ mr: 1, verticalAlign: "middle" }} />
// //                   Candidate Information
// //                 </Typography>
// //                 <Divider sx={{ mb: 2 }} />
// //                 <Box display="flex" alignItems="center" mb={2}>
// //                   <Email sx={{ mr: 2, color: "text.secondary" }} />
// //                   <Typography variant="body2">{candidateInfo.email}</Typography>
// //                 </Box>
// //                 <Box display="flex" alignItems="center" mb={2}>
// //                   <Phone sx={{ mr: 2, color: "text.secondary" }} />
// //                   <Typography variant="body2">{candidateInfo.phone}</Typography>
// //                 </Box>
// //                 <Box display="flex" alignItems="center" mb={2}>
// //                   <Work sx={{ mr: 2, color: "text.secondary" }} />
// //                   <Typography variant="body2">{candidateInfo.department}</Typography>
// //                 </Box>
// //               </CardContent>
// //             </Card>
// //           </Grid>

// //           <Grid item xs={12} md={8}>
// //             <Card elevation={2}>
// //               <CardContent>
// //                 <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
// //                   <TrendingUp sx={{ mr: 1, verticalAlign: "middle" }} />
// //                   Evaluation Details
// //                 </Typography>
// //                 <Divider sx={{ mb: 3 }} />

// //                 <Box component="form" onSubmit={handleSubmit}>
// //                   <Grid container spacing={3}>
// //                     <Grid item xs={12}>
// //                       <FormControl fullWidth>
// //                         <InputLabel>Recommendation</InputLabel>
// //                         <Select
// //                           name="recommendation"
// //                           value={formData.recommendation}
// //                           label="Recommendation"
// //                           onChange={handleChange}
// //                         >
// //                           <MenuItem value="HIRE">Hire</MenuItem>
// //                           <MenuItem value="CONSIDER">Consider</MenuItem>
// //                           <MenuItem value="NO_HIRE">No Hire</MenuItem>
// //                         </Select>
// //                       </FormControl>
// //                     </Grid>

// //                     <Grid item xs={12} sm={4}>
// //                       <Typography variant="body1" fontWeight="medium" gutterBottom>
// //                         Technical Skills
// //                       </Typography>
// //                       <Rating
// //                         name="technical_skills_rating"
// //                         value={formData.technical_skills_rating / 2} // Display as 5 stars
// //                         precision={0.5}
// //                         onChange={(event, newValue) => handleRatingChange("technical_skills_rating", newValue)}
// //                         size="large"
// //                       />
// //                     </Grid>
// //                     <Grid item xs={12} sm={4}>
// //                       <Typography variant="body1" fontWeight="medium" gutterBottom>
// //                         Communication Skills
// //                       </Typography>
// //                       <Rating
// //                         name="communication_skills_rating"
// //                         value={formData.communication_skills_rating / 2} // Display as 5 stars
// //                         precision={0.5}
// //                         onChange={(event, newValue) => handleRatingChange("communication_skills_rating", newValue)}
// //                         size="large"
// //                       />
// //                     </Grid>
// //                     <Grid item xs={12} sm={4}>
// //                       <Typography variant="body1" fontWeight="medium" gutterBottom>
// //                         Cultural Fit
// //                       </Typography>
// //                       <Rating
// //                         name="cultural_fit_rating"
// //                         value={formData.cultural_fit_rating / 2} // Display as 5 stars
// //                         precision={0.5}
// //                         onChange={(event, newValue) => handleRatingChange("cultural_fit_rating", newValue)}
// //                         size="large"
// //                       />
// //                     </Grid>

// //                     <Grid item xs={12}>
// //                       <TextField
// //                         name="strengths"
// //                         fullWidth
// //                         multiline
// //                         rows={3}
// //                         label="Strengths"
// //                         placeholder="Describe the candidate's key strengths..."
// //                         value={formData.strengths}
// //                         onChange={handleChange}
// //                       />
// //                     </Grid>

// //                     <Grid item xs={12}>
// //                       <TextField
// //                         name="weaknesses"
// //                         fullWidth
// //                         multiline
// //                         rows={3}
// //                         label="Weaknesses / Areas for Improvement"
// //                         placeholder="Describe areas where the candidate can improve..."
// //                         value={formData.weaknesses}
// //                         onChange={handleChange}
// //                       />
// //                     </Grid>

// //                     <Grid item xs={12}>
// //                       <TextField
// //                         name="overall_comments"
// //                         fullWidth
// //                         multiline
// //                         rows={4}
// //                         label="Overall Comments"
// //                         placeholder="Provide a summary or any additional feedback..."
// //                         value={formData.overall_comments}
// //                         onChange={handleChange}
// //                       />
// //                     </Grid>

// //                     <Grid item xs={12}>
// //                       <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
// //                         <Button variant="outlined" size="large">
// //                           Save Draft
// //                         </Button>
// //                         <Button
// //                           type="submit"
// //                           variant="contained"
// //                           size="large"
// //                           startIcon={<CheckCircle />}
// //                           sx={{
// //                             background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
// //                             boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
// //                           }}
// //                         >
// //                           Submit Evaluation
// //                         </Button>
// //                       </Box>
// //                     </Grid>
// //                   </Grid>
// //                 </Box>
// //               </CardContent>
// //             </Card>
// //           </Grid>
// //         </Grid>
// //       </Container>
// //     </Layout>
// //   );
// // }

// // import { useState, useEffect } from "react"
// // import { useParams } from "react-router-dom" // Corrected import for standard React apps
// // import axios from "axios"
// // import {
// //   Container,
// //   Paper,
// //   Typography,
// //   Grid,
// //   Card,
// //   CardContent,
// //   Rating,
// //   TextField,
// //   Button,
// //   Box,
// //   Avatar,
// //   Divider,
// //   FormControl,
// //   InputLabel,
// //   Select,
// //   MenuItem,
// //   CircularProgress,
// //   Alert,
// // } from "@mui/material"
// // import { Person, Email, Phone, Work, TrendingUp, CheckCircle } from "@mui/icons-material"
// // import Layout from "../common/Layout"

// // export default function EvaluationPage() {
// //   const [formData, setFormData] = useState({
// //     recommendation: "",
// //     technical_skills_rating: 0,
// //     communication_skills_rating: 0,
// //     cultural_fit_rating: 0,
// //     strengths: "",
// //     weaknesses: "",
// //     overall_comments: "",
// //   })

// //   const [candidateInfo, setCandidateInfo] = useState({
// //     name: "Loading...",
// //     email: "",
// //     phone: "",
// //     position: "",
// //     department: "",
// //     avatar: "/professional-woman-diverse.png",
// //   })

// //   const [evaluatorInfo, setEvaluatorInfo] = useState({
// //     name: "",
// //     title: "",
// //     evaluationDate: "",
// //   })

// //   const [overallRating, setOverallRating] = useState(0)
// //   const [loading, setLoading] = useState(true)
// //   const [error, setError] = useState(null)

// //   const { id: interviewId } = useParams() // Get the 'id' parameter from the URL

// //   useEffect(() => {
// //     const { technical_skills_rating, communication_skills_rating, cultural_fit_rating } = formData
// //     const ratings = [technical_skills_rating, communication_skills_rating, cultural_fit_rating].filter(
// //       (r) => r > 0
// //     )

// //     if (ratings.length > 0) {
// //       const avg = ratings.reduce((sum, current) => sum + current, 0) / ratings.length
// //       setOverallRating(avg.toFixed(1))
// //     } else {
// //       setOverallRating(0)
// //     }
// //   }, [formData.technical_skills_rating, formData.communication_skills_rating, formData.cultural_fit_rating])

// //   useEffect(() => {
// //     if (!interviewId) {
// //       setError("Interview ID is missing from the URL.")
// //       setLoading(false)
// //       return
// //     }

// //     const fetchData = async () => {
// //       setLoading(true)
// //       setError(null)
// //       const token = localStorage.getItem("accessToken")

// //       if (!token) {
// //         setError("Authentication token not found. Please log in.")
// //         setLoading(false)
// //         return
// //       }

// //       const headers = {
// //         Authorization: `Bearer ${token}`,
// //       }

// //       try {
// //         // NOTE: Candidate info is often fetched from a separate endpoint.
// //         // Using static data for the candidate's profile for now.
// //         setCandidateInfo({
// //           name: "Sarah Johnson",
// //           email: "sarah.johnson@email.com",
// //           phone: "+1 (555) 123-4567",
// //           position: "Senior Frontend Developer",
// //           department: "Engineering",
// //           avatar: "/professional-woman-diverse.png",
// //         })

// //         // Fetch existing feedback
// //         const feedbackResponse = await axios.get(`https://tdtlworld.com/dss-gatep-backend/api/interviews/${interviewId}/feedback/`, {
// //           headers,
// //         })

// //         if (feedbackResponse.data) {
// //           const data = feedbackResponse.data
// //           setFormData({
// //             recommendation: data.recommendation || "",
// //             technical_skills_rating: data.technical_skills_rating || 0,
// //             communication_skills_rating: data.communication_skills_rating || 0,
// //             cultural_fit_rating: data.cultural_fit_rating || 0,
// //             strengths: data.strengths || "",
// //             weaknesses: data.weaknesses || "",
// //             overall_comments: data.overall_comments || "",
// //           })
// //           setEvaluatorInfo({
// //             name: data.interviewer_name || "N/A",
// //             title: "Engineering Manager", // Assuming a static title
// //             evaluationDate: data.submitted_at
// //               ? new Date(data.submitted_at).toLocaleDateString("en-US", {
// //                   year: "numeric",
// //                   month: "long",
// //                   day: "numeric",
// //                 })
// //               : new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
// //           })
// //         }
// //       } catch (err) {
// //         if (err.response && err.response.status === 404) {
// //           console.log("No existing feedback found. Ready for new submission.")
// //           setEvaluatorInfo({
// //             name: "Michael Chen", // Placeholder name for new feedback
// //             title: "Engineering Manager",
// //             evaluationDate: new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
// //           })
// //         } else {
// //           setError("Failed to fetch evaluation data. Please try again.")
// //           console.error(err)
// //         }
// //       } finally {
// //         setLoading(false)
// //       }
// //     }

// //     fetchData()
// //   }, [interviewId])

// //   const handleChange = (e) => {
// //     const { name, value } = e.target
// //     setFormData((prev) => ({
// //       ...prev,
// //       [name]: value,
// //     }))
// //   }

// //   const handleRatingChange = (field, value) => {
// //     setFormData((prev) => ({
// //       ...prev,
// //       [field]: value !== null ? value * 2 : 0, // Scale from 5-star rating to a 1-10 score
// //     }))
// //   }

// //   const handleSubmit = async (e) => {
// //     e.preventDefault()
// //     setError(null)
// //     const token = localStorage.getItem("accessToken")

// //     if (!token) {
// //       setError("Authentication token not found.")
// //       return
// //     }

// //     // Ensure ratings are integers as per the API payload
// //     const payload = {
// //       ...formData,
// //       technical_skills_rating: Math.round(formData.technical_skills_rating),
// //       communication_skills_rating: Math.round(formData.communication_skills_rating),
// //       cultural_fit_rating: Math.round(formData.cultural_fit_rating),
// //     }

// //     try {
// //       const response = await axios.post(
// //         `https://tdtlworld.com/dss-gatep-backend/api/${interviewId}/submit-feedback/`,
// //         payload,
// //         {
// //           headers: {
// //             Authorization: `Bearer ${token}`,
// //             "Content-Type": "application/json",
// //           },
// //         }
// //       )
// //       alert(response.data.message || "Feedback submitted successfully!")
// //     } catch (err) {
// //       setError("Failed to submit evaluation. Please check your input and try again.")
// //       console.error(err)
// //     }
// //   }

// //   if (loading) {
// //     return (
// //       <Layout title="Loading Evaluation">
// //         <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: "80vh" }}>
// //           <CircularProgress />
// //         </Box>
// //       </Layout>
// //     )
// //   }

// //   return (
// //     <Layout title="Evaluation">
// //       <Container maxWidth="lg" sx={{ py: 4 }}>
// //         {error && (
// //           <Alert severity="error" sx={{ mb: 3 }}>
// //             {error}
// //           </Alert>
// //         )}
// //         <Paper
// //           elevation={3}
// //           sx={{ p: 3, mb: 4, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}
// //         >
// //           <Box display="flex" alignItems="center" gap={3}>
// //             <Avatar src={candidateInfo.avatar} sx={{ width: 80, height: 80, border: "3px solid white" }} />
// //             <Box flex={1}>
// //               <Typography variant="h4" fontWeight="bold" gutterBottom>
// //                 Interview Feedback
// //               </Typography>
// //               <Typography variant="h6" sx={{ opacity: 0.9 }}>
// //                 {candidateInfo.name} - {candidateInfo.position}
// //               </Typography>
// //               <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
// //                 Evaluation Date: {evaluatorInfo.evaluationDate} | Evaluator: {evaluatorInfo.name}
// //               </Typography>
// //             </Box>
// //             <Box textAlign="center">
// //               <Typography variant="h3" fontWeight="bold">
// //                 {overallRating}
// //               </Typography>
// //               <Typography variant="body2">Overall Score / 10</Typography>
// //             </Box>
// //           </Box>
// //         </Paper>

// //         <Grid container spacing={4}>
// //           <Grid item xs={12} md={4}>
// //             <Card elevation={2} sx={{ height: "fit-content" }}>
// //               <CardContent>
// //                 <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
// //                   <Person sx={{ mr: 1, verticalAlign: "middle" }} />
// //                   Candidate Information
// //                 </Typography>
// //                 <Divider sx={{ mb: 2 }} />
// //                 <Box display="flex" alignItems="center" mb={2}>
// //                   <Email sx={{ mr: 2, color: "text.secondary" }} />
// //                   <Typography variant="body2">{candidateInfo.email}</Typography>
// //                 </Box>
// //                 <Box display="flex" alignItems="center" mb={2}>
// //                   <Phone sx={{ mr: 2, color: "text.secondary" }} />
// //                   <Typography variant="body2">{candidateInfo.phone}</Typography>
// //                 </Box>
// //                 <Box display="flex" alignItems="center" mb={2}>
// //                   <Work sx={{ mr: 2, color: "text.secondary" }} />
// //                   <Typography variant="body2">{candidateInfo.department}</Typography>
// //                 </Box>
// //               </CardContent>
// //             </Card>
// //           </Grid>

// //           <Grid item xs={12} md={8}>
// //             <Card elevation={2}>
// //               <CardContent>
// //                 <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
// //                   <TrendingUp sx={{ mr: 1, verticalAlign: "middle" }} />
// //                   Evaluation Details
// //                 </Typography>
// //                 <Divider sx={{ mb: 3 }} />

// //                 <Box component="form" onSubmit={handleSubmit}>
// //                   <Grid container spacing={3}>
// //                     <Grid item xs={12}>
// //                       <FormControl fullWidth>
// //                         <InputLabel>Recommendation</InputLabel>
// //                         <Select
// //                           name="recommendation"
// //                           value={formData.recommendation}
// //                           label="Recommendation"
// //                           onChange={handleChange}
// //                         >
// //                           <MenuItem value="HIRE">Hire</MenuItem>
// //                           <MenuItem value="CONSIDER">Consider</MenuItem>
// //                           <MenuItem value="NO_HIRE">No Hire</MenuItem>
// //                         </Select>
// //                       </FormControl>
// //                     </Grid>

// //                     <Grid item xs={12} sm={4}>
// //                       <Typography variant="body1" fontWeight="medium" gutterBottom>
// //                         Technical Skills
// //                       </Typography>
// //                       <Rating
// //                         name="technical_skills_rating"
// //                         value={formData.technical_skills_rating / 2}
// //                         precision={0.5}
// //                         onChange={(event, newValue) => handleRatingChange("technical_skills_rating", newValue)}
// //                         size="large"
// //                       />
// //                     </Grid>
// //                     <Grid item xs={12} sm={4}>
// //                       <Typography variant="body1" fontWeight="medium" gutterBottom>
// //                         Communication Skills
// //                       </Typography>
// //                       <Rating
// //                         name="communication_skills_rating"
// //                         value={formData.communication_skills_rating / 2}
// //                         precision={0.5}
// //                         onChange={(event, newValue) => handleRatingChange("communication_skills_rating", newValue)}
// //                         size="large"
// //                       />
// //                     </Grid>
// //                     <Grid item xs={12} sm={4}>
// //                       <Typography variant="body1" fontWeight="medium" gutterBottom>
// //                         Cultural Fit
// //                       </Typography>
// //                       <Rating
// //                         name="cultural_fit_rating"
// //                         value={formData.cultural_fit_rating / 2}
// //                         precision={0.5}
// //                         onChange={(event, newValue) => handleRatingChange("cultural_fit_rating", newValue)}
// //                         size="large"
// //                       />
// //                     </Grid>

// //                     <Grid item xs={12}>
// //                       <TextField
// //                         name="strengths"
// //                         fullWidth
// //                         multiline
// //                         rows={3}
// //                         label="Strengths"
// //                         placeholder="Describe the candidate's key strengths..."
// //                         value={formData.strengths}
// //                         onChange={handleChange}
// //                       />
// //                     </Grid>

// //                     <Grid item xs={12}>
// //                       <TextField
// //                         name="weaknesses"
// //                         fullWidth
// //                         multiline
// //                         rows={3}
// //                         label="Weaknesses / Areas for Improvement"
// //                         placeholder="Describe areas where the candidate can improve..."
// //                         value={formData.weaknesses}
// //                         onChange={handleChange}
// //                       />
// //                     </Grid>

// //                     <Grid item xs={12}>
// //                       <TextField
// //                         name="overall_comments"
// //                         fullWidth
// //                         multiline
// //                         rows={4}
// //                         label="Overall Comments"
// //                         placeholder="Provide a summary or any additional feedback..."
// //                         value={formData.overall_comments}
// //                         onChange={handleChange}
// //                       />
// //                     </Grid>

// //                     <Grid item xs={12}>
// //                       <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
// //                         <Button variant="outlined" size="large">
// //                           Save Draft
// //                         </Button>
// //                         <Button
// //                           type="submit"
// //                           variant="contained"
// //                           size="large"
// //                           startIcon={<CheckCircle />}
// //                           sx={{
// //                             background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
// //                             boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
// //                           }}
// //                         >
// //                           Submit Evaluation
// //                         </Button>
// //                       </Box>
// //                     </Grid>
// //                   </Grid>
// //                 </Box>
// //               </CardContent>
// //             </Card>
// //           </Grid>
// //         </Grid>
// //       </Container>
// //     </Layout>
// //   )
// // }

// // // "use client"

// // // import { useState } from "react"
// // // import {
// // //   Container,
// // //   Paper,
// // //   Typography,
// // //   Grid,
// // //   Card,
// // //   CardContent,
// // //   Rating,
// // //   TextField,
// // //   Button,
// // //   Chip,
// // //   Box,
// // //   Avatar,
// // //   Divider,
// // //   LinearProgress,
// // //   FormControl,
// // //   InputLabel,
// // //   Select,
// // //   MenuItem,
// // //   FormControlLabel,
// // //   FormLabel,
// // //   Checkbox,
// // //   FormGroup,
// // // } from "@mui/material"
// // // import { Person, Email, Phone, Work, School, Star, TrendingUp, Assessment, CheckCircle } from "@mui/icons-material"
// // // import Layout from "../common/Layout"

// // // export default function EvaluationPage() {
// // //   const [formData, setFormData] = useState({
// // //     overallRating: 4,
// // //     technicalSkills: 4.5,
// // //     communication: 4,
// // //     teamwork: 5,
// // //     problemSolving: 4,
// // //     leadership: 3.5,
// // //     recommendation: "highly-recommend",
// // //     strengths: [],
// // //     improvements: "",
// // //     additionalComments: "",
// // //   })

// // //   // Static evaluation data
// // //   const candidateInfo = {
// // //     name: "Sarah Johnson",
// // //     email: "sarah.johnson@email.com",
// // //     phone: "+1 (555) 123-4567",
// // //     position: "Senior Frontend Developer",
// // //     department: "Engineering",
// // //     evaluationDate: "March 15, 2024",
// // //     evaluator: "Michael Chen",
// // //     evaluatorTitle: "Engineering Manager",
// // //     avatar: "/professional-woman-diverse.png",
// // //   }

// // //   const skillCategories = [
// // //     { name: "Technical Skills", rating: 4.5, color: "#1976d2" },
// // //     { name: "Communication", rating: 4.0, color: "#388e3c" },
// // //     { name: "Problem Solving", rating: 4.2, color: "#f57c00" },
// // //     { name: "Teamwork", rating: 4.8, color: "#7b1fa2" },
// // //     { name: "Leadership", rating: 3.8, color: "#d32f2f" },
// // //     { name: "Adaptability", rating: 4.3, color: "#0288d1" },
// // //   ]

// // //   const performanceMetrics = [
// // //     { label: "Project Completion Rate", value: 95, color: "#4caf50" },
// // //     { label: "Code Quality Score", value: 88, color: "#2196f3" },
// // //     { label: "Client Satisfaction", value: 92, color: "#ff9800" },
// // //     { label: "Team Collaboration", value: 96, color: "#9c27b0" },
// // //   ]

// // //   const strengthOptions = [
// // //     "Technical Expertise",
// // //     "Problem Solving",
// // //     "Communication",
// // //     "Leadership",
// // //     "Creativity",
// // //     "Time Management",
// // //     "Team Collaboration",
// // //     "Adaptability",
// // //   ]

// // //   const handleRatingChange = (field, value) => {
// // //     setFormData((prev) => ({
// // //       ...prev,
// // //       [field]: value,
// // //     }))
// // //   }

// // //   const handleStrengthToggle = (strength) => {
// // //     setFormData((prev) => ({
// // //       ...prev,
// // //       strengths: prev.strengths.includes(strength)
// // //         ? prev.strengths.filter((s) => s !== strength)
// // //         : [...prev.strengths, strength],
// // //     }))
// // //   }

// // //   return (
// // //   <Layout title="Evaluation">
// // //     <Container maxWidth="lg" sx={{ py: 4 }}>
// // //       {/* Header */}
// // //       <Paper
// // //         elevation={3}
// // //         sx={{ p: 3, mb: 4, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}
// // //       >
// // //         <Box display="flex" alignItems="center" gap={3}>
// // //           <Avatar src={candidateInfo.avatar} sx={{ width: 80, height: 80, border: "3px solid white" }} />
// // //           <Box flex={1}>
// // //             <Typography variant="h4" fontWeight="bold" gutterBottom>
// // //               Employee Evaluation
// // //             </Typography>
// // //             <Typography variant="h6" sx={{ opacity: 0.9 }}>
// // //               {candidateInfo.name} - {candidateInfo.position}
// // //             </Typography>
// // //             <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
// // //               Evaluation Date: {candidateInfo.evaluationDate} | Evaluator: {candidateInfo.evaluator}
// // //             </Typography>
// // //           </Box>
// // //           <Box textAlign="center">
// // //             <Typography variant="h3" fontWeight="bold">
// // //               {formData.overallRating}
// // //             </Typography>
// // //             <Typography variant="body2">Overall Rating</Typography>
// // //           </Box>
// // //         </Box>
// // //       </Paper>

// // //       <Grid container spacing={4}>
// // //         {/* Candidate Information */}
// // //         <Grid item xs={12} md={4}>
// // //           <Card elevation={2} sx={{ height: "fit-content" }}>
// // //             <CardContent>
// // //               <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
// // //                 <Person sx={{ mr: 1, verticalAlign: "middle" }} />
// // //                 Candidate Information
// // //               </Typography>
// // //               <Divider sx={{ mb: 2 }} />

// // //               <Box display="flex" alignItems="center" mb={2}>
// // //                 <Email sx={{ mr: 2, color: "text.secondary" }} />
// // //                 <Typography variant="body2">{candidateInfo.email}</Typography>
// // //               </Box>

// // //               <Box display="flex" alignItems="center" mb={2}>
// // //                 <Phone sx={{ mr: 2, color: "text.secondary" }} />
// // //                 <Typography variant="body2">{candidateInfo.phone}</Typography>
// // //               </Box>

// // //               <Box display="flex" alignItems="center" mb={2}>
// // //                 <Work sx={{ mr: 2, color: "text.secondary" }} />
// // //                 <Typography variant="body2">{candidateInfo.department}</Typography>
// // //               </Box>

// // //               <Box display="flex" alignItems="center">
// // //                 <School sx={{ mr: 2, color: "text.secondary" }} />
// // //                 <Typography variant="body2">{candidateInfo.evaluatorTitle}</Typography>
// // //               </Box>
// // //             </CardContent>
// // //           </Card>

// // //           {/* Performance Metrics */}
// // //           <Card elevation={2} sx={{ mt: 3 }}>
// // //             <CardContent>
// // //               <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
// // //                 <Assessment sx={{ mr: 1, verticalAlign: "middle" }} />
// // //                 Performance Metrics
// // //               </Typography>
// // //               <Divider sx={{ mb: 2 }} />

// // //               {performanceMetrics.map((metric, index) => (
// // //                 <Box key={index} mb={2}>
// // //                   <Box display="flex" justifyContent="space-between" mb={1}>
// // //                     <Typography variant="body2" fontWeight="medium">
// // //                       {metric.label}
// // //                     </Typography>
// // //                     <Typography variant="body2" fontWeight="bold">
// // //                       {metric.value}%
// // //                     </Typography>
// // //                   </Box>
// // //                   <LinearProgress
// // //                     variant="determinate"
// // //                     value={metric.value}
// // //                     sx={{
// // //                       height: 8,
// // //                       borderRadius: 4,
// // //                       backgroundColor: "grey.200",
// // //                       "& .MuiLinearProgress-bar": {
// // //                         backgroundColor: metric.color,
// // //                         borderRadius: 4,
// // //                       },
// // //                     }}
// // //                   />
// // //                 </Box>
// // //               ))}
// // //             </CardContent>
// // //           </Card>
// // //         </Grid>

// // //         {/* Skills Assessment */}
// // //         <Grid item xs={12} md={8}>
// // //           <Card elevation={2}>
// // //             <CardContent>
// // //               <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
// // //                 <Star sx={{ mr: 1, verticalAlign: "middle" }} />
// // //                 Skills Assessment
// // //               </Typography>
// // //               <Divider sx={{ mb: 3 }} />

// // //               <Grid container spacing={3}>
// // //                 {skillCategories.map((skill, index) => (
// // //                   <Grid item xs={12} sm={6} key={index}>
// // //                     <Box>
// // //                       <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
// // //                         <Typography variant="body1" fontWeight="medium">
// // //                           {skill.name}
// // //                         </Typography>
// // //                         <Chip
// // //                           label={skill.rating}
// // //                           size="small"
// // //                           sx={{
// // //                             backgroundColor: skill.color,
// // //                             color: "white",
// // //                             fontWeight: "bold",
// // //                           }}
// // //                         />
// // //                       </Box>
// // //                       <Rating
// // //                         value={skill.rating}
// // //                         precision={0.5}
// // //                         readOnly
// // //                         sx={{
// // //                           "& .MuiRating-iconFilled": {
// // //                             color: skill.color,
// // //                           },
// // //                         }}
// // //                       />
// // //                     </Box>
// // //                   </Grid>
// // //                 ))}
// // //               </Grid>
// // //             </CardContent>
// // //           </Card>

// // //           {/* Evaluation Form */}
// // //           <Card elevation={2} sx={{ mt: 3 }}>
// // //             <CardContent>
// // //               <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
// // //                 <TrendingUp sx={{ mr: 1, verticalAlign: "middle" }} />
// // //                 Detailed Evaluation
// // //               </Typography>
// // //               <Divider sx={{ mb: 3 }} />

// // //               <Grid container spacing={3}>
// // //                 {/* Overall Rating */}
// // //                 <Grid item xs={12} sm={6}>
// // //                   <Typography variant="body1" fontWeight="medium" gutterBottom>
// // //                     Overall Performance Rating
// // //                   </Typography>
// // //                   <Rating
// // //                     value={formData.overallRating}
// // //                     onChange={(event, newValue) => handleRatingChange("overallRating", newValue)}
// // //                     size="large"
// // //                     sx={{ mb: 2 }}
// // //                   />
// // //                 </Grid>

// // //                 {/* Recommendation */}
// // //                 <Grid item xs={12} sm={6}>
// // //                   <FormControl fullWidth>
// // //                     <InputLabel>Recommendation</InputLabel>
// // //                     <Select
// // //                       value={formData.recommendation}
// // //                       label="Recommendation"
// // //                       onChange={(e) => handleRatingChange("recommendation", e.target.value)}
// // //                     >
// // //                       <MenuItem value="highly-recommend">Highly Recommend</MenuItem>
// // //                       <MenuItem value="recommend">Recommend</MenuItem>
// // //                       <MenuItem value="neutral">Neutral</MenuItem>
// // //                       <MenuItem value="not-recommend">Do Not Recommend</MenuItem>
// // //                     </Select>
// // //                   </FormControl>
// // //                 </Grid>

// // //                 {/* Key Strengths */}
// // //                 <Grid item xs={12}>
// // //                   <FormLabel component="legend" sx={{ fontWeight: "medium", mb: 2 }}>
// // //                     Key Strengths (Select all that apply)
// // //                   </FormLabel>
// // //                   <FormGroup row>
// // //                     {strengthOptions.map((strength) => (
// // //                       <FormControlLabel
// // //                         key={strength}
// // //                         control={
// // //                           <Checkbox
// // //                             checked={formData.strengths.includes(strength)}
// // //                             onChange={() => handleStrengthToggle(strength)}
// // //                             color="primary"
// // //                           />
// // //                         }
// // //                         label={strength}
// // //                         sx={{ minWidth: "200px" }}
// // //                       />
// // //                     ))}
// // //                   </FormGroup>
// // //                 </Grid>

// // //                 {/* Areas for Improvement */}
// // //                 <Grid item xs={12}>
// // //                   <TextField
// // //                     fullWidth
// // //                     multiline
// // //                     rows={3}
// // //                     label="Areas for Improvement"
// // //                     placeholder="Describe specific areas where the employee can improve..."
// // //                     value={formData.improvements}
// // //                     onChange={(e) => handleRatingChange("improvements", e.target.value)}
// // //                   />
// // //                 </Grid>

// // //                 {/* Additional Comments */}
// // //                 <Grid item xs={12}>
// // //                   <TextField
// // //                     fullWidth
// // //                     multiline
// // //                     rows={4}
// // //                     label="Additional Comments"
// // //                     placeholder="Any additional feedback or observations..."
// // //                     value={formData.additionalComments}
// // //                     onChange={(e) => handleRatingChange("additionalComments", e.target.value)}
// // //                   />
// // //                 </Grid>

// // //                 {/* Action Buttons */}
// // //                 <Grid item xs={12}>
// // //                   <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
// // //                     <Button variant="outlined" size="large">
// // //                       Save Draft
// // //                     </Button>
// // //                     <Button
// // //                       variant="contained"
// // //                       size="large"
// // //                       startIcon={<CheckCircle />}
// // //                       sx={{
// // //                         background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
// // //                         boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
// // //                       }}
// // //                     >
// // //                       Submit Evaluation
// // //                     </Button>
// // //                   </Box>
// // //                 </Grid>
// // //               </Grid>
// // //             </CardContent>
// // //           </Card>
// // //         </Grid>
// // //       </Grid>
// // //     </Container>
// // //     </Layout>
// // //   )
// // // }


// // // "use client"

// // // import { useState } from "react"
// // // import {
// // //   Container,
// // //   Paper,
// // //   Typography,
// // //   Grid,
// // //   Card,
// // //   CardContent,
// // //   Rating,
// // //   TextField,
// // //   Button,
// // //   Chip,
// // //   Box,
// // //   Avatar,
// // //   Divider,
// // //   LinearProgress,
// // //   FormControl,
// // //   InputLabel,
// // //   Select,
// // //   MenuItem,
// // //   FormControlLabel,
// // //   FormLabel,
// // //   Checkbox,
// // //   FormGroup,
// // // } from "@mui/material"
// // // import { Person, Email, Phone, Work, School, Star, TrendingUp, Assessment, CheckCircle } from "@mui/icons-material"
// // // import Layout from "../common/Layout";


// // // export default function EvaluationPage() {
// // //   const [formData, setFormData] = useState({
// // //     overallRating: 4,
// // //     technicalSkills: 4.5,
// // //     communication: 4,
// // //     teamwork: 5,
// // //     problemSolving: 4,
// // //     leadership: 3.5,
// // //     recommendation: "highly-recommend",
// // //     strengths: [],
// // //     improvements: "",
// // //     additionalComments: "",
// // //   })

// // //   // Static evaluation data
// // //   const candidateInfo = {
// // //     name: "Sarah Johnson",
// // //     email: "sarah.johnson@email.com",
// // //     phone: "+1 (555) 123-4567",
// // //     position: "Senior Frontend Developer",
// // //     department: "Engineering",
// // //     evaluationDate: "March 15, 2024",
// // //     evaluator: "Michael Chen",
// // //     evaluatorTitle: "Engineering Manager",
// // //     avatar: "/professional-woman-diverse.png",
// // //   }

// // //   const skillCategories = [
// // //     { name: "Technical Skills", rating: 4.5, color: "#1976d2" },
// // //     { name: "Communication", rating: 4.0, color: "#388e3c" },
// // //     { name: "Problem Solving", rating: 4.2, color: "#f57c00" },
// // //     { name: "Teamwork", rating: 4.8, color: "#7b1fa2" },
// // //     { name: "Leadership", rating: 3.8, color: "#d32f2f" },
// // //     { name: "Adaptability", rating: 4.3, color: "#0288d1" },
// // //   ]

// // //   const performanceMetrics = [
// // //     { label: "Project Completion Rate", value: 95, color: "#4caf50" },
// // //     { label: "Code Quality Score", value: 88, color: "#2196f3" },
// // //     { label: "Client Satisfaction", value: 92, color: "#ff9800" },
// // //     { label: "Team Collaboration", value: 96, color: "#9c27b0" },
// // //   ]

// // //   const strengthOptions = [
// // //     "Technical Expertise",
// // //     "Problem Solving",
// // //     "Communication",
// // //     "Leadership",
// // //     "Creativity",
// // //     "Time Management",
// // //     "Team Collaboration",
// // //     "Adaptability",
// // //   ]

// // //   const handleRatingChange = (field, value) => {
// // //     setFormData((prev) => ({
// // //       ...prev,
// // //       [field]: value,
// // //     }))
// // //   }

// // //   const handleStrengthToggle = (strength) => {
// // //     setFormData((prev) => ({
// // //       ...prev,
// // //       strengths: prev.strengths.includes(strength)
// // //         ? prev.strengths.filter((s) => s !== strength)
// // //         : [...prev.strengths, strength],
// // //     }))
// // //   }

// // //   return (
// // //     <Layout title="Evaluation ">
// // //     <Container maxWidth="lg" sx={{ py: 4 }}>
// // //       {/* Header */}
// // //       <Paper
// // //         elevation={3}
// // //         sx={{ p: 3, mb: 4, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}
// // //       >
// // //         <Box display="flex" alignItems="center" gap={3}>
// // //           <Avatar src={candidateInfo.avatar} sx={{ width: 80, height: 80, border: "3px solid white" }} />
// // //           <Box flex={1}>
// // //             <Typography variant="h4" fontWeight="bold" gutterBottom>
// // //               Employee Evaluation
// // //             </Typography>
// // //             <Typography variant="h6" sx={{ opacity: 0.9 }}>
// // //               {candidateInfo.name} - {candidateInfo.position}
// // //             </Typography>
// // //             <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
// // //               Evaluation Date: {candidateInfo.evaluationDate} | Evaluator: {candidateInfo.evaluator}
// // //             </Typography>
// // //           </Box>
// // //           <Box textAlign="center">
// // //             <Typography variant="h3" fontWeight="bold">
// // //               {formData.overallRating}
// // //             </Typography>
// // //             <Typography variant="body2">Overall Rating</Typography>
// // //           </Box>
// // //         </Box>
// // //       </Paper>

// // //       <Grid container spacing={4}>
// // //         {/* Candidate Information */}
// // //         <Grid item xs={12} md={4}>
// // //           <Card elevation={2} sx={{ height: "fit-content" }}>
// // //             <CardContent>
// // //               <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
// // //                 <Person sx={{ mr: 1, verticalAlign: "middle" }} />
// // //                 Candidate Information
// // //               </Typography>
// // //               <Divider sx={{ mb: 2 }} />

// // //               <Box display="flex" alignItems="center" mb={2}>
// // //                 <Email sx={{ mr: 2, color: "text.secondary" }} />
// // //                 <Typography variant="body2">{candidateInfo.email}</Typography>
// // //               </Box>

// // //               <Box display="flex" alignItems="center" mb={2}>
// // //                 <Phone sx={{ mr: 2, color: "text.secondary" }} />
// // //                 <Typography variant="body2">{candidateInfo.phone}</Typography>
// // //               </Box>

// // //               <Box display="flex" alignItems="center" mb={2}>
// // //                 <Work sx={{ mr: 2, color: "text.secondary" }} />
// // //                 <Typography variant="body2">{candidateInfo.department}</Typography>
// // //               </Box>

// // //               <Box display="flex" alignItems="center">
// // //                 <School sx={{ mr: 2, color: "text.secondary" }} />
// // //                 <Typography variant="body2">{candidateInfo.evaluatorTitle}</Typography>
// // //               </Box>
// // //             </CardContent>
// // //           </Card>

// // //           {/* Performance Metrics */}
// // //           <Card elevation={2} sx={{ mt: 3 }}>
// // //             <CardContent>
// // //               <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
// // //                 <Assessment sx={{ mr: 1, verticalAlign: "middle" }} />
// // //                 Performance Metrics
// // //               </Typography>
// // //               <Divider sx={{ mb: 2 }} />

// // //               {performanceMetrics.map((metric, index) => (
// // //                 <Box key={index} mb={2}>
// // //                   <Box display="flex" justifyContent="space-between" mb={1}>
// // //                     <Typography variant="body2" fontWeight="medium">
// // //                       {metric.label}
// // //                     </Typography>
// // //                     <Typography variant="body2" fontWeight="bold">
// // //                       {metric.value}%
// // //                     </Typography>
// // //                   </Box>
// // //                   <LinearProgress
// // //                     variant="determinate"
// // //                     value={metric.value}
// // //                     sx={{
// // //                       height: 8,
// // //                       borderRadius: 4,
// // //                       backgroundColor: "grey.200",
// // //                       "& .MuiLinearProgress-bar": {
// // //                         backgroundColor: metric.color,
// // //                         borderRadius: 4,
// // //                       },
// // //                     }}
// // //                   />
// // //                 </Box>
// // //               ))}
// // //             </CardContent>
// // //           </Card>
// // //         </Grid>

// // //         {/* Skills Assessment */}
// // //         <Grid item xs={12} md={8}>
// // //           <Card elevation={2}>
// // //             <CardContent>
// // //               <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
// // //                 <Star sx={{ mr: 1, verticalAlign: "middle" }} />
// // //                 Skills Assessment
// // //               </Typography>
// // //               <Divider sx={{ mb: 3 }} />

// // //               <Grid container spacing={3}>
// // //                 {skillCategories.map((skill, index) => (
// // //                   <Grid item xs={12} sm={6} key={index}>
// // //                     <Box>
// // //                       <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
// // //                         <Typography variant="body1" fontWeight="medium">
// // //                           {skill.name}
// // //                         </Typography>
// // //                         <Chip
// // //                           label={skill.rating}
// // //                           size="small"
// // //                           sx={{
// // //                             backgroundColor: skill.color,
// // //                             color: "white",
// // //                             fontWeight: "bold",
// // //                           }}
// // //                         />
// // //                       </Box>
// // //                       <Rating
// // //                         value={skill.rating}
// // //                         precision={0.5}
// // //                         readOnly
// // //                         sx={{
// // //                           "& .MuiRating-iconFilled": {
// // //                             color: skill.color,
// // //                           },
// // //                         }}
// // //                       />
// // //                     </Box>
// // //                   </Grid>
// // //                 ))}
// // //               </Grid>
// // //             </CardContent>
// // //           </Card>

// // //           {/* Evaluation Form */}
// // //           <Card elevation={2} sx={{ mt: 3 }}>
// // //             <CardContent>
// // //               <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
// // //                 <TrendingUp sx={{ mr: 1, verticalAlign: "middle" }} />
// // //                 Detailed Evaluation
// // //               </Typography>
// // //               <Divider sx={{ mb: 3 }} />

// // //               <Grid container spacing={3}>
// // //                 {/* Overall Rating */}
// // //                 <Grid item xs={12} sm={6}>
// // //                   <Typography variant="body1" fontWeight="medium" gutterBottom>
// // //                     Overall Performance Rating
// // //                   </Typography>
// // //                   <Rating
// // //                     value={formData.overallRating}
// // //                     onChange={(event, newValue) => handleRatingChange("overallRating", newValue)}
// // //                     size="large"
// // //                     sx={{ mb: 2 }}
// // //                   />
// // //                 </Grid>

// // //                 {/* Recommendation */}
// // //                 <Grid item xs={12} sm={6}>
// // //                   <FormControl fullWidth>
// // //                     <InputLabel>Recommendation</InputLabel>
// // //                     <Select
// // //                       value={formData.recommendation}
// // //                       label="Recommendation"
// // //                       onChange={(e) => handleRatingChange("recommendation", e.target.value)}
// // //                     >
// // //                       <MenuItem value="highly-recommend">Highly Recommend</MenuItem>
// // //                       <MenuItem value="recommend">Recommend</MenuItem>
// // //                       <MenuItem value="neutral">Neutral</MenuItem>
// // //                       <MenuItem value="not-recommend">Do Not Recommend</MenuItem>
// // //                     </Select>
// // //                   </FormControl>
// // //                 </Grid>

// // //                 {/* Key Strengths */}
// // //                 <Grid item xs={12}>
// // //                   <FormLabel component="legend" sx={{ fontWeight: "medium", mb: 2 }}>
// // //                     Key Strengths (Select all that apply)
// // //                   </FormLabel>
// // //                   <FormGroup row>
// // //                     {strengthOptions.map((strength) => (
// // //                       <FormControlLabel
// // //                         key={strength}
// // //                         control={
// // //                           <Checkbox
// // //                             checked={formData.strengths.includes(strength)}
// // //                             onChange={() => handleStrengthToggle(strength)}
// // //                             color="primary"
// // //                           />
// // //                         }
// // //                         label={strength}
// // //                         sx={{ minWidth: "200px" }}
// // //                       />
// // //                     ))}
// // //                   </FormGroup>
// // //                 </Grid>

// // //                 {/* Areas for Improvement */}
// // //                 <Grid item xs={12}>
// // //                   <TextField
// // //                     fullWidth
// // //                     multiline
// // //                     rows={3}
// // //                     label="Areas for Improvement"
// // //                     placeholder="Describe specific areas where the employee can improve..."
// // //                     value={formData.improvements}
// // //                     onChange={(e) => handleRatingChange("improvements", e.target.value)}
// // //                   />
// // //                 </Grid>

// // //                 {/* Additional Comments */}
// // //                 <Grid item xs={12}>
// // //                   <TextField
// // //                     fullWidth
// // //                     multiline
// // //                     rows={4}
// // //                     label="Additional Comments"
// // //                     placeholder="Any additional feedback or observations..."
// // //                     value={formData.additionalComments}
// // //                     onChange={(e) => handleRatingChange("additionalComments", e.target.value)}
// // //                   />
// // //                 </Grid>

// // //                 {/* Action Buttons */}
// // //                 <Grid item xs={12}>
// // //                   <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
// // //                     <Button variant="outlined" size="large">
// // //                       Save Draft
// // //                     </Button>
// // //                     <Button
// // //                       variant="contained"
// // //                       size="large"
// // //                       startIcon={<CheckCircle />}
// // //                       sx={{
// // //                         background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
// // //                         boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
// // //                       }}
// // //                     >
// // //                       Submit Evaluation
// // //                     </Button>
// // //                   </Box>
// // //                 </Grid>
// // //               </Grid>
// // //             </CardContent>
// // //           </Card>
// // //         </Grid>
// // //       </Grid>
// // //     </Container>
// // //     </Layout>
// // //   )
// // // }
